import React from "react";
import i18next from "i18next";
import _ from "lodash";

import { darkGrey, errorColor, infoColor, orangeColor, primary, purpleColor, theme } from "style/globalStyle";
import ColoredPrivilegeName from "containers/administration/roles/privileges/common/ColoredPrivilegeName";
import { CalculationResultType } from "./CalculatorUtils";
import { ActivityLevel, AttributeType, FieldDisplayVariant, FieldType, Operator, Subscription } from "./Constants";
import { formatDateOperatorLabel } from "./RulesUtils";
import {
  getEsgScopesFields,
  getEsgScoresColor,
  getFinancialStrengthScoreColorAndLabel,
} from './DataBlocksUtils';
import { hasOption } from "./Utils";
import i18n from '../../i18n/i18n';
import { EcovadisPerformanceColor, EcovadisCurrentStageProgressSteps, EcovadisScoresEnum, EVScorePerfomance } from "./EcovadisUtils";
import { getInduedColor } from "./InduedUtils";
import { SecurityRatingGlobalScorePerformanceColor, SecurityRatingGlobalScoreMaturityEnum, SecurityRatingPerformanceColor, SecurityRatingPerformanceEnum, SecurityRatingScoresEnum } from "./SecurityRatingUtils";
import { CreditsafeForEventsBasic, CreditsafeForEventsWithDetail } from "./CreditsafeUtils";

export const RoleBasedAccessControlContext = React.createContext({
  hasAnyOfPrivileges: () => { return false },
  hasAnyOfPrivilegesOnScopedResourceImpl: () => { return false },
  hasAnyOfPrivilegesOnPropertyId: () => {return false},
  isGlobalAdmin: false,
  isSigUser: false
});

export const SOURCE_TAB_OPTIONS = [
  Subscription.INDUED, 
  Subscription.TRANSPARENCY, 
  Subscription.DATABLOCKS_FINANCIAL_STRENGTH, 
  Subscription.DATABLOCKS_ESG,
  Subscription.OWLIN, 
  Subscription.ECOVADIS, 
  Subscription.DATA_HUB
];

export const RolePrivilegesEditorContext = React.createContext({
  isCurrentReadOnly: false,
});

export const isEventPrivilege = (privilegeName) => {
  return privilegeName.startsWith("PRIVILEGE_EVENT") && !privilegeName.includes("TASK");
}
export const isEventTaskPrivilege = (privilegeName) => {
  return privilegeName.startsWith("PRIVILEGE_EVENT") && privilegeName.includes("TASK");
}

export const ROLE_NAME_MAX_LENGTH = 100;

export const EVENT_CHECK_INDETERMINED = "INDETERMINED";


export const ScopedResourceType = {
  THIRD_PARTY: 'THIRD_PARTY',
  DOSSIER: 'DOSSIER',
  DOCUMENT: 'DOCUMENT'
}

const PrivilegeType = {
  CREATE: 'CREATE',
  VIEW: 'VIEW',
  EDIT: 'EDIT',
  DELETE: 'DELETE',
  OTHER: 'OTHER',
  ALL: 'ALL'
}

//!\\ DO NOT MODIFY
function createPrivilegeEnum() {

  const privileges = {
    //Personalization
    PRIVILEGE_PERSONALIZE_THIRD_PARTY_LABEL: {
      name: "PRIVILEGE_PERSONALIZE_THIRD_PARTY_LABEL",
      childPrivileges: [],
      privilegeType: PrivilegeType.EDIT
    },
    PRIVILEGE_PERSONALIZE: {
      name: "PRIVILEGE_PERSONALIZE",
      childPrivileges: [],
      privilegeType: PrivilegeType.EDIT
    },


    //Configuration
    PRIVILEGE_VIEW_THIRD_PARTY_ATTRIBUTE: {
      name: "PRIVILEGE_VIEW_THIRD_PARTY_ATTRIBUTE",
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_EDIT_THIRD_PARTY_ATTRIBUTE_OPTIONS: {
      name: "PRIVILEGE_EDIT_THIRD_PARTY_ATTRIBUTE_OPTIONS",
      childPrivileges: [],
      privilegeType: PrivilegeType.EDIT
    },
    PRIVILEGE_EDIT_THIRD_PARTY_ATTRIBUTE: {
      name: "PRIVILEGE_EDIT_THIRD_PARTY_ATTRIBUTE",
      childPrivileges: [],
      privilegeType: PrivilegeType.EDIT
    },
    PRIVILEGE_CREATE_THIRD_PARTY_ATTRIBUTE: {
      name: "PRIVILEGE_CREATE_THIRD_PARTY_ATTRIBUTE",
      childPrivileges: [],
      privilegeType: PrivilegeType.CREATE
    },
    PRIVILEGE_DELETE_THIRD_PARTY_ATTRIBUTE: {
      name: "PRIVILEGE_DELETE_THIRD_PARTY_ATTRIBUTE",
      childPrivileges: [],
      privilegeType: PrivilegeType.CREATE
    },
    PRIVILEGE_ALL_THIRD_PARTY_ATTRIBUTE: {
      name: "PRIVILEGE_ALL_THIRD_PARTY_ATTRIBUTE",
      childPrivileges: [],
      privilegeType: PrivilegeType.ALL
    },

    PRIVILEGE_VIEW_DOSSIER_ATTRIBUTE: {
      name: "PRIVILEGE_VIEW_DOSSIER_ATTRIBUTE",
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_EDIT_DOSSIER_ATTRIBUTE_OPTIONS: {
      name: "PRIVILEGE_EDIT_DOSSIER_ATTRIBUTE_OPTIONS",
      childPrivileges: [],
      privilegeType: PrivilegeType.EDIT
    },
    PRIVILEGE_EDIT_DOSSIER_ATTRIBUTE: {
      name: "PRIVILEGE_EDIT_DOSSIER_ATTRIBUTE",
      childPrivileges: [],
      privilegeType: PrivilegeType.EDIT
    },
    PRIVILEGE_CREATE_DOSSIER_ATTRIBUTE: {
      name: "PRIVILEGE_CREATE_DOSSIER_ATTRIBUTE",
      childPrivileges: [],
      privilegeType: PrivilegeType.CREATE
    },
    PRIVILEGE_DELETE_DOSSIER_ATTRIBUTE: {
      name: "PRIVILEGE_DELETE_DOSSIER_ATTRIBUTE",
      childPrivileges: [],
      privilegeType: PrivilegeType.DELETE
    },
    PRIVILEGE_ALL_DOSSIER_ATTRIBUTE: {
      name: "PRIVILEGE_ALL_DOSSIER_ATTRIBUTE",
      childPrivileges: [],
      privilegeType: PrivilegeType.ALL
    },

    PRIVILEGE_VIEW_CONTEXT_ATTRIBUTE: {
      name: "PRIVILEGE_VIEW_CONTEXT_ATTRIBUTE",
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_EDIT_CONTEXT_ATTRIBUTE_VALUE: {
      name: "PRIVILEGE_EDIT_CONTEXT_ATTRIBUTE_VALUE",
      childPrivileges: [],
      privilegeType: PrivilegeType.EDIT
    },
    PRIVILEGE_EDIT_CONTEXT_ATTRIBUTE: {
      name: "PRIVILEGE_EDIT_CONTEXT_ATTRIBUTE",
      childPrivileges: [],
      privilegeType: PrivilegeType.EDIT
    },
    PRIVILEGE_CREATE_CONTEXT_ATTRIBUTE: {
      name: "PRIVILEGE_CREATE_CONTEXT_ATTRIBUTE",
      childPrivileges: [],
      privilegeType: PrivilegeType.CREATE
    },
    PRIVILEGE_DELETE_CONTEXT_ATTRIBUTE: {
      name: "PRIVILEGE_DELETE_CONTEXT_ATTRIBUTE",
      childPrivileges: [],
      privilegeType: PrivilegeType.CREATE
    },
    PRIVILEGE_ALL_CONTEXT_ATTRIBUTE: {
      name: "PRIVILEGE_ALL_CONTEXT_ATTRIBUTE",
      childPrivileges: [],
      privilegeType: PrivilegeType.ALL
    },

    // Partners attributes
    PRIVILEGE_VIEW_PARTNER_ATTRIBUTE: {
      name: "PRIVILEGE_VIEW_PARTNER_ATTRIBUTE",
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_EDIT_PARTNER_ATTRIBUTE_OPTIONS: {
      name: "PRIVILEGE_EDIT_PARTNER_ATTRIBUTE_OPTIONS",
      childPrivileges: [],
      privilegeType: PrivilegeType.EDIT
    },
    PRIVILEGE_EDIT_PARTNER_ATTRIBUTE: {
      name: "PRIVILEGE_EDIT_PARTNER_ATTRIBUTE",
      childPrivileges: [],
      privilegeType: PrivilegeType.EDIT
    },
    PRIVILEGE_CREATE_PARTNER_ATTRIBUTE: {
      name: "PRIVILEGE_CREATE_PARTNER_ATTRIBUTE",
      childPrivileges: [],
      privilegeType: PrivilegeType.CREATE
    },
    PRIVILEGE_DELETE_PARTNER_ATTRIBUTE: {
      name: "PRIVILEGE_DELETE_PARTNER_ATTRIBUTE",
      childPrivileges: [],
      privilegeType: PrivilegeType.CREATE
    },
    PRIVILEGE_ALL_PARTNER_ATTRIBUTE: {
      name: "PRIVILEGE_ALL_PARTNER_ATTRIBUTE",
      childPrivileges: [],
      privilegeType: PrivilegeType.ALL
    },

    PRIVILEGE_VIEW_RULE: {
      name: "PRIVILEGE_VIEW_RULE",
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_EDIT_RULE_PARAMETERS: {
      name: "PRIVILEGE_EDIT_RULE_PARAMETERS",
      childPrivileges: [],
      privilegeType: PrivilegeType.EDIT
    },
    PRIVILEGE_EDIT_RULE_STATUS: {
      name: "PRIVILEGE_EDIT_RULE_STATUS",
      childPrivileges: [],
      privilegeType: PrivilegeType.EDIT
    },
    PRIVILEGE_EDIT_RULE: {
      name: "PRIVILEGE_EDIT_RULE",
      childPrivileges: [],
      privilegeType: PrivilegeType.EDIT
    },
    PRIVILEGE_CREATE_RULE: {
      name: "PRIVILEGE_CREATE_RULE",
      childPrivileges: [],
      privilegeType: PrivilegeType.CREATE
    },
    PRIVILEGE_DELETE_RULE: {
      name: "PRIVILEGE_DELETE_RULE",
      childPrivileges: [],
      privilegeType: PrivilegeType.DELETE
    },
    PRIVILEGE_ALL_RULE: {
      name: "PRIVILEGE_ALL_RULE",
      childPrivileges: [],
      privilegeType: PrivilegeType.ALL
    },

    PRIVILEGE_VIEW_CALCULATION: {
      name: "PRIVILEGE_VIEW_CALCULATION",
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_EDIT_CALCULATION_PARAMETERS: {
      name: "PRIVILEGE_EDIT_CALCULATION_PARAMETERS",
      childPrivileges: [],
      privilegeType: PrivilegeType.EDIT
    },
    PRIVILEGE_EDIT_CALCULATION_STATUS: {
      name: "PRIVILEGE_EDIT_CALCULATION_STATUS",
      childPrivileges: [],
      privilegeType: PrivilegeType.EDIT
    },
    PRIVILEGE_EDIT_CALCULATION: {
      name: "PRIVILEGE_EDIT_CALCULATION",
      childPrivileges: [],
      privilegeType: PrivilegeType.EDIT
    },
    PRIVILEGE_CREATE_CALCULATION: {
      name: "PRIVILEGE_CREATE_CALCULATION",
      childPrivileges: [],
      privilegeType: PrivilegeType.CREATE
    },
    PRIVILEGE_DELETE_CALCULATION: {
      name: "PRIVILEGE_DELETE_CALCULATION",
      childPrivileges: [],
      privilegeType: PrivilegeType.DELETE
    },
    PRIVILEGE_ALL_CALCULATION: {
      name: "PRIVILEGE_ALL_CALCULATION",
      childPrivileges: [],
      privilegeType: PrivilegeType.ALL
    },

    PRIVILEGE_VIEW_THEME: {
      name: "PRIVILEGE_VIEW_THEME",
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_EDIT_THEME_PARAMETERS: {
      name: "PRIVILEGE_EDIT_THEME_PARAMETERS",
      childPrivileges: [],
      privilegeType: PrivilegeType.EDIT
    },
    PRIVILEGE_EDIT_THEME_STATUS: {
      name: "PRIVILEGE_EDIT_THEME_STATUS",
      childPrivileges: [],
      privilegeType: PrivilegeType.EDIT
    },
    PRIVILEGE_EDIT_THEME: {
      name: "PRIVILEGE_EDIT_THEME",
      childPrivileges: [],
      privilegeType: PrivilegeType.EDIT
    },
    PRIVILEGE_CREATE_THEME: {
      name: "PRIVILEGE_CREATE_THEME",
      childPrivileges: [],
      privilegeType: PrivilegeType.CREATE
    },
    PRIVILEGE_DELETE_THEME: {
      name: "PRIVILEGE_DELETE_THEME",
      childPrivileges: [],
      privilegeType: PrivilegeType.DELETE
    },
    PRIVILEGE_ALL_THEME: {
      name: "PRIVILEGE_ALL_THEME",
      childPrivileges: [],
      privilegeType: PrivilegeType.ALL
    },

    PRIVILEGE_VIEW_CONSUMPTION: {
      name: "PRIVILEGE_VIEW_CONSUMPTION",
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },

    PRIVILEGE_VIEW_ROLE: {
      name: "PRIVILEGE_VIEW_ROLE",
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_EDIT_ROLE: {
      name: "PRIVILEGE_EDIT_ROLE",
      childPrivileges: [],
      privilegeType: PrivilegeType.EDIT
    },
    PRIVILEGE_CREATE_ROLE: {
      name: "PRIVILEGE_CREATE_ROLE",
      childPrivileges: [],
      privilegeType: PrivilegeType.CREATE
    },
    PRIVILEGE_DELETE_ROLE: {
      name: "PRIVILEGE_DELETE_ROLE",
      childPrivileges: [],
      privilegeType: PrivilegeType.DELETE
    },
    PRIVILEGE_ALL_ROLE: {
      name: "PRIVILEGE_ALL_ROLE",
      childPrivileges: [],
      privilegeType: PrivilegeType.ALL
    },

    PRIVILEGE_VIEW_USER: {
      name: "PRIVILEGE_VIEW_USER",
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_EDIT_USER: {
      name: "PRIVILEGE_EDIT_USER",
      childPrivileges: [],
      privilegeType: PrivilegeType.EDIT
    },
    PRIVILEGE_CREATE_USER: {
      name: "PRIVILEGE_CREATE_USER",
      childPrivileges: [],
      privilegeType: PrivilegeType.CREATE
    },
    PRIVILEGE_DELETE_USER: {
      name: "PRIVILEGE_DELETE_USER",
      childPrivileges: [],
      privilegeType: PrivilegeType.DELETE
    },
    PRIVILEGE_ALL_USER: {
      name: "PRIVILEGE_ALL_USER",
      childPrivileges: [],
      privilegeType: PrivilegeType.ALL
    },

    // Contexte : configuration
    PRIVILEGE_VIEW_CONTEXT_MODEL: {
      name: "PRIVILEGE_VIEW_CONTEXT_MODEL",
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_EDIT_CONTEXT_MODEL: {
      name: "PRIVILEGE_EDIT_CONTEXT_MODEL",
      childPrivileges: [],
      privilegeType: PrivilegeType.EDIT
    },
    PRIVILEGE_CREATE_CONTEXT_MODEL: {
      name: "PRIVILEGE_CREATE_CONTEXT_MODEL",
      childPrivileges: [],
      privilegeType: PrivilegeType.CREATE
    },
    PRIVILEGE_DELETE_CONTEXT_MODEL: {
      name: "PRIVILEGE_DELETE_CONTEXT_MODEL",
      childPrivileges: [],
      privilegeType: PrivilegeType.DELETE
    },
    PRIVILEGE_EDIT_CONTEXT_MODEL_STATUS: {
      name: "PRIVILEGE_EDIT_CONTEXT_MODEL_STATUS",
      childPrivileges: [],
      privilegeType: PrivilegeType.EDIT
    },
    PRIVILEGE_EDIT_CONTEXT_MODEL_PARAMETERS: {
      name: "PRIVILEGE_EDIT_CONTEXT_MODEL_PARAMETERS",
      childPrivileges: [],
      privilegeType: PrivilegeType.EDIT
    },

    // Demandes - Documents
    PRIVILEGE_VIEW_AUTHORIZED_DOCUMENT_TYPE: {
      name: "PRIVILEGE_VIEW_AUTHORIZED_DOCUMENT_TYPE",
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },

    // Demandes - Modèle de dossiers
    PRIVILEGE_VIEW_DOSSIER_TYPE: {
      name: "PRIVILEGE_VIEW_DOSSIER_TYPE",
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_EDIT_DOSSIER_TYPE_STATUS: {
      name: "PRIVILEGE_EDIT_DOSSIER_TYPE_STATUS",
      childPrivileges: [],
      privilegeType: PrivilegeType.EDIT
    },
    PRIVILEGE_EDIT_DOSSIER_TYPE_PARAMETERS: {
      name: "PRIVILEGE_EDIT_DOSSIER_TYPE_PARAMETERS",
      childPrivileges: [],
      privilegeType: PrivilegeType.EDIT
    },
    PRIVILEGE_EDIT_DOSSIER_TYPE: {
      name: "PRIVILEGE_EDIT_DOSSIER_TYPE",
      childPrivileges: [],
      privilegeType: PrivilegeType.EDIT
    },
    PRIVILEGE_CREATE_DOSSIER_TYPE: {
      name: "PRIVILEGE_CREATE_DOSSIER_TYPE",
      childPrivileges: [],
      privilegeType: PrivilegeType.CREATE
    },
    PRIVILEGE_DELETE_DOSSIER_TYPE: {
      name: "PRIVILEGE_DELETE_DOSSIER_TYPE",
      childPrivileges: [],
      privilegeType: PrivilegeType.DELETE
    },

    // Imports et mise à jour
    PRIVILEGE_VIEW_IMPORTS_AND_UPDATES: {
      name: "PRIVILEGE_VIEW_IMPORTS_AND_UPDATES",
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_CREATE_IMPORTS_AND_UPDATES: {
      name: "PRIVILEGE_CREATE_IMPORTS_AND_UPDATES",
      childPrivileges: [],
      privilegeType: PrivilegeType.CREATE
    },

    //Features
    PRIVILEGE_VIEW_THIRD_PARTY_ID_CARD: {
      name: "PRIVILEGE_VIEW_THIRD_PARTY_ID_CARD",
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },

    PRIVILEGE_VIEW_THIRD_PARTY_ATTRIBUTES_VALUES_AND_INDICATORS: {
      name: "PRIVILEGE_VIEW_THIRD_PARTY_ATTRIBUTES_VALUES_AND_INDICATORS",
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },

    PRIVILEGE_VIEW_THIRD_PARTY_ATTRIBUTE_VALUE_ALL: {
      name: "PRIVILEGE_VIEW_THIRD_PARTY_ATTRIBUTE_VALUE_ALL",
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_EDIT_THIRD_PARTY_ATTRIBUTE_VALUE_ALL: {
      name: "PRIVILEGE_EDIT_THIRD_PARTY_ATTRIBUTE_VALUE_ALL",
      childPrivileges: [],
      privilegeType: PrivilegeType.EDIT
    },
    PRIVILEGE_EDIT_THIRD_PARTY_ATTRIBUTE_VALUE: {
      name: "PRIVILEGE_EDIT_THIRD_PARTY_ATTRIBUTE_VALUE",
      childPrivileges: [],
      privilegeType: PrivilegeType.EDIT
    },
    PRIVILEGE_VIEW_THIRD_PARTY_ATTRIBUTE_VALUE: {
      name: "PRIVILEGE_VIEW_THIRD_PARTY_ATTRIBUTE_VALUE",
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_VIEW_THIRD_PARTY_CALCULATED_ATTRIBUTE_ALL: {
      name: "PRIVILEGE_VIEW_THIRD_PARTY_CALCULATED_ATTRIBUTE_ALL",
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_VIEW_THIRD_PARTY_CALCULATED_ATTRIBUTE: {
      name: "PRIVILEGE_VIEW_THIRD_PARTY_CALCULATED_ATTRIBUTE",
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_VIEW_THIRD_PARTY_INDICATOR_ALL: {
      name: "PRIVILEGE_VIEW_THIRD_PARTY_INDICATOR_ALL",
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_VIEW_THIRD_PARTY_INDICATOR: {
      name: "PRIVILEGE_VIEW_THIRD_PARTY_INDICATOR",
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },

    PRIVILEGE_VIEW_THIRD_PARTY_OFFICERS_AGENTS: {
      name: "PRIVILEGE_VIEW_THIRD_PARTY_OFFICERS_AGENTS",
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_VIEW_THIRD_PARTY_BALANCE_SHEET: {
      name: "PRIVILEGE_VIEW_THIRD_PARTY_BALANCE_SHEET",
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_VIEW_THIRD_PARTY_RISKS: {
      name: "PRIVILEGE_VIEW_THIRD_PARTY_RISKS",
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_VIEW_THIRD_PARTY_NOTES: {
      name: "PRIVILEGE_VIEW_THIRD_PARTY_NOTES",
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_CREATE_THIRD_PARTY_NOTES: {
      name: "PRIVILEGE_CREATE_THIRD_PARTY_NOTES",
      childPrivileges: [],
      privilegeType: PrivilegeType.CREATE
    },
    PRIVILEGE_EDIT_THIRD_PARTY_NOTES: {
      name: "PRIVILEGE_EDIT_THIRD_PARTY_NOTES",
      childPrivileges: [],
      privilegeType: PrivilegeType.EDIT
    },
    PRIVILEGE_CREATE_THIRD_PARTY_UNMANAGED: {
      name: "PRIVILEGE_CREATE_THIRD_PARTY_UNMANAGED",
      childPrivileges: [],
      privilegeType: PrivilegeType.CREATE
    },
    PRIVILEGE_EDIT_THIRD_PARTY_UNMANAGED: {
      name: "PRIVILEGE_EDIT_THIRD_PARTY_UNMANAGED",
      childPrivileges: [],
      privilegeType: PrivilegeType.EDIT
    },
    PRIVILEGE_VIEW_THIRD_PARTY_STATUS: {
      name: "PRIVILEGE_VIEW_THIRD_PARTY_STATUS",
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_VIEW_THIRD_PARTY: {
      name: "PRIVILEGE_VIEW_THIRD_PARTY",
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_EDIT_THIRD_PARTY: {
      name: "PRIVILEGE_EDIT_THIRD_PARTY",
      childPrivileges: [],
      privilegeType: PrivilegeType.EDIT
    },
    PRIVILEGE_CREATE_THIRD_PARTY: {
      name: "PRIVILEGE_CREATE_THIRD_PARTY",
      childPrivileges: [],
      privilegeType: PrivilegeType.CREATE
    },
    PRIVILEGE_ARCHIVE_THIRD_PARTY: {
      name: "PRIVILEGE_ARCHIVE_THIRD_PARTY",
      childPrivileges: [],
      privilegeType: PrivilegeType.DELETE
    },
    PRIVILEGE_ALL_THIRD_PARTY: {
      name: "PRIVILEGE_ALL_THIRD_PARTY",
      childPrivileges: [],
      privilegeType: PrivilegeType.ALL
    },

    PRIVILEGE_EDIT_THIRD_PARTY_CODE: {
          name: "PRIVILEGE_EDIT_THIRD_PARTY_CODE",
          childPrivileges: [],
          privilegeType: PrivilegeType.EDIT
    },

    PRIVILEGE_VIEW_DOSSIER_INFORMATION: {
      name: "PRIVILEGE_VIEW_DOSSIER_INFORMATION",
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_VIEW_DOSSIER_ATTRIBUTES_VALUES_AND_INDICATORS: {
      name: "PRIVILEGE_VIEW_DOSSIER_ATTRIBUTES_VALUES_AND_INDICATORS",
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },

    PRIVILEGE_VIEW_DOSSIER_ATTRIBUTE_VALUE_ALL: {
      name: "PRIVILEGE_VIEW_DOSSIER_ATTRIBUTE_VALUE_ALL",
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_EDIT_DOSSIER_ATTRIBUTE_VALUE_ALL: {
      name: "PRIVILEGE_EDIT_DOSSIER_ATTRIBUTE_VALUE_ALL",
      childPrivileges: [],
      privilegeType: PrivilegeType.EDIT
    },
    PRIVILEGE_VIEW_DOSSIER_ATTRIBUTE_VALUE: {
      name: "PRIVILEGE_VIEW_DOSSIER_ATTRIBUTE_VALUE",
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_EDIT_DOSSIER_ATTRIBUTE_VALUE: {
      name: "PRIVILEGE_EDIT_DOSSIER_ATTRIBUTE_VALUE",
      childPrivileges: [],
      privilegeType: PrivilegeType.EDIT
    },
    PRIVILEGE_VIEW_DOSSIER_INDICATOR: {
      name: "PRIVILEGE_VIEW_DOSSIER_INDICATOR",
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_VIEW_DOSSIER_INDICATOR_ALL: {
      name: "PRIVILEGE_VIEW_DOSSIER_INDICATOR_ALL",
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_VIEW_DOSSIER_CALCULATED_ATTRIBUTE: {
      name: "PRIVILEGE_VIEW_DOSSIER_CALCULATED_ATTRIBUTE",
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_VIEW_DOSSIER_CALCULATED_ATTRIBUTE_ALL: {
      name: "PRIVILEGE_VIEW_DOSSIER_CALCULATED_ATTRIBUTE_ALL",
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },

    PRIVILEGE_VIEW_DOSSIER_STATUS: {
      name: "PRIVILEGE_VIEW_DOSSIER_STATUS",
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_VIEW_DOSSIER: {
      name: "PRIVILEGE_VIEW_DOSSIER",
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_EDIT_DOSSIER_STATUS: {
      name: "PRIVILEGE_EDIT_DOSSIER_STATUS",
      childPrivileges: [],
      privilegeType: PrivilegeType.EDIT
    },
    PRIVILEGE_VIEW_DOSSIER_NOTES: {
      name: "PRIVILEGE_VIEW_DOSSIER_NOTES",
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_CREATE_DOSSIER_NOTES: {
      name: "PRIVILEGE_CREATE_DOSSIER_NOTES",
      childPrivileges: [],
      privilegeType: PrivilegeType.CREATE
    },
    PRIVILEGE_EDIT_DOSSIER_NOTES: {
      name: "PRIVILEGE_EDIT_DOSSIER_NOTES",
      childPrivileges: [],
      privilegeType: PrivilegeType.EDIT
    },
    PRIVILEGE_EDIT_DOSSIER: {
      name: "PRIVILEGE_EDIT_DOSSIER",
      childPrivileges: [],
      privilegeType: PrivilegeType.EDIT
    },
    PRIVILEGE_CREATE_DOSSIER: {
      name: "PRIVILEGE_CREATE_DOSSIER",
      childPrivileges: [],
      privilegeType: PrivilegeType.CREATE
    },
    PRIVILEGE_ARCHIVE_DOSSIER: {
      name: "PRIVILEGE_ARCHIVE_DOSSIER",
      childPrivileges: [],
      privilegeType: PrivilegeType.DELETE
    },
    PRIVILEGE_ALL_DOSSIER: {
      name: "PRIVILEGE_ALL_DOSSIER",
      childPrivileges: [],
      privilegeType: PrivilegeType.ALL
    },

    // Reports/Exports
    PRIVILEGE_VIEW_REPORTS_ALL: {
      name: "PRIVILEGE_VIEW_REPORTS_ALL",
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_VIEW_REPORTS: {
      name: "PRIVILEGE_VIEW_REPORTS",
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },

    PRIVILEGE_VIEW_DOCUMENT: {
      name: "PRIVILEGE_VIEW_DOCUMENT",
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_EDIT_DOCUMENT_ANSWER: {
      name: "PRIVILEGE_EDIT_DOCUMENT_ANSWER",
      childPrivileges: [],
      privilegeType: PrivilegeType.EDIT
    },
    PRIVILEGE_EDIT_DOCUMENT_STATUS: {
      name: "PRIVILEGE_EDIT_DOCUMENT_STATUS",
      childPrivileges: [],
      privilegeType: PrivilegeType.EDIT
    },
    PRIVILEGE_EDIT_DOCUMENT_APPROVE: {
      name: "PRIVILEGE_EDIT_DOCUMENT_APPROVE",
      childPrivileges: [],
      privilegeType: PrivilegeType.EDIT
    },
    PRIVILEGE_EDIT_DOCUMENT_REJECT: {
      name: "PRIVILEGE_EDIT_DOCUMENT_REJECT",
      childPrivileges: [],
      privilegeType: PrivilegeType.EDIT
    },
    PRIVILEGE_EDIT_DOCUMENT: {
      name: "PRIVILEGE_EDIT_DOCUMENT",
      childPrivileges: [],
      privilegeType: PrivilegeType.EDIT
    },
    PRIVILEGE_CREATE_DOCUMENT: {
      name: "PRIVILEGE_CREATE_DOCUMENT",
      childPrivileges: [],
      privilegeType: PrivilegeType.CREATE
    },
    PRIVILEGE_DELETE_DOCUMENT: {
      name: "PRIVILEGE_DELETE_DOCUMENT",
      childPrivileges: [],
      privilegeType: PrivilegeType.DELETE
    },
    PRIVILEGE_ALL_DOCUMENT: {
      name: "PRIVILEGE_ALL_DOCUMENT",
      childPrivileges: [],
      privilegeType: PrivilegeType.ALL
    },
    PRIVILEGE_VIEW_DOCUMENT_NOTES: {
      name: "PRIVILEGE_VIEW_DOCUMENT_NOTES",
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_CREATE_DOCUMENT_NOTES: {
      name: "PRIVILEGE_CREATE_DOCUMENT_NOTES",
      childPrivileges: [],
      privilegeType: PrivilegeType.CREATE
    },
    PRIVILEGE_EDIT_DOCUMENT_NOTES: {
      name: "PRIVILEGE_EDIT_DOCUMENT_NOTES",
      childPrivileges: [],
      privilegeType: PrivilegeType.EDIT
    },


    PRIVILEGE_VIEW_CONTACT: {
      name: "PRIVILEGE_VIEW_CONTACT",
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_EDIT_CONTACT: {
      name: "PRIVILEGE_EDIT_CONTACT",
      childPrivileges: [],
      privilegeType: PrivilegeType.EDIT
    },
    PRIVILEGE_CREATE_CONTACT: {
      name: "PRIVILEGE_CREATE_CONTACT",
      childPrivileges: [],
      privilegeType: PrivilegeType.CREATE
    },
    PRIVILEGE_DELETE_CONTACT: {
      name: "PRIVILEGE_DELETE_CONTACT",
      childPrivileges: [],
      privilegeType: PrivilegeType.DELETE
    },
    PRIVILEGE_ALL_CONTACT: {
      name: "PRIVILEGE_ALL_CONTACT",
      childPrivileges: [],
      privilegeType: PrivilegeType.ALL
    },

    //context
    PRIVILEGE_VIEW_CONTEXT: {
      name: "PRIVILEGE_VIEW_CONTEXT",
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_CREATE_CONTEXT: {
      name: "PRIVILEGE_CREATE_CONTEXT",
      childPrivileges: [],
      privilegeType: PrivilegeType.CREATE
    },
    PRIVILEGE_EDIT_CONTEXT: {
      name: "PRIVILEGE_EDIT_CONTEXT",
      childPrivileges: [],
      privilegeType: PrivilegeType.EDIT
    },
    PRIVILEGE_ARCHIVE_CONTEXT: {
      name: "PRIVILEGE_ARCHIVE_CONTEXT",
      childPrivileges: [],
      privilegeType: PrivilegeType.DELETE
    },
    PRIVILEGE_VIEW_SUPPLYCHAIN_CONTEXT: {
      name: "PRIVILEGE_VIEW_SUPPLYCHAIN_CONTEXT",
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_ACTIVATE_SUPPLYCHAIN_CONTEXT: {
      name: "PRIVILEGE_ACTIVATE_SUPPLYCHAIN_CONTEXT",
      childPrivileges: [],
      privilegeType: PrivilegeType.OTHER
    },
    PRIVILEGE_ADD_THIRDPARTY_CONTEXT: {
      name: "PRIVILEGE_ADD_THIRDPARTY_CONTEXT",
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_VIEW_CONTEXT_INFORMATION: {
      name: "PRIVILEGE_VIEW_CONTEXT_INFORMATION",
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_VIEW_CONTEXT_ATTRIBUTES: {
      name: "PRIVILEGE_VIEW_CONTEXT_ATTRIBUTES",
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_EDIT_CONTEXT_ATTRIBUTES: {
      name: "PRIVILEGE_EDIT_CONTEXT_ATTRIBUTES",
      childPrivileges: [],
      privilegeType: PrivilegeType.EDIT
    },
    PRIVILEGE_VIEW_CONTEXT_THIRD_PARTY_ATTRIBUTES: {
      name: "PRIVILEGE_VIEW_CONTEXT_THIRD_PARTY_ATTRIBUTES",
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_EDIT_CONTEXT_THIRD_PARTY_ATTRIBUTES: {
      name: "PRIVILEGE_EDIT_CONTEXT_THIRD_PARTY_ATTRIBUTES",
      childPrivileges: [],
      privilegeType: PrivilegeType.EDIT
    },
    PRIVILEGE_VIEW_CONTEXT_THIRD_PARTY_DEMAND_ATTRIBUTES: {
      name: "PRIVILEGE_VIEW_CONTEXT_THIRD_PARTY_DEMAND_ATTRIBUTES",
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_EDIT_CONTEXT_THIRD_PARTY_DEMAND_ATTRIBUTES: {
      name: "PRIVILEGE_EDIT_CONTEXT_THIRD_PARTY_DEMAND_ATTRIBUTES",
      childPrivileges: [],
      privilegeType: PrivilegeType.EDIT
    },
    PRIVILEGE_VIEW_CONTEXT_NOTES: {
      name: "PRIVILEGE_VIEW_CONTEXT_NOTES",
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_CREATE_CONTEXT_NOTES: {
      name: "PRIVILEGE_CREATE_CONTEXT_NOTES",
      childPrivileges: [],
      privilegeType: PrivilegeType.CREATE
    },
    PRIVILEGE_EDIT_CONTEXT_NOTES: {
      name: "PRIVILEGE_EDIT_CONTEXT_NOTES",
      childPrivileges: [],
      privilegeType: PrivilegeType.EDIT
    },
    PRIVILEGE_ALL_CONTEXT: {
      name: "PRIVILEGE_ALL_CONTEXT",
      childPrivileges: [],
      privilegeType: PrivilegeType.ALL
    },

    //Dashboard
    PRIVILEGE_VIEW_DASHBOARD: {
      name: "PRIVILEGE_VIEW_DASHBOARD",
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },


    // EVENTS
    // Events Dossiers
    PRIVILEGE_EVENT_DOSSIER: {
      name: "PRIVILEGE_EVENT_DOSSIER",
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_EVENT_DOSSIER_ADDED: {
      name: "PRIVILEGE_EVENT_DOSSIER_ADDED",
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_EVENT_DOSSIER_ARCHIVED: {
      name: "PRIVILEGE_EVENT_DOSSIER_ARCHIVED",
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_EVENT_DOSSIER_UPDATED: {
      name: "PRIVILEGE_EVENT_DOSSIER_UPDATED",
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_EVENT_DOSSIER_ATTRIBUTE_UPDATED: {
      name: "PRIVILEGE_EVENT_DOSSIER_ATTRIBUTE_UPDATED",
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_EVENT_DOSSIER_ATTRIBUTE_UPDATED_ALL: {
      name: "PRIVILEGE_EVENT_DOSSIER_ATTRIBUTE_UPDATED_ALL",
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_EVENT_DOSSIER_CALCULATED_ATTRIBUTE_UPDATED: {
      name : "PRIVILEGE_EVENT_DOSSIER_CALCULATED_ATTRIBUTE_UPDATED", 
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_EVENT_DOSSIER_CALCULATED_ATTRIBUTE_UPDATED_ALL: {
      name : "PRIVILEGE_EVENT_DOSSIER_CALCULATED_ATTRIBUTE_UPDATED_ALL", 
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_EVENT_DOSSIER_INDICATOR_UPDATED: {
      name : "PRIVILEGE_EVENT_DOSSIER_INDICATOR_UPDATED", 
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_EVENT_DOSSIER_INDICATOR_UPDATED_ALL: {
      name : "PRIVILEGE_EVENT_DOSSIER_INDICATOR_UPDATED_ALL", 
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    // PRIVILEGE_EVENT_DOSSIER_STATUS_UPDATED: {
    //   name: "PRIVILEGE_EVENT_DOSSIER_STATUS_UPDATED",
    //   childPrivileges: [],
    //   privilegeType: PrivilegeType.VIEW
    // },
    PRIVILEGE_EVENT_DOSSIER_COMMENT: {
      name: "PRIVILEGE_EVENT_DOSSIER_COMMENT",
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_EVENT_DOSSIER_COMMENT_ADDED: {
      name: "PRIVILEGE_EVENT_DOSSIER_COMMENT_ADDED",
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_EVENT_DOSSIER_COMMENT_UPDATED: {
      name: "PRIVILEGE_EVENT_DOSSIER_COMMENT_UPDATED",
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_EVENT_DOSSIER_COMMENT_DELETED: {
      name: "PRIVILEGE_EVENT_DOSSIER_COMMENT_DELETED",
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_EVENT_DOSSIER_COMMENT_MENTION_ADDED: {
      name: "PRIVILEGE_EVENT_DOSSIER_COMMENT_MENTION_ADDED",
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_EVENT_DOSSIER_COMMENT_MENTION_UPDATED: {
      name: "PRIVILEGE_EVENT_DOSSIER_COMMENT_MENTION_UPDATED",
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_EVENT_DOSSIER_COMMENT_MENTION_DELETED: {
      name: "PRIVILEGE_EVENT_DOSSIER_COMMENT_MENTION_DELETED",
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_EVENT_DOSSIER_STATUS_UPDATED_ABSENT: {
      name: "PRIVILEGE_EVENT_DOSSIER_STATUS_UPDATED_ABSENT",
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_EVENT_DOSSIER_STATUS_UPDATED_ONGOING: {
      name: "PRIVILEGE_EVENT_DOSSIER_STATUS_UPDATED_ONGOING",
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_EVENT_DOSSIER_STATUS_UPDATED_ACTION_REQUIRED: {
      name: "PRIVILEGE_EVENT_DOSSIER_STATUS_UPDATED_ACTION_REQUIRED",
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_EVENT_DOSSIER_STATUS_UPDATED_VALID: {
      name: "PRIVILEGE_EVENT_DOSSIER_STATUS_UPDATED_VALID",
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_EVENT_DOSSIER_STATUS_UPDATED_BELOW_EXPECTATION: {
      name: "PRIVILEGE_EVENT_DOSSIER_STATUS_UPDATED_BELOW_EXPECTATION",
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_EVENT_DOSSIER_STATUS_UPDATED_INVALID: {
      name: "PRIVILEGE_EVENT_DOSSIER_STATUS_UPDATED_INVALID",
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_EVENT_DOSSIER_STATUS_UPDATED: {
      name: "PRIVILEGE_EVENT_DOSSIER_STATUS_UPDATED",
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },

    // Event Document
    PRIVILEGE_EVENT_DOCUMENT: {
      name: "PRIVILEGE_EVENT_DOCUMENT",
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_EVENT_DOCUMENT_ADDED: {
      name: "PRIVILEGE_EVENT_DOCUMENT_ADDED",
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_EVENT_DOCUMENT_STOPPED: {
      name: "PRIVILEGE_EVENT_DOCUMENT_STOPPED",
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_EVENT_DOCUMENT_ANSWER_ADDED: {
      name: "PRIVILEGE_EVENT_DOCUMENT_ANSWER_ADDED",
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_EVENT_DOCUMENT_ANSWER_UPDATED: {
      name: "PRIVILEGE_EVENT_DOCUMENT_ANSWER_UPDATED",
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_EVENT_DOCUMENT_ANSWER_INTERRUPTED: {
      name: "PRIVILEGE_EVENT_DOCUMENT_ANSWER_INTERRUPTED",
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_EVENT_DOCUMENT_ANSWER_SCOPE_UPDATED: {
      name: "PRIVILEGE_EVENT_DOCUMENT_ANSWER_SCOPE_UPDATED",
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_EVENT_DOCUMENT_ANSWER_EXPIRED: {
      name: "PRIVILEGE_EVENT_DOCUMENT_ANSWER_EXPIRED",
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_EVENT_DOCUMENT_ANSWER_ACCEPTED: {
      name: "PRIVILEGE_EVENT_DOCUMENT_ANSWER_ACCEPTED",
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_EVENT_DOCUMENT_ANSWER_DENIED: {
      name: "PRIVILEGE_EVENT_DOCUMENT_ANSWER_DENIED",
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_EVENT_DOCUMENT_ANSWER_STATUS_UPDATE_ABSENT: {
      name: "PRIVILEGE_EVENT_DOCUMENT_ANSWER_STATUS_UPDATE_ABSENT",
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_EVENT_DOCUMENT_ANSWER_STATUS_UPDATE_ONGOING: {
      name: "PRIVILEGE_EVENT_DOCUMENT_ANSWER_STATUS_UPDATE_ONGOING",
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_EVENT_DOCUMENT_ANSWER_STATUS_UPDATE_ACTION_REQUIRED: {
      name: "PRIVILEGE_EVENT_DOCUMENT_ANSWER_STATUS_UPDATE_ACTION_REQUIRED",
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_EVENT_DOCUMENT_ANSWER_STATUS_UPDATE_VALID: {
      name: "PRIVILEGE_EVENT_DOCUMENT_ANSWER_STATUS_UPDATE_VALID",
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_EVENT_DOCUMENT_ANSWER_STATUS_UPDATE_BELOW_EXPECTATION: {
      name: "PRIVILEGE_EVENT_DOCUMENT_ANSWER_STATUS_UPDATE_BELOW_EXPECTATION",
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_EVENT_DOCUMENT_ANSWER_STATUS_UPDATE_INVALID: {
      name: "PRIVILEGE_EVENT_DOCUMENT_ANSWER_STATUS_UPDATE_INVALID",
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_EVENT_DOCUMENT_ANSWER_STATUS_UPDATE: {
      name: "PRIVILEGE_EVENT_DOCUMENT_ANSWER_STATUS_UPDATE",
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_EVENT_DOCUMENT_COMMENT: {
      name: "PRIVILEGE_EVENT_DOCUMENT_COMMENT",
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_EVENT_DOCUMENT_COMMENT_ADDED: {
      name: "PRIVILEGE_EVENT_DOCUMENT_COMMENT_ADDED",
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_EVENT_DOCUMENT_COMMENT_UPDATED: {
      name: "PRIVILEGE_EVENT_DOCUMENT_COMMENT_UPDATED",
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_EVENT_DOCUMENT_COMMENT_DELETED: {
      name: "PRIVILEGE_EVENT_DOCUMENT_COMMENT_DELETED",
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_EVENT_DOCUMENT_COMMENT_MENTION_ADDED: {
      name: "PRIVILEGE_EVENT_DOCUMENT_COMMENT_MENTION_ADDED",
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_EVENT_DOCUMENT_COMMENT_MENTION_UPDATED: {
      name: "PRIVILEGE_EVENT_DOCUMENT_COMMENT_MENTION_UPDATED",
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_EVENT_DOCUMENT_COMMENT_MENTION_DELETED: {
      name: "PRIVILEGE_EVENT_DOCUMENT_COMMENT_MENTION_DELETED",
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_EVENT_DOCUMENT_DELETION_WARNING: {
      name: "PRIVILEGE_EVENT_DOCUMENT_DELETION_WARNING",
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_EVENT_DOCUMENT_TASKS: {
      name: "PRIVILEGE_EVENT_DOCUMENT_TASKS",
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_EVENT_DOCUMENT_TASK_AWAITING_VALIDATION: {
      name: "PRIVILEGE_EVENT_DOCUMENT_TASK_AWAITING_VALIDATION",
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_EVENT_DOCUMENT_TASK_DUE_DILIGENCE: {
      name: "PRIVILEGE_EVENT_DOCUMENT_TASK_DUE_DILIGENCE",
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },

    PRIVILEGE_EVENT_TASK_INDUED_FALSE_POSITIVE: {
      name: "PRIVILEGE_EVENT_TASK_INDUED_FALSE_POSITIVE",
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    // PRIVILEGE_EVENT_DOCUMENT_INDUED: {
    //   name: "PRIVILEGE_EVENT_DOCUMENT_INDUED",
    //   childPrivileges: [],
    //   privilegeType: PrivilegeType.VIEW
    // },
    // PRIVILEGE_EVENT_DOCUMENT_INDUED_REPORT_GENERATED: {
    //   name: "PRIVILEGE_EVENT_DOCUMENT_INDUED_REPORT_GENERATED",
    //   childPrivileges: [],
    //   privilegeType: PrivilegeType.VIEW
    // },
    // PRIVILEGE_EVENT_DOCUMENT_INDUED_REPORT_RECEIVED: {
    //   name: "PRIVILEGE_EVENT_DOCUMENT_INDUED_REPORT_RECEIVED",
    //   childPrivileges: [],
    //   privilegeType: PrivilegeType.VIEW
    // },

    // Events Tiers
    PRIVILEGE_EVENT_THIRD_PARTY: {
      name : "PRIVILEGE_EVENT_THIRD_PARTY", 
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_EVENT_THIRD_PARTY_ADDED: {
      name: "PRIVILEGE_EVENT_THIRD_PARTY_ADDED",
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_EVENT_THIRD_PARTY_ARCHIVED: {
      name: "PRIVILEGE_EVENT_THIRD_PARTY_ARCHIVED",
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_EVENT_THIRD_PARTY_CODE_UPDATED: {
      name : "PRIVILEGE_EVENT_THIRD_PARTY_CODE_UPDATED",
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_EVENT_THIRD_PARTY_SOURCE_CHANGED_TO_UNMANAGED: {
      name : "PRIVILEGE_EVENT_THIRD_PARTY_SOURCE_CHANGED_TO_UNMANAGED",
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_EVENT_THIRD_PARTY_PROCEDURE_COLLECTIVE_UPDATED: {
      name : "PRIVILEGE_EVENT_THIRD_PARTY_PROCEDURE_COLLECTIVE_UPDATED",
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_EVENT_THIRD_PARTY_PROCEDURE_COLLECTIVE_UPDATED_ETAPE_01: {
      name : "PRIVILEGE_EVENT_THIRD_PARTY_PROCEDURE_COLLECTIVE_UPDATED_ETAPE_01",
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_EVENT_THIRD_PARTY_PROCEDURE_COLLECTIVE_UPDATED_ETAPE_02: {
      name : "PRIVILEGE_EVENT_THIRD_PARTY_PROCEDURE_COLLECTIVE_UPDATED_ETAPE_02",
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_EVENT_THIRD_PARTY_PROCEDURE_COLLECTIVE_UPDATED_ETAPE_03: {
      name : "PRIVILEGE_EVENT_THIRD_PARTY_PROCEDURE_COLLECTIVE_UPDATED_ETAPE_03",
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_EVENT_THIRD_PARTY_PROCEDURE_COLLECTIVE_UPDATED_ETAPE_04: {
      name : "PRIVILEGE_EVENT_THIRD_PARTY_PROCEDURE_COLLECTIVE_UPDATED_ETAPE_04",
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_EVENT_THIRD_PARTY_PROCEDURE_COLLECTIVE_UPDATED_ETAPE_05: {
      name : "PRIVILEGE_EVENT_THIRD_PARTY_PROCEDURE_COLLECTIVE_UPDATED_ETAPE_05",
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_EVENT_THIRD_PARTY_PROCEDURE_COLLECTIVE_UPDATED_ETAPE_06: {
      name : "PRIVILEGE_EVENT_THIRD_PARTY_PROCEDURE_COLLECTIVE_UPDATED_ETAPE_06",
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_EVENT_THIRD_PARTY_PROCEDURE_COLLECTIVE_UPDATED_ETAPE_07: {
      name : "PRIVILEGE_EVENT_THIRD_PARTY_PROCEDURE_COLLECTIVE_UPDATED_ETAPE_07",
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_EVENT_THIRD_PARTY_PROCEDURE_COLLECTIVE_UPDATED_ETAPE_08: {
      name : "PRIVILEGE_EVENT_THIRD_PARTY_PROCEDURE_COLLECTIVE_UPDATED_ETAPE_08",
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_EVENT_THIRD_PARTY_PROCEDURE_COLLECTIVE_UPDATED_ETAPE_09: {
      name : "PRIVILEGE_EVENT_THIRD_PARTY_PROCEDURE_COLLECTIVE_UPDATED_ETAPE_09",
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_EVENT_THIRD_PARTY_PROCEDURE_COLLECTIVE_UPDATED_ETAPE_10: {
      name : "PRIVILEGE_EVENT_THIRD_PARTY_PROCEDURE_COLLECTIVE_UPDATED_ETAPE_10",
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_EVENT_THIRD_PARTY_PROCEDURE_COLLECTIVE_UPDATED_ETAPE_11: {
      name : "PRIVILEGE_EVENT_THIRD_PARTY_PROCEDURE_COLLECTIVE_UPDATED_ETAPE_11",
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_EVENT_THIRD_PARTY_PROCEDURE_COLLECTIVE_UPDATED_ETAPE_12: {
      name : "PRIVILEGE_EVENT_THIRD_PARTY_PROCEDURE_COLLECTIVE_UPDATED_ETAPE_12",
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_EVENT_THIRD_PARTY_PROCEDURE_COLLECTIVE_UPDATED_ETAPE_13: {
      name : "PRIVILEGE_EVENT_THIRD_PARTY_PROCEDURE_COLLECTIVE_UPDATED_ETAPE_13",
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_EVENT_THIRD_PARTY_PROCEDURE_COLLECTIVE_UPDATED_ETAPE_14: {
      name : "PRIVILEGE_EVENT_THIRD_PARTY_PROCEDURE_COLLECTIVE_UPDATED_ETAPE_14",
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_EVENT_THIRD_PARTY_PROCEDURE_COLLECTIVE_UPDATED_ETAPE_15: {
      name : "PRIVILEGE_EVENT_THIRD_PARTY_PROCEDURE_COLLECTIVE_UPDATED_ETAPE_15",
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_EVENT_THIRD_PARTY_PROCEDURE_COLLECTIVE_UPDATED_ETAPE_16: {
      name : "PRIVILEGE_EVENT_THIRD_PARTY_PROCEDURE_COLLECTIVE_UPDATED_ETAPE_16",
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_EVENT_THIRD_PARTY_PROCEDURE_COLLECTIVE_UPDATED_ETAPE_17: {
      name : "PRIVILEGE_EVENT_THIRD_PARTY_PROCEDURE_COLLECTIVE_UPDATED_ETAPE_17",
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_EVENT_THIRD_PARTY_PROCEDURE_COLLECTIVE_UPDATED_ETAPE_18: {
      name : "PRIVILEGE_EVENT_THIRD_PARTY_PROCEDURE_COLLECTIVE_UPDATED_ETAPE_18",
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_EVENT_THIRD_PARTY_PROCEDURE_COLLECTIVE_UPDATED_ETAPE_19: {
      name : "PRIVILEGE_EVENT_THIRD_PARTY_PROCEDURE_COLLECTIVE_UPDATED_ETAPE_19",
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_EVENT_THIRD_PARTY_PROCEDURE_COLLECTIVE_UPDATED_ETAPE_20: {
      name : "PRIVILEGE_EVENT_THIRD_PARTY_PROCEDURE_COLLECTIVE_UPDATED_ETAPE_20",
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_EVENT_THIRD_PARTY_ATTRIBUTE_UPDATED: {
      name : "PRIVILEGE_EVENT_THIRD_PARTY_ATTRIBUTE_UPDATED", 
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_EVENT_THIRD_PARTY_LEGAL_ANNOUNCEMENT_CREATION: {
      name : "PRIVILEGE_EVENT_THIRD_PARTY_LEGAL_ANNOUNCEMENT_CREATION",
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_EVENT_THIRD_PARTY_LEGAL_ANNOUNCEMENT_CHANGES: {
      name : "PRIVILEGE_EVENT_THIRD_PARTY_LEGAL_ANNOUNCEMENT_CHANGES",
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_EVENT_THIRD_PARTY_LEGAL_ANNOUNCEMENT_CESSATION: {
      name : "PRIVILEGE_EVENT_THIRD_PARTY_LEGAL_ANNOUNCEMENT_CESSATION",
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_EVENT_THIRD_PARTY_LEGAL_ANNOUNCEMENT_TRANSFERT: {
      name : "PRIVILEGE_EVENT_THIRD_PARTY_LEGAL_ANNOUNCEMENT_TRANSFERT",
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_EVENT_THIRD_PARTY_ATTRIBUTE_UPDATED_ALL: {
      name : "PRIVILEGE_EVENT_THIRD_PARTY_ATTRIBUTE_UPDATED_ALL", 
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_EVENT_THIRD_PARTY_CALCULATED_ATTRIBUTE_UPDATED: {
      name : "PRIVILEGE_EVENT_THIRD_PARTY_CALCULATED_ATTRIBUTE_UPDATED", 
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_EVENT_THIRD_PARTY_CALCULATED_ATTRIBUTE_UPDATED_ALL: {
      name : "PRIVILEGE_EVENT_THIRD_PARTY_CALCULATED_ATTRIBUTE_UPDATED_ALL", 
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_EVENT_THIRD_PARTY_INDICATOR_UPDATED: {
      name : "PRIVILEGE_EVENT_THIRD_PARTY_INDICATOR_UPDATED", 
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_EVENT_THIRD_PARTY_INDICATOR_UPDATED_ALL: {
      name : "PRIVILEGE_EVENT_THIRD_PARTY_INDICATOR_UPDATED_ALL", 
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    //Events Notes de tiers
    PRIVILEGE_EVENT_THIRD_PARTY_COMMENT: {
      name: "PRIVILEGE_EVENT_THIRD_PARTY_COMMENT",
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_EVENT_THIRD_PARTY_COMMENT_ADDED: {
      name: "PRIVILEGE_EVENT_THIRD_PARTY_COMMENT_ADDED",
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_EVENT_THIRD_PARTY_COMMENT_UPDATED: {
      name: "PRIVILEGE_EVENT_THIRD_PARTY_COMMENT_UPDATED",
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_EVENT_THIRD_PARTY_COMMENT_DELETED: {
      name: "PRIVILEGE_EVENT_THIRD_PARTY_COMMENT_DELETED",
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_EVENT_THIRD_PARTY_COMMENT_MENTION_ADDED: {
      name: "PRIVILEGE_EVENT_THIRD_PARTY_COMMENT_MENTION_ADDED",
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_EVENT_THIRD_PARTY_COMMENT_MENTION_UPDATED: {
      name: "PRIVILEGE_EVENT_THIRD_PARTY_COMMENT_MENTION_UPDATED",
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_EVENT_THIRD_PARTY_COMMENT_MENTION_DELETED: {
      name: "PRIVILEGE_EVENT_THIRD_PARTY_COMMENT_MENTION_DELETED",
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    // Events ThirdParty Contacts
    PRIVILEGE_EVENT_THIRD_PARTY_CONTACT: {
      name: "PRIVILEGE_EVENT_THIRD_PARTY_CONTACT",
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_EVENT_THIRD_PARTY_CONTACT_INVITED: {
      name: "PRIVILEGE_EVENT_THIRD_PARTY_CONTACT_INVITED",
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_EVENT_THIRD_PARTY_CONTACT_RE_INVITED: {
      name: "PRIVILEGE_EVENT_THIRD_PARTY_CONTACT_RE_INVITED",
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_EVENT_THIRD_PARTY_CONTACT_RE_INVITED_AUTO: {
      name: "PRIVILEGE_EVENT_THIRD_PARTY_CONTACT_RE_INVITED_AUTO",
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_EVENT_THIRD_PARTY_CONTACT_DELETED: {
      name: "PRIVILEGE_EVENT_THIRD_PARTY_CONTACT_DELETED",
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_EVENT_THIRD_PARTY_CONTACT_DELETED_DUE_TO_NO_FAVORABLE_RESPONSE: {
      name: "PRIVILEGE_EVENT_THIRD_PARTY_CONTACT_DELETED_DUE_TO_NO_FAVORABLE_RESPONSE",
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_EVENT_THIRD_PARTY_CONTACT_WEEKLY_REMINDER: {
      name: "PRIVILEGE_EVENT_THIRD_PARTY_CONTACT_WEEKLY_REMINDER",
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    // ESG events
    PRIVILEGE_EVENT_THIRD_PARTY_ESG_UPDATED: {
      name : "PRIVILEGE_EVENT_THIRD_PARTY_ESG_UPDATED", 
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_EVENT_THIRD_PARTY_ESG_ACTIVATED: {
      name : "PRIVILEGE_EVENT_THIRD_PARTY_ESG_ACTIVATED", 
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_EVENT_THIRD_PARTY_ESG_DEACTIVATED: {
      name : "PRIVILEGE_EVENT_THIRD_PARTY_ESG_DEACTIVATED", 
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_EVENT_THIRD_PARTY_ESG_UPDATED_GLOBAL: {
      name : "PRIVILEGE_EVENT_THIRD_PARTY_ESG_UPDATED_GLOBAL", 
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_EVENT_THIRD_PARTY_ESG_UPDATED_GLOBAL_SCORE_1: {
      name : "PRIVILEGE_EVENT_THIRD_PARTY_ESG_UPDATED_GLOBAL_SCORE_1", 
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_EVENT_THIRD_PARTY_ESG_UPDATED_GLOBAL_SCORE_2: {
      name : "PRIVILEGE_EVENT_THIRD_PARTY_ESG_UPDATED_GLOBAL_SCORE_2", 
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_EVENT_THIRD_PARTY_ESG_UPDATED_GLOBAL_SCORE_3: {
      name : "PRIVILEGE_EVENT_THIRD_PARTY_ESG_UPDATED_GLOBAL_SCORE_3", 
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_EVENT_THIRD_PARTY_ESG_UPDATED_GLOBAL_SCORE_4: {
      name : "PRIVILEGE_EVENT_THIRD_PARTY_ESG_UPDATED_GLOBAL_SCORE_4", 
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_EVENT_THIRD_PARTY_ESG_UPDATED_GLOBAL_SCORE_5: {
      name : "PRIVILEGE_EVENT_THIRD_PARTY_ESG_UPDATED_GLOBAL_SCORE_5", 
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_EVENT_THIRD_PARTY_ESG_UPDATED_ENVIRONMENTAL: {
      name : "PRIVILEGE_EVENT_THIRD_PARTY_ESG_UPDATED_ENVIRONMENTAL", 
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_EVENT_THIRD_PARTY_ESG_UPDATED_ENVIRONMENTAL_SCORE_1: {
      name : "PRIVILEGE_EVENT_THIRD_PARTY_ESG_UPDATED_ENVIRONMENTAL_SCORE_1", 
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_EVENT_THIRD_PARTY_ESG_UPDATED_ENVIRONMENTAL_SCORE_2: {
      name : "PRIVILEGE_EVENT_THIRD_PARTY_ESG_UPDATED_ENVIRONMENTAL_SCORE_2", 
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_EVENT_THIRD_PARTY_ESG_UPDATED_ENVIRONMENTAL_SCORE_3: {
      name : "PRIVILEGE_EVENT_THIRD_PARTY_ESG_UPDATED_ENVIRONMENTAL_SCORE_3", 
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_EVENT_THIRD_PARTY_ESG_UPDATED_ENVIRONMENTAL_SCORE_4: {
      name : "PRIVILEGE_EVENT_THIRD_PARTY_ESG_UPDATED_ENVIRONMENTAL_SCORE_4", 
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_EVENT_THIRD_PARTY_ESG_UPDATED_ENVIRONMENTAL_SCORE_5: {
      name : "PRIVILEGE_EVENT_THIRD_PARTY_ESG_UPDATED_ENVIRONMENTAL_SCORE_5", 
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_EVENT_THIRD_PARTY_ESG_UPDATED_SOCIAL: {
      name : "PRIVILEGE_EVENT_THIRD_PARTY_ESG_UPDATED_SOCIAL", 
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_EVENT_THIRD_PARTY_ESG_UPDATED_SOCIAL_SCORE_1: {
      name : "PRIVILEGE_EVENT_THIRD_PARTY_ESG_UPDATED_SOCIAL_SCORE_1", 
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_EVENT_THIRD_PARTY_ESG_UPDATED_SOCIAL_SCORE_2: {
      name : "PRIVILEGE_EVENT_THIRD_PARTY_ESG_UPDATED_SOCIAL_SCORE_2", 
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_EVENT_THIRD_PARTY_ESG_UPDATED_SOCIAL_SCORE_3: {
      name : "PRIVILEGE_EVENT_THIRD_PARTY_ESG_UPDATED_SOCIAL_SCORE_3", 
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_EVENT_THIRD_PARTY_ESG_UPDATED_SOCIAL_SCORE_4: {
      name : "PRIVILEGE_EVENT_THIRD_PARTY_ESG_UPDATED_SOCIAL_SCORE_4", 
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_EVENT_THIRD_PARTY_ESG_UPDATED_SOCIAL_SCORE_5: {
      name : "PRIVILEGE_EVENT_THIRD_PARTY_ESG_UPDATED_SOCIAL_SCORE_5", 
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_EVENT_THIRD_PARTY_ESG_UPDATED_GOVERNANCE: {
      name : "PRIVILEGE_EVENT_THIRD_PARTY_ESG_UPDATED_GOVERNANCE", 
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_EVENT_THIRD_PARTY_ESG_UPDATED_GOVERNANCE_SCORE_1: {
      name : "PRIVILEGE_EVENT_THIRD_PARTY_ESG_UPDATED_GOVERNANCE_SCORE_1", 
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_EVENT_THIRD_PARTY_ESG_UPDATED_GOVERNANCE_SCORE_2: {
      name : "PRIVILEGE_EVENT_THIRD_PARTY_ESG_UPDATED_GOVERNANCE_SCORE_2", 
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_EVENT_THIRD_PARTY_ESG_UPDATED_GOVERNANCE_SCORE_3: {
      name : "PRIVILEGE_EVENT_THIRD_PARTY_ESG_UPDATED_GOVERNANCE_SCORE_3", 
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_EVENT_THIRD_PARTY_ESG_UPDATED_GOVERNANCE_SCORE_4: {
      name : "PRIVILEGE_EVENT_THIRD_PARTY_ESG_UPDATED_GOVERNANCE_SCORE_4", 
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_EVENT_THIRD_PARTY_ESG_UPDATED_GOVERNANCE_SCORE_5: {
      name : "PRIVILEGE_EVENT_THIRD_PARTY_ESG_UPDATED_GOVERNANCE_SCORE_5", 
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },

    //Ecovadis events
    PRIVILEGE_EVENT_THIRD_PARTY_ECOVADIS_UPDATED_GLOBAL_SCORE: {
      name : "PRIVILEGE_EVENT_THIRD_PARTY_ECOVADIS_UPDATED_GLOBAL_SCORE", 
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_EVENT_THIRD_PARTY_ECOVADIS_UPDATED_GLOBAL_SCORE_NOT_STARTED: {
      name : "PRIVILEGE_EVENT_THIRD_PARTY_ECOVADIS_UPDATED_GLOBAL_SCORE_NOT_STARTED", 
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_EVENT_THIRD_PARTY_ECOVADIS_UPDATED_GLOBAL_SCORE_NOT_PUBLISHED: {
      name : "PRIVILEGE_EVENT_THIRD_PARTY_ECOVADIS_UPDATED_GLOBAL_SCORE_NOT_PUBLISHED", 
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_EVENT_THIRD_PARTY_ECOVADIS_UPDATED_GLOBAL_SCORE_INSUFFICIENT: {
      name : "PRIVILEGE_EVENT_THIRD_PARTY_ECOVADIS_UPDATED_GLOBAL_SCORE_INSUFFICIENT", 
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_EVENT_THIRD_PARTY_ECOVADIS_UPDATED_GLOBAL_SCORE_PARTIAL: {
      name : "PRIVILEGE_EVENT_THIRD_PARTY_ECOVADIS_UPDATED_GLOBAL_SCORE_PARTIAL", 
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_EVENT_THIRD_PARTY_ECOVADIS_UPDATED_GLOBAL_SCORE_GOOD: {
      name : "PRIVILEGE_EVENT_THIRD_PARTY_ECOVADIS_UPDATED_GLOBAL_SCORE_GOOD", 
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_EVENT_THIRD_PARTY_ECOVADIS_UPDATED_GLOBAL_SCORE_ADVANCED: {
      name : "PRIVILEGE_EVENT_THIRD_PARTY_ECOVADIS_UPDATED_GLOBAL_SCORE_ADVANCED", 
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_EVENT_THIRD_PARTY_ECOVADIS_UPDATED_GLOBAL_SCORE_OUTSTANDING: {
      name : "PRIVILEGE_EVENT_THIRD_PARTY_ECOVADIS_UPDATED_GLOBAL_SCORE_OUTSTANDING", 
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_EVENT_THIRD_PARTY_ECOVADIS_UPDATED_ENV_SCORE: {
      name : "PRIVILEGE_EVENT_THIRD_PARTY_ECOVADIS_UPDATED_ENV_SCORE", 
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_EVENT_THIRD_PARTY_ECOVADIS_UPDATED_ENV_SCORE_NOT_STARTED: {
      name : "PRIVILEGE_EVENT_THIRD_PARTY_ECOVADIS_UPDATED_ENV_SCORE_NOT_STARTED", 
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_EVENT_THIRD_PARTY_ECOVADIS_UPDATED_ENV_SCORE_NOT_PUBLISHED: {
      name : "PRIVILEGE_EVENT_THIRD_PARTY_ECOVADIS_UPDATED_ENV_SCORE_NOT_PUBLISHED", 
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_EVENT_THIRD_PARTY_ECOVADIS_UPDATED_ENV_SCORE_INSUFFICIENT: {
      name : "PRIVILEGE_EVENT_THIRD_PARTY_ECOVADIS_UPDATED_ENV_SCORE_INSUFFICIENT", 
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_EVENT_THIRD_PARTY_ECOVADIS_UPDATED_ENV_SCORE_PARTIAL: {
      name : "PRIVILEGE_EVENT_THIRD_PARTY_ECOVADIS_UPDATED_ENV_SCORE_PARTIAL", 
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_EVENT_THIRD_PARTY_ECOVADIS_UPDATED_ENV_SCORE_GOOD: {
      name : "PRIVILEGE_EVENT_THIRD_PARTY_ECOVADIS_UPDATED_ENV_SCORE_GOOD", 
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_EVENT_THIRD_PARTY_ECOVADIS_UPDATED_ENV_SCORE_ADVANCED: {
      name : "PRIVILEGE_EVENT_THIRD_PARTY_ECOVADIS_UPDATED_ENV_SCORE_ADVANCED", 
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_EVENT_THIRD_PARTY_ECOVADIS_UPDATED_ENV_SCORE_OUTSTANDING: {
      name : "PRIVILEGE_EVENT_THIRD_PARTY_ECOVADIS_UPDATED_ENV_SCORE_OUTSTANDING", 
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_EVENT_THIRD_PARTY_ECOVADIS_UPDATED_LAB_SCORE: {
      name : "PRIVILEGE_EVENT_THIRD_PARTY_ECOVADIS_UPDATED_LAB_SCORE", 
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_EVENT_THIRD_PARTY_ECOVADIS_UPDATED_LAB_SCORE_NOT_STARTED: {
      name : "PRIVILEGE_EVENT_THIRD_PARTY_ECOVADIS_UPDATED_LAB_SCORE_NOT_STARTED", 
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_EVENT_THIRD_PARTY_ECOVADIS_UPDATED_LAB_SCORE_NOT_PUBLISHED: {
      name : "PRIVILEGE_EVENT_THIRD_PARTY_ECOVADIS_UPDATED_LAB_SCORE_NOT_PUBLISHED", 
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_EVENT_THIRD_PARTY_ECOVADIS_UPDATED_LAB_SCORE_INSUFFICIENT: {
      name : "PRIVILEGE_EVENT_THIRD_PARTY_ECOVADIS_UPDATED_LAB_SCORE_INSUFFICIENT", 
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_EVENT_THIRD_PARTY_ECOVADIS_UPDATED_LAB_SCORE_PARTIAL: {
      name : "PRIVILEGE_EVENT_THIRD_PARTY_ECOVADIS_UPDATED_LAB_SCORE_PARTIAL", 
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_EVENT_THIRD_PARTY_ECOVADIS_UPDATED_LAB_SCORE_GOOD: {
      name : "PRIVILEGE_EVENT_THIRD_PARTY_ECOVADIS_UPDATED_LAB_SCORE_GOOD", 
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_EVENT_THIRD_PARTY_ECOVADIS_UPDATED_LAB_SCORE_ADVANCED: {
      name : "PRIVILEGE_EVENT_THIRD_PARTY_ECOVADIS_UPDATED_LAB_SCORE_ADVANCED", 
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_EVENT_THIRD_PARTY_ECOVADIS_UPDATED_LAB_SCORE_OUTSTANDING: {
      name : "PRIVILEGE_EVENT_THIRD_PARTY_ECOVADIS_UPDATED_LAB_SCORE_OUTSTANDING", 
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_EVENT_THIRD_PARTY_ECOVADIS_UPDATED_FBP_SCORE: {
      name : "PRIVILEGE_EVENT_THIRD_PARTY_ECOVADIS_UPDATED_FBP_SCORE", 
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_EVENT_THIRD_PARTY_ECOVADIS_UPDATED_FBP_SCORE_NOT_STARTED: {
      name : "PRIVILEGE_EVENT_THIRD_PARTY_ECOVADIS_UPDATED_FBP_SCORE_NOT_STARTED", 
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_EVENT_THIRD_PARTY_ECOVADIS_UPDATED_FBP_SCORE_NOT_PUBLISHED: {
      name : "PRIVILEGE_EVENT_THIRD_PARTY_ECOVADIS_UPDATED_FBP_SCORE_NOT_PUBLISHED", 
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_EVENT_THIRD_PARTY_ECOVADIS_UPDATED_FBP_SCORE_INSUFFICIENT: {
      name : "PRIVILEGE_EVENT_THIRD_PARTY_ECOVADIS_UPDATED_FBP_SCORE_INSUFFICIENT", 
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_EVENT_THIRD_PARTY_ECOVADIS_UPDATED_FBP_SCORE_PARTIAL: {
      name : "PRIVILEGE_EVENT_THIRD_PARTY_ECOVADIS_UPDATED_FBP_SCORE_PARTIAL", 
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_EVENT_THIRD_PARTY_ECOVADIS_UPDATED_FBP_SCORE_GOOD: {
      name : "PRIVILEGE_EVENT_THIRD_PARTY_ECOVADIS_UPDATED_FBP_SCORE_GOOD", 
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_EVENT_THIRD_PARTY_ECOVADIS_UPDATED_FBP_SCORE_ADVANCED: {
      name : "PRIVILEGE_EVENT_THIRD_PARTY_ECOVADIS_UPDATED_FBP_SCORE_ADVANCED", 
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_EVENT_THIRD_PARTY_ECOVADIS_UPDATED_FBP_SCORE_OUTSTANDING: {
      name : "PRIVILEGE_EVENT_THIRD_PARTY_ECOVADIS_UPDATED_FBP_SCORE_OUTSTANDING", 
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_EVENT_THIRD_PARTY_ECOVADIS_UPDATED_SUP_SCORE: {
      name : "PRIVILEGE_EVENT_THIRD_PARTY_ECOVADIS_UPDATED_SUP_SCORE", 
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_EVENT_THIRD_PARTY_ECOVADIS_UPDATED_SUP_SCORE_NOT_STARTED: {
      name : "PRIVILEGE_EVENT_THIRD_PARTY_ECOVADIS_UPDATED_SUP_SCORE_NOT_STARTED", 
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_EVENT_THIRD_PARTY_ECOVADIS_UPDATED_SUP_SCORE_NOT_PUBLISHED: {
      name : "PRIVILEGE_EVENT_THIRD_PARTY_ECOVADIS_UPDATED_SUP_SCORE_NOT_PUBLISHED", 
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_EVENT_THIRD_PARTY_ECOVADIS_UPDATED_SUP_SCORE_INSUFFICIENT: {
      name : "PRIVILEGE_EVENT_THIRD_PARTY_ECOVADIS_UPDATED_SUP_SCORE_INSUFFICIENT", 
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_EVENT_THIRD_PARTY_ECOVADIS_UPDATED_SUP_SCORE_PARTIAL: {
      name : "PRIVILEGE_EVENT_THIRD_PARTY_ECOVADIS_UPDATED_SUP_SCORE_PARTIAL", 
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_EVENT_THIRD_PARTY_ECOVADIS_UPDATED_SUP_SCORE_GOOD: {
      name : "PRIVILEGE_EVENT_THIRD_PARTY_ECOVADIS_UPDATED_SUP_SCORE_GOOD", 
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_EVENT_THIRD_PARTY_ECOVADIS_UPDATED_SUP_SCORE_ADVANCED: {
      name : "PRIVILEGE_EVENT_THIRD_PARTY_ECOVADIS_UPDATED_SUP_SCORE_ADVANCED", 
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_EVENT_THIRD_PARTY_ECOVADIS_UPDATED_SUP_SCORE_OUTSTANDING: {
      name : "PRIVILEGE_EVENT_THIRD_PARTY_ECOVADIS_UPDATED_SUP_SCORE_OUTSTANDING", 
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_EVENT_THIRD_PARTY_ECOVADIS_UPDATED_CURRENT_STAGE_STATUS: {
      name : "PRIVILEGE_EVENT_THIRD_PARTY_ECOVADIS_UPDATED_CURRENT_STAGE_STATUS", 
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_EVENT_THIRD_PARTY_ECOVADIS_UPDATED_CURRENT_STAGE_STATUS_REGISTRATION: {
      name : "PRIVILEGE_EVENT_THIRD_PARTY_ECOVADIS_UPDATED_CURRENT_STAGE_STATUS_REGISTRATION", 
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_EVENT_THIRD_PARTY_ECOVADIS_UPDATED_CURRENT_STAGE_STATUS_QUESTIONNAIRE: {
      name : "PRIVILEGE_EVENT_THIRD_PARTY_ECOVADIS_UPDATED_CURRENT_STAGE_STATUS_QUESTIONNAIRE", 
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_EVENT_THIRD_PARTY_ECOVADIS_UPDATED_CURRENT_STAGE_STATUS_EXPERT_ANALYSIS: {
      name : "PRIVILEGE_EVENT_THIRD_PARTY_ECOVADIS_UPDATED_CURRENT_STAGE_STATUS_EXPERT_ANALYSIS", 
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_EVENT_THIRD_PARTY_ECOVADIS_UPDATED_CURRENT_STAGE_STATUS_RESULTS: {
      name : "PRIVILEGE_EVENT_THIRD_PARTY_ECOVADIS_UPDATED_CURRENT_STAGE_STATUS_RESULTS", 
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_EVENT_THIRD_PARTY_ECOVADIS_UPDATED: {
      name : "PRIVILEGE_EVENT_THIRD_PARTY_ECOVADIS_UPDATED", 
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_EVENT_THIRD_PARTY_ECOVADIS_ACTIVATED: {
      name : "PRIVILEGE_EVENT_THIRD_PARTY_ECOVADIS_ACTIVATED", 
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_EVENT_THIRD_PARTY_ECOVADIS_DEACTIVATED: {
      name : "PRIVILEGE_EVENT_THIRD_PARTY_ECOVADIS_DEACTIVATED", 
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },

    // financial Strength events
    PRIVILEGE_EVENT_THIRD_PARTY_FINANCIAL_STRENGTH_UPDATED: {
      name : "PRIVILEGE_EVENT_THIRD_PARTY_FINANCIAL_STRENGTH_UPDATED", 
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_EVENT_THIRD_PARTY_FINANCIAL_STRENGTH_UPDATED_SCORE_UNKNOWN: {
      name : "PRIVILEGE_EVENT_THIRD_PARTY_FINANCIAL_STRENGTH_UPDATED_SCORE_UNKNOWN", 
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_EVENT_THIRD_PARTY_FINANCIAL_STRENGTH_UPDATED_SCORE_VERY_LOW: {
      name : "PRIVILEGE_EVENT_THIRD_PARTY_FINANCIAL_STRENGTH_UPDATED_SCORE_VERY_LOW", 
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_EVENT_THIRD_PARTY_FINANCIAL_STRENGTH_UPDATED_SCORE_LOW: {
      name : "PRIVILEGE_EVENT_THIRD_PARTY_FINANCIAL_STRENGTH_UPDATED_SCORE_LOW", 
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_EVENT_THIRD_PARTY_FINANCIAL_STRENGTH_UPDATED_SCORE_MEDIUM: {
      name : "PRIVILEGE_EVENT_THIRD_PARTY_FINANCIAL_STRENGTH_UPDATED_SCORE_MEDIUM", 
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_EVENT_THIRD_PARTY_FINANCIAL_STRENGTH_UPDATED_SCORE_HIGH: {
      name : "PRIVILEGE_EVENT_THIRD_PARTY_FINANCIAL_STRENGTH_UPDATED_SCORE_HIGH", 
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_EVENT_THIRD_PARTY_FINANCIAL_STRENGTH_ACTIVATED: {
      name : "PRIVILEGE_EVENT_THIRD_PARTY_FINANCIAL_STRENGTH_ACTIVATED", 
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_EVENT_THIRD_PARTY_FINANCIAL_STRENGTH_DEACTIVATED: {
      name : "PRIVILEGE_EVENT_THIRD_PARTY_FINANCIAL_STRENGTH_DEACTIVATED", 
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },

    // IndueD Events
    PRIVILEGE_EVENT_THIRD_PARTY_INDUED_ACTIVATED: {
      name : "PRIVILEGE_EVENT_THIRD_PARTY_INDUED_EACTIVATED", 
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_EVENT_THIRD_PARTY_INDUED_DEACTIVATED: {
      name : "PRIVILEGE_EVENT_THIRD_PARTY_INDUED_DEACTIVATED", 
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_EVENT_THIRD_PARTY_INDUED_UPDATED: {
      name : "PRIVILEGE_EVENT_THIRD_PARTY_INDUED_UPDATED", 
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_EVENT_THIRD_PARTY_INDUED_UPDATED_STATUS: {
      name : "PRIVILEGE_EVENT_THIRD_PARTY_INDUED_UPDATED_STATUS", 
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_EVENT_THIRD_PARTY_INDUED_UPDATED_RISK: {
      name : "PRIVILEGE_EVENT_THIRD_PARTY_INDUED_UPDATED_RISK", 
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_EVENT_THIRD_PARTY_INDUED_UPDATED_RISK_SCORE_0: {
      name : "PRIVILEGE_EVENT_THIRD_PARTY_INDUED_UPDATED_RISK_SCORE_0", 
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_EVENT_THIRD_PARTY_INDUED_UPDATED_RISK_SCORE_1: {
      name : "PRIVILEGE_EVENT_THIRD_PARTY_INDUED_UPDATED_RISK_SCORE_1", 
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_EVENT_THIRD_PARTY_INDUED_UPDATED_RISK_SCORE_2: {
      name : "PRIVILEGE_EVENT_THIRD_PARTY_INDUED_UPDATED_RISK_SCORE_2", 
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_EVENT_THIRD_PARTY_INDUED_UPDATED_RISK_SCORE_3: {
      name : "PRIVILEGE_EVENT_THIRD_PARTY_INDUED_UPDATED_RISK_SCORE_3", 
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_EVENT_THIRD_PARTY_INDUED_UPDATED_COUNTRY: {
      name : "PRIVILEGE_EVENT_THIRD_PARTY_INDUED_UPDATED_COUNTRY", 
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_EVENT_THIRD_PARTY_INDUED_UPDATED_COUNTRY_SCORE_0: {
      name : "PRIVILEGE_EVENT_THIRD_PARTY_INDUED_UPDATED_COUNTRY_SCORE_0", 
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_EVENT_THIRD_PARTY_INDUED_UPDATED_COUNTRY_SCORE_1: {
      name : "PRIVILEGE_EVENT_THIRD_PARTY_INDUED_UPDATED_COUNTRY_SCORE_1", 
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_EVENT_THIRD_PARTY_INDUED_UPDATED_COUNTRY_SCORE_2: {
      name : "PRIVILEGE_EVENT_THIRD_PARTY_INDUED_UPDATED_COUNTRY_SCORE_2", 
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_EVENT_THIRD_PARTY_INDUED_UPDATED_COUNTRY_SCORE_3: {
      name : "PRIVILEGE_EVENT_THIRD_PARTY_INDUED_UPDATED_COUNTRY_SCORE_3", 
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_EVENT_THIRD_PARTY_INDUED_UPDATED_ACTIVITY: {
      name : "PRIVILEGE_EVENT_THIRD_PARTY_INDUED_UPDATED_ACTIVITY", 
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_EVENT_THIRD_PARTY_INDUED_UPDATED_ACTIVITY_SCORE_0: {
      name : "PRIVILEGE_EVENT_THIRD_PARTY_INDUED_UPDATED_ACTIVITY_SCORE_0", 
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_EVENT_THIRD_PARTY_INDUED_UPDATED_ACTIVITY_SCORE_1: {
      name : "PRIVILEGE_EVENT_THIRD_PARTY_INDUED_UPDATED_ACTIVITY_SCORE_1", 
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_EVENT_THIRD_PARTY_INDUED_UPDATED_ACTIVITY_SCORE_2: {
      name : "PRIVILEGE_EVENT_THIRD_PARTY_INDUED_UPDATED_ACTIVITY_SCORE_2", 
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_EVENT_THIRD_PARTY_INDUED_UPDATED_ACTIVITY_SCORE_3: {
      name : "PRIVILEGE_EVENT_THIRD_PARTY_INDUED_UPDATED_ACTIVITY_SCORE_3", 
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_EVENT_THIRD_PARTY_INDUED_UPDATED_SANCTIONS: {
      name : "PRIVILEGE_EVENT_THIRD_PARTY_INDUED_UPDATED_SANCTIONS", 
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_EVENT_THIRD_PARTY_INDUED_UPDATED_SANCTIONS_SCORE_0: {
      name : "PRIVILEGE_EVENT_THIRD_PARTY_INDUED_UPDATED_SANCTIONS_SCORE_0", 
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_EVENT_THIRD_PARTY_INDUED_UPDATED_SANCTIONS_SCORE_1: {
      name : "PRIVILEGE_EVENT_THIRD_PARTY_INDUED_UPDATED_SANCTIONS_SCORE_1", 
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_EVENT_THIRD_PARTY_INDUED_UPDATED_SANCTIONS_SCORE_2: {
      name : "PRIVILEGE_EVENT_THIRD_PARTY_INDUED_UPDATED_SANCTIONS_SCORE_2", 
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_EVENT_THIRD_PARTY_INDUED_UPDATED_SANCTIONS_SCORE_3: {
      name : "PRIVILEGE_EVENT_THIRD_PARTY_INDUED_UPDATED_SANCTIONS_SCORE_3", 
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_EVENT_THIRD_PARTY_INDUED_UPDATED_PEP: {
      name : "PRIVILEGE_EVENT_THIRD_PARTY_INDUED_UPDATED_PEP", 
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_EVENT_THIRD_PARTY_INDUED_UPDATED_PEP_SCORE_0: {
      name : "PRIVILEGE_EVENT_THIRD_PARTY_INDUED_UPDATED_PEP_SCORE_0", 
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_EVENT_THIRD_PARTY_INDUED_UPDATED_PEP_SCORE_1: {
      name : "PRIVILEGE_EVENT_THIRD_PARTY_INDUED_UPDATED_PEP_SCORE_1", 
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_EVENT_THIRD_PARTY_INDUED_UPDATED_PEP_SCORE_2: {
      name : "PRIVILEGE_EVENT_THIRD_PARTY_INDUED_UPDATED_PEP_SCORE_2", 
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_EVENT_THIRD_PARTY_INDUED_UPDATED_PEP_SCORE_3: {
      name : "PRIVILEGE_EVENT_THIRD_PARTY_INDUED_UPDATED_PEP_SCORE_3", 
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_EVENT_THIRD_PARTY_INDUED_UPDATED_PN: {
      name : "PRIVILEGE_EVENT_THIRD_PARTY_INDUED_UPDATED_PN", 
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_EVENT_THIRD_PARTY_INDUED_UPDATED_PN_SCORE_0: {
      name : "PRIVILEGE_EVENT_THIRD_PARTY_INDUED_UPDATED_PN_SCORE_0", 
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_EVENT_THIRD_PARTY_INDUED_UPDATED_PN_SCORE_1: {
      name : "PRIVILEGE_EVENT_THIRD_PARTY_INDUED_UPDATED_PN_SCORE_1", 
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_EVENT_THIRD_PARTY_INDUED_UPDATED_PN_SCORE_2: {
      name : "PRIVILEGE_EVENT_THIRD_PARTY_INDUED_UPDATED_PN_SCORE_2", 
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_EVENT_THIRD_PARTY_INDUED_UPDATED_PN_SCORE_3: {
      name : "PRIVILEGE_EVENT_THIRD_PARTY_INDUED_UPDATED_PN_SCORE_3", 
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },


    //Security Rating events
    PRIVILEGE_EVENT_THIRD_PARTY_SECURITY_RATING_UPDATED: {
      name : "PRIVILEGE_EVENT_THIRD_PARTY_SECURITY_RATING_UPDATED", 
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_EVENT_THIRD_PARTY_SECURITY_RATING_UPDATED_GLOBAL_SCORE_ADVANCED: {
      name : "PRIVILEGE_EVENT_THIRD_PARTY_SECURITY_RATING_UPDATED_GLOBAL_SCORE_ADVANCED", 
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_EVENT_THIRD_PARTY_SECURITY_RATING_UPDATED_GLOBAL_SCORE_INTERMEDIATE: {
      name : "PRIVILEGE_EVENT_THIRD_PARTY_SECURITY_RATING_UPDATED_GLOBAL_SCORE_INTERMEDIATE", 
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_EVENT_THIRD_PARTY_SECURITY_RATING_UPDATED_GLOBAL_SCORE_BASIC: {
      name : "PRIVILEGE_EVENT_THIRD_PARTY_SECURITY_RATING_UPDATED_GLOBAL_SCORE_BASIC", 
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_EVENT_THIRD_PARTY_SECURITY_RATING_UPDATED_ATTACK_SURFACE_SCORE: {
      name : "PRIVILEGE_EVENT_THIRD_PARTY_SECURITY_RATING_UPDATED_ATTACK_SURFACE_SCORE", 
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_EVENT_THIRD_PARTY_SECURITY_RATING_UPDATED_ATTACK_SURFACE_SCORE_A: {
      name : "PRIVILEGE_EVENT_THIRD_PARTY_SECURITY_RATING_UPDATED_ATTACK_SURFACE_SCORE_A", 
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_EVENT_THIRD_PARTY_SECURITY_RATING_UPDATED_ATTACK_SURFACE_SCORE_B: {
      name : "PRIVILEGE_EVENT_THIRD_PARTY_SECURITY_RATING_UPDATED_ATTACK_SURFACE_SCORE_B", 
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_EVENT_THIRD_PARTY_SECURITY_RATING_UPDATED_ATTACK_SURFACE_SCORE_C: {
      name : "PRIVILEGE_EVENT_THIRD_PARTY_SECURITY_RATING_UPDATED_ATTACK_SURFACE_SCORE_C", 
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_EVENT_THIRD_PARTY_SECURITY_RATING_UPDATED_ATTACK_SURFACE_SCORE_D: {
      name : "PRIVILEGE_EVENT_THIRD_PARTY_SECURITY_RATING_UPDATED_ATTACK_SURFACE_SCORE_D", 
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_EVENT_THIRD_PARTY_SECURITY_RATING_UPDATED_ATTACK_SURFACE_SCORE_E: {
      name : "PRIVILEGE_EVENT_THIRD_PARTY_SECURITY_RATING_UPDATED_ATTACK_SURFACE_SCORE_E", 
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_EVENT_THIRD_PARTY_SECURITY_RATING_UPDATED_MAIL_SCORE: {
      name : "PRIVILEGE_EVENT_THIRD_PARTY_SECURITY_RATING_UPDATED_MAIL_SCORE", 
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_EVENT_THIRD_PARTY_SECURITY_RATING_UPDATED_MAIL_SCORE_A: {
      name : "PRIVILEGE_EVENT_THIRD_PARTY_SECURITY_RATING_UPDATED_MAIL_SCORE_A", 
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_EVENT_THIRD_PARTY_SECURITY_RATING_UPDATED_MAIL_SCORE_B: {
      name : "PRIVILEGE_EVENT_THIRD_PARTY_SECURITY_RATING_UPDATED_MAIL_SCORE_B", 
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_EVENT_THIRD_PARTY_SECURITY_RATING_UPDATED_MAIL_SCORE_C: {
      name : "PRIVILEGE_EVENT_THIRD_PARTY_SECURITY_RATING_UPDATED_MAIL_SCORE_C", 
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_EVENT_THIRD_PARTY_SECURITY_RATING_UPDATED_MAIL_SCORE_D: {
      name : "PRIVILEGE_EVENT_THIRD_PARTY_SECURITY_RATING_UPDATED_MAIL_SCORE_D", 
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_EVENT_THIRD_PARTY_SECURITY_RATING_UPDATED_MAIL_SCORE_E: {
      name : "PRIVILEGE_EVENT_THIRD_PARTY_SECURITY_RATING_UPDATED_MAIL_SCORE_E", 
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_EVENT_THIRD_PARTY_SECURITY_RATING_UPDATED_WEB_SCORE: {
      name : "PRIVILEGE_EVENT_THIRD_PARTY_SECURITY_RATING_UPDATED_WEB_SCORE", 
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_EVENT_THIRD_PARTY_SECURITY_RATING_UPDATED_WEB_SCORE_A: {
      name : "PRIVILEGE_EVENT_THIRD_PARTY_SECURITY_RATING_UPDATED_WEB_SCORE_A", 
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_EVENT_THIRD_PARTY_SECURITY_RATING_UPDATED_WEB_SCORE_B: {
      name : "PRIVILEGE_EVENT_THIRD_PARTY_SECURITY_RATING_UPDATED_WEB_SCORE_B", 
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_EVENT_THIRD_PARTY_SECURITY_RATING_UPDATED_WEB_SCORE_C: {
      name : "PRIVILEGE_EVENT_THIRD_PARTY_SECURITY_RATING_UPDATED_WEB_SCORE_C", 
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_EVENT_THIRD_PARTY_SECURITY_RATING_UPDATED_WEB_SCORE_D: {
      name : "PRIVILEGE_EVENT_THIRD_PARTY_SECURITY_RATING_UPDATED_WEB_SCORE_D", 
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_EVENT_THIRD_PARTY_SECURITY_RATING_UPDATED_WEB_SCORE_E: {
      name : "PRIVILEGE_EVENT_THIRD_PARTY_SECURITY_RATING_UPDATED_WEB_SCORE_E", 
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_EVENT_THIRD_PARTY_SECURITY_RATING_UPDATED_SECURITY_CONTROL_SCORE: {
      name : "PRIVILEGE_EVENT_THIRD_PARTY_SECURITY_RATING_UPDATED_SECURITY_CONTROL_SCORE", 
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_EVENT_THIRD_PARTY_SECURITY_RATING_UPDATED_SECURITY_CONTROL_SCORE_A: {
      name : "PRIVILEGE_EVENT_THIRD_PARTY_SECURITY_RATING_UPDATED_SECURITY_CONTROL_SCORE_A", 
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_EVENT_THIRD_PARTY_SECURITY_RATING_UPDATED_SECURITY_CONTROL_SCORE_B: {
      name : "PRIVILEGE_EVENT_THIRD_PARTY_SECURITY_RATING_UPDATED_SECURITY_CONTROL_SCORE_B", 
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_EVENT_THIRD_PARTY_SECURITY_RATING_UPDATED_SECURITY_CONTROL_SCORE_C: {
      name : "PRIVILEGE_EVENT_THIRD_PARTY_SECURITY_RATING_UPDATED_SECURITY_CONTROL_SCORE_C", 
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_EVENT_THIRD_PARTY_SECURITY_RATING_UPDATED_SECURITY_CONTROL_SCORE_D: {
      name : "PRIVILEGE_EVENT_THIRD_PARTY_SECURITY_RATING_UPDATED_SECURITY_CONTROL_SCORE_D", 
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_EVENT_THIRD_PARTY_SECURITY_RATING_UPDATED_SECURITY_CONTROL_SCORE_E: {
      name : "PRIVILEGE_EVENT_THIRD_PARTY_SECURITY_RATING_UPDATED_SECURITY_CONTROL_SCORE_E", 
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_EVENT_THIRD_PARTY_SECURITY_RATING_UPDATED_VULNERABILITY_SCORE: {
      name : "PRIVILEGE_EVENT_THIRD_PARTY_SECURITY_RATING_UPDATED_VULNERABILITY_SCORE", 
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_EVENT_THIRD_PARTY_SECURITY_RATING_UPDATED_VULNERABILITY_SCORE_A: {
      name : "PRIVILEGE_EVENT_THIRD_PARTY_SECURITY_RATING_UPDATED_VULNERABILITY_SCORE_A", 
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_EVENT_THIRD_PARTY_SECURITY_RATING_UPDATED_VULNERABILITY_SCORE_B: {
      name : "PRIVILEGE_EVENT_THIRD_PARTY_SECURITY_RATING_UPDATED_VULNERABILITY_SCORE_B", 
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_EVENT_THIRD_PARTY_SECURITY_RATING_UPDATED_VULNERABILITY_SCORE_C: {
      name : "PRIVILEGE_EVENT_THIRD_PARTY_SECURITY_RATING_UPDATED_VULNERABILITY_SCORE_C", 
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_EVENT_THIRD_PARTY_SECURITY_RATING_UPDATED_VULNERABILITY_SCORE_D: {
      name : "PRIVILEGE_EVENT_THIRD_PARTY_SECURITY_RATING_UPDATED_VULNERABILITY_SCORE_D", 
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_EVENT_THIRD_PARTY_SECURITY_RATING_UPDATED_VULNERABILITY_SCORE_E: {
      name : "PRIVILEGE_EVENT_THIRD_PARTY_SECURITY_RATING_UPDATED_VULNERABILITY_SCORE_E", 
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_EVENT_THIRD_PARTY_SECURITY_RATING_UPDATED_VULNERABILITY_SNAPSHOT_SCORE: {
      name : "PRIVILEGE_EVENT_THIRD_PARTY_SECURITY_RATING_UPDATED_VULNERABILITY_SNAPSHOT_SCORE", 
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_EVENT_THIRD_PARTY_SECURITY_RATING_UPDATED_VULNERABILITY_SNAPSHOT_SCORE_A: {
      name : "PRIVILEGE_EVENT_THIRD_PARTY_SECURITY_RATING_UPDATED_VULNERABILITY_SNAPSHOT_SCORE_A", 
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_EVENT_THIRD_PARTY_SECURITY_RATING_UPDATED_VULNERABILITY_SNAPSHOT_SCORE_B: {
      name : "PRIVILEGE_EVENT_THIRD_PARTY_SECURITY_RATING_UPDATED_VULNERABILITY_SNAPSHOT_SCORE_B", 
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_EVENT_THIRD_PARTY_SECURITY_RATING_UPDATED_VULNERABILITY_SNAPSHOT_SCORE_C: {
      name : "PRIVILEGE_EVENT_THIRD_PARTY_SECURITY_RATING_UPDATED_VULNERABILITY_SNAPSHOT_SCORE_C", 
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_EVENT_THIRD_PARTY_SECURITY_RATING_UPDATED_VULNERABILITY_SNAPSHOT_SCORE_D: {
      name : "PRIVILEGE_EVENT_THIRD_PARTY_SECURITY_RATING_UPDATED_VULNERABILITY_SNAPSHOT_SCORE_D", 
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_EVENT_THIRD_PARTY_SECURITY_RATING_UPDATED_VULNERABILITY_SNAPSHOT_SCORE_E: {
      name : "PRIVILEGE_EVENT_THIRD_PARTY_SECURITY_RATING_UPDATED_VULNERABILITY_SNAPSHOT_SCORE_E", 
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_EVENT_THIRD_PARTY_SECURITY_RATING_UPDATED_SECURITY_INCIDENT_SCORE: {
      name : "PRIVILEGE_EVENT_THIRD_PARTY_SECURITY_RATING_UPDATED_SECURITY_INCIDENT_SCORE", 
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_EVENT_THIRD_PARTY_SECURITY_RATING_UPDATED_SECURITY_INCIDENT_SCORE_A: {
      name : "PRIVILEGE_EVENT_THIRD_PARTY_SECURITY_RATING_UPDATED_SECURITY_INCIDENT_SCORE_A", 
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_EVENT_THIRD_PARTY_SECURITY_RATING_UPDATED_SECURITY_INCIDENT_SCORE_B: {
      name : "PRIVILEGE_EVENT_THIRD_PARTY_SECURITY_RATING_UPDATED_SECURITY_INCIDENT_SCORE_B", 
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_EVENT_THIRD_PARTY_SECURITY_RATING_UPDATED_SECURITY_INCIDENT_SCORE_C: {
      name : "PRIVILEGE_EVENT_THIRD_PARTY_SECURITY_RATING_UPDATED_SECURITY_INCIDENT_SCORE_C", 
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_EVENT_THIRD_PARTY_SECURITY_RATING_UPDATED_SECURITY_INCIDENT_SCORE_D: {
      name : "PRIVILEGE_EVENT_THIRD_PARTY_SECURITY_RATING_UPDATED_SECURITY_INCIDENT_SCORE_D", 
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_EVENT_THIRD_PARTY_SECURITY_RATING_UPDATED_SECURITY_INCIDENT_SCORE_E: {
      name : "PRIVILEGE_EVENT_THIRD_PARTY_SECURITY_RATING_UPDATED_SECURITY_INCIDENT_SCORE_E", 
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },

    // Creditsafe
    PRIVILEGE_EVENT_THIRD_PARTY_CREDITSAFE_UPDATED: {
      name : "PRIVILEGE_EVENT_THIRD_PARTY_CREDITSAFE_UPDATED", 
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_EVENT_THIRD_PARTY_CREDITSAFE_UPDATED_INTERNATIONALSCORE: {
      name : "PRIVILEGE_EVENT_THIRD_PARTY_CREDITSAFE_UPDATED_INTERNATIONALSCORE", 
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_EVENT_THIRD_PARTY_CREDITSAFE_UPDATED_INTERNATIONALSCORE_A: {
      name : "PRIVILEGE_EVENT_THIRD_PARTY_CREDITSAFE_UPDATED_INTERNATIONALSCORE_A", 
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_EVENT_THIRD_PARTY_CREDITSAFE_UPDATED_INTERNATIONALSCORE_B: {
      name : "PRIVILEGE_EVENT_THIRD_PARTY_CREDITSAFE_UPDATED_INTERNATIONALSCORE_B", 
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_EVENT_THIRD_PARTY_CREDITSAFE_UPDATED_INTERNATIONALSCORE_C: {
      name : "PRIVILEGE_EVENT_THIRD_PARTY_CREDITSAFE_UPDATED_INTERNATIONALSCORE_C", 
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_EVENT_THIRD_PARTY_CREDITSAFE_UPDATED_INTERNATIONALSCORE_D: {
      name : "PRIVILEGE_EVENT_THIRD_PARTY_CREDITSAFE_UPDATED_INTERNATIONALSCORE_D", 
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_EVENT_THIRD_PARTY_CREDITSAFE_UPDATED_INTERNATIONALSCORE_E: {
      name : "PRIVILEGE_EVENT_THIRD_PARTY_CREDITSAFE_UPDATED_INTERNATIONALSCORE_E", 
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_EVENT_THIRD_PARTY_CREDITSAFE_UPDATED_ACTIVITYEXPOSURE: {
      name : "PRIVILEGE_EVENT_THIRD_PARTY_CREDITSAFE_UPDATED_ACTIVITYEXPOSURE", 
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_EVENT_THIRD_PARTY_CREDITSAFE_UPDATED_ACTIVITYEXPOSURE_A: {
      name : "PRIVILEGE_EVENT_THIRD_PARTY_CREDITSAFE_UPDATED_ACTIVITYEXPOSURE_A", 
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_EVENT_THIRD_PARTY_CREDITSAFE_UPDATED_ACTIVITYEXPOSURE_B: {
      name : "PRIVILEGE_EVENT_THIRD_PARTY_CREDITSAFE_UPDATED_ACTIVITYEXPOSURE_B", 
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_EVENT_THIRD_PARTY_CREDITSAFE_UPDATED_ACTIVITYEXPOSURE_C: {
      name : "PRIVILEGE_EVENT_THIRD_PARTY_CREDITSAFE_UPDATED_ACTIVITYEXPOSURE_C", 
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_EVENT_THIRD_PARTY_CREDITSAFE_UPDATED_ACTIVITYEXPOSURE_D: {
      name : "PRIVILEGE_EVENT_THIRD_PARTY_CREDITSAFE_UPDATED_ACTIVITYEXPOSURE_D", 
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_EVENT_THIRD_PARTY_CREDITSAFE_UPDATED_PREFERENTIALSTATUS: {
      name : "PRIVILEGE_EVENT_THIRD_PARTY_CREDITSAFE_UPDATED_PREFERENTIALSTATUS", 
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_EVENT_THIRD_PARTY_CREDITSAFE_UPDATED_PREFERENTIALSTATUS_ZERO: {
      name : "PRIVILEGE_EVENT_THIRD_PARTY_CREDITSAFE_UPDATED_PREFERENTIALSTATUS_ZERO", 
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_EVENT_THIRD_PARTY_CREDITSAFE_UPDATED_PREFERENTIALSTATUS_FOLLOW: {
      name : "PRIVILEGE_EVENT_THIRD_PARTY_CREDITSAFE_UPDATED_PREFERENTIALSTATUS_FOLLOW", 
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_EVENT_THIRD_PARTY_CREDITSAFE_UPDATED_PREFERENTIALSTATUS_STOPPED: {
      name : "PRIVILEGE_EVENT_THIRD_PARTY_CREDITSAFE_UPDATED_PREFERENTIALSTATUS_STOPPED", 
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_EVENT_THIRD_PARTY_CREDITSAFE_UPDATED_PREFERENTIALSTATUS_WAITING: {
      name : "PRIVILEGE_EVENT_THIRD_PARTY_CREDITSAFE_UPDATED_PREFERENTIALSTATUS_WAITING", 
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_EVENT_THIRD_PARTY_CREDITSAFE_UPDATED_PREFERENTIALSTATUS_TRESOR: {
      name : "PRIVILEGE_EVENT_THIRD_PARTY_CREDITSAFE_UPDATED_PREFERENTIALSTATUS_TRESOR", 
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_EVENT_THIRD_PARTY_CREDITSAFE_UPDATED_PREFERENTIALSTATUS_URSSAF: {
      name : "PRIVILEGE_EVENT_THIRD_PARTY_CREDITSAFE_UPDATED_PREFERENTIALSTATUS_URSSAF", 
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_EVENT_THIRD_PARTY_CREDITSAFE_UPDATED_PREFERENTIALSTATUS_HISTORIC: {
      name : "PRIVILEGE_EVENT_THIRD_PARTY_CREDITSAFE_UPDATED_PREFERENTIALSTATUS_HISTORIC", 
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_EVENT_THIRD_PARTY_CREDITSAFE_UPDATED_PREFERENTIALSTATUS_DETECTED: {
      name : "PRIVILEGE_EVENT_THIRD_PARTY_CREDITSAFE_UPDATED_PREFERENTIALSTATUS_DETECTED", 
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_EVENT_THIRD_PARTY_CREDITSAFE_UPDATED_NATIONALSCOREVALUE: {
      name : "PRIVILEGE_EVENT_THIRD_PARTY_CREDITSAFE_UPDATED_NATIONALSCOREVALUE", 
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_EVENT_THIRD_PARTY_CREDITSAFE_UPDATED_CREDIT_LIMIT: {
      name : "PRIVILEGE_EVENT_THIRD_PARTY_CREDITSAFE_UPDATED_CREDIT_LIMIT", 
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_EVENT_THIRD_PARTY_CREDITSAFE_UPDATED_DBT: {
      name : "PRIVILEGE_EVENT_THIRD_PARTY_CREDITSAFE_UPDATED_DBT", 
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_EVENT_THIRD_PARTY_CREDITSAFE_UPDATED_COMPANYEXPOSURE: {
      name : "PRIVILEGE_EVENT_THIRD_PARTY_CREDITSAFE_UPDATED_COMPANYEXPOSURE", 
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_EVENT_THIRD_PARTY_CREDITSAFE_UPDATED_FINANCIALSTRENGTH: {
      name : "PRIVILEGE_EVENT_THIRD_PARTY_CREDITSAFE_UPDATED_FINANCIALSTRENGTH", 
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },

    //Events privileges dataHub
    PRIVILEGE_EVENT_THIRD_PARTY_DATA_HUB_UPDATED: {
      name : "PRIVILEGE_EVENT_THIRD_PARTY_DATA_HUB_UPDATED",
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_EVENT_THIRD_PARTY_DATA_HUB_UPDATED_ALL: {
      name : "PRIVILEGE_EVENT_THIRD_PARTY_DATA_HUB_UPDATED_ALL",
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },

    //Data sources
    PRIVILEGE_VIEW_INDUED_SOURCE: {
      name: "PRIVILEGE_VIEW_INDUED_SOURCE",
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_REFRESH_INDUED_SOURCE: {
      name: "PRIVILEGE_REFRESH_INDUED_SOURCE",
      childPrivileges: [],
      privilegeType: PrivilegeType.EDIT
    },
    PRIVILEGE_VIEW_TRANSPARENCY_SOURCE: {
      name: "PRIVILEGE_VIEW_TRANSPARENCY_SOURCE",
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_VIEW_DATA_HUB_SOURCE: {
      name: "PRIVILEGE_VIEW_DATA_HUB_SOURCE",
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_VIEW_FINANCIAL_STRENGTH_SOURCE: {
      name: "PRIVILEGE_VIEW_FINANCIAL_STRENGTH_SOURCE",
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_EDIT_FINANCIAL_STRENGTH_SOURCE: {
      name: "PRIVILEGE_EDIT_FINANCIAL_STRENGTH_SOURCE",
      childPrivileges: [],
      privilegeType: PrivilegeType.EDIT
    },
    PRIVILEGE_VIEW_OWLIN_SOURCE: {
      name: "PRIVILEGE_VIEW_OWLIN_SOURCE",
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_EDIT_OWLIN_SOURCE: {
      name: "PRIVILEGE_EDIT_OWLIN_SOURCE",
      childPrivileges: [],
      privilegeType: PrivilegeType.EDIT
    },
    PRIVILEGE_VIEW_ECOVADIS_SOURCE: {
      name: "PRIVILEGE_VIEW_ECOVADIS_SOURCE",
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_EDIT_ECOVADIS_SOURCE: {
      name: "PRIVILEGE_EDIT_ECOVADIS_SOURCE",
      childPrivileges: [],
      privilegeType: PrivilegeType.EDIT
    },
    PRIVILEGE_VIEW_DNB_ESG_SOURCE: {
      name: "PRIVILEGE_VIEW_DNB_ESG_SOURCE",
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_EDIT_DNB_ESG_SOURCE: {
      name: "PRIVILEGE_EDIT_DNB_ESG_SOURCE",
      childPrivileges: [],
      privilegeType: PrivilegeType.EDIT
    },
    PRIVILEGE_VIEW_SECURITY_RATING_SOURCE: {
      name: "PRIVILEGE_VIEW_SECURITY_RATING_SOURCE",
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_EDIT_SECURITY_RATING_SOURCE: {
      name: "PRIVILEGE_EDIT_SECURITY_RATING_SOURCE",
      childPrivileges: [],
      privilegeType: PrivilegeType.EDIT
    },
    PRIVILEGE_VIEW_CREDITSAFE_SOURCE: {
      name: "PRIVILEGE_VIEW_CREDITSAFE_SOURCE",
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_EDIT_CREDITSAFE_SOURCE: {
      name: "PRIVILEGE_EDIT_CREDITSAFE_SOURCE",
      childPrivileges: [],
      privilegeType: PrivilegeType.EDIT
    },

    //AUDIT TRAIL
    PRIVILEGE_VIEW_THIRD_PARTY_AUDIT_TRAIL: {
      name: "PRIVILEGE_VIEW_THIRD_PARTY_AUDIT_TRAIL",
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_VIEW_DOSSIER_AUDIT_TRAIL: {
      name: "PRIVILEGE_VIEW_DOSSIER_AUDIT_TRAIL",
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    PRIVILEGE_VIEW_DOCUMENT_AUDIT_TRAIL: {
      name: "PRIVILEGE_VIEW_DOCUMENT_AUDIT_TRAIL",
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },

    //SIG
    //TODO: à affiner plus tard
    SIG_ACCOUNTS_DETAILS_VIEW: {
      name: "SIG_ACCOUNTS_DETAILS_VIEW",
      childPrivileges: [],
      privilegeType: PrivilegeType.VIEW
    },
    SIG_ACCOUNTS_DETAILS_EDIT: {
      name: "SIG_ACCOUNTS_DETAILS_EDIT",
      childPrivileges: [],
      privilegeType: PrivilegeType.EDIT
    }
  };

  //SIG
  privileges.SIG_ACCOUNTS_DETAILS_EDIT.childPrivileges = [
    privileges.SIG_ACCOUNTS_DETAILS_VIEW
  ]

  //Personalization
  privileges.PRIVILEGE_PERSONALIZE.childPrivileges = [
    privileges.PRIVILEGE_PERSONALIZE_THIRD_PARTY_LABEL
  ];

  //Configuration
  privileges.PRIVILEGE_EDIT_THIRD_PARTY_ATTRIBUTE_OPTIONS.childPrivileges = [
    privileges.PRIVILEGE_VIEW_THIRD_PARTY_ATTRIBUTE
  ];
  privileges.PRIVILEGE_EDIT_THIRD_PARTY_ATTRIBUTE.childPrivileges = [
    privileges.PRIVILEGE_VIEW_THIRD_PARTY_ATTRIBUTE,
    privileges.PRIVILEGE_EDIT_THIRD_PARTY_ATTRIBUTE_OPTIONS
  ];
  privileges.PRIVILEGE_CREATE_THIRD_PARTY_ATTRIBUTE.childPrivileges = [
    privileges.PRIVILEGE_VIEW_THIRD_PARTY_ATTRIBUTE
  ];
  privileges.PRIVILEGE_DELETE_THIRD_PARTY_ATTRIBUTE.childPrivileges = [
    privileges.PRIVILEGE_VIEW_THIRD_PARTY_ATTRIBUTE,
  ];
  privileges.PRIVILEGE_ALL_THIRD_PARTY_ATTRIBUTE.childPrivileges = [
    privileges.PRIVILEGE_VIEW_THIRD_PARTY_ATTRIBUTE,
    privileges.PRIVILEGE_EDIT_THIRD_PARTY_ATTRIBUTE,
    privileges.PRIVILEGE_CREATE_THIRD_PARTY_ATTRIBUTE,
    privileges.PRIVILEGE_DELETE_THIRD_PARTY_ATTRIBUTE
  ];


  privileges.PRIVILEGE_EDIT_DOSSIER_ATTRIBUTE_OPTIONS.childPrivileges = [
    privileges.PRIVILEGE_VIEW_DOSSIER_ATTRIBUTE
  ];
  privileges.PRIVILEGE_EDIT_DOSSIER_ATTRIBUTE.childPrivileges = [
    privileges.PRIVILEGE_VIEW_DOSSIER_ATTRIBUTE,
    privileges.PRIVILEGE_EDIT_DOSSIER_ATTRIBUTE_OPTIONS
  ];
  privileges.PRIVILEGE_CREATE_DOSSIER_ATTRIBUTE.childPrivileges = [
    privileges.PRIVILEGE_VIEW_DOSSIER_ATTRIBUTE
  ];
  privileges.PRIVILEGE_DELETE_DOSSIER_ATTRIBUTE.childPrivileges = [
    privileges.PRIVILEGE_VIEW_DOSSIER_ATTRIBUTE,
  ];
  privileges.PRIVILEGE_ALL_DOSSIER_ATTRIBUTE.childPrivileges = [
    privileges.PRIVILEGE_VIEW_DOSSIER_ATTRIBUTE,
    privileges.PRIVILEGE_EDIT_DOSSIER_ATTRIBUTE,
    privileges.PRIVILEGE_CREATE_DOSSIER_ATTRIBUTE,
    privileges.PRIVILEGE_DELETE_DOSSIER_ATTRIBUTE
  ];

  privileges.PRIVILEGE_EDIT_CONTEXT_ATTRIBUTE_VALUE.childPrivileges = [
    privileges.PRIVILEGE_VIEW_CONTEXT_ATTRIBUTE
  ];
  privileges.PRIVILEGE_EDIT_CONTEXT_ATTRIBUTE.childPrivileges = [
    privileges.PRIVILEGE_VIEW_CONTEXT_ATTRIBUTE,
    privileges.PRIVILEGE_EDIT_CONTEXT_ATTRIBUTE_VALUE
  ];
  privileges.PRIVILEGE_CREATE_CONTEXT_ATTRIBUTE.childPrivileges = [
    privileges.PRIVILEGE_VIEW_CONTEXT_ATTRIBUTE
  ];
  privileges.PRIVILEGE_DELETE_CONTEXT_ATTRIBUTE.childPrivileges = [
    privileges.PRIVILEGE_VIEW_CONTEXT_ATTRIBUTE,
  ];
  privileges.PRIVILEGE_ALL_CONTEXT_ATTRIBUTE.childPrivileges = [
    privileges.PRIVILEGE_VIEW_CONTEXT_ATTRIBUTE,
    privileges.PRIVILEGE_EDIT_CONTEXT_ATTRIBUTE,
    privileges.PRIVILEGE_CREATE_CONTEXT_ATTRIBUTE,
    privileges.PRIVILEGE_DELETE_CONTEXT_ATTRIBUTE
  ];

  privileges.PRIVILEGE_EDIT_PARTNER_ATTRIBUTE_OPTIONS.childPrivileges = [
    privileges.PRIVILEGE_VIEW_PARTNER_ATTRIBUTE
  ];
  privileges.PRIVILEGE_EDIT_PARTNER_ATTRIBUTE.childPrivileges = [
    privileges.PRIVILEGE_VIEW_PARTNER_ATTRIBUTE,
    privileges.PRIVILEGE_EDIT_PARTNER_ATTRIBUTE_OPTIONS
  ];
  privileges.PRIVILEGE_CREATE_PARTNER_ATTRIBUTE.childPrivileges = [
    privileges.PRIVILEGE_VIEW_PARTNER_ATTRIBUTE
  ];
  privileges.PRIVILEGE_DELETE_PARTNER_ATTRIBUTE.childPrivileges = [
    privileges.PRIVILEGE_VIEW_PARTNER_ATTRIBUTE,
  ];
  privileges.PRIVILEGE_ALL_PARTNER_ATTRIBUTE.childPrivileges = [
    privileges.PRIVILEGE_VIEW_PARTNER_ATTRIBUTE,
    privileges.PRIVILEGE_EDIT_PARTNER_ATTRIBUTE,
    privileges.PRIVILEGE_CREATE_PARTNER_ATTRIBUTE,
    privileges.PRIVILEGE_DELETE_PARTNER_ATTRIBUTE
  ];

  privileges.PRIVILEGE_EDIT_RULE_PARAMETERS.childPrivileges = [
    privileges.PRIVILEGE_VIEW_RULE
  ];
  privileges.PRIVILEGE_EDIT_RULE_STATUS.childPrivileges = [
    privileges.PRIVILEGE_VIEW_RULE,
  ];
  privileges.PRIVILEGE_EDIT_RULE.childPrivileges = [
    privileges.PRIVILEGE_VIEW_RULE,
    privileges.PRIVILEGE_EDIT_RULE_PARAMETERS,
    privileges.PRIVILEGE_EDIT_RULE_STATUS,
  ];
  privileges.PRIVILEGE_CREATE_RULE.childPrivileges = [
    privileges.PRIVILEGE_VIEW_RULE
  ];
  privileges.PRIVILEGE_DELETE_RULE.childPrivileges = [
    privileges.PRIVILEGE_VIEW_RULE,
  ];
  privileges.PRIVILEGE_ALL_RULE.childPrivileges = [
    privileges.PRIVILEGE_VIEW_RULE,
    privileges.PRIVILEGE_EDIT_RULE,
    privileges.PRIVILEGE_CREATE_RULE,
    privileges.PRIVILEGE_DELETE_RULE
  ];

  privileges.PRIVILEGE_EDIT_CALCULATION_PARAMETERS.childPrivileges = [
    privileges.PRIVILEGE_VIEW_CALCULATION
  ];
  privileges.PRIVILEGE_EDIT_CALCULATION_STATUS.childPrivileges = [
    privileges.PRIVILEGE_VIEW_CALCULATION,
  ];
  privileges.PRIVILEGE_EDIT_CALCULATION.childPrivileges = [
    privileges.PRIVILEGE_VIEW_CALCULATION,
    privileges.PRIVILEGE_EDIT_CALCULATION_PARAMETERS,
    privileges.PRIVILEGE_EDIT_CALCULATION_STATUS,
  ];
  privileges.PRIVILEGE_CREATE_CALCULATION.childPrivileges = [
    privileges.PRIVILEGE_VIEW_CALCULATION
  ];
  privileges.PRIVILEGE_DELETE_CALCULATION.childPrivileges = [
    privileges.PRIVILEGE_VIEW_CALCULATION,
  ];
  privileges.PRIVILEGE_ALL_CALCULATION.childPrivileges = [
    privileges.PRIVILEGE_VIEW_CALCULATION,
    privileges.PRIVILEGE_EDIT_CALCULATION,
    privileges.PRIVILEGE_CREATE_CALCULATION,
    privileges.PRIVILEGE_DELETE_CALCULATION
  ];

  privileges.PRIVILEGE_EDIT_THEME_PARAMETERS.childPrivileges = [
    privileges.PRIVILEGE_VIEW_THEME
  ];
  privileges.PRIVILEGE_EDIT_THEME_STATUS.childPrivileges = [
    privileges.PRIVILEGE_VIEW_THEME,
  ];
  privileges.PRIVILEGE_EDIT_THEME.childPrivileges = [
    privileges.PRIVILEGE_VIEW_THEME,
    privileges.PRIVILEGE_EDIT_THEME_PARAMETERS,
    privileges.PRIVILEGE_EDIT_THEME_STATUS,
  ];
  privileges.PRIVILEGE_CREATE_THEME.childPrivileges = [
    privileges.PRIVILEGE_VIEW_THEME
  ];
  privileges.PRIVILEGE_DELETE_THEME.childPrivileges = [
    privileges.PRIVILEGE_VIEW_THEME,
  ];
  privileges.PRIVILEGE_ALL_THEME.childPrivileges = [
    privileges.PRIVILEGE_VIEW_THEME,
    privileges.PRIVILEGE_EDIT_THEME,
    privileges.PRIVILEGE_CREATE_THEME,
    privileges.PRIVILEGE_DELETE_THEME
  ];


  privileges.PRIVILEGE_EDIT_ROLE.childPrivileges = [
    privileges.PRIVILEGE_VIEW_ROLE,
  ];
  privileges.PRIVILEGE_CREATE_ROLE.childPrivileges = [
    privileges.PRIVILEGE_VIEW_ROLE
  ];
  privileges.PRIVILEGE_DELETE_ROLE.childPrivileges = [
    privileges.PRIVILEGE_VIEW_ROLE,
  ];
  privileges.PRIVILEGE_ALL_ROLE.childPrivileges = [
    privileges.PRIVILEGE_VIEW_ROLE,
    privileges.PRIVILEGE_EDIT_ROLE,
    privileges.PRIVILEGE_CREATE_ROLE,
    privileges.PRIVILEGE_DELETE_ROLE
  ];


  privileges.PRIVILEGE_EDIT_USER.childPrivileges = [
    privileges.PRIVILEGE_VIEW_USER,
  ];
  privileges.PRIVILEGE_CREATE_USER.childPrivileges = [
    privileges.PRIVILEGE_VIEW_USER
  ];
  privileges.PRIVILEGE_DELETE_USER.childPrivileges = [
    privileges.PRIVILEGE_VIEW_USER,
  ];
  privileges.PRIVILEGE_ALL_USER.childPrivileges = [
    privileges.PRIVILEGE_VIEW_USER,
    privileges.PRIVILEGE_EDIT_USER,
    privileges.PRIVILEGE_CREATE_USER,
    privileges.PRIVILEGE_DELETE_USER
  ];

  // Demandes - template de dossiers
  privileges.PRIVILEGE_EDIT_DOSSIER_TYPE_STATUS.childPrivileges = [
    privileges.PRIVILEGE_VIEW_DOSSIER_TYPE,
  ];
  privileges.PRIVILEGE_EDIT_DOSSIER_TYPE.childPrivileges = [
    privileges.PRIVILEGE_VIEW_DOSSIER_TYPE,
    privileges.PRIVILEGE_EDIT_DOSSIER_TYPE_STATUS,
    privileges.PRIVILEGE_EDIT_DOSSIER_TYPE_PARAMETERS,
  ];
  privileges.PRIVILEGE_CREATE_DOSSIER_TYPE.childPrivileges = [
    privileges.PRIVILEGE_VIEW_DOSSIER_TYPE
  ];
  privileges.PRIVILEGE_DELETE_DOSSIER_TYPE.childPrivileges = [
    privileges.PRIVILEGE_VIEW_DOSSIER_TYPE,
  ];
  privileges.PRIVILEGE_EDIT_DOSSIER_TYPE_PARAMETERS.childPrivileges = [
    privileges.PRIVILEGE_VIEW_DOSSIER_TYPE,
  ];

  // Import et mise à jour
  privileges.PRIVILEGE_CREATE_IMPORTS_AND_UPDATES.childPrivileges = [
    privileges.PRIVILEGE_VIEW_IMPORTS_AND_UPDATES
  ];

  //Features
  privileges.PRIVILEGE_VIEW_THIRD_PARTY.childPrivileges = [
    privileges.PRIVILEGE_VIEW_THIRD_PARTY_ID_CARD,
    privileges.PRIVILEGE_VIEW_THIRD_PARTY_OFFICERS_AGENTS,
    privileges.PRIVILEGE_VIEW_THIRD_PARTY_BALANCE_SHEET,
    privileges.PRIVILEGE_VIEW_THIRD_PARTY_RISKS,
    privileges.PRIVILEGE_VIEW_THIRD_PARTY_STATUS,
  ];
  privileges.PRIVILEGE_EDIT_THIRD_PARTY.childPrivileges = [
    privileges.PRIVILEGE_VIEW_THIRD_PARTY,
    privileges.PRIVILEGE_EDIT_THIRD_PARTY_CODE
  ];
  privileges.PRIVILEGE_CREATE_THIRD_PARTY.childPrivileges = [
    privileges.PRIVILEGE_VIEW_THIRD_PARTY
  ];
  privileges.PRIVILEGE_ARCHIVE_THIRD_PARTY.childPrivileges = [
    privileges.PRIVILEGE_VIEW_THIRD_PARTY,
  ];
  privileges.PRIVILEGE_ALL_THIRD_PARTY.childPrivileges = [
    privileges.PRIVILEGE_VIEW_THIRD_PARTY,
    privileges.PRIVILEGE_EDIT_THIRD_PARTY,
    privileges.PRIVILEGE_CREATE_THIRD_PARTY,
    privileges.PRIVILEGE_ARCHIVE_THIRD_PARTY
  ];

  privileges.PRIVILEGE_VIEW_THIRD_PARTY_ATTRIBUTES_VALUES_AND_INDICATORS.childPrivileges = [
    privileges.PRIVILEGE_VIEW_THIRD_PARTY_ATTRIBUTE_VALUE_ALL,
    privileges.PRIVILEGE_VIEW_THIRD_PARTY_INDICATOR_ALL,
    privileges.PRIVILEGE_VIEW_THIRD_PARTY_CALCULATED_ATTRIBUTE_ALL
  ]

  privileges.PRIVILEGE_EDIT_THIRD_PARTY_ATTRIBUTE_VALUE_ALL.childPrivileges = [
    privileges.PRIVILEGE_EDIT_THIRD_PARTY_ATTRIBUTE_VALUE,
    privileges.PRIVILEGE_VIEW_THIRD_PARTY_ATTRIBUTE_VALUE_ALL,
  ]

  privileges.PRIVILEGE_VIEW_THIRD_PARTY_ATTRIBUTE_VALUE_ALL.childPrivileges = [
    privileges.PRIVILEGE_VIEW_THIRD_PARTY_ATTRIBUTE_VALUE
  ]

  privileges.PRIVILEGE_VIEW_THIRD_PARTY_INDICATOR_ALL.childPrivileges = [
    privileges.PRIVILEGE_VIEW_THIRD_PARTY_INDICATOR
  ]
  privileges.PRIVILEGE_VIEW_THIRD_PARTY_CALCULATED_ATTRIBUTE_ALL.childPrivileges = [
    privileges.PRIVILEGE_VIEW_THIRD_PARTY_CALCULATED_ATTRIBUTE
  ]

  privileges.PRIVILEGE_CREATE_THIRD_PARTY_NOTES.childPrivileges = [
    privileges.PRIVILEGE_VIEW_THIRD_PARTY_NOTES,
  ];
  privileges.PRIVILEGE_EDIT_THIRD_PARTY_NOTES.childPrivileges = [
    privileges.PRIVILEGE_VIEW_THIRD_PARTY_NOTES,
    privileges.PRIVILEGE_CREATE_THIRD_PARTY_NOTES,
  ];

  privileges.PRIVILEGE_EDIT_THIRD_PARTY_UNMANAGED.childPrivileges = [
    privileges.PRIVILEGE_CREATE_THIRD_PARTY_UNMANAGED,
  ];

  privileges.PRIVILEGE_VIEW_DOSSIER.childPrivileges = [
    privileges.PRIVILEGE_VIEW_DOSSIER_INFORMATION,
    privileges.PRIVILEGE_VIEW_DOSSIER_STATUS
  ];

  privileges.PRIVILEGE_VIEW_DOSSIER_ATTRIBUTE_VALUE_ALL.childPrivileges = [
    privileges.PRIVILEGE_VIEW_DOSSIER_ATTRIBUTE_VALUE,
  ];
  privileges.PRIVILEGE_EDIT_DOSSIER_ATTRIBUTE_VALUE_ALL.childPrivileges = [
    privileges.PRIVILEGE_EDIT_DOSSIER_ATTRIBUTE_VALUE,
    privileges.PRIVILEGE_VIEW_DOSSIER_ATTRIBUTE_VALUE_ALL
  ];

  privileges.PRIVILEGE_VIEW_DOSSIER_INDICATOR_ALL.childPrivileges = [
    privileges.PRIVILEGE_VIEW_DOSSIER_INDICATOR
  ]
  privileges.PRIVILEGE_VIEW_DOSSIER_CALCULATED_ATTRIBUTE_ALL.childPrivileges = [
    privileges.PRIVILEGE_VIEW_DOSSIER_CALCULATED_ATTRIBUTE
  ]

  privileges.PRIVILEGE_VIEW_DOSSIER_ATTRIBUTES_VALUES_AND_INDICATORS.childPrivileges = [
    privileges.PRIVILEGE_VIEW_DOSSIER_ATTRIBUTE_VALUE_ALL,
    privileges.PRIVILEGE_VIEW_DOSSIER_INDICATOR_ALL,
    privileges.PRIVILEGE_VIEW_DOSSIER_CALCULATED_ATTRIBUTE_ALL
  ]

  privileges.PRIVILEGE_EDIT_DOSSIER_STATUS.childPrivileges = [
    privileges.PRIVILEGE_VIEW_DOSSIER,
  ];
  privileges.PRIVILEGE_EDIT_DOSSIER.childPrivileges = [
    privileges.PRIVILEGE_VIEW_DOSSIER,
    privileges.PRIVILEGE_EDIT_DOSSIER_STATUS,
  ];
  privileges.PRIVILEGE_CREATE_DOSSIER.childPrivileges = [
    privileges.PRIVILEGE_VIEW_DOSSIER
  ];
  privileges.PRIVILEGE_ARCHIVE_DOSSIER.childPrivileges = [
    privileges.PRIVILEGE_VIEW_DOSSIER,
  ];
  privileges.PRIVILEGE_CREATE_DOSSIER_NOTES.childPrivileges = [
    privileges.PRIVILEGE_VIEW_DOSSIER_NOTES,
  ];
  privileges.PRIVILEGE_EDIT_DOSSIER_NOTES.childPrivileges = [
    privileges.PRIVILEGE_VIEW_DOSSIER_NOTES,
    privileges.PRIVILEGE_CREATE_DOSSIER_NOTES,
  ];
  privileges.PRIVILEGE_ALL_DOSSIER.childPrivileges = [
    privileges.PRIVILEGE_VIEW_DOSSIER,
    privileges.PRIVILEGE_EDIT_DOSSIER,
    privileges.PRIVILEGE_CREATE_DOSSIER,
    privileges.PRIVILEGE_ARCHIVE_DOSSIER
  ];

  privileges.PRIVILEGE_EDIT_DOCUMENT_STATUS.childPrivileges = [
    privileges.PRIVILEGE_VIEW_DOCUMENT,
  ];
  privileges.PRIVILEGE_EDIT_DOCUMENT_ANSWER.childPrivileges = [
    privileges.PRIVILEGE_VIEW_DOCUMENT,
  ];
  privileges.PRIVILEGE_EDIT_DOCUMENT_APPROVE.childPrivileges = [
    privileges.PRIVILEGE_VIEW_DOCUMENT,
  ];
  privileges.PRIVILEGE_EDIT_DOCUMENT_REJECT.childPrivileges = [
    privileges.PRIVILEGE_VIEW_DOCUMENT,
  ];
  privileges.PRIVILEGE_EDIT_DOCUMENT.childPrivileges = [
    privileges.PRIVILEGE_VIEW_DOCUMENT,
    privileges.PRIVILEGE_EDIT_DOCUMENT_ANSWER,
    privileges.PRIVILEGE_EDIT_DOCUMENT_STATUS,
    privileges.PRIVILEGE_EDIT_DOCUMENT_APPROVE,
    privileges.PRIVILEGE_EDIT_DOCUMENT_REJECT,
  ];
  privileges.PRIVILEGE_CREATE_DOCUMENT.childPrivileges = [
    privileges.PRIVILEGE_VIEW_DOCUMENT
  ];
  privileges.PRIVILEGE_DELETE_DOCUMENT.childPrivileges = [
    privileges.PRIVILEGE_VIEW_DOCUMENT,
  ];
  privileges.PRIVILEGE_CREATE_DOCUMENT_NOTES.childPrivileges = [
    privileges.PRIVILEGE_VIEW_DOCUMENT_NOTES,
  ];
  privileges.PRIVILEGE_EDIT_DOCUMENT_NOTES.childPrivileges = [
    privileges.PRIVILEGE_VIEW_DOCUMENT_NOTES,
    privileges.PRIVILEGE_CREATE_DOCUMENT_NOTES,
  ];
  privileges.PRIVILEGE_ALL_DOCUMENT.childPrivileges = [
    privileges.PRIVILEGE_VIEW_DOCUMENT,
    privileges.PRIVILEGE_EDIT_DOCUMENT,
    privileges.PRIVILEGE_CREATE_DOCUMENT,
    privileges.PRIVILEGE_DELETE_DOCUMENT
  ];


  privileges.PRIVILEGE_EDIT_CONTACT.childPrivileges = [
    privileges.PRIVILEGE_VIEW_CONTACT,
  ];
  privileges.PRIVILEGE_CREATE_CONTACT.childPrivileges = [
    privileges.PRIVILEGE_VIEW_CONTACT
  ];
  privileges.PRIVILEGE_DELETE_CONTACT.childPrivileges = [
    privileges.PRIVILEGE_VIEW_CONTACT,
  ];
  privileges.PRIVILEGE_ALL_CONTACT.childPrivileges = [
    privileges.PRIVILEGE_VIEW_CONTACT,
    privileges.PRIVILEGE_EDIT_CONTACT,
    privileges.PRIVILEGE_CREATE_CONTACT,
    privileges.PRIVILEGE_DELETE_CONTACT
  ];

  privileges.PRIVILEGE_VIEW_CONTEXT.childPrivileges = [
    privileges.PRIVILEGE_VIEW_CONTEXT_INFORMATION,
    privileges.PRIVILEGE_VIEW_CONTEXT_ATTRIBUTES,
    privileges.PRIVILEGE_VIEW_CONTEXT_THIRD_PARTY_ATTRIBUTES,
    privileges.PRIVILEGE_VIEW_CONTEXT_THIRD_PARTY_DEMAND_ATTRIBUTES
  ];
  privileges.PRIVILEGE_CREATE_CONTEXT.childPrivileges = [
    privileges.PRIVILEGE_VIEW_CONTEXT
  ];
  privileges.PRIVILEGE_EDIT_CONTEXT.childPrivileges = [
    privileges.PRIVILEGE_VIEW_CONTEXT
  ];
  privileges.PRIVILEGE_ARCHIVE_CONTEXT.childPrivileges = [
    privileges.PRIVILEGE_VIEW_CONTEXT,
  ];

  privileges.PRIVILEGE_EDIT_CONTEXT_ATTRIBUTES.childPrivileges = [
    privileges.PRIVILEGE_VIEW_CONTEXT_ATTRIBUTES
  ];
  privileges.PRIVILEGE_EDIT_CONTEXT_THIRD_PARTY_ATTRIBUTES.childPrivileges = [
    privileges.PRIVILEGE_VIEW_CONTEXT_THIRD_PARTY_ATTRIBUTES
  ];
  privileges.PRIVILEGE_EDIT_CONTEXT_THIRD_PARTY_DEMAND_ATTRIBUTES.childPrivileges = [
    privileges.PRIVILEGE_VIEW_CONTEXT_THIRD_PARTY_DEMAND_ATTRIBUTES
  ];
  privileges.PRIVILEGE_CREATE_CONTEXT_NOTES.childPrivileges = [
    privileges.PRIVILEGE_VIEW_CONTEXT_NOTES,
  ];
  privileges.PRIVILEGE_EDIT_CONTEXT_NOTES.childPrivileges = [
    privileges.PRIVILEGE_VIEW_CONTEXT_NOTES,
    privileges.PRIVILEGE_CREATE_CONTEXT_NOTES,
  ];
  privileges.PRIVILEGE_ALL_CONTEXT.childPrivileges = [
    privileges.PRIVILEGE_VIEW_CONTEXT,
    privileges.PRIVILEGE_EDIT_CONTEXT,
    privileges.PRIVILEGE_CREATE_CONTEXT,
    privileges.PRIVILEGE_ARCHIVE_CONTEXT
  ];
  // Contexte : configuration
  privileges.PRIVILEGE_EDIT_CONTEXT_MODEL.childPrivileges = [
    privileges.PRIVILEGE_VIEW_CONTEXT_MODEL,
    privileges.PRIVILEGE_EDIT_CONTEXT_MODEL_STATUS,
    privileges.PRIVILEGE_EDIT_CONTEXT_MODEL_PARAMETERS
  ];
  privileges.PRIVILEGE_EDIT_CONTEXT_MODEL_STATUS.childPrivileges = [
    privileges.PRIVILEGE_VIEW_CONTEXT_MODEL
  ];
  privileges.PRIVILEGE_EDIT_CONTEXT_MODEL_PARAMETERS.childPrivileges = [
    privileges.PRIVILEGE_VIEW_CONTEXT_MODEL
  ];
  privileges.PRIVILEGE_CREATE_CONTEXT_MODEL.childPrivileges = [
    privileges.PRIVILEGE_VIEW_CONTEXT_MODEL
  ];
  privileges.PRIVILEGE_DELETE_CONTEXT_MODEL.childPrivileges = [
    privileges.PRIVILEGE_VIEW_CONTEXT_MODEL
  ];

  // Events
  // Events Dossier
  privileges.PRIVILEGE_EVENT_DOSSIER.childPrivileges = [
    privileges.PRIVILEGE_EVENT_DOSSIER_ADDED,
    privileges.PRIVILEGE_EVENT_DOSSIER_ARCHIVED,
    privileges.PRIVILEGE_EVENT_DOSSIER_UPDATED,
    privileges.PRIVILEGE_EVENT_DOSSIER_ATTRIBUTE_UPDATED,
    privileges.PRIVILEGE_EVENT_DOSSIER_CALCULATED_ATTRIBUTE_UPDATED, 
    privileges.PRIVILEGE_EVENT_DOSSIER_INDICATOR_UPDATED,
    privileges.PRIVILEGE_EVENT_DOSSIER_STATUS_UPDATED,
    privileges.PRIVILEGE_EVENT_DOSSIER_COMMENT,
  ];
  privileges.PRIVILEGE_EVENT_DOSSIER_COMMENT.childPrivileges = [
    privileges.PRIVILEGE_EVENT_DOSSIER_COMMENT_ADDED,
    privileges.PRIVILEGE_EVENT_DOSSIER_COMMENT_UPDATED,
    privileges.PRIVILEGE_EVENT_DOSSIER_COMMENT_DELETED,
    privileges.PRIVILEGE_EVENT_DOSSIER_COMMENT_MENTION_ADDED,
    privileges.PRIVILEGE_EVENT_DOSSIER_COMMENT_MENTION_UPDATED,
    privileges.PRIVILEGE_EVENT_DOSSIER_COMMENT_MENTION_DELETED,
  ];
  privileges.PRIVILEGE_EVENT_DOSSIER_STATUS_UPDATED.childPrivileges = [
    privileges.PRIVILEGE_EVENT_DOSSIER_STATUS_UPDATED_ABSENT,
    privileges.PRIVILEGE_EVENT_DOSSIER_STATUS_UPDATED_ONGOING,
    privileges.PRIVILEGE_EVENT_DOSSIER_STATUS_UPDATED_ACTION_REQUIRED,
    privileges.PRIVILEGE_EVENT_DOSSIER_STATUS_UPDATED_VALID,
    privileges.PRIVILEGE_EVENT_DOSSIER_STATUS_UPDATED_BELOW_EXPECTATION,
    privileges.PRIVILEGE_EVENT_DOSSIER_STATUS_UPDATED_INVALID,
  ];

  // Events Document
  privileges.PRIVILEGE_EVENT_DOCUMENT.childPrivileges = [
    privileges.PRIVILEGE_EVENT_DOCUMENT_ADDED,
    privileges.PRIVILEGE_EVENT_DOCUMENT_STOPPED,
    privileges.PRIVILEGE_EVENT_DOCUMENT_ANSWER_ADDED,
    privileges.PRIVILEGE_EVENT_DOCUMENT_ANSWER_UPDATED,
    privileges.PRIVILEGE_EVENT_DOCUMENT_ANSWER_INTERRUPTED,
    privileges.PRIVILEGE_EVENT_DOCUMENT_ANSWER_SCOPE_UPDATED,
    privileges.PRIVILEGE_EVENT_DOCUMENT_ANSWER_EXPIRED,
    privileges.PRIVILEGE_EVENT_DOCUMENT_ANSWER_ACCEPTED,
    privileges.PRIVILEGE_EVENT_DOCUMENT_ANSWER_DENIED,
    privileges.PRIVILEGE_EVENT_DOCUMENT_COMMENT,
    privileges.PRIVILEGE_EVENT_DOCUMENT_ANSWER_STATUS_UPDATE,
    privileges.PRIVILEGE_EVENT_DOCUMENT_TASKS,
    // privileges.PRIVILEGE_EVENT_DOCUMENT_INDUED,
  ];
  privileges.PRIVILEGE_EVENT_DOCUMENT_COMMENT.childPrivileges = [
    privileges.PRIVILEGE_EVENT_DOCUMENT_COMMENT_ADDED,
    privileges.PRIVILEGE_EVENT_DOCUMENT_COMMENT_UPDATED,
    privileges.PRIVILEGE_EVENT_DOCUMENT_COMMENT_DELETED,
    privileges.PRIVILEGE_EVENT_DOCUMENT_COMMENT_MENTION_ADDED,
    privileges.PRIVILEGE_EVENT_DOCUMENT_COMMENT_MENTION_UPDATED,
    privileges.PRIVILEGE_EVENT_DOCUMENT_COMMENT_MENTION_DELETED,
  ];
  privileges.PRIVILEGE_EVENT_DOCUMENT_ANSWER_STATUS_UPDATE.childPrivileges = [
    privileges.PRIVILEGE_EVENT_DOCUMENT_ANSWER_STATUS_UPDATE_ABSENT,
    privileges.PRIVILEGE_EVENT_DOCUMENT_ANSWER_STATUS_UPDATE_ONGOING,
    privileges.PRIVILEGE_EVENT_DOCUMENT_ANSWER_STATUS_UPDATE_ACTION_REQUIRED,
    privileges.PRIVILEGE_EVENT_DOCUMENT_ANSWER_STATUS_UPDATE_VALID,
    privileges.PRIVILEGE_EVENT_DOCUMENT_ANSWER_STATUS_UPDATE_BELOW_EXPECTATION,
    privileges.PRIVILEGE_EVENT_DOCUMENT_ANSWER_STATUS_UPDATE_INVALID,
  ];
  privileges.PRIVILEGE_EVENT_DOCUMENT_TASKS.childPrivileges = [
    privileges.PRIVILEGE_EVENT_DOCUMENT_TASK_AWAITING_VALIDATION,
    privileges.PRIVILEGE_EVENT_DOCUMENT_TASK_DUE_DILIGENCE,
    privileges.PRIVILEGE_EVENT_TASK_INDUED_FALSE_POSITIVE,
  ];

  // Events Tiers
  privileges.PRIVILEGE_EVENT_THIRD_PARTY.childPrivileges = [
    //N.B. Ajouter seulement les evenements dont on veut pouvoir s'abonner
    privileges.PRIVILEGE_EVENT_THIRD_PARTY_ADDED,
    privileges.PRIVILEGE_EVENT_THIRD_PARTY_ARCHIVED,
    privileges.PRIVILEGE_EVENT_THIRD_PARTY_CODE_UPDATED,
    privileges.PRIVILEGE_EVENT_THIRD_PARTY_SOURCE_CHANGED_TO_UNMANAGED,
    privileges.PRIVILEGE_EVENT_THIRD_PARTY_PROCEDURE_COLLECTIVE_UPDATED,
    privileges.PRIVILEGE_EVENT_THIRD_PARTY_LEGAL_ANNOUNCEMENT_CREATION,
    privileges.PRIVILEGE_EVENT_THIRD_PARTY_LEGAL_ANNOUNCEMENT_CHANGES,
    privileges.PRIVILEGE_EVENT_THIRD_PARTY_LEGAL_ANNOUNCEMENT_CESSATION,
    privileges.PRIVILEGE_EVENT_THIRD_PARTY_LEGAL_ANNOUNCEMENT_TRANSFERT,
    privileges.PRIVILEGE_EVENT_THIRD_PARTY_CONTACT,
    privileges.PRIVILEGE_EVENT_THIRD_PARTY_COMMENT,
    privileges.PRIVILEGE_EVENT_THIRD_PARTY_ATTRIBUTE_UPDATED, 
    privileges.PRIVILEGE_EVENT_THIRD_PARTY_CALCULATED_ATTRIBUTE_UPDATED, 
    privileges.PRIVILEGE_EVENT_THIRD_PARTY_INDICATOR_UPDATED,
    privileges.PRIVILEGE_EVENT_THIRD_PARTY_ESG_UPDATED,
    privileges.PRIVILEGE_EVENT_THIRD_PARTY_ECOVADIS_UPDATED,
    privileges.PRIVILEGE_EVENT_THIRD_PARTY_FINANCIAL_STRENGTH_UPDATED,
    privileges.PRIVILEGE_EVENT_THIRD_PARTY_INDUED_UPDATED,
    privileges.PRIVILEGE_EVENT_THIRD_PARTY_SECURITY_RATING_UPDATED,
    privileges.PRIVILEGE_EVENT_THIRD_PARTY_CREDITSAFE_UPDATED,
    privileges.PRIVILEGE_EVENT_THIRD_PARTY_DATA_HUB_UPDATED
  ];
  privileges.PRIVILEGE_EVENT_THIRD_PARTY_COMMENT.childPrivileges = [
    privileges.PRIVILEGE_EVENT_THIRD_PARTY_COMMENT_ADDED,
    privileges.PRIVILEGE_EVENT_THIRD_PARTY_COMMENT_UPDATED,
    privileges.PRIVILEGE_EVENT_THIRD_PARTY_COMMENT_DELETED,
    privileges.PRIVILEGE_EVENT_THIRD_PARTY_COMMENT_MENTION_ADDED,
    privileges.PRIVILEGE_EVENT_THIRD_PARTY_COMMENT_MENTION_UPDATED,
    privileges.PRIVILEGE_EVENT_THIRD_PARTY_COMMENT_MENTION_DELETED,
  ];
  privileges.PRIVILEGE_EVENT_THIRD_PARTY_CONTACT.childPrivileges = [
    privileges.PRIVILEGE_EVENT_THIRD_PARTY_CONTACT_INVITED,
    privileges.PRIVILEGE_EVENT_THIRD_PARTY_CONTACT_RE_INVITED,
    privileges.PRIVILEGE_EVENT_THIRD_PARTY_CONTACT_RE_INVITED_AUTO,
    privileges.PRIVILEGE_EVENT_THIRD_PARTY_CONTACT_DELETED,
    privileges.PRIVILEGE_EVENT_THIRD_PARTY_CONTACT_DELETED_DUE_TO_NO_FAVORABLE_RESPONSE,
  ];
  privileges.PRIVILEGE_EVENT_THIRD_PARTY_ESG_UPDATED.childPrivileges = [
    privileges.PRIVILEGE_EVENT_THIRD_PARTY_ESG_UPDATED_GLOBAL, 
    privileges.PRIVILEGE_EVENT_THIRD_PARTY_ESG_UPDATED_ENVIRONMENTAL,
    privileges.PRIVILEGE_EVENT_THIRD_PARTY_ESG_UPDATED_SOCIAL,
    privileges.PRIVILEGE_EVENT_THIRD_PARTY_ESG_UPDATED_GOVERNANCE
  ];
  privileges.PRIVILEGE_EVENT_THIRD_PARTY_ESG_UPDATED_GLOBAL.childPrivileges = [
    privileges.PRIVILEGE_EVENT_THIRD_PARTY_ESG_UPDATED_GLOBAL_SCORE_1, 
    privileges.PRIVILEGE_EVENT_THIRD_PARTY_ESG_UPDATED_GLOBAL_SCORE_2, 
    privileges.PRIVILEGE_EVENT_THIRD_PARTY_ESG_UPDATED_GLOBAL_SCORE_3, 
    privileges.PRIVILEGE_EVENT_THIRD_PARTY_ESG_UPDATED_GLOBAL_SCORE_4, 
    privileges.PRIVILEGE_EVENT_THIRD_PARTY_ESG_UPDATED_GLOBAL_SCORE_5
  ];
  privileges.PRIVILEGE_EVENT_THIRD_PARTY_ESG_UPDATED_ENVIRONMENTAL.childPrivileges = [
    privileges.PRIVILEGE_EVENT_THIRD_PARTY_ESG_UPDATED_ENVIRONMENTAL_SCORE_1, 
    privileges.PRIVILEGE_EVENT_THIRD_PARTY_ESG_UPDATED_ENVIRONMENTAL_SCORE_2, 
    privileges.PRIVILEGE_EVENT_THIRD_PARTY_ESG_UPDATED_ENVIRONMENTAL_SCORE_3, 
    privileges.PRIVILEGE_EVENT_THIRD_PARTY_ESG_UPDATED_ENVIRONMENTAL_SCORE_4, 
    privileges.PRIVILEGE_EVENT_THIRD_PARTY_ESG_UPDATED_ENVIRONMENTAL_SCORE_5
  ];
  privileges.PRIVILEGE_EVENT_THIRD_PARTY_ESG_UPDATED_SOCIAL.childPrivileges = [
    privileges.PRIVILEGE_EVENT_THIRD_PARTY_ESG_UPDATED_SOCIAL_SCORE_1, 
    privileges.PRIVILEGE_EVENT_THIRD_PARTY_ESG_UPDATED_SOCIAL_SCORE_2, 
    privileges.PRIVILEGE_EVENT_THIRD_PARTY_ESG_UPDATED_SOCIAL_SCORE_3, 
    privileges.PRIVILEGE_EVENT_THIRD_PARTY_ESG_UPDATED_SOCIAL_SCORE_4, 
    privileges.PRIVILEGE_EVENT_THIRD_PARTY_ESG_UPDATED_SOCIAL_SCORE_5
  ];
  privileges.PRIVILEGE_EVENT_THIRD_PARTY_ESG_UPDATED_GOVERNANCE.childPrivileges = [
    privileges.PRIVILEGE_EVENT_THIRD_PARTY_ESG_UPDATED_GOVERNANCE_SCORE_1, 
    privileges.PRIVILEGE_EVENT_THIRD_PARTY_ESG_UPDATED_GOVERNANCE_SCORE_2, 
    privileges.PRIVILEGE_EVENT_THIRD_PARTY_ESG_UPDATED_GOVERNANCE_SCORE_3, 
    privileges.PRIVILEGE_EVENT_THIRD_PARTY_ESG_UPDATED_GOVERNANCE_SCORE_4, 
    privileges.PRIVILEGE_EVENT_THIRD_PARTY_ESG_UPDATED_GOVERNANCE_SCORE_5
  ];

  privileges.PRIVILEGE_EVENT_THIRD_PARTY_ECOVADIS_UPDATED_GLOBAL_SCORE.childPrivileges = [
    privileges.PRIVILEGE_EVENT_THIRD_PARTY_ECOVADIS_UPDATED_GLOBAL_SCORE_NOT_STARTED,
    privileges.PRIVILEGE_EVENT_THIRD_PARTY_ECOVADIS_UPDATED_GLOBAL_SCORE_NOT_PUBLISHED,
    privileges.PRIVILEGE_EVENT_THIRD_PARTY_ECOVADIS_UPDATED_GLOBAL_SCORE_INSUFFICIENT,
    privileges.PRIVILEGE_EVENT_THIRD_PARTY_ECOVADIS_UPDATED_GLOBAL_SCORE_PARTIAL,
    privileges.PRIVILEGE_EVENT_THIRD_PARTY_ECOVADIS_UPDATED_GLOBAL_SCORE_GOOD,
    privileges.PRIVILEGE_EVENT_THIRD_PARTY_ECOVADIS_UPDATED_GLOBAL_SCORE_ADVANCED,
    privileges.PRIVILEGE_EVENT_THIRD_PARTY_ECOVADIS_UPDATED_GLOBAL_SCORE_OUTSTANDING
  ];
  privileges.PRIVILEGE_EVENT_THIRD_PARTY_ECOVADIS_UPDATED_ENV_SCORE.childPrivileges = [
    privileges.PRIVILEGE_EVENT_THIRD_PARTY_ECOVADIS_UPDATED_ENV_SCORE_NOT_STARTED,
    privileges.PRIVILEGE_EVENT_THIRD_PARTY_ECOVADIS_UPDATED_ENV_SCORE_NOT_PUBLISHED,
    privileges.PRIVILEGE_EVENT_THIRD_PARTY_ECOVADIS_UPDATED_ENV_SCORE_INSUFFICIENT,
    privileges.PRIVILEGE_EVENT_THIRD_PARTY_ECOVADIS_UPDATED_ENV_SCORE_PARTIAL,
    privileges.PRIVILEGE_EVENT_THIRD_PARTY_ECOVADIS_UPDATED_ENV_SCORE_GOOD,
    privileges.PRIVILEGE_EVENT_THIRD_PARTY_ECOVADIS_UPDATED_ENV_SCORE_ADVANCED,
    privileges.PRIVILEGE_EVENT_THIRD_PARTY_ECOVADIS_UPDATED_ENV_SCORE_OUTSTANDING
  ];
  privileges.PRIVILEGE_EVENT_THIRD_PARTY_ECOVADIS_UPDATED_LAB_SCORE.childPrivileges = [
    privileges.PRIVILEGE_EVENT_THIRD_PARTY_ECOVADIS_UPDATED_LAB_SCORE_NOT_STARTED,
    privileges.PRIVILEGE_EVENT_THIRD_PARTY_ECOVADIS_UPDATED_LAB_SCORE_NOT_PUBLISHED,
    privileges.PRIVILEGE_EVENT_THIRD_PARTY_ECOVADIS_UPDATED_LAB_SCORE_INSUFFICIENT,
    privileges.PRIVILEGE_EVENT_THIRD_PARTY_ECOVADIS_UPDATED_LAB_SCORE_PARTIAL,
    privileges.PRIVILEGE_EVENT_THIRD_PARTY_ECOVADIS_UPDATED_LAB_SCORE_GOOD,
    privileges.PRIVILEGE_EVENT_THIRD_PARTY_ECOVADIS_UPDATED_LAB_SCORE_ADVANCED,
    privileges.PRIVILEGE_EVENT_THIRD_PARTY_ECOVADIS_UPDATED_LAB_SCORE_OUTSTANDING
  ];
  privileges.PRIVILEGE_EVENT_THIRD_PARTY_ECOVADIS_UPDATED_FBP_SCORE.childPrivileges = [
    privileges.PRIVILEGE_EVENT_THIRD_PARTY_ECOVADIS_UPDATED_FBP_SCORE_NOT_STARTED,
    privileges.PRIVILEGE_EVENT_THIRD_PARTY_ECOVADIS_UPDATED_FBP_SCORE_NOT_PUBLISHED,
    privileges.PRIVILEGE_EVENT_THIRD_PARTY_ECOVADIS_UPDATED_FBP_SCORE_INSUFFICIENT,
    privileges.PRIVILEGE_EVENT_THIRD_PARTY_ECOVADIS_UPDATED_FBP_SCORE_PARTIAL,
    privileges.PRIVILEGE_EVENT_THIRD_PARTY_ECOVADIS_UPDATED_FBP_SCORE_GOOD,
    privileges.PRIVILEGE_EVENT_THIRD_PARTY_ECOVADIS_UPDATED_FBP_SCORE_ADVANCED,
    privileges.PRIVILEGE_EVENT_THIRD_PARTY_ECOVADIS_UPDATED_FBP_SCORE_OUTSTANDING
  ];
  privileges.PRIVILEGE_EVENT_THIRD_PARTY_ECOVADIS_UPDATED_SUP_SCORE.childPrivileges = [
    privileges.PRIVILEGE_EVENT_THIRD_PARTY_ECOVADIS_UPDATED_SUP_SCORE_NOT_STARTED,
    privileges.PRIVILEGE_EVENT_THIRD_PARTY_ECOVADIS_UPDATED_SUP_SCORE_NOT_PUBLISHED,
    privileges.PRIVILEGE_EVENT_THIRD_PARTY_ECOVADIS_UPDATED_SUP_SCORE_INSUFFICIENT,
    privileges.PRIVILEGE_EVENT_THIRD_PARTY_ECOVADIS_UPDATED_SUP_SCORE_PARTIAL,
    privileges.PRIVILEGE_EVENT_THIRD_PARTY_ECOVADIS_UPDATED_SUP_SCORE_GOOD,
    privileges.PRIVILEGE_EVENT_THIRD_PARTY_ECOVADIS_UPDATED_SUP_SCORE_ADVANCED,
    privileges.PRIVILEGE_EVENT_THIRD_PARTY_ECOVADIS_UPDATED_SUP_SCORE_OUTSTANDING
  ];
  privileges.PRIVILEGE_EVENT_THIRD_PARTY_ECOVADIS_UPDATED_CURRENT_STAGE_STATUS.childPrivileges = [
    privileges.PRIVILEGE_EVENT_THIRD_PARTY_ECOVADIS_UPDATED_CURRENT_STAGE_STATUS_REGISTRATION,
    privileges.PRIVILEGE_EVENT_THIRD_PARTY_ECOVADIS_UPDATED_CURRENT_STAGE_STATUS_QUESTIONNAIRE,
    privileges.PRIVILEGE_EVENT_THIRD_PARTY_ECOVADIS_UPDATED_CURRENT_STAGE_STATUS_EXPERT_ANALYSIS,
    privileges.PRIVILEGE_EVENT_THIRD_PARTY_ECOVADIS_UPDATED_CURRENT_STAGE_STATUS_RESULTS
  ];
  privileges.PRIVILEGE_EVENT_THIRD_PARTY_ECOVADIS_UPDATED.childPrivileges = [
    privileges.PRIVILEGE_EVENT_THIRD_PARTY_ECOVADIS_UPDATED_CURRENT_STAGE_STATUS,
    privileges.PRIVILEGE_EVENT_THIRD_PARTY_ECOVADIS_UPDATED_GLOBAL_SCORE,
    privileges.PRIVILEGE_EVENT_THIRD_PARTY_ECOVADIS_UPDATED_ENV_SCORE,
    privileges.PRIVILEGE_EVENT_THIRD_PARTY_ECOVADIS_UPDATED_LAB_SCORE,
    privileges.PRIVILEGE_EVENT_THIRD_PARTY_ECOVADIS_UPDATED_FBP_SCORE,
    privileges.PRIVILEGE_EVENT_THIRD_PARTY_ECOVADIS_UPDATED_SUP_SCORE
  ];
  privileges.PRIVILEGE_EVENT_THIRD_PARTY_FINANCIAL_STRENGTH_UPDATED.childPrivileges = [
    privileges.PRIVILEGE_EVENT_THIRD_PARTY_FINANCIAL_STRENGTH_UPDATED_SCORE_UNKNOWN, 
    privileges.PRIVILEGE_EVENT_THIRD_PARTY_FINANCIAL_STRENGTH_UPDATED_SCORE_VERY_LOW,
    privileges.PRIVILEGE_EVENT_THIRD_PARTY_FINANCIAL_STRENGTH_UPDATED_SCORE_LOW,
    privileges.PRIVILEGE_EVENT_THIRD_PARTY_FINANCIAL_STRENGTH_UPDATED_SCORE_MEDIUM,
    privileges.PRIVILEGE_EVENT_THIRD_PARTY_FINANCIAL_STRENGTH_UPDATED_SCORE_HIGH,
  ];
  privileges.PRIVILEGE_EVENT_THIRD_PARTY_INDUED_UPDATED.childPrivileges = [
    privileges.PRIVILEGE_EVENT_THIRD_PARTY_INDUED_UPDATED_STATUS, 
    privileges.PRIVILEGE_EVENT_THIRD_PARTY_INDUED_UPDATED_RISK,
    privileges.PRIVILEGE_EVENT_THIRD_PARTY_INDUED_UPDATED_COUNTRY,
    privileges.PRIVILEGE_EVENT_THIRD_PARTY_INDUED_UPDATED_ACTIVITY,
    privileges.PRIVILEGE_EVENT_THIRD_PARTY_INDUED_UPDATED_SANCTIONS,
    privileges.PRIVILEGE_EVENT_THIRD_PARTY_INDUED_UPDATED_PEP,
    privileges.PRIVILEGE_EVENT_THIRD_PARTY_INDUED_UPDATED_PN
  ];
  privileges.PRIVILEGE_EVENT_THIRD_PARTY_INDUED_UPDATED_RISK.childPrivileges = [
    privileges.PRIVILEGE_EVENT_THIRD_PARTY_INDUED_UPDATED_RISK_SCORE_0, 
    privileges.PRIVILEGE_EVENT_THIRD_PARTY_INDUED_UPDATED_RISK_SCORE_1,
    privileges.PRIVILEGE_EVENT_THIRD_PARTY_INDUED_UPDATED_RISK_SCORE_2,
    privileges.PRIVILEGE_EVENT_THIRD_PARTY_INDUED_UPDATED_RISK_SCORE_3,
  ];
  privileges.PRIVILEGE_EVENT_THIRD_PARTY_INDUED_UPDATED_COUNTRY.childPrivileges = [
    privileges.PRIVILEGE_EVENT_THIRD_PARTY_INDUED_UPDATED_COUNTRY_SCORE_0, 
    privileges.PRIVILEGE_EVENT_THIRD_PARTY_INDUED_UPDATED_COUNTRY_SCORE_1,
    privileges.PRIVILEGE_EVENT_THIRD_PARTY_INDUED_UPDATED_COUNTRY_SCORE_2,
    privileges.PRIVILEGE_EVENT_THIRD_PARTY_INDUED_UPDATED_COUNTRY_SCORE_3,
  ];
  privileges.PRIVILEGE_EVENT_THIRD_PARTY_INDUED_UPDATED_ACTIVITY.childPrivileges = [
    privileges.PRIVILEGE_EVENT_THIRD_PARTY_INDUED_UPDATED_ACTIVITY_SCORE_0, 
    privileges.PRIVILEGE_EVENT_THIRD_PARTY_INDUED_UPDATED_ACTIVITY_SCORE_1,
    privileges.PRIVILEGE_EVENT_THIRD_PARTY_INDUED_UPDATED_ACTIVITY_SCORE_2,
    privileges.PRIVILEGE_EVENT_THIRD_PARTY_INDUED_UPDATED_ACTIVITY_SCORE_3,
  ];
  privileges.PRIVILEGE_EVENT_THIRD_PARTY_INDUED_UPDATED_SANCTIONS.childPrivileges = [
    privileges.PRIVILEGE_EVENT_THIRD_PARTY_INDUED_UPDATED_SANCTIONS_SCORE_0, 
    privileges.PRIVILEGE_EVENT_THIRD_PARTY_INDUED_UPDATED_SANCTIONS_SCORE_1,
    privileges.PRIVILEGE_EVENT_THIRD_PARTY_INDUED_UPDATED_SANCTIONS_SCORE_2,
    privileges.PRIVILEGE_EVENT_THIRD_PARTY_INDUED_UPDATED_SANCTIONS_SCORE_3,
  ];
  privileges.PRIVILEGE_EVENT_THIRD_PARTY_INDUED_UPDATED_PEP.childPrivileges = [
    privileges.PRIVILEGE_EVENT_THIRD_PARTY_INDUED_UPDATED_PEP_SCORE_0, 
    privileges.PRIVILEGE_EVENT_THIRD_PARTY_INDUED_UPDATED_PEP_SCORE_1,
    privileges.PRIVILEGE_EVENT_THIRD_PARTY_INDUED_UPDATED_PEP_SCORE_2,
    privileges.PRIVILEGE_EVENT_THIRD_PARTY_INDUED_UPDATED_PEP_SCORE_3,
  ];
  privileges.PRIVILEGE_EVENT_THIRD_PARTY_INDUED_UPDATED_PN.childPrivileges = [
    privileges.PRIVILEGE_EVENT_THIRD_PARTY_INDUED_UPDATED_PN_SCORE_0, 
    privileges.PRIVILEGE_EVENT_THIRD_PARTY_INDUED_UPDATED_PN_SCORE_1,
    privileges.PRIVILEGE_EVENT_THIRD_PARTY_INDUED_UPDATED_PN_SCORE_2,
    privileges.PRIVILEGE_EVENT_THIRD_PARTY_INDUED_UPDATED_PN_SCORE_3,
  ];

    privileges.PRIVILEGE_EVENT_THIRD_PARTY_PROCEDURE_COLLECTIVE_UPDATED.childPrivileges = [
    privileges.PRIVILEGE_EVENT_THIRD_PARTY_PROCEDURE_COLLECTIVE_UPDATED_ETAPE_01,
    privileges.PRIVILEGE_EVENT_THIRD_PARTY_PROCEDURE_COLLECTIVE_UPDATED_ETAPE_02,
    privileges.PRIVILEGE_EVENT_THIRD_PARTY_PROCEDURE_COLLECTIVE_UPDATED_ETAPE_03,
    privileges.PRIVILEGE_EVENT_THIRD_PARTY_PROCEDURE_COLLECTIVE_UPDATED_ETAPE_04,
    privileges.PRIVILEGE_EVENT_THIRD_PARTY_PROCEDURE_COLLECTIVE_UPDATED_ETAPE_05,
    privileges.PRIVILEGE_EVENT_THIRD_PARTY_PROCEDURE_COLLECTIVE_UPDATED_ETAPE_06,
    privileges.PRIVILEGE_EVENT_THIRD_PARTY_PROCEDURE_COLLECTIVE_UPDATED_ETAPE_07,
    privileges.PRIVILEGE_EVENT_THIRD_PARTY_PROCEDURE_COLLECTIVE_UPDATED_ETAPE_08,
    privileges.PRIVILEGE_EVENT_THIRD_PARTY_PROCEDURE_COLLECTIVE_UPDATED_ETAPE_09,
    privileges.PRIVILEGE_EVENT_THIRD_PARTY_PROCEDURE_COLLECTIVE_UPDATED_ETAPE_10,
    privileges.PRIVILEGE_EVENT_THIRD_PARTY_PROCEDURE_COLLECTIVE_UPDATED_ETAPE_11,
    privileges.PRIVILEGE_EVENT_THIRD_PARTY_PROCEDURE_COLLECTIVE_UPDATED_ETAPE_12,
    privileges.PRIVILEGE_EVENT_THIRD_PARTY_PROCEDURE_COLLECTIVE_UPDATED_ETAPE_13,
    privileges.PRIVILEGE_EVENT_THIRD_PARTY_PROCEDURE_COLLECTIVE_UPDATED_ETAPE_14,
    privileges.PRIVILEGE_EVENT_THIRD_PARTY_PROCEDURE_COLLECTIVE_UPDATED_ETAPE_15,
    privileges.PRIVILEGE_EVENT_THIRD_PARTY_PROCEDURE_COLLECTIVE_UPDATED_ETAPE_16,
    privileges.PRIVILEGE_EVENT_THIRD_PARTY_PROCEDURE_COLLECTIVE_UPDATED_ETAPE_17,
    privileges.PRIVILEGE_EVENT_THIRD_PARTY_PROCEDURE_COLLECTIVE_UPDATED_ETAPE_18,
    privileges.PRIVILEGE_EVENT_THIRD_PARTY_PROCEDURE_COLLECTIVE_UPDATED_ETAPE_19,
    privileges.PRIVILEGE_EVENT_THIRD_PARTY_PROCEDURE_COLLECTIVE_UPDATED_ETAPE_20
  ]


  privileges.PRIVILEGE_EVENT_THIRD_PARTY_SECURITY_RATING_UPDATED_ATTACK_SURFACE_SCORE.childPrivileges = [
    privileges.PRIVILEGE_EVENT_THIRD_PARTY_SECURITY_RATING_UPDATED_ATTACK_SURFACE_SCORE_A,
    privileges.PRIVILEGE_EVENT_THIRD_PARTY_SECURITY_RATING_UPDATED_ATTACK_SURFACE_SCORE_B,
    privileges.PRIVILEGE_EVENT_THIRD_PARTY_SECURITY_RATING_UPDATED_ATTACK_SURFACE_SCORE_C,
    privileges.PRIVILEGE_EVENT_THIRD_PARTY_SECURITY_RATING_UPDATED_ATTACK_SURFACE_SCORE_D,
    privileges.PRIVILEGE_EVENT_THIRD_PARTY_SECURITY_RATING_UPDATED_ATTACK_SURFACE_SCORE_E,
  ];
  privileges.PRIVILEGE_EVENT_THIRD_PARTY_SECURITY_RATING_UPDATED_MAIL_SCORE.childPrivileges = [
    privileges.PRIVILEGE_EVENT_THIRD_PARTY_SECURITY_RATING_UPDATED_MAIL_SCORE_A,
    privileges.PRIVILEGE_EVENT_THIRD_PARTY_SECURITY_RATING_UPDATED_MAIL_SCORE_B,
    privileges.PRIVILEGE_EVENT_THIRD_PARTY_SECURITY_RATING_UPDATED_MAIL_SCORE_C,
    privileges.PRIVILEGE_EVENT_THIRD_PARTY_SECURITY_RATING_UPDATED_MAIL_SCORE_D,
    privileges.PRIVILEGE_EVENT_THIRD_PARTY_SECURITY_RATING_UPDATED_MAIL_SCORE_E,
  ];
  privileges.PRIVILEGE_EVENT_THIRD_PARTY_SECURITY_RATING_UPDATED_WEB_SCORE.childPrivileges = [
    privileges.PRIVILEGE_EVENT_THIRD_PARTY_SECURITY_RATING_UPDATED_WEB_SCORE_A,
    privileges.PRIVILEGE_EVENT_THIRD_PARTY_SECURITY_RATING_UPDATED_WEB_SCORE_B,
    privileges.PRIVILEGE_EVENT_THIRD_PARTY_SECURITY_RATING_UPDATED_WEB_SCORE_C,
    privileges.PRIVILEGE_EVENT_THIRD_PARTY_SECURITY_RATING_UPDATED_WEB_SCORE_D,
    privileges.PRIVILEGE_EVENT_THIRD_PARTY_SECURITY_RATING_UPDATED_WEB_SCORE_E,
  ];
  privileges.PRIVILEGE_EVENT_THIRD_PARTY_SECURITY_RATING_UPDATED_SECURITY_CONTROL_SCORE.childPrivileges = [
    privileges.PRIVILEGE_EVENT_THIRD_PARTY_SECURITY_RATING_UPDATED_SECURITY_CONTROL_SCORE_A,
    privileges.PRIVILEGE_EVENT_THIRD_PARTY_SECURITY_RATING_UPDATED_SECURITY_CONTROL_SCORE_B,
    privileges.PRIVILEGE_EVENT_THIRD_PARTY_SECURITY_RATING_UPDATED_SECURITY_CONTROL_SCORE_C,
    privileges.PRIVILEGE_EVENT_THIRD_PARTY_SECURITY_RATING_UPDATED_SECURITY_CONTROL_SCORE_D,
    privileges.PRIVILEGE_EVENT_THIRD_PARTY_SECURITY_RATING_UPDATED_SECURITY_CONTROL_SCORE_E,
  ];
  privileges.PRIVILEGE_EVENT_THIRD_PARTY_SECURITY_RATING_UPDATED_VULNERABILITY_SCORE.childPrivileges = [
    privileges.PRIVILEGE_EVENT_THIRD_PARTY_SECURITY_RATING_UPDATED_VULNERABILITY_SCORE_A,
    privileges.PRIVILEGE_EVENT_THIRD_PARTY_SECURITY_RATING_UPDATED_VULNERABILITY_SCORE_B,
    privileges.PRIVILEGE_EVENT_THIRD_PARTY_SECURITY_RATING_UPDATED_VULNERABILITY_SCORE_C,
    privileges.PRIVILEGE_EVENT_THIRD_PARTY_SECURITY_RATING_UPDATED_VULNERABILITY_SCORE_D,
    privileges.PRIVILEGE_EVENT_THIRD_PARTY_SECURITY_RATING_UPDATED_VULNERABILITY_SCORE_E,
  ];
  privileges.PRIVILEGE_EVENT_THIRD_PARTY_SECURITY_RATING_UPDATED_VULNERABILITY_SNAPSHOT_SCORE.childPrivileges = [
    privileges.PRIVILEGE_EVENT_THIRD_PARTY_SECURITY_RATING_UPDATED_VULNERABILITY_SNAPSHOT_SCORE_A,
    privileges.PRIVILEGE_EVENT_THIRD_PARTY_SECURITY_RATING_UPDATED_VULNERABILITY_SNAPSHOT_SCORE_B,
    privileges.PRIVILEGE_EVENT_THIRD_PARTY_SECURITY_RATING_UPDATED_VULNERABILITY_SNAPSHOT_SCORE_C,
    privileges.PRIVILEGE_EVENT_THIRD_PARTY_SECURITY_RATING_UPDATED_VULNERABILITY_SNAPSHOT_SCORE_D,
    privileges.PRIVILEGE_EVENT_THIRD_PARTY_SECURITY_RATING_UPDATED_VULNERABILITY_SNAPSHOT_SCORE_E,
  ];
  privileges.PRIVILEGE_EVENT_THIRD_PARTY_SECURITY_RATING_UPDATED_SECURITY_INCIDENT_SCORE.childPrivileges = [
    privileges.PRIVILEGE_EVENT_THIRD_PARTY_SECURITY_RATING_UPDATED_SECURITY_INCIDENT_SCORE_A,
    privileges.PRIVILEGE_EVENT_THIRD_PARTY_SECURITY_RATING_UPDATED_SECURITY_INCIDENT_SCORE_B,
    privileges.PRIVILEGE_EVENT_THIRD_PARTY_SECURITY_RATING_UPDATED_SECURITY_INCIDENT_SCORE_C,
    privileges.PRIVILEGE_EVENT_THIRD_PARTY_SECURITY_RATING_UPDATED_SECURITY_INCIDENT_SCORE_D,
    privileges.PRIVILEGE_EVENT_THIRD_PARTY_SECURITY_RATING_UPDATED_SECURITY_INCIDENT_SCORE_E,
  ];
  privileges.PRIVILEGE_EVENT_THIRD_PARTY_SECURITY_RATING_UPDATED.childPrivileges = [
    privileges.PRIVILEGE_EVENT_THIRD_PARTY_SECURITY_RATING_UPDATED_GLOBAL_SCORE_ADVANCED,
    privileges.PRIVILEGE_EVENT_THIRD_PARTY_SECURITY_RATING_UPDATED_GLOBAL_SCORE_INTERMEDIATE,
    privileges.PRIVILEGE_EVENT_THIRD_PARTY_SECURITY_RATING_UPDATED_GLOBAL_SCORE_BASIC,
    privileges.PRIVILEGE_EVENT_THIRD_PARTY_SECURITY_RATING_UPDATED_ATTACK_SURFACE_SCORE,
    privileges.PRIVILEGE_EVENT_THIRD_PARTY_SECURITY_RATING_UPDATED_MAIL_SCORE,
    privileges.PRIVILEGE_EVENT_THIRD_PARTY_SECURITY_RATING_UPDATED_WEB_SCORE,
    privileges.PRIVILEGE_EVENT_THIRD_PARTY_SECURITY_RATING_UPDATED_SECURITY_CONTROL_SCORE,
    privileges.PRIVILEGE_EVENT_THIRD_PARTY_SECURITY_RATING_UPDATED_VULNERABILITY_SCORE,
    privileges.PRIVILEGE_EVENT_THIRD_PARTY_SECURITY_RATING_UPDATED_VULNERABILITY_SNAPSHOT_SCORE,
    privileges.PRIVILEGE_EVENT_THIRD_PARTY_SECURITY_RATING_UPDATED_SECURITY_INCIDENT_SCORE
  ];
  // Creditsafe
  privileges.PRIVILEGE_EVENT_THIRD_PARTY_CREDITSAFE_UPDATED_INTERNATIONALSCORE.childPrivileges = [
    privileges.PRIVILEGE_EVENT_THIRD_PARTY_CREDITSAFE_UPDATED_INTERNATIONALSCORE_A,
    privileges.PRIVILEGE_EVENT_THIRD_PARTY_CREDITSAFE_UPDATED_INTERNATIONALSCORE_B,
    privileges.PRIVILEGE_EVENT_THIRD_PARTY_CREDITSAFE_UPDATED_INTERNATIONALSCORE_C,
    privileges.PRIVILEGE_EVENT_THIRD_PARTY_CREDITSAFE_UPDATED_INTERNATIONALSCORE_D,
    privileges.PRIVILEGE_EVENT_THIRD_PARTY_CREDITSAFE_UPDATED_INTERNATIONALSCORE_E,
  ];
  privileges.PRIVILEGE_EVENT_THIRD_PARTY_CREDITSAFE_UPDATED_ACTIVITYEXPOSURE.childPrivileges = [
    privileges.PRIVILEGE_EVENT_THIRD_PARTY_CREDITSAFE_UPDATED_ACTIVITYEXPOSURE_A,
    privileges.PRIVILEGE_EVENT_THIRD_PARTY_CREDITSAFE_UPDATED_ACTIVITYEXPOSURE_B,
    privileges.PRIVILEGE_EVENT_THIRD_PARTY_CREDITSAFE_UPDATED_ACTIVITYEXPOSURE_C,
    privileges.PRIVILEGE_EVENT_THIRD_PARTY_CREDITSAFE_UPDATED_ACTIVITYEXPOSURE_D,
  ];
  privileges.PRIVILEGE_EVENT_THIRD_PARTY_CREDITSAFE_UPDATED_PREFERENTIALSTATUS.childPrivileges = [
    privileges.PRIVILEGE_EVENT_THIRD_PARTY_CREDITSAFE_UPDATED_PREFERENTIALSTATUS_ZERO,
    privileges.PRIVILEGE_EVENT_THIRD_PARTY_CREDITSAFE_UPDATED_PREFERENTIALSTATUS_FOLLOW,
    privileges.PRIVILEGE_EVENT_THIRD_PARTY_CREDITSAFE_UPDATED_PREFERENTIALSTATUS_STOPPED,
    privileges.PRIVILEGE_EVENT_THIRD_PARTY_CREDITSAFE_UPDATED_PREFERENTIALSTATUS_WAITING,
    privileges.PRIVILEGE_EVENT_THIRD_PARTY_CREDITSAFE_UPDATED_PREFERENTIALSTATUS_TRESOR,
    privileges.PRIVILEGE_EVENT_THIRD_PARTY_CREDITSAFE_UPDATED_PREFERENTIALSTATUS_URSSAF,
    privileges.PRIVILEGE_EVENT_THIRD_PARTY_CREDITSAFE_UPDATED_PREFERENTIALSTATUS_HISTORIC,
    privileges.PRIVILEGE_EVENT_THIRD_PARTY_CREDITSAFE_UPDATED_PREFERENTIALSTATUS_DETECTED,
  ];
  privileges.PRIVILEGE_EVENT_THIRD_PARTY_CREDITSAFE_UPDATED.childPrivileges = [
    privileges.PRIVILEGE_EVENT_THIRD_PARTY_CREDITSAFE_UPDATED_INTERNATIONALSCORE,
    privileges.PRIVILEGE_EVENT_THIRD_PARTY_CREDITSAFE_UPDATED_ACTIVITYEXPOSURE,
    privileges.PRIVILEGE_EVENT_THIRD_PARTY_CREDITSAFE_UPDATED_PREFERENTIALSTATUS,
    privileges.PRIVILEGE_EVENT_THIRD_PARTY_CREDITSAFE_UPDATED_NATIONALSCOREVALUE,
    privileges.PRIVILEGE_EVENT_THIRD_PARTY_CREDITSAFE_UPDATED_CREDIT_LIMIT,
    privileges.PRIVILEGE_EVENT_THIRD_PARTY_CREDITSAFE_UPDATED_DBT,
    privileges.PRIVILEGE_EVENT_THIRD_PARTY_CREDITSAFE_UPDATED_COMPANYEXPOSURE,
    privileges.PRIVILEGE_EVENT_THIRD_PARTY_CREDITSAFE_UPDATED_FINANCIALSTRENGTH
  ];

  //Data sources
  privileges.PRIVILEGE_REFRESH_INDUED_SOURCE.childPrivileges = [
    privileges.PRIVILEGE_VIEW_INDUED_SOURCE
  ];
  privileges.PRIVILEGE_EDIT_FINANCIAL_STRENGTH_SOURCE.childPrivileges = [
    privileges.PRIVILEGE_VIEW_FINANCIAL_STRENGTH_SOURCE
  ];
  privileges.PRIVILEGE_EDIT_ECOVADIS_SOURCE.childPrivileges = [
    privileges.PRIVILEGE_VIEW_ECOVADIS_SOURCE
  ];
  privileges.PRIVILEGE_EDIT_SECURITY_RATING_SOURCE.childPrivileges = [
    privileges.PRIVILEGE_VIEW_SECURITY_RATING_SOURCE
  ];
  privileges.PRIVILEGE_EDIT_CREDITSAFE_SOURCE.childPrivileges = [
    privileges.PRIVILEGE_VIEW_CREDITSAFE_SOURCE
  ];


  // ADD NECESSARY PRIVILEGES
  //-- attributes, calculated attributes and indicators, on third party
  privileges.PRIVILEGE_EVENT_THIRD_PARTY_ATTRIBUTE_UPDATED.necessaryPrivileges = [
    privileges.PRIVILEGE_VIEW_THIRD_PARTY_ATTRIBUTE_VALUE
  ]
  privileges.PRIVILEGE_EVENT_THIRD_PARTY_ATTRIBUTE_UPDATED_ALL.necessaryPrivileges = [
    privileges.PRIVILEGE_VIEW_THIRD_PARTY_ATTRIBUTE_VALUE_ALL
  ]
  privileges.PRIVILEGE_EVENT_THIRD_PARTY_CALCULATED_ATTRIBUTE_UPDATED.necessaryPrivileges = [
    privileges.PRIVILEGE_VIEW_THIRD_PARTY_CALCULATED_ATTRIBUTE
  ]
  privileges.PRIVILEGE_EVENT_THIRD_PARTY_CALCULATED_ATTRIBUTE_UPDATED_ALL.necessaryPrivileges = [
    privileges.PRIVILEGE_VIEW_THIRD_PARTY_CALCULATED_ATTRIBUTE_ALL
  ]
  privileges.PRIVILEGE_EVENT_THIRD_PARTY_INDICATOR_UPDATED.necessaryPrivileges = [
    privileges.PRIVILEGE_VIEW_THIRD_PARTY_INDICATOR
  ]
  privileges.PRIVILEGE_EVENT_THIRD_PARTY_INDICATOR_UPDATED_ALL.necessaryPrivileges = [
    privileges.PRIVILEGE_VIEW_THIRD_PARTY_INDICATOR_ALL
  ]
  //-- notes, on third party
  privileges.PRIVILEGE_EVENT_THIRD_PARTY_COMMENT.necessaryPrivileges = [
    privileges.PRIVILEGE_VIEW_THIRD_PARTY_NOTES
  ]
  privileges.PRIVILEGE_EVENT_THIRD_PARTY_COMMENT_ADDED.necessaryPrivileges = [
    privileges.PRIVILEGE_VIEW_THIRD_PARTY_NOTES
  ]
  privileges.PRIVILEGE_EVENT_THIRD_PARTY_COMMENT_UPDATED.necessaryPrivileges = [
    privileges.PRIVILEGE_VIEW_THIRD_PARTY_NOTES
  ]
  privileges.PRIVILEGE_EVENT_THIRD_PARTY_COMMENT_DELETED.necessaryPrivileges = [
    privileges.PRIVILEGE_VIEW_THIRD_PARTY_NOTES
  ]
  privileges.PRIVILEGE_EVENT_THIRD_PARTY_COMMENT_MENTION_ADDED.necessaryPrivileges = [
    privileges.PRIVILEGE_VIEW_THIRD_PARTY_NOTES
  ]
  privileges.PRIVILEGE_EVENT_THIRD_PARTY_COMMENT_MENTION_UPDATED.necessaryPrivileges = [
    privileges.PRIVILEGE_VIEW_THIRD_PARTY_NOTES
  ]
  privileges.PRIVILEGE_EVENT_THIRD_PARTY_COMMENT_MENTION_DELETED.necessaryPrivileges = [
    privileges.PRIVILEGE_VIEW_THIRD_PARTY_NOTES
  ]
  //-- contacts
  privileges.PRIVILEGE_EVENT_THIRD_PARTY_CONTACT.necessaryPrivileges = [
    privileges.PRIVILEGE_VIEW_CONTACT
  ]
  privileges.PRIVILEGE_EVENT_THIRD_PARTY_CONTACT_DELETED.necessaryPrivileges = [
    privileges.PRIVILEGE_VIEW_CONTACT
  ]
  privileges.PRIVILEGE_EVENT_THIRD_PARTY_CONTACT_DELETED_DUE_TO_NO_FAVORABLE_RESPONSE.necessaryPrivileges = [
    privileges.PRIVILEGE_VIEW_CONTACT
  ]
  privileges.PRIVILEGE_EVENT_THIRD_PARTY_CONTACT_INVITED.necessaryPrivileges = [
    privileges.PRIVILEGE_VIEW_CONTACT
  ]
  privileges.PRIVILEGE_EVENT_THIRD_PARTY_CONTACT_RE_INVITED.necessaryPrivileges = [
    privileges.PRIVILEGE_VIEW_CONTACT
  ]
  privileges.PRIVILEGE_EVENT_THIRD_PARTY_CONTACT_RE_INVITED_AUTO.necessaryPrivileges = [
    privileges.PRIVILEGE_VIEW_CONTACT
  ]
  //-- ESG events
  privileges.PRIVILEGE_EVENT_THIRD_PARTY_ESG_ACTIVATED.necessaryPrivileges = [
    privileges.PRIVILEGE_VIEW_DNB_ESG_SOURCE
  ]
  privileges.PRIVILEGE_EVENT_THIRD_PARTY_ESG_DEACTIVATED.necessaryPrivileges = [
    privileges.PRIVILEGE_VIEW_DNB_ESG_SOURCE
  ]
  privileges.PRIVILEGE_EVENT_THIRD_PARTY_ESG_UPDATED.necessaryPrivileges = [
    privileges.PRIVILEGE_VIEW_DNB_ESG_SOURCE
  ]
  privileges.PRIVILEGE_EVENT_THIRD_PARTY_ESG_UPDATED_ENVIRONMENTAL.necessaryPrivileges = [
    privileges.PRIVILEGE_VIEW_DNB_ESG_SOURCE
  ]
  privileges.PRIVILEGE_EVENT_THIRD_PARTY_ESG_UPDATED_ENVIRONMENTAL_SCORE_1.necessaryPrivileges = [
    privileges.PRIVILEGE_VIEW_DNB_ESG_SOURCE
  ]
  privileges.PRIVILEGE_EVENT_THIRD_PARTY_ESG_UPDATED_ENVIRONMENTAL_SCORE_2.necessaryPrivileges = [
    privileges.PRIVILEGE_VIEW_DNB_ESG_SOURCE
  ]
  privileges.PRIVILEGE_EVENT_THIRD_PARTY_ESG_UPDATED_ENVIRONMENTAL_SCORE_3.necessaryPrivileges = [
    privileges.PRIVILEGE_VIEW_DNB_ESG_SOURCE
  ]
  privileges.PRIVILEGE_EVENT_THIRD_PARTY_ESG_UPDATED_ENVIRONMENTAL_SCORE_4.necessaryPrivileges = [
    privileges.PRIVILEGE_VIEW_DNB_ESG_SOURCE
  ]
  privileges.PRIVILEGE_EVENT_THIRD_PARTY_ESG_UPDATED_ENVIRONMENTAL_SCORE_5.necessaryPrivileges = [
    privileges.PRIVILEGE_VIEW_DNB_ESG_SOURCE
  ]
  privileges.PRIVILEGE_EVENT_THIRD_PARTY_ESG_UPDATED_GLOBAL.necessaryPrivileges = [
    privileges.PRIVILEGE_VIEW_DNB_ESG_SOURCE
  ]
  privileges.PRIVILEGE_EVENT_THIRD_PARTY_ESG_UPDATED_GLOBAL_SCORE_1.necessaryPrivileges = [
    privileges.PRIVILEGE_VIEW_DNB_ESG_SOURCE
  ]
  privileges.PRIVILEGE_EVENT_THIRD_PARTY_ESG_UPDATED_GLOBAL_SCORE_2.necessaryPrivileges = [
    privileges.PRIVILEGE_VIEW_DNB_ESG_SOURCE
  ]
  privileges.PRIVILEGE_EVENT_THIRD_PARTY_ESG_UPDATED_GLOBAL_SCORE_3.necessaryPrivileges = [
    privileges.PRIVILEGE_VIEW_DNB_ESG_SOURCE
  ]
  privileges.PRIVILEGE_EVENT_THIRD_PARTY_ESG_UPDATED_GLOBAL_SCORE_4.necessaryPrivileges = [
    privileges.PRIVILEGE_VIEW_DNB_ESG_SOURCE
  ]
  privileges.PRIVILEGE_EVENT_THIRD_PARTY_ESG_UPDATED_GLOBAL_SCORE_5.necessaryPrivileges = [
    privileges.PRIVILEGE_VIEW_DNB_ESG_SOURCE
  ]
  privileges.PRIVILEGE_EVENT_THIRD_PARTY_ESG_UPDATED_GOVERNANCE.necessaryPrivileges = [
    privileges.PRIVILEGE_VIEW_DNB_ESG_SOURCE
  ]
  privileges.PRIVILEGE_EVENT_THIRD_PARTY_ESG_UPDATED_GOVERNANCE_SCORE_1.necessaryPrivileges = [
    privileges.PRIVILEGE_VIEW_DNB_ESG_SOURCE
  ]
  privileges.PRIVILEGE_EVENT_THIRD_PARTY_ESG_UPDATED_GOVERNANCE_SCORE_2.necessaryPrivileges = [
    privileges.PRIVILEGE_VIEW_DNB_ESG_SOURCE
  ]
  privileges.PRIVILEGE_EVENT_THIRD_PARTY_ESG_UPDATED_GOVERNANCE_SCORE_3.necessaryPrivileges = [
    privileges.PRIVILEGE_VIEW_DNB_ESG_SOURCE
  ]
  privileges.PRIVILEGE_EVENT_THIRD_PARTY_ESG_UPDATED_GOVERNANCE_SCORE_4.necessaryPrivileges = [
    privileges.PRIVILEGE_VIEW_DNB_ESG_SOURCE
  ]
  privileges.PRIVILEGE_EVENT_THIRD_PARTY_ESG_UPDATED_GOVERNANCE_SCORE_5.necessaryPrivileges = [
    privileges.PRIVILEGE_VIEW_DNB_ESG_SOURCE
  ]
  privileges.PRIVILEGE_EVENT_THIRD_PARTY_ESG_UPDATED_SOCIAL.necessaryPrivileges = [
    privileges.PRIVILEGE_VIEW_DNB_ESG_SOURCE
  ]
  privileges.PRIVILEGE_EVENT_THIRD_PARTY_ESG_UPDATED_SOCIAL_SCORE_1.necessaryPrivileges = [
    privileges.PRIVILEGE_VIEW_DNB_ESG_SOURCE
  ]
  privileges.PRIVILEGE_EVENT_THIRD_PARTY_ESG_UPDATED_SOCIAL_SCORE_2.necessaryPrivileges = [
    privileges.PRIVILEGE_VIEW_DNB_ESG_SOURCE
  ]
  privileges.PRIVILEGE_EVENT_THIRD_PARTY_ESG_UPDATED_SOCIAL_SCORE_3.necessaryPrivileges = [
    privileges.PRIVILEGE_VIEW_DNB_ESG_SOURCE
  ]
  privileges.PRIVILEGE_EVENT_THIRD_PARTY_ESG_UPDATED_SOCIAL_SCORE_4.necessaryPrivileges = [
    privileges.PRIVILEGE_VIEW_DNB_ESG_SOURCE
  ]
  privileges.PRIVILEGE_EVENT_THIRD_PARTY_ESG_UPDATED_SOCIAL_SCORE_5.necessaryPrivileges = [
    privileges.PRIVILEGE_VIEW_DNB_ESG_SOURCE
  ]
  //-- Ecovadis events
  privileges.PRIVILEGE_EVENT_THIRD_PARTY_ECOVADIS_ACTIVATED.necessaryPrivileges = [
    privileges.PRIVILEGE_VIEW_ECOVADIS_SOURCE
  ]
  privileges.PRIVILEGE_EVENT_THIRD_PARTY_ECOVADIS_DEACTIVATED.necessaryPrivileges = [
    privileges.PRIVILEGE_VIEW_ECOVADIS_SOURCE
  ]
  privileges.PRIVILEGE_EVENT_THIRD_PARTY_ECOVADIS_UPDATED.necessaryPrivileges = [
    privileges.PRIVILEGE_VIEW_ECOVADIS_SOURCE
  ]
  privileges.PRIVILEGE_EVENT_THIRD_PARTY_ECOVADIS_UPDATED_CURRENT_STAGE_STATUS.necessaryPrivileges = [
    privileges.PRIVILEGE_VIEW_ECOVADIS_SOURCE
  ]
  privileges.PRIVILEGE_EVENT_THIRD_PARTY_ECOVADIS_UPDATED_CURRENT_STAGE_STATUS_EXPERT_ANALYSIS.necessaryPrivileges = [
    privileges.PRIVILEGE_VIEW_ECOVADIS_SOURCE
  ]
  privileges.PRIVILEGE_EVENT_THIRD_PARTY_ECOVADIS_UPDATED_CURRENT_STAGE_STATUS_QUESTIONNAIRE.necessaryPrivileges = [
    privileges.PRIVILEGE_VIEW_ECOVADIS_SOURCE
  ]
  privileges.PRIVILEGE_EVENT_THIRD_PARTY_ECOVADIS_UPDATED_CURRENT_STAGE_STATUS_REGISTRATION.necessaryPrivileges = [
    privileges.PRIVILEGE_VIEW_ECOVADIS_SOURCE
  ]
  privileges.PRIVILEGE_EVENT_THIRD_PARTY_ECOVADIS_UPDATED_CURRENT_STAGE_STATUS_RESULTS.necessaryPrivileges = [
    privileges.PRIVILEGE_VIEW_ECOVADIS_SOURCE
  ]
  privileges.PRIVILEGE_EVENT_THIRD_PARTY_ECOVADIS_UPDATED_ENV_SCORE.necessaryPrivileges = [
    privileges.PRIVILEGE_VIEW_ECOVADIS_SOURCE
  ]
   privileges.PRIVILEGE_EVENT_THIRD_PARTY_ECOVADIS_UPDATED_ENV_SCORE_ADVANCED.necessaryPrivileges = [
    privileges.PRIVILEGE_VIEW_ECOVADIS_SOURCE
  ]
  privileges.PRIVILEGE_EVENT_THIRD_PARTY_ECOVADIS_UPDATED_ENV_SCORE_GOOD.necessaryPrivileges = [
    privileges.PRIVILEGE_VIEW_ECOVADIS_SOURCE
  ]
  privileges.PRIVILEGE_EVENT_THIRD_PARTY_ECOVADIS_UPDATED_ENV_SCORE_INSUFFICIENT.necessaryPrivileges = [
    privileges.PRIVILEGE_VIEW_ECOVADIS_SOURCE
  ]
  privileges.PRIVILEGE_EVENT_THIRD_PARTY_ECOVADIS_UPDATED_ENV_SCORE_NOT_PUBLISHED.necessaryPrivileges = [
    privileges.PRIVILEGE_VIEW_ECOVADIS_SOURCE
  ]
  privileges.PRIVILEGE_EVENT_THIRD_PARTY_ECOVADIS_UPDATED_ENV_SCORE_NOT_STARTED.necessaryPrivileges = [
    privileges.PRIVILEGE_VIEW_ECOVADIS_SOURCE
  ]
  privileges.PRIVILEGE_EVENT_THIRD_PARTY_ECOVADIS_UPDATED_ENV_SCORE_OUTSTANDING.necessaryPrivileges = [
    privileges.PRIVILEGE_VIEW_ECOVADIS_SOURCE
  ]
  privileges.PRIVILEGE_EVENT_THIRD_PARTY_ECOVADIS_UPDATED_ENV_SCORE_PARTIAL.necessaryPrivileges = [
    privileges.PRIVILEGE_VIEW_ECOVADIS_SOURCE
  ]
  privileges.PRIVILEGE_EVENT_THIRD_PARTY_ECOVADIS_UPDATED_FBP_SCORE.necessaryPrivileges = [
    privileges.PRIVILEGE_VIEW_ECOVADIS_SOURCE
  ]
  privileges.PRIVILEGE_EVENT_THIRD_PARTY_ECOVADIS_UPDATED_FBP_SCORE_ADVANCED.necessaryPrivileges = [
    privileges.PRIVILEGE_VIEW_ECOVADIS_SOURCE
  ]
  privileges.PRIVILEGE_EVENT_THIRD_PARTY_ECOVADIS_UPDATED_FBP_SCORE_GOOD.necessaryPrivileges = [
    privileges.PRIVILEGE_VIEW_ECOVADIS_SOURCE
  ]
  privileges.PRIVILEGE_EVENT_THIRD_PARTY_ECOVADIS_UPDATED_FBP_SCORE_INSUFFICIENT.necessaryPrivileges = [
    privileges.PRIVILEGE_VIEW_ECOVADIS_SOURCE
  ]
  privileges.PRIVILEGE_EVENT_THIRD_PARTY_ECOVADIS_UPDATED_FBP_SCORE_NOT_PUBLISHED.necessaryPrivileges = [
    privileges.PRIVILEGE_VIEW_ECOVADIS_SOURCE
  ]
  privileges.PRIVILEGE_EVENT_THIRD_PARTY_ECOVADIS_UPDATED_FBP_SCORE_NOT_STARTED.necessaryPrivileges = [
    privileges.PRIVILEGE_VIEW_ECOVADIS_SOURCE
  ]
  privileges.PRIVILEGE_EVENT_THIRD_PARTY_ECOVADIS_UPDATED_FBP_SCORE_OUTSTANDING.necessaryPrivileges = [
    privileges.PRIVILEGE_VIEW_ECOVADIS_SOURCE
  ]
  privileges.PRIVILEGE_EVENT_THIRD_PARTY_ECOVADIS_UPDATED_FBP_SCORE_PARTIAL.necessaryPrivileges = [
    privileges.PRIVILEGE_VIEW_ECOVADIS_SOURCE
  ]
  privileges.PRIVILEGE_EVENT_THIRD_PARTY_ECOVADIS_UPDATED_GLOBAL_SCORE.necessaryPrivileges = [
    privileges.PRIVILEGE_VIEW_ECOVADIS_SOURCE
  ]
  privileges.PRIVILEGE_EVENT_THIRD_PARTY_ECOVADIS_UPDATED_GLOBAL_SCORE_ADVANCED.necessaryPrivileges = [
    privileges.PRIVILEGE_VIEW_ECOVADIS_SOURCE
  ]
  privileges.PRIVILEGE_EVENT_THIRD_PARTY_ECOVADIS_UPDATED_GLOBAL_SCORE_GOOD.necessaryPrivileges = [
    privileges.PRIVILEGE_VIEW_ECOVADIS_SOURCE
  ]
  privileges.PRIVILEGE_EVENT_THIRD_PARTY_ECOVADIS_UPDATED_GLOBAL_SCORE_INSUFFICIENT.necessaryPrivileges = [
    privileges.PRIVILEGE_VIEW_ECOVADIS_SOURCE
  ]
  privileges.PRIVILEGE_EVENT_THIRD_PARTY_ECOVADIS_UPDATED_GLOBAL_SCORE_NOT_PUBLISHED.necessaryPrivileges = [
    privileges.PRIVILEGE_VIEW_ECOVADIS_SOURCE
  ]
  privileges.PRIVILEGE_EVENT_THIRD_PARTY_ECOVADIS_UPDATED_GLOBAL_SCORE_NOT_STARTED.necessaryPrivileges = [
    privileges.PRIVILEGE_VIEW_ECOVADIS_SOURCE
  ]
  privileges.PRIVILEGE_EVENT_THIRD_PARTY_ECOVADIS_UPDATED_GLOBAL_SCORE_OUTSTANDING.necessaryPrivileges = [
    privileges.PRIVILEGE_VIEW_ECOVADIS_SOURCE
  ]
  privileges.PRIVILEGE_EVENT_THIRD_PARTY_ECOVADIS_UPDATED_GLOBAL_SCORE_PARTIAL.necessaryPrivileges = [
    privileges.PRIVILEGE_VIEW_ECOVADIS_SOURCE
  ]
  privileges.PRIVILEGE_EVENT_THIRD_PARTY_ECOVADIS_UPDATED_LAB_SCORE.necessaryPrivileges = [
    privileges.PRIVILEGE_VIEW_ECOVADIS_SOURCE
  ]
  privileges.PRIVILEGE_EVENT_THIRD_PARTY_ECOVADIS_UPDATED_LAB_SCORE_ADVANCED.necessaryPrivileges = [
    privileges.PRIVILEGE_VIEW_ECOVADIS_SOURCE
  ]
  privileges.PRIVILEGE_EVENT_THIRD_PARTY_ECOVADIS_UPDATED_LAB_SCORE_GOOD.necessaryPrivileges = [
    privileges.PRIVILEGE_VIEW_ECOVADIS_SOURCE
  ]
  privileges.PRIVILEGE_EVENT_THIRD_PARTY_ECOVADIS_UPDATED_LAB_SCORE_INSUFFICIENT.necessaryPrivileges = [
    privileges.PRIVILEGE_VIEW_ECOVADIS_SOURCE
  ]
  privileges.PRIVILEGE_EVENT_THIRD_PARTY_ECOVADIS_UPDATED_LAB_SCORE_NOT_PUBLISHED.necessaryPrivileges = [
    privileges.PRIVILEGE_VIEW_ECOVADIS_SOURCE
  ]
  privileges.PRIVILEGE_EVENT_THIRD_PARTY_ECOVADIS_UPDATED_LAB_SCORE_NOT_STARTED.necessaryPrivileges = [
    privileges.PRIVILEGE_VIEW_ECOVADIS_SOURCE
  ]
  privileges.PRIVILEGE_EVENT_THIRD_PARTY_ECOVADIS_UPDATED_LAB_SCORE_OUTSTANDING.necessaryPrivileges = [
    privileges.PRIVILEGE_VIEW_ECOVADIS_SOURCE
  ]
  privileges.PRIVILEGE_EVENT_THIRD_PARTY_ECOVADIS_UPDATED_LAB_SCORE_PARTIAL.necessaryPrivileges = [
    privileges.PRIVILEGE_VIEW_ECOVADIS_SOURCE
  ]
  privileges.PRIVILEGE_EVENT_THIRD_PARTY_ECOVADIS_UPDATED_SUP_SCORE.necessaryPrivileges = [
    privileges.PRIVILEGE_VIEW_ECOVADIS_SOURCE
  ]
  privileges.PRIVILEGE_EVENT_THIRD_PARTY_ECOVADIS_UPDATED_SUP_SCORE_ADVANCED.necessaryPrivileges = [
    privileges.PRIVILEGE_VIEW_ECOVADIS_SOURCE
  ]
  privileges.PRIVILEGE_EVENT_THIRD_PARTY_ECOVADIS_UPDATED_SUP_SCORE_GOOD.necessaryPrivileges = [
    privileges.PRIVILEGE_VIEW_ECOVADIS_SOURCE
  ]
  privileges.PRIVILEGE_EVENT_THIRD_PARTY_ECOVADIS_UPDATED_SUP_SCORE_INSUFFICIENT.necessaryPrivileges = [
    privileges.PRIVILEGE_VIEW_ECOVADIS_SOURCE
  ]
  privileges.PRIVILEGE_EVENT_THIRD_PARTY_ECOVADIS_UPDATED_SUP_SCORE_NOT_PUBLISHED.necessaryPrivileges = [
    privileges.PRIVILEGE_VIEW_ECOVADIS_SOURCE
  ]
  privileges.PRIVILEGE_EVENT_THIRD_PARTY_ECOVADIS_UPDATED_SUP_SCORE_NOT_STARTED.necessaryPrivileges = [
    privileges.PRIVILEGE_VIEW_ECOVADIS_SOURCE
  ]
  privileges.PRIVILEGE_EVENT_THIRD_PARTY_ECOVADIS_UPDATED_SUP_SCORE_OUTSTANDING.necessaryPrivileges = [
    privileges.PRIVILEGE_VIEW_ECOVADIS_SOURCE
  ]
  privileges.PRIVILEGE_EVENT_THIRD_PARTY_ECOVADIS_UPDATED_SUP_SCORE_PARTIAL.necessaryPrivileges = [
    privileges.PRIVILEGE_VIEW_ECOVADIS_SOURCE
  ]
  //--Financial Strength events
  privileges.PRIVILEGE_EVENT_THIRD_PARTY_FINANCIAL_STRENGTH_ACTIVATED.necessaryPrivileges = [
    privileges.PRIVILEGE_VIEW_FINANCIAL_STRENGTH_SOURCE
  ]
  privileges.PRIVILEGE_EVENT_THIRD_PARTY_FINANCIAL_STRENGTH_DEACTIVATED.necessaryPrivileges = [
    privileges.PRIVILEGE_VIEW_FINANCIAL_STRENGTH_SOURCE
  ]
  privileges.PRIVILEGE_EVENT_THIRD_PARTY_FINANCIAL_STRENGTH_UPDATED.necessaryPrivileges = [
    privileges.PRIVILEGE_VIEW_FINANCIAL_STRENGTH_SOURCE
  ]
  privileges.PRIVILEGE_EVENT_THIRD_PARTY_FINANCIAL_STRENGTH_UPDATED_SCORE_HIGH.necessaryPrivileges = [
    privileges.PRIVILEGE_VIEW_FINANCIAL_STRENGTH_SOURCE
  ]
  privileges.PRIVILEGE_EVENT_THIRD_PARTY_FINANCIAL_STRENGTH_UPDATED_SCORE_LOW.necessaryPrivileges = [
    privileges.PRIVILEGE_VIEW_FINANCIAL_STRENGTH_SOURCE
  ]
  privileges.PRIVILEGE_EVENT_THIRD_PARTY_FINANCIAL_STRENGTH_UPDATED_SCORE_MEDIUM.necessaryPrivileges = [
    privileges.PRIVILEGE_VIEW_FINANCIAL_STRENGTH_SOURCE
  ]
  privileges.PRIVILEGE_EVENT_THIRD_PARTY_FINANCIAL_STRENGTH_UPDATED_SCORE_UNKNOWN.necessaryPrivileges = [
    privileges.PRIVILEGE_VIEW_FINANCIAL_STRENGTH_SOURCE
  ]
  privileges.PRIVILEGE_EVENT_THIRD_PARTY_FINANCIAL_STRENGTH_UPDATED_SCORE_VERY_LOW.necessaryPrivileges = [
    privileges.PRIVILEGE_VIEW_FINANCIAL_STRENGTH_SOURCE
  ]
  //--IndueD Events
  privileges.PRIVILEGE_EVENT_THIRD_PARTY_INDUED_ACTIVATED.necessaryPrivileges = [
    privileges.PRIVILEGE_VIEW_INDUED_SOURCE
  ]
  privileges.PRIVILEGE_EVENT_THIRD_PARTY_INDUED_DEACTIVATED.necessaryPrivileges = [
    privileges.PRIVILEGE_VIEW_INDUED_SOURCE
  ]
  privileges.PRIVILEGE_EVENT_THIRD_PARTY_INDUED_UPDATED.necessaryPrivileges = [
    privileges.PRIVILEGE_VIEW_INDUED_SOURCE
  ]
  privileges.PRIVILEGE_EVENT_THIRD_PARTY_INDUED_UPDATED_ACTIVITY.necessaryPrivileges = [
    privileges.PRIVILEGE_VIEW_INDUED_SOURCE
  ]
  privileges.PRIVILEGE_EVENT_THIRD_PARTY_INDUED_UPDATED_ACTIVITY_SCORE_0.necessaryPrivileges = [
    privileges.PRIVILEGE_VIEW_INDUED_SOURCE
  ]
  privileges.PRIVILEGE_EVENT_THIRD_PARTY_INDUED_UPDATED_ACTIVITY_SCORE_1.necessaryPrivileges = [
    privileges.PRIVILEGE_VIEW_INDUED_SOURCE
  ]
  privileges.PRIVILEGE_EVENT_THIRD_PARTY_INDUED_UPDATED_ACTIVITY_SCORE_2.necessaryPrivileges = [
    privileges.PRIVILEGE_VIEW_INDUED_SOURCE
  ]
  privileges.PRIVILEGE_EVENT_THIRD_PARTY_INDUED_UPDATED_ACTIVITY_SCORE_3.necessaryPrivileges = [
    privileges.PRIVILEGE_VIEW_INDUED_SOURCE
  ]
  privileges.PRIVILEGE_EVENT_THIRD_PARTY_INDUED_UPDATED_COUNTRY.necessaryPrivileges = [
    privileges.PRIVILEGE_VIEW_INDUED_SOURCE
  ]
  privileges.PRIVILEGE_EVENT_THIRD_PARTY_INDUED_UPDATED_COUNTRY_SCORE_0.necessaryPrivileges = [
    privileges.PRIVILEGE_VIEW_INDUED_SOURCE
  ]
  privileges.PRIVILEGE_EVENT_THIRD_PARTY_INDUED_UPDATED_COUNTRY_SCORE_1.necessaryPrivileges = [
    privileges.PRIVILEGE_VIEW_INDUED_SOURCE
  ]
   privileges.PRIVILEGE_EVENT_THIRD_PARTY_INDUED_UPDATED_COUNTRY_SCORE_2.necessaryPrivileges = [
    privileges.PRIVILEGE_VIEW_INDUED_SOURCE
  ]
   privileges.PRIVILEGE_EVENT_THIRD_PARTY_INDUED_UPDATED_COUNTRY_SCORE_3.necessaryPrivileges = [
    privileges.PRIVILEGE_VIEW_INDUED_SOURCE
  ]
  privileges.PRIVILEGE_EVENT_THIRD_PARTY_INDUED_UPDATED_PEP.necessaryPrivileges = [
    privileges.PRIVILEGE_VIEW_INDUED_SOURCE
  ]
  privileges.PRIVILEGE_EVENT_THIRD_PARTY_INDUED_UPDATED_PEP_SCORE_0.necessaryPrivileges = [
    privileges.PRIVILEGE_VIEW_INDUED_SOURCE
  ]
  privileges.PRIVILEGE_EVENT_THIRD_PARTY_INDUED_UPDATED_PEP_SCORE_1.necessaryPrivileges = [
    privileges.PRIVILEGE_VIEW_INDUED_SOURCE
  ]
  privileges.PRIVILEGE_EVENT_THIRD_PARTY_INDUED_UPDATED_PEP_SCORE_2.necessaryPrivileges = [
    privileges.PRIVILEGE_VIEW_INDUED_SOURCE
  ]
  privileges.PRIVILEGE_EVENT_THIRD_PARTY_INDUED_UPDATED_PEP_SCORE_3.necessaryPrivileges = [
    privileges.PRIVILEGE_VIEW_INDUED_SOURCE
  ]
  privileges.PRIVILEGE_EVENT_THIRD_PARTY_INDUED_UPDATED_PN.necessaryPrivileges = [
    privileges.PRIVILEGE_VIEW_INDUED_SOURCE
  ]
  privileges.PRIVILEGE_EVENT_THIRD_PARTY_INDUED_UPDATED_PN_SCORE_0.necessaryPrivileges = [
    privileges.PRIVILEGE_VIEW_INDUED_SOURCE
  ]
  privileges.PRIVILEGE_EVENT_THIRD_PARTY_INDUED_UPDATED_PN_SCORE_1.necessaryPrivileges = [
    privileges.PRIVILEGE_VIEW_INDUED_SOURCE
  ]
  privileges.PRIVILEGE_EVENT_THIRD_PARTY_INDUED_UPDATED_PN_SCORE_2.necessaryPrivileges = [
    privileges.PRIVILEGE_VIEW_INDUED_SOURCE
  ]
  privileges.PRIVILEGE_EVENT_THIRD_PARTY_INDUED_UPDATED_PN_SCORE_3.necessaryPrivileges = [
    privileges.PRIVILEGE_VIEW_INDUED_SOURCE
  ]
  privileges.PRIVILEGE_EVENT_THIRD_PARTY_INDUED_UPDATED_RISK.necessaryPrivileges = [
    privileges.PRIVILEGE_VIEW_INDUED_SOURCE
  ]
  privileges.PRIVILEGE_EVENT_THIRD_PARTY_INDUED_UPDATED_RISK_SCORE_0.necessaryPrivileges = [
    privileges.PRIVILEGE_VIEW_INDUED_SOURCE
  ]
  privileges.PRIVILEGE_EVENT_THIRD_PARTY_INDUED_UPDATED_RISK_SCORE_1.necessaryPrivileges = [
    privileges.PRIVILEGE_VIEW_INDUED_SOURCE
  ]
  privileges.PRIVILEGE_EVENT_THIRD_PARTY_INDUED_UPDATED_RISK_SCORE_2.necessaryPrivileges = [
    privileges.PRIVILEGE_VIEW_INDUED_SOURCE
  ]
  privileges.PRIVILEGE_EVENT_THIRD_PARTY_INDUED_UPDATED_RISK_SCORE_3.necessaryPrivileges = [
    privileges.PRIVILEGE_VIEW_INDUED_SOURCE
  ]
  privileges.PRIVILEGE_EVENT_THIRD_PARTY_INDUED_UPDATED_SANCTIONS.necessaryPrivileges = [
    privileges.PRIVILEGE_VIEW_INDUED_SOURCE
  ]
  privileges.PRIVILEGE_EVENT_THIRD_PARTY_INDUED_UPDATED_SANCTIONS_SCORE_0.necessaryPrivileges = [
    privileges.PRIVILEGE_VIEW_INDUED_SOURCE
  ]
  privileges.PRIVILEGE_EVENT_THIRD_PARTY_INDUED_UPDATED_SANCTIONS_SCORE_1.necessaryPrivileges = [
    privileges.PRIVILEGE_VIEW_INDUED_SOURCE
  ]
  privileges.PRIVILEGE_EVENT_THIRD_PARTY_INDUED_UPDATED_SANCTIONS_SCORE_2.necessaryPrivileges = [
    privileges.PRIVILEGE_VIEW_INDUED_SOURCE
  ]
  privileges.PRIVILEGE_EVENT_THIRD_PARTY_INDUED_UPDATED_SANCTIONS_SCORE_3.necessaryPrivileges = [
    privileges.PRIVILEGE_VIEW_INDUED_SOURCE
  ]
  privileges.PRIVILEGE_EVENT_THIRD_PARTY_INDUED_UPDATED_STATUS.necessaryPrivileges = [
    
  ]
  //Security Ratings events
  privileges.PRIVILEGE_EVENT_THIRD_PARTY_SECURITY_RATING_UPDATED.necessaryPrivileges = [
    privileges.PRIVILEGE_VIEW_SECURITY_RATING_SOURCE
  ]
  privileges.PRIVILEGE_EVENT_THIRD_PARTY_SECURITY_RATING_UPDATED_ATTACK_SURFACE_SCORE.necessaryPrivileges = [
    privileges.PRIVILEGE_VIEW_SECURITY_RATING_SOURCE
  ]
  privileges.PRIVILEGE_EVENT_THIRD_PARTY_SECURITY_RATING_UPDATED_ATTACK_SURFACE_SCORE_A.necessaryPrivileges = [
    privileges.PRIVILEGE_VIEW_SECURITY_RATING_SOURCE
  ]
  privileges.PRIVILEGE_EVENT_THIRD_PARTY_SECURITY_RATING_UPDATED_ATTACK_SURFACE_SCORE_B.necessaryPrivileges = [
    privileges.PRIVILEGE_VIEW_SECURITY_RATING_SOURCE
  ]
  privileges.PRIVILEGE_EVENT_THIRD_PARTY_SECURITY_RATING_UPDATED_ATTACK_SURFACE_SCORE_C.necessaryPrivileges = [
    privileges.PRIVILEGE_VIEW_SECURITY_RATING_SOURCE
  ]
  privileges.PRIVILEGE_EVENT_THIRD_PARTY_SECURITY_RATING_UPDATED_ATTACK_SURFACE_SCORE_D.necessaryPrivileges = [
    privileges.PRIVILEGE_VIEW_SECURITY_RATING_SOURCE
  ]
  privileges.PRIVILEGE_EVENT_THIRD_PARTY_SECURITY_RATING_UPDATED_ATTACK_SURFACE_SCORE_E.necessaryPrivileges = [
    privileges.PRIVILEGE_VIEW_SECURITY_RATING_SOURCE
  ]
  privileges.PRIVILEGE_EVENT_THIRD_PARTY_SECURITY_RATING_UPDATED_GLOBAL_SCORE_BASIC.necessaryPrivileges = [
    privileges.PRIVILEGE_VIEW_SECURITY_RATING_SOURCE
  ]
  privileges.PRIVILEGE_EVENT_THIRD_PARTY_SECURITY_RATING_UPDATED_GLOBAL_SCORE_ADVANCED.necessaryPrivileges = [
    privileges.PRIVILEGE_VIEW_SECURITY_RATING_SOURCE
  ]
  privileges.PRIVILEGE_EVENT_THIRD_PARTY_SECURITY_RATING_UPDATED_GLOBAL_SCORE_INTERMEDIATE.necessaryPrivileges = [
    privileges.PRIVILEGE_VIEW_SECURITY_RATING_SOURCE
  ]
  privileges.PRIVILEGE_EVENT_THIRD_PARTY_SECURITY_RATING_UPDATED_MAIL_SCORE.necessaryPrivileges = [
    privileges.PRIVILEGE_VIEW_SECURITY_RATING_SOURCE
  ]
  privileges.PRIVILEGE_EVENT_THIRD_PARTY_SECURITY_RATING_UPDATED_MAIL_SCORE_A.necessaryPrivileges = [
    privileges.PRIVILEGE_VIEW_SECURITY_RATING_SOURCE
  ]
  privileges.PRIVILEGE_EVENT_THIRD_PARTY_SECURITY_RATING_UPDATED_MAIL_SCORE_B.necessaryPrivileges = [
    privileges.PRIVILEGE_VIEW_SECURITY_RATING_SOURCE
  ]
  privileges.PRIVILEGE_EVENT_THIRD_PARTY_SECURITY_RATING_UPDATED_MAIL_SCORE_C.necessaryPrivileges = [
    privileges.PRIVILEGE_VIEW_SECURITY_RATING_SOURCE
  ]
  privileges.PRIVILEGE_EVENT_THIRD_PARTY_SECURITY_RATING_UPDATED_MAIL_SCORE_D.necessaryPrivileges = [
    privileges.PRIVILEGE_VIEW_SECURITY_RATING_SOURCE
  ]
  privileges.PRIVILEGE_EVENT_THIRD_PARTY_SECURITY_RATING_UPDATED_MAIL_SCORE_E.necessaryPrivileges = [
    privileges.PRIVILEGE_VIEW_SECURITY_RATING_SOURCE
  ]
  privileges.PRIVILEGE_EVENT_THIRD_PARTY_SECURITY_RATING_UPDATED_SECURITY_CONTROL_SCORE.necessaryPrivileges = [
    privileges.PRIVILEGE_VIEW_SECURITY_RATING_SOURCE
  ]
  privileges.PRIVILEGE_EVENT_THIRD_PARTY_SECURITY_RATING_UPDATED_SECURITY_CONTROL_SCORE_A.necessaryPrivileges = [
    privileges.PRIVILEGE_VIEW_SECURITY_RATING_SOURCE
  ]
  privileges.PRIVILEGE_EVENT_THIRD_PARTY_SECURITY_RATING_UPDATED_SECURITY_CONTROL_SCORE_B.necessaryPrivileges = [
    privileges.PRIVILEGE_VIEW_SECURITY_RATING_SOURCE
  ]
  privileges.PRIVILEGE_EVENT_THIRD_PARTY_SECURITY_RATING_UPDATED_SECURITY_CONTROL_SCORE_C.necessaryPrivileges = [
    privileges.PRIVILEGE_VIEW_SECURITY_RATING_SOURCE
  ]
  privileges.PRIVILEGE_EVENT_THIRD_PARTY_SECURITY_RATING_UPDATED_SECURITY_CONTROL_SCORE_D.necessaryPrivileges = [
    privileges.PRIVILEGE_VIEW_SECURITY_RATING_SOURCE
  ]
  privileges.PRIVILEGE_EVENT_THIRD_PARTY_SECURITY_RATING_UPDATED_SECURITY_CONTROL_SCORE_E.necessaryPrivileges = [
    privileges.PRIVILEGE_VIEW_SECURITY_RATING_SOURCE
  ]
  privileges.PRIVILEGE_EVENT_THIRD_PARTY_SECURITY_RATING_UPDATED_SECURITY_INCIDENT_SCORE.necessaryPrivileges = [
    privileges.PRIVILEGE_VIEW_SECURITY_RATING_SOURCE
  ]
  privileges.PRIVILEGE_EVENT_THIRD_PARTY_SECURITY_RATING_UPDATED_SECURITY_INCIDENT_SCORE_A.necessaryPrivileges = [
    privileges.PRIVILEGE_VIEW_SECURITY_RATING_SOURCE
  ]
  privileges.PRIVILEGE_EVENT_THIRD_PARTY_SECURITY_RATING_UPDATED_SECURITY_INCIDENT_SCORE_B.necessaryPrivileges = [
    privileges.PRIVILEGE_VIEW_SECURITY_RATING_SOURCE
  ]
  privileges.PRIVILEGE_EVENT_THIRD_PARTY_SECURITY_RATING_UPDATED_SECURITY_INCIDENT_SCORE_C.necessaryPrivileges = [
    privileges.PRIVILEGE_VIEW_SECURITY_RATING_SOURCE
  ]
  privileges.PRIVILEGE_EVENT_THIRD_PARTY_SECURITY_RATING_UPDATED_SECURITY_INCIDENT_SCORE_D.necessaryPrivileges = [
    privileges.PRIVILEGE_VIEW_SECURITY_RATING_SOURCE
  ]
  privileges.PRIVILEGE_EVENT_THIRD_PARTY_SECURITY_RATING_UPDATED_SECURITY_INCIDENT_SCORE_E.necessaryPrivileges = [
    privileges.PRIVILEGE_VIEW_SECURITY_RATING_SOURCE
  ]
  privileges.PRIVILEGE_EVENT_THIRD_PARTY_SECURITY_RATING_UPDATED_VULNERABILITY_SCORE.necessaryPrivileges = [
    privileges.PRIVILEGE_VIEW_SECURITY_RATING_SOURCE
  ]
  privileges.PRIVILEGE_EVENT_THIRD_PARTY_SECURITY_RATING_UPDATED_VULNERABILITY_SCORE_A.necessaryPrivileges = [
    privileges.PRIVILEGE_VIEW_SECURITY_RATING_SOURCE
  ]
  privileges.PRIVILEGE_EVENT_THIRD_PARTY_SECURITY_RATING_UPDATED_VULNERABILITY_SCORE_B.necessaryPrivileges = [
    privileges.PRIVILEGE_VIEW_SECURITY_RATING_SOURCE
  ]
  privileges.PRIVILEGE_EVENT_THIRD_PARTY_SECURITY_RATING_UPDATED_VULNERABILITY_SCORE_C.necessaryPrivileges = [
    privileges.PRIVILEGE_VIEW_SECURITY_RATING_SOURCE
  ]
  privileges.PRIVILEGE_EVENT_THIRD_PARTY_SECURITY_RATING_UPDATED_VULNERABILITY_SCORE_D.necessaryPrivileges = [
    privileges.PRIVILEGE_VIEW_SECURITY_RATING_SOURCE
  ]
  privileges.PRIVILEGE_EVENT_THIRD_PARTY_SECURITY_RATING_UPDATED_VULNERABILITY_SCORE_E.necessaryPrivileges = [
    privileges.PRIVILEGE_VIEW_SECURITY_RATING_SOURCE
  ]
  privileges.PRIVILEGE_EVENT_THIRD_PARTY_SECURITY_RATING_UPDATED_VULNERABILITY_SNAPSHOT_SCORE.necessaryPrivileges = [
    privileges.PRIVILEGE_VIEW_SECURITY_RATING_SOURCE
  ]
  privileges.PRIVILEGE_EVENT_THIRD_PARTY_SECURITY_RATING_UPDATED_VULNERABILITY_SNAPSHOT_SCORE_A.necessaryPrivileges = [
    privileges.PRIVILEGE_VIEW_SECURITY_RATING_SOURCE
  ]
  privileges.PRIVILEGE_EVENT_THIRD_PARTY_SECURITY_RATING_UPDATED_VULNERABILITY_SNAPSHOT_SCORE_B.necessaryPrivileges = [
    privileges.PRIVILEGE_VIEW_SECURITY_RATING_SOURCE
  ]
  privileges.PRIVILEGE_EVENT_THIRD_PARTY_SECURITY_RATING_UPDATED_VULNERABILITY_SNAPSHOT_SCORE_C.necessaryPrivileges = [
    privileges.PRIVILEGE_VIEW_SECURITY_RATING_SOURCE
  ]
  privileges.PRIVILEGE_EVENT_THIRD_PARTY_SECURITY_RATING_UPDATED_VULNERABILITY_SNAPSHOT_SCORE_D.necessaryPrivileges = [
    privileges.PRIVILEGE_VIEW_SECURITY_RATING_SOURCE
  ]
  privileges.PRIVILEGE_EVENT_THIRD_PARTY_SECURITY_RATING_UPDATED_VULNERABILITY_SNAPSHOT_SCORE_E.necessaryPrivileges = [
    privileges.PRIVILEGE_VIEW_SECURITY_RATING_SOURCE
  ]
  privileges.PRIVILEGE_EVENT_THIRD_PARTY_SECURITY_RATING_UPDATED_WEB_SCORE.necessaryPrivileges = [
    privileges.PRIVILEGE_VIEW_SECURITY_RATING_SOURCE
  ]
  privileges.PRIVILEGE_EVENT_THIRD_PARTY_SECURITY_RATING_UPDATED_WEB_SCORE_A.necessaryPrivileges = [
    privileges.PRIVILEGE_VIEW_SECURITY_RATING_SOURCE
  ]
  privileges.PRIVILEGE_EVENT_THIRD_PARTY_SECURITY_RATING_UPDATED_WEB_SCORE_B.necessaryPrivileges = [
    privileges.PRIVILEGE_VIEW_SECURITY_RATING_SOURCE
  ]
  privileges.PRIVILEGE_EVENT_THIRD_PARTY_SECURITY_RATING_UPDATED_WEB_SCORE_C.necessaryPrivileges = [
    privileges.PRIVILEGE_VIEW_SECURITY_RATING_SOURCE
  ]
  privileges.PRIVILEGE_EVENT_THIRD_PARTY_SECURITY_RATING_UPDATED_WEB_SCORE_D.necessaryPrivileges = [
    privileges.PRIVILEGE_VIEW_SECURITY_RATING_SOURCE
  ]
  privileges.PRIVILEGE_EVENT_THIRD_PARTY_SECURITY_RATING_UPDATED_WEB_SCORE_E.necessaryPrivileges = [
    privileges.PRIVILEGE_VIEW_SECURITY_RATING_SOURCE
  ]
  // Creditsafe
  privileges.PRIVILEGE_EVENT_THIRD_PARTY_CREDITSAFE_UPDATED_INTERNATIONALSCORE.necessaryPrivileges = [
    privileges.PRIVILEGE_VIEW_CREDITSAFE_SOURCE
  ]
  privileges.PRIVILEGE_EVENT_THIRD_PARTY_CREDITSAFE_UPDATED_INTERNATIONALSCORE_A.necessaryPrivileges = [
    privileges.PRIVILEGE_VIEW_CREDITSAFE_SOURCE
  ]
  privileges.PRIVILEGE_EVENT_THIRD_PARTY_CREDITSAFE_UPDATED_INTERNATIONALSCORE_B.necessaryPrivileges = [
    privileges.PRIVILEGE_VIEW_CREDITSAFE_SOURCE
  ]
  privileges.PRIVILEGE_EVENT_THIRD_PARTY_CREDITSAFE_UPDATED_INTERNATIONALSCORE_C.necessaryPrivileges = [
    privileges.PRIVILEGE_VIEW_CREDITSAFE_SOURCE
  ]
  privileges.PRIVILEGE_EVENT_THIRD_PARTY_CREDITSAFE_UPDATED_INTERNATIONALSCORE_D.necessaryPrivileges = [
    privileges.PRIVILEGE_VIEW_CREDITSAFE_SOURCE
  ]
  privileges.PRIVILEGE_EVENT_THIRD_PARTY_CREDITSAFE_UPDATED_INTERNATIONALSCORE_E.necessaryPrivileges = [
    privileges.PRIVILEGE_VIEW_CREDITSAFE_SOURCE
  ]
  privileges.PRIVILEGE_EVENT_THIRD_PARTY_CREDITSAFE_UPDATED_ACTIVITYEXPOSURE.necessaryPrivileges = [
    privileges.PRIVILEGE_VIEW_CREDITSAFE_SOURCE
  ]
  privileges.PRIVILEGE_EVENT_THIRD_PARTY_CREDITSAFE_UPDATED_ACTIVITYEXPOSURE_A.necessaryPrivileges = [
    privileges.PRIVILEGE_VIEW_CREDITSAFE_SOURCE
  ]
  privileges.PRIVILEGE_EVENT_THIRD_PARTY_CREDITSAFE_UPDATED_ACTIVITYEXPOSURE_B.necessaryPrivileges = [
    privileges.PRIVILEGE_VIEW_CREDITSAFE_SOURCE
  ]
  privileges.PRIVILEGE_EVENT_THIRD_PARTY_CREDITSAFE_UPDATED_ACTIVITYEXPOSURE_C.necessaryPrivileges = [
    privileges.PRIVILEGE_VIEW_CREDITSAFE_SOURCE
  ]
  privileges.PRIVILEGE_EVENT_THIRD_PARTY_CREDITSAFE_UPDATED_ACTIVITYEXPOSURE_D.necessaryPrivileges = [
    privileges.PRIVILEGE_VIEW_CREDITSAFE_SOURCE
  ]
  
  // DataHub
  privileges.PRIVILEGE_EVENT_THIRD_PARTY_DATA_HUB_UPDATED_ALL.necessaryPrivileges = [
    privileges.PRIVILEGE_VIEW_DATA_HUB_SOURCE
  ]
  privileges.PRIVILEGE_EVENT_THIRD_PARTY_DATA_HUB_UPDATED.necessaryPrivileges = [
    privileges.PRIVILEGE_VIEW_DATA_HUB_SOURCE
  ]


  privileges.PRIVILEGE_EVENT_THIRD_PARTY_PROCEDURE_COLLECTIVE_UPDATED.necessaryPrivileges = [
    privileges.PRIVILEGE_VIEW_THIRD_PARTY_ID_CARD
  ]
  privileges.PRIVILEGE_EVENT_THIRD_PARTY_PROCEDURE_COLLECTIVE_UPDATED_ETAPE_01.necessaryPrivileges = [
    privileges.PRIVILEGE_VIEW_THIRD_PARTY_ID_CARD
  ]
  privileges.PRIVILEGE_EVENT_THIRD_PARTY_PROCEDURE_COLLECTIVE_UPDATED_ETAPE_02.necessaryPrivileges = [
    privileges.PRIVILEGE_VIEW_THIRD_PARTY_ID_CARD
  ]
  privileges.PRIVILEGE_EVENT_THIRD_PARTY_PROCEDURE_COLLECTIVE_UPDATED_ETAPE_03.necessaryPrivileges = [
    privileges.PRIVILEGE_VIEW_THIRD_PARTY_ID_CARD
  ]
  privileges.PRIVILEGE_EVENT_THIRD_PARTY_PROCEDURE_COLLECTIVE_UPDATED_ETAPE_04.necessaryPrivileges = [
    privileges.PRIVILEGE_VIEW_THIRD_PARTY_ID_CARD
  ]
  privileges.PRIVILEGE_EVENT_THIRD_PARTY_PROCEDURE_COLLECTIVE_UPDATED_ETAPE_05.necessaryPrivileges = [
    privileges.PRIVILEGE_VIEW_THIRD_PARTY_ID_CARD
  ]
  privileges.PRIVILEGE_EVENT_THIRD_PARTY_PROCEDURE_COLLECTIVE_UPDATED_ETAPE_06.necessaryPrivileges = [
    privileges.PRIVILEGE_VIEW_THIRD_PARTY_ID_CARD
  ]
  privileges.PRIVILEGE_EVENT_THIRD_PARTY_PROCEDURE_COLLECTIVE_UPDATED_ETAPE_07.necessaryPrivileges = [
    privileges.PRIVILEGE_VIEW_THIRD_PARTY_ID_CARD
  ]
  privileges.PRIVILEGE_EVENT_THIRD_PARTY_PROCEDURE_COLLECTIVE_UPDATED_ETAPE_08.necessaryPrivileges = [
    privileges.PRIVILEGE_VIEW_THIRD_PARTY_ID_CARD
  ]
  privileges.PRIVILEGE_EVENT_THIRD_PARTY_PROCEDURE_COLLECTIVE_UPDATED_ETAPE_09.necessaryPrivileges = [
    privileges.PRIVILEGE_VIEW_THIRD_PARTY_ID_CARD
  ]
  privileges.PRIVILEGE_EVENT_THIRD_PARTY_PROCEDURE_COLLECTIVE_UPDATED_ETAPE_10.necessaryPrivileges = [
    privileges.PRIVILEGE_VIEW_THIRD_PARTY_ID_CARD
  ]
  privileges.PRIVILEGE_EVENT_THIRD_PARTY_PROCEDURE_COLLECTIVE_UPDATED_ETAPE_11.necessaryPrivileges = [
    privileges.PRIVILEGE_VIEW_THIRD_PARTY_ID_CARD
  ]
  privileges.PRIVILEGE_EVENT_THIRD_PARTY_PROCEDURE_COLLECTIVE_UPDATED_ETAPE_12.necessaryPrivileges = [
    privileges.PRIVILEGE_VIEW_THIRD_PARTY_ID_CARD
  ]
  privileges.PRIVILEGE_EVENT_THIRD_PARTY_PROCEDURE_COLLECTIVE_UPDATED_ETAPE_13.necessaryPrivileges = [
    privileges.PRIVILEGE_VIEW_THIRD_PARTY_ID_CARD
  ]
  privileges.PRIVILEGE_EVENT_THIRD_PARTY_PROCEDURE_COLLECTIVE_UPDATED_ETAPE_14.necessaryPrivileges = [
    privileges.PRIVILEGE_VIEW_THIRD_PARTY_ID_CARD
  ]
  privileges.PRIVILEGE_EVENT_THIRD_PARTY_PROCEDURE_COLLECTIVE_UPDATED_ETAPE_15.necessaryPrivileges = [
    privileges.PRIVILEGE_VIEW_THIRD_PARTY_ID_CARD
  ]
  privileges.PRIVILEGE_EVENT_THIRD_PARTY_PROCEDURE_COLLECTIVE_UPDATED_ETAPE_16.necessaryPrivileges = [
    privileges.PRIVILEGE_VIEW_THIRD_PARTY_ID_CARD
  ]
  privileges.PRIVILEGE_EVENT_THIRD_PARTY_PROCEDURE_COLLECTIVE_UPDATED_ETAPE_17.necessaryPrivileges = [
    privileges.PRIVILEGE_VIEW_THIRD_PARTY_ID_CARD
  ]
  privileges.PRIVILEGE_EVENT_THIRD_PARTY_PROCEDURE_COLLECTIVE_UPDATED_ETAPE_18.necessaryPrivileges = [
    privileges.PRIVILEGE_VIEW_THIRD_PARTY_ID_CARD
  ]
  privileges.PRIVILEGE_EVENT_THIRD_PARTY_PROCEDURE_COLLECTIVE_UPDATED_ETAPE_19.necessaryPrivileges = [
    privileges.PRIVILEGE_VIEW_THIRD_PARTY_ID_CARD
  ]
  privileges.PRIVILEGE_EVENT_THIRD_PARTY_PROCEDURE_COLLECTIVE_UPDATED_ETAPE_20.necessaryPrivileges = [
    privileges.PRIVILEGE_VIEW_THIRD_PARTY_ID_CARD
  ]


  //add necessaryPrivileges for events dossier
  //-- update attribute
  privileges.PRIVILEGE_EVENT_DOSSIER_ATTRIBUTE_UPDATED.necessaryPrivileges = [
    privileges.PRIVILEGE_VIEW_DOSSIER_ATTRIBUTE_VALUE
  ]
  privileges.PRIVILEGE_EVENT_DOSSIER_ATTRIBUTE_UPDATED_ALL.necessaryPrivileges = [
    privileges.PRIVILEGE_VIEW_DOSSIER_ATTRIBUTE_VALUE_ALL
  ]
  privileges.PRIVILEGE_EVENT_DOSSIER_CALCULATED_ATTRIBUTE_UPDATED.necessaryPrivileges = [
    privileges.PRIVILEGE_VIEW_DOSSIER_CALCULATED_ATTRIBUTE
  ]
  privileges.PRIVILEGE_EVENT_DOSSIER_CALCULATED_ATTRIBUTE_UPDATED_ALL.necessaryPrivileges = [
    privileges.PRIVILEGE_VIEW_DOSSIER_CALCULATED_ATTRIBUTE_ALL
  ]
  privileges.PRIVILEGE_EVENT_DOSSIER_INDICATOR_UPDATED.necessaryPrivileges = [
    privileges.PRIVILEGE_VIEW_DOSSIER_INDICATOR
  ]
  privileges.PRIVILEGE_EVENT_DOSSIER_INDICATOR_UPDATED_ALL.necessaryPrivileges = [
    privileges.PRIVILEGE_VIEW_DOSSIER_INDICATOR_ALL
  ]
  //-- notes dossier
  privileges.PRIVILEGE_EVENT_DOSSIER_COMMENT.necessaryPrivileges = [
    privileges.PRIVILEGE_VIEW_DOSSIER_NOTES
  ]
  privileges.PRIVILEGE_EVENT_DOSSIER_COMMENT_ADDED.necessaryPrivileges = [
    privileges.PRIVILEGE_VIEW_DOSSIER_NOTES
  ]
  privileges.PRIVILEGE_EVENT_DOSSIER_COMMENT_UPDATED.necessaryPrivileges = [
    privileges.PRIVILEGE_VIEW_DOSSIER_NOTES
  ]
  privileges.PRIVILEGE_EVENT_DOSSIER_COMMENT_DELETED.necessaryPrivileges = [
    privileges.PRIVILEGE_VIEW_DOSSIER_NOTES
  ]
  privileges.PRIVILEGE_EVENT_DOSSIER_COMMENT_MENTION_ADDED.necessaryPrivileges = [
    privileges.PRIVILEGE_VIEW_DOSSIER_NOTES
  ]
  privileges.PRIVILEGE_EVENT_DOSSIER_COMMENT_MENTION_UPDATED.necessaryPrivileges = [
    privileges.PRIVILEGE_VIEW_DOSSIER_NOTES
  ]
  privileges.PRIVILEGE_EVENT_DOSSIER_COMMENT_MENTION_DELETED.necessaryPrivileges = [
    privileges.PRIVILEGE_VIEW_DOSSIER_NOTES
  ]
  //-- modification du dossier
  privileges.PRIVILEGE_EVENT_DOSSIER_UPDATED.necessaryPrivileges = [
    privileges.PRIVILEGE_VIEW_DOSSIER_INFORMATION
  ]

  //-- notes documents
  privileges.PRIVILEGE_EVENT_DOCUMENT_COMMENT.necessaryPrivileges = [
    privileges.PRIVILEGE_VIEW_DOCUMENT_NOTES
  ]
  privileges.PRIVILEGE_EVENT_DOCUMENT_COMMENT_ADDED.necessaryPrivileges = [
    privileges.PRIVILEGE_VIEW_DOCUMENT_NOTES
  ]
  privileges.PRIVILEGE_EVENT_DOCUMENT_COMMENT_UPDATED.necessaryPrivileges = [
    privileges.PRIVILEGE_VIEW_DOCUMENT_NOTES
  ]
  privileges.PRIVILEGE_EVENT_DOCUMENT_COMMENT_DELETED.necessaryPrivileges = [
    privileges.PRIVILEGE_VIEW_DOCUMENT_NOTES
  ]
  privileges.PRIVILEGE_EVENT_DOCUMENT_COMMENT_MENTION_ADDED.necessaryPrivileges = [
    privileges.PRIVILEGE_VIEW_DOCUMENT_NOTES
  ]
  privileges.PRIVILEGE_EVENT_DOCUMENT_COMMENT_MENTION_UPDATED.necessaryPrivileges = [
    privileges.PRIVILEGE_VIEW_DOCUMENT_NOTES
  ]
  privileges.PRIVILEGE_EVENT_DOCUMENT_COMMENT_MENTION_DELETED.necessaryPrivileges = [
    privileges.PRIVILEGE_VIEW_DOCUMENT_NOTES
  ]

  return privileges;
}

export const PrivilegeEnum = createPrivilegeEnum();

export const getPrivilegeAscendants = (privilege) => {
  let ascendants = new Set();

  for (let key in PrivilegeEnum) {
    const currentPrivilege = PrivilegeEnum[key];

    if (currentPrivilege.childPrivileges.includes(privilege)) {
      ascendants.add(key);

      const ascendantsOfCurrent = getPrivilegeAscendants(currentPrivilege);
      ascendantsOfCurrent.forEach(ascendant => ascendants.add(ascendant));
    }
  }

  return ascendants;
}

export const getPrivilegeDescendants = (privilege) => {
  let descendants = new Set();

  for (let key in PrivilegeEnum) {
    const currentPrivilege = PrivilegeEnum[key];

    if (privilege.childPrivileges.includes(currentPrivilege)) {
      descendants.add(key);

      const descendantsOfCurrent = getPrivilegeDescendants(currentPrivilege);
      descendantsOfCurrent.forEach(descendant => descendants.add(descendant));
    }
  }

  return descendants;
}

//Configuration
export const THIRD_PARTY_ATTRIBUTE_MANAGEMENT_ACTIONS = (togglePrivileges, grantPrivileges, revokePrivileges, hasCurrentAnyOfPrivileges, hasBaseAnyOfPrivileges) => ({
  rootActions: [
    {
      name: i18next.t("roles.switchButton.read"),
      action: (isActive) => { togglePrivileges(PrivilegeEnum.PRIVILEGE_VIEW_THIRD_PARTY_ATTRIBUTE); },
      isActive: hasCurrentAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_VIEW_THIRD_PARTY_ATTRIBUTE),
      isGrantable: hasBaseAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_VIEW_THIRD_PARTY_ATTRIBUTE),
    },
    {
      name: i18next.t("roles.switchButton.editValue"),
      action: (isActive) => {
        !isActive ?
          grantPrivileges(
            PrivilegeEnum.PRIVILEGE_EDIT_THIRD_PARTY_ATTRIBUTE_OPTIONS
          )
          :
          revokePrivileges(
            PrivilegeEnum.PRIVILEGE_EDIT_THIRD_PARTY_ATTRIBUTE_OPTIONS,
            PrivilegeEnum.PRIVILEGE_CREATE_THIRD_PARTY_ATTRIBUTE,
            PrivilegeEnum.PRIVILEGE_DELETE_THIRD_PARTY_ATTRIBUTE
          );
      },
      isActive: hasCurrentAnyOfPrivileges(
        PrivilegeEnum.PRIVILEGE_EDIT_THIRD_PARTY_ATTRIBUTE_OPTIONS,
        PrivilegeEnum.PRIVILEGE_CREATE_THIRD_PARTY_ATTRIBUTE,
        PrivilegeEnum.PRIVILEGE_DELETE_THIRD_PARTY_ATTRIBUTE
      ),
      isGrantable: hasBaseAnyOfPrivileges(
        PrivilegeEnum.PRIVILEGE_EDIT_THIRD_PARTY_ATTRIBUTE_OPTIONS,
        PrivilegeEnum.PRIVILEGE_CREATE_THIRD_PARTY_ATTRIBUTE,
        PrivilegeEnum.PRIVILEGE_DELETE_THIRD_PARTY_ATTRIBUTE
      ),
    },
    {
      name: i18next.t("roles.switchButton.editAttribute"),
      action: (isActive) => {
        !isActive ?
          grantPrivileges(PrivilegeEnum.PRIVILEGE_ALL_THIRD_PARTY_ATTRIBUTE)
          :
          revokePrivileges(
            PrivilegeEnum.PRIVILEGE_EDIT_THIRD_PARTY_ATTRIBUTE,
            PrivilegeEnum.PRIVILEGE_CREATE_THIRD_PARTY_ATTRIBUTE,
            PrivilegeEnum.PRIVILEGE_DELETE_THIRD_PARTY_ATTRIBUTE,
          );
      },
      isActive: hasCurrentAnyOfPrivileges(
        PrivilegeEnum.PRIVILEGE_EDIT_THIRD_PARTY_ATTRIBUTE,
        PrivilegeEnum.PRIVILEGE_CREATE_THIRD_PARTY_ATTRIBUTE,
        PrivilegeEnum.PRIVILEGE_DELETE_THIRD_PARTY_ATTRIBUTE
      ),
      isGrantable: hasBaseAnyOfPrivileges(
        PrivilegeEnum.PRIVILEGE_EDIT_THIRD_PARTY_ATTRIBUTE,
        PrivilegeEnum.PRIVILEGE_CREATE_THIRD_PARTY_ATTRIBUTE,
        PrivilegeEnum.PRIVILEGE_DELETE_THIRD_PARTY_ATTRIBUTE
      ),
    }
  ],
  items: [],
  actions: [],
});

export const DOSSIER_ATTRIBUTE_MANAGEMENT_ACTIONS = (togglePrivileges, grantPrivileges, revokePrivileges, hasCurrentAnyOfPrivileges, hasBaseAnyOfPrivileges, hasCurrentAllOfPrivileges, hasBaseAllOfPrivileges) => ({
  rootActions: [
    {
      name: i18next.t("roles.switchButton.read"),
      action: (isActive) => { togglePrivileges(PrivilegeEnum.PRIVILEGE_VIEW_DOSSIER_ATTRIBUTE) },
      isActive: hasCurrentAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_VIEW_DOSSIER_ATTRIBUTE),
      isGrantable: hasBaseAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_VIEW_DOSSIER_ATTRIBUTE),
    },
    {
      name: i18next.t("roles.switchButton.editValue"),
      action: (isActive) => {
        !isActive ?
          grantPrivileges(PrivilegeEnum.PRIVILEGE_EDIT_DOSSIER_ATTRIBUTE_OPTIONS)
          :
          revokePrivileges(
            PrivilegeEnum.PRIVILEGE_EDIT_DOSSIER_ATTRIBUTE_OPTIONS,
            PrivilegeEnum.PRIVILEGE_CREATE_DOSSIER_ATTRIBUTE,
            PrivilegeEnum.PRIVILEGE_DELETE_DOSSIER_ATTRIBUTE
          );
      },
      isActive: hasCurrentAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_EDIT_DOSSIER_ATTRIBUTE_OPTIONS),
      isGrantable: hasBaseAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_EDIT_DOSSIER_ATTRIBUTE_OPTIONS),
    },
    {
      name: i18next.t("roles.switchButton.editAttribute"),
      action: (isActive) => {
        !isActive ?
          grantPrivileges(PrivilegeEnum.PRIVILEGE_ALL_DOSSIER_ATTRIBUTE)
          :
          revokePrivileges(
            PrivilegeEnum.PRIVILEGE_EDIT_DOSSIER_ATTRIBUTE,
            PrivilegeEnum.PRIVILEGE_CREATE_DOSSIER_ATTRIBUTE,
            PrivilegeEnum.PRIVILEGE_DELETE_DOSSIER_ATTRIBUTE
          );
      },
      isActive: hasCurrentAnyOfPrivileges(
        PrivilegeEnum.PRIVILEGE_EDIT_DOSSIER_ATTRIBUTE,
        PrivilegeEnum.PRIVILEGE_CREATE_DOSSIER_ATTRIBUTE,
        PrivilegeEnum.PRIVILEGE_DELETE_DOSSIER_ATTRIBUTE,
      ),
      isGrantable: hasBaseAnyOfPrivileges(
        PrivilegeEnum.PRIVILEGE_EDIT_DOSSIER_ATTRIBUTE,
        PrivilegeEnum.PRIVILEGE_CREATE_DOSSIER_ATTRIBUTE,
        PrivilegeEnum.PRIVILEGE_DELETE_DOSSIER_ATTRIBUTE,
      ),
    }
  ],
  items: [],
  actions: [],
});

export const CONTEXT_ATTRIBUTE_MANAGEMENT_ACTIONS = (togglePrivileges, grantPrivileges, revokePrivileges, hasCurrentAnyOfPrivileges, hasBaseAnyOfPrivileges) => ({
  rootActions: [
    {
      name: i18next.t("roles.switchButton.read"),
      action: (isActive) => { togglePrivileges(PrivilegeEnum.PRIVILEGE_VIEW_CONTEXT_ATTRIBUTE) },
      isActive: hasCurrentAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_VIEW_CONTEXT_ATTRIBUTE),
      isGrantable: hasBaseAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_VIEW_CONTEXT_ATTRIBUTE),
    },
    {
      name: i18next.t("roles.switchButton.editValue"),
      action: (isActive) => {
        !isActive ?
          grantPrivileges(PrivilegeEnum.PRIVILEGE_EDIT_CONTEXT_ATTRIBUTE_VALUE)
          :
          revokePrivileges(
            PrivilegeEnum.PRIVILEGE_EDIT_CONTEXT_ATTRIBUTE_VALUE,
            PrivilegeEnum.PRIVILEGE_CREATE_CONTEXT_ATTRIBUTE,
            PrivilegeEnum.PRIVILEGE_DELETE_CONTEXT_ATTRIBUTE
          );
      },
      isActive: hasCurrentAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_EDIT_CONTEXT_ATTRIBUTE_VALUE),
      isGrantable: hasBaseAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_EDIT_CONTEXT_ATTRIBUTE_VALUE),
    },
    {
      name: i18next.t("roles.switchButton.editAttribute"),
      action: (isActive) => {
        !isActive ?
          grantPrivileges(PrivilegeEnum.PRIVILEGE_ALL_CONTEXT_ATTRIBUTE)
          :
          revokePrivileges(
            PrivilegeEnum.PRIVILEGE_EDIT_CONTEXT_ATTRIBUTE,
            PrivilegeEnum.PRIVILEGE_CREATE_CONTEXT_ATTRIBUTE,
            PrivilegeEnum.PRIVILEGE_DELETE_CONTEXT_ATTRIBUTE
          );
      },
      isActive: hasCurrentAnyOfPrivileges(
        PrivilegeEnum.PRIVILEGE_EDIT_CONTEXT_ATTRIBUTE,
        PrivilegeEnum.PRIVILEGE_CREATE_CONTEXT_ATTRIBUTE,
        PrivilegeEnum.PRIVILEGE_DELETE_CONTEXT_ATTRIBUTE,
      ),
      isGrantable: hasBaseAnyOfPrivileges(
        PrivilegeEnum.PRIVILEGE_EDIT_CONTEXT_ATTRIBUTE,
        PrivilegeEnum.PRIVILEGE_CREATE_CONTEXT_ATTRIBUTE,
        PrivilegeEnum.PRIVILEGE_DELETE_CONTEXT_ATTRIBUTE,
      ),
    }
  ],
  items: [],
  actions: [],
});


export const PARTNER_ATTRIBUTE_MANAGEMENT_ACTIONS = (togglePrivileges, grantPrivileges, revokePrivileges, hasCurrentAnyOfPrivileges, hasBaseAnyOfPrivileges) => ({
  rootActions: [
    {
      name: i18next.t("roles.switchButton.read"),
      action: (isActive) => { togglePrivileges(PrivilegeEnum.PRIVILEGE_VIEW_PARTNER_ATTRIBUTE); },
      isActive: hasCurrentAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_VIEW_PARTNER_ATTRIBUTE),
      isGrantable: hasBaseAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_VIEW_PARTNER_ATTRIBUTE),
    },
    {
      name: i18next.t("roles.switchButton.editValue"),
      action: (isActive) => {
        !isActive ?
          grantPrivileges(
            PrivilegeEnum.PRIVILEGE_EDIT_PARTNER_ATTRIBUTE_OPTIONS
          )
          :
          revokePrivileges(
            PrivilegeEnum.PRIVILEGE_EDIT_PARTNER_ATTRIBUTE_OPTIONS,
            PrivilegeEnum.PRIVILEGE_CREATE_PARTNER_ATTRIBUTE,
            PrivilegeEnum.PRIVILEGE_DELETE_PARTNER_ATTRIBUTE
          );
      },
      isActive: hasCurrentAnyOfPrivileges(
        PrivilegeEnum.PRIVILEGE_EDIT_PARTNER_ATTRIBUTE_OPTIONS,
        PrivilegeEnum.PRIVILEGE_CREATE_PARTNER_ATTRIBUTE,
        PrivilegeEnum.PRIVILEGE_DELETE_PARTNER_ATTRIBUTE
      ),
      isGrantable: hasBaseAnyOfPrivileges(
        PrivilegeEnum.PRIVILEGE_EDIT_PARTNER_ATTRIBUTE_OPTIONS,
        PrivilegeEnum.PRIVILEGE_CREATE_PARTNER_ATTRIBUTE,
        PrivilegeEnum.PRIVILEGE_DELETE_PARTNER_ATTRIBUTE
      ),
    },
    {
      name: i18next.t("roles.switchButton.editAttribute"),
      action: (isActive) => {
        !isActive ?
          grantPrivileges(PrivilegeEnum.PRIVILEGE_ALL_PARTNER_ATTRIBUTE)
          :
          revokePrivileges(
            PrivilegeEnum.PRIVILEGE_EDIT_PARTNER_ATTRIBUTE,
            PrivilegeEnum.PRIVILEGE_CREATE_PARTNER_ATTRIBUTE,
            PrivilegeEnum.PRIVILEGE_DELETE_PARTNER_ATTRIBUTE,
          );
      },
      isActive: hasCurrentAnyOfPrivileges(
        PrivilegeEnum.PRIVILEGE_EDIT_PARTNER_ATTRIBUTE,
        PrivilegeEnum.PRIVILEGE_CREATE_PARTNER_ATTRIBUTE,
        PrivilegeEnum.PRIVILEGE_DELETE_PARTNER_ATTRIBUTE
      ),
      isGrantable: hasBaseAnyOfPrivileges(
        PrivilegeEnum.PRIVILEGE_EDIT_PARTNER_ATTRIBUTE,
        PrivilegeEnum.PRIVILEGE_CREATE_PARTNER_ATTRIBUTE,
        PrivilegeEnum.PRIVILEGE_DELETE_PARTNER_ATTRIBUTE
      ),
    }
  ],
  items: [],
  actions: [],
});

export const RULE_MANAGEMENT_ACTIONS = (togglePrivileges, grantPrivileges, revokePrivileges, hasCurrentAnyOfPrivileges, hasBaseAnyOfPrivileges) => ({
  rootActions: [
    {
      name: i18next.t("roles.switchButton.read"),
      action: (isActive) => { togglePrivileges(PrivilegeEnum.PRIVILEGE_VIEW_RULE) },
      isActive: hasCurrentAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_VIEW_RULE),
      isGrantable: hasBaseAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_VIEW_RULE),
    },
    {
      name: i18next.t("roles.switchButton.edit"),
      action: (isActive) => {
        togglePrivileges(
          PrivilegeEnum.PRIVILEGE_EDIT_RULE_PARAMETERS,
          PrivilegeEnum.PRIVILEGE_CREATE_RULE,
          PrivilegeEnum.PRIVILEGE_DELETE_RULE
        )
      },
      isActive: hasCurrentAnyOfPrivileges(
        PrivilegeEnum.PRIVILEGE_EDIT_RULE_PARAMETERS,
        PrivilegeEnum.PRIVILEGE_CREATE_RULE,
        PrivilegeEnum.PRIVILEGE_DELETE_RULE
      ),
      isGrantable: hasBaseAnyOfPrivileges(
        PrivilegeEnum.PRIVILEGE_EDIT_RULE_PARAMETERS,
        PrivilegeEnum.PRIVILEGE_CREATE_RULE,
        PrivilegeEnum.PRIVILEGE_DELETE_RULE
      ),
    },
    {
      name: i18next.t("roles.switchButton.toggleActive"),
      action: (isActive) => { togglePrivileges(PrivilegeEnum.PRIVILEGE_EDIT_RULE_STATUS) },
      isActive: hasCurrentAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_EDIT_RULE_STATUS,),
      isGrantable: hasBaseAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_EDIT_RULE_STATUS),
    }
  ],
  items: [],
  actions: [],
});

export const CALCULATION_MANAGEMENT_ACTIONS = (togglePrivileges, grantPrivileges, revokePrivileges, hasCurrentAnyOfPrivileges, hasBaseAnyOfPrivileges) => ({
  rootActions: [
    {
      name: i18next.t("roles.switchButton.read"),
      action: (isActive) => { togglePrivileges(PrivilegeEnum.PRIVILEGE_VIEW_CALCULATION) },
      isActive: hasCurrentAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_VIEW_CALCULATION),
      isGrantable: hasBaseAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_VIEW_CALCULATION),
    },
    {
      name: i18next.t("roles.switchButton.edit"),
      action: (isActive) => {
        togglePrivileges(
          PrivilegeEnum.PRIVILEGE_EDIT_CALCULATION_PARAMETERS,
          PrivilegeEnum.PRIVILEGE_CREATE_CALCULATION,
          PrivilegeEnum.PRIVILEGE_DELETE_CALCULATION
        )
      },
      isActive: hasCurrentAnyOfPrivileges(
        PrivilegeEnum.PRIVILEGE_EDIT_CALCULATION_PARAMETERS,
        PrivilegeEnum.PRIVILEGE_CREATE_CALCULATION,
        PrivilegeEnum.PRIVILEGE_DELETE_CALCULATION
      ),
      isGrantable: hasBaseAnyOfPrivileges(
        PrivilegeEnum.PRIVILEGE_EDIT_CALCULATION_PARAMETERS,
        PrivilegeEnum.PRIVILEGE_CREATE_CALCULATION,
        PrivilegeEnum.PRIVILEGE_DELETE_CALCULATION
      ),
    },
    {
      name: i18next.t("roles.switchButton.toggleActive"),
      action: (isActive) => { togglePrivileges(PrivilegeEnum.PRIVILEGE_EDIT_CALCULATION_STATUS) },
      isActive: hasCurrentAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_EDIT_CALCULATION_STATUS,),
      isGrantable: hasBaseAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_EDIT_CALCULATION_STATUS),
    }
  ],
  items: [],
  actions: [],
});

export const ROLE_MANAGEMENT_ACTIONS = (togglePrivileges, grantPrivileges, revokePrivileges, hasCurrentAnyOfPrivileges, hasBaseAnyOfPrivileges) => ({
  rootActions: [
    {
      name: i18next.t("roles.switchButton.read"),
      action: (isActive) => togglePrivileges(PrivilegeEnum.PRIVILEGE_VIEW_ROLE),
      isActive: hasCurrentAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_VIEW_ROLE),
      isGrantable: hasBaseAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_VIEW_ROLE),
    },
    {
      name: i18next.t("roles.switchButton.editSame"),
      action: (isActive) => {
        !isActive ?
          grantPrivileges(PrivilegeEnum.PRIVILEGE_ALL_ROLE)
          :
          revokePrivileges(
            PrivilegeEnum.PRIVILEGE_EDIT_ROLE,
            PrivilegeEnum.PRIVILEGE_CREATE_ROLE,
            PrivilegeEnum.PRIVILEGE_DELETE_ROLE
          )
      },
      isActive: hasCurrentAnyOfPrivileges(
        PrivilegeEnum.PRIVILEGE_EDIT_ROLE,
        PrivilegeEnum.PRIVILEGE_CREATE_ROLE,
        PrivilegeEnum.PRIVILEGE_DELETE_ROLE
      ),
      isGrantable: hasBaseAnyOfPrivileges(
        PrivilegeEnum.PRIVILEGE_EDIT_ROLE,
        PrivilegeEnum.PRIVILEGE_CREATE_ROLE,
        PrivilegeEnum.PRIVILEGE_DELETE_ROLE
      ),
    },
  ],
  items: [],
  actions: [],
});

export const THEME_MANAGEMENT_ACTIONS = (togglePrivileges, grantPrivileges, revokePrivileges, hasCurrentAnyOfPrivileges, hasBaseAnyOfPrivileges) => ({
  rootActions: [
    {
      name: i18next.t("roles.switchButton.read"),
      action: (isActive) => {
        togglePrivileges(PrivilegeEnum.PRIVILEGE_VIEW_THEME)
      },
      isActive: hasCurrentAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_VIEW_THEME),
      isGrantable: hasBaseAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_VIEW_THEME),
    },
    {
      name: i18next.t("roles.switchButton.edit"),
      action: (isActive) => {
        togglePrivileges(
          PrivilegeEnum.PRIVILEGE_EDIT_THEME_PARAMETERS,
          PrivilegeEnum.PRIVILEGE_CREATE_THEME,
          PrivilegeEnum.PRIVILEGE_DELETE_THEME
        )
      },
      isActive: hasCurrentAnyOfPrivileges(
        PrivilegeEnum.PRIVILEGE_EDIT_THEME_PARAMETERS,
        PrivilegeEnum.PRIVILEGE_CREATE_THEME,
        PrivilegeEnum.PRIVILEGE_DELETE_THEME
      ),
      isGrantable: hasBaseAnyOfPrivileges(
        PrivilegeEnum.PRIVILEGE_EDIT_THEME_PARAMETERS,
        PrivilegeEnum.PRIVILEGE_CREATE_THEME,
        PrivilegeEnum.PRIVILEGE_DELETE_THEME
      ),
    },
    {
      name: i18next.t("roles.switchButton.toggleActive"),
      action: (isActive) => {
        togglePrivileges(PrivilegeEnum.PRIVILEGE_EDIT_THEME_STATUS)
      },
      isActive: hasCurrentAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_EDIT_THEME_STATUS,),
      isGrantable: hasBaseAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_EDIT_THEME_STATUS),
    }
  ],
  items: [],
  actions: [],
});

export const CONSUMPTION_MANAGEMENT_ACTIONS = (togglePrivileges, grantPrivileges, revokePrivileges, hasCurrentAnyOfPrivileges, hasBaseAnyOfPrivileges) => ({
  rootActions: [
    {
      name: i18next.t("roles.switchButton.read"),
      action: (isActive) => {
        togglePrivileges(PrivilegeEnum.PRIVILEGE_VIEW_CONSUMPTION)
      },
      isActive: hasCurrentAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_VIEW_CONSUMPTION),
      isGrantable: hasBaseAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_VIEW_CONSUMPTION),
    },
  ],
  items: [],
  actions: [],
});

export const CONTEXT_MODEL_MANAGEMENT_ACTIONS = (togglePrivileges, grantPrivileges, revokePrivileges, hasCurrentAnyOfPrivileges, hasBaseAnyOfPrivileges) => ({
  rootActions: [
    {
      name: i18next.t("roles.switchButton.read"),
      action: (isActive) => {togglePrivileges(PrivilegeEnum.PRIVILEGE_VIEW_CONTEXT_MODEL)},
      isActive: hasCurrentAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_VIEW_CONTEXT_MODEL),
      isGrantable: hasBaseAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_VIEW_CONTEXT_MODEL),
    },
    {
      name: i18next.t("roles.switchButton.edit"),
      action: (isActive) => {
        togglePrivileges(
          PrivilegeEnum.PRIVILEGE_EDIT_CONTEXT_MODEL_PARAMETERS,
          PrivilegeEnum.PRIVILEGE_CREATE_CONTEXT_MODEL,
          PrivilegeEnum.PRIVILEGE_DELETE_CONTEXT_MODEL,
          )
      },
      isActive: hasCurrentAnyOfPrivileges(
        PrivilegeEnum.PRIVILEGE_EDIT_CONTEXT_MODEL_PARAMETERS,
        PrivilegeEnum.PRIVILEGE_CREATE_CONTEXT_MODEL,
        PrivilegeEnum.PRIVILEGE_DELETE_CONTEXT_MODEL,
      ),
      isGrantable: hasBaseAnyOfPrivileges(
        PrivilegeEnum.PRIVILEGE_EDIT_CONTEXT_MODEL_PARAMETERS,
        PrivilegeEnum.PRIVILEGE_CREATE_CONTEXT_MODEL,
        PrivilegeEnum.PRIVILEGE_DELETE_CONTEXT_MODEL,
      ),
    },
    {
      name: i18next.t("roles.switchButton.toggleActive"),
      action: (isActive) => {
        togglePrivileges(PrivilegeEnum.PRIVILEGE_EDIT_CONTEXT_MODEL_STATUS)
      },
      isActive: hasCurrentAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_EDIT_CONTEXT_MODEL_STATUS),
      isGrantable: hasBaseAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_EDIT_CONTEXT_MODEL_STATUS),
    }
  ],
  items: [],
  actions: [],
});


export const REQUEST_MANAGEMENT_ACTIONS = (
  togglePrivileges,
  grantPrivileges,
  revokePrivileges,
  hasCurrentAnyOfPrivileges,
  hasBaseAnyOfPrivileges,
  hasCurrentAllOfPrivileges,
  hasBaseAllOfPrivileges
) => ({
  rootActions: [
    {
      name: i18next.t('roles.switchButton.read'),
      action: (isActive) => {
        !isActive
          ? grantPrivileges(
              PrivilegeEnum.PRIVILEGE_VIEW_AUTHORIZED_DOCUMENT_TYPE,
              PrivilegeEnum.PRIVILEGE_VIEW_DOSSIER_TYPE
            )
          : revokePrivileges(
              PrivilegeEnum.PRIVILEGE_VIEW_AUTHORIZED_DOCUMENT_TYPE,
              PrivilegeEnum.PRIVILEGE_VIEW_DOSSIER_TYPE
            );
      },
      isActive: hasCurrentAllOfPrivileges(
        PrivilegeEnum.PRIVILEGE_VIEW_AUTHORIZED_DOCUMENT_TYPE,
        PrivilegeEnum.PRIVILEGE_VIEW_DOSSIER_TYPE
      ),
      isGrantable: hasBaseAllOfPrivileges(
        PrivilegeEnum.PRIVILEGE_VIEW_AUTHORIZED_DOCUMENT_TYPE,
        PrivilegeEnum.PRIVILEGE_VIEW_DOSSIER_TYPE
      ),
    },
  ],
  items: [
    {
      name: i18next.t('adminSettings.demandesDocuments'),
      actions: [
        {
          name: i18next.t('roles.switchButton.read'),
          action: (isActive) => {
            togglePrivileges(
              PrivilegeEnum.PRIVILEGE_VIEW_AUTHORIZED_DOCUMENT_TYPE
            );
          },
          isActive: hasCurrentAnyOfPrivileges(
            PrivilegeEnum.PRIVILEGE_VIEW_AUTHORIZED_DOCUMENT_TYPE
          ),
          isGrantable: hasBaseAnyOfPrivileges(
            PrivilegeEnum.PRIVILEGE_VIEW_AUTHORIZED_DOCUMENT_TYPE
          ),
        },
      ],
    },
    {
      name: i18next.t('adminSettings.demandesDossiers'),
      actions: [
        {
          name: i18next.t('roles.switchButton.read'),
          action: (isActive) => {
            togglePrivileges(PrivilegeEnum.PRIVILEGE_VIEW_DOSSIER_TYPE);
          },
          isActive: hasCurrentAnyOfPrivileges(
            PrivilegeEnum.PRIVILEGE_VIEW_DOSSIER_TYPE
          ),
          isGrantable: hasBaseAnyOfPrivileges(
            PrivilegeEnum.PRIVILEGE_VIEW_DOSSIER_TYPE
          ),
        },
        {
          name: i18next.t('roles.switchButton.edit'),
          action: (isActive) => {
            togglePrivileges(
              PrivilegeEnum.PRIVILEGE_EDIT_DOSSIER_TYPE_PARAMETERS,
              PrivilegeEnum.PRIVILEGE_CREATE_DOSSIER_TYPE,
              PrivilegeEnum.PRIVILEGE_DELETE_DOSSIER_TYPE,
            );
            },
          isActive: hasCurrentAnyOfPrivileges(
            PrivilegeEnum.PRIVILEGE_EDIT_DOSSIER_TYPE_PARAMETERS,
            PrivilegeEnum.PRIVILEGE_CREATE_DOSSIER_TYPE,
            PrivilegeEnum.PRIVILEGE_DELETE_DOSSIER_TYPE,
          ),
          isGrantable: hasBaseAnyOfPrivileges(
            PrivilegeEnum.PRIVILEGE_EDIT_DOSSIER_TYPE_PARAMETERS,
            PrivilegeEnum.PRIVILEGE_CREATE_DOSSIER_TYPE,
            PrivilegeEnum.PRIVILEGE_DELETE_DOSSIER_TYPE,
          ),
        },
        {
          name: i18next.t('roles.switchButton.toggleActive'),
          action: (isActive) => {
            togglePrivileges(PrivilegeEnum.PRIVILEGE_EDIT_DOSSIER_TYPE_STATUS);
          },
          isActive: hasCurrentAnyOfPrivileges(
            PrivilegeEnum.PRIVILEGE_EDIT_DOSSIER_TYPE_STATUS
          ),
          isGrantable: hasBaseAnyOfPrivileges(
            PrivilegeEnum.PRIVILEGE_EDIT_DOSSIER_TYPE_STATUS
          ),
        },
      ],
    },
  ],
  actions: [],
});

export const IMPORTS_AND_UPDATES_MANAGEMENT_ACTIONS = (togglePrivileges, grantPrivileges, revokePrivileges, hasCurrentAnyOfPrivileges, hasBaseAnyOfPrivileges) => ({
  rootActions: [
    {
      name: i18next.t("roles.switchButton.read"),
      action: (isActive) => {
        togglePrivileges(PrivilegeEnum.PRIVILEGE_VIEW_IMPORTS_AND_UPDATES)
      },
      isActive: hasCurrentAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_VIEW_IMPORTS_AND_UPDATES),
      isGrantable: hasBaseAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_VIEW_IMPORTS_AND_UPDATES),
    },
    {
      name: i18next.t("roles.switchButton.add"),
      action: (isActive) => {
        togglePrivileges(PrivilegeEnum.PRIVILEGE_CREATE_IMPORTS_AND_UPDATES)
      },
      isActive: hasCurrentAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_CREATE_IMPORTS_AND_UPDATES,),
      isGrantable: hasBaseAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_CREATE_IMPORTS_AND_UPDATES),
    }
  ],
  items: [],
  actions: [],
});

export const USER_MANAGEMENT_ACTIONS = (togglePrivileges, grantPrivileges, revokePrivileges, hasCurrentAnyOfPrivileges, hasBaseAnyOfPrivileges) => ({
  rootActions: [
    {
      name: i18next.t("roles.switchButton.readAll"),
      action: (isActive) => { togglePrivileges(PrivilegeEnum.PRIVILEGE_VIEW_USER) },
      isActive: hasCurrentAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_VIEW_USER),
      isGrantable: hasBaseAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_VIEW_USER),
    },
    {
      name: i18next.t("roles.switchButton.invite"),
      action: (isActive) => {
        !isActive ?
          grantPrivileges(PrivilegeEnum.PRIVILEGE_ALL_USER)
          :
          revokePrivileges(
            PrivilegeEnum.PRIVILEGE_EDIT_USER,
            PrivilegeEnum.PRIVILEGE_CREATE_USER,
            PrivilegeEnum.PRIVILEGE_DELETE_USER
          )
      },
      isActive: hasCurrentAnyOfPrivileges(
        PrivilegeEnum.PRIVILEGE_EDIT_USER,
        PrivilegeEnum.PRIVILEGE_CREATE_USER,
        PrivilegeEnum.PRIVILEGE_DELETE_USER
      ),
      isGrantable: hasBaseAnyOfPrivileges(
        PrivilegeEnum.PRIVILEGE_EDIT_USER,
        PrivilegeEnum.PRIVILEGE_CREATE_USER,
        PrivilegeEnum.PRIVILEGE_DELETE_USER
      ),
    },
  ],
  items: [],
  actions: [],
});

//Events Dossier
export const DOSSIER_EVENT_MANAGEMENT = (
  togglePrivileges, 
  hasCurrentAnyOfPrivileges, 
  hasBaseAnyOfPrivileges, 
  hasCurrentAllOfPrivileges, 
  hasBaseAllOfPrivileges, 
  selectAll, 
  unselectAll,
  dossierAttributes, 
  dossierCalculatedAttributes,
  dossierIndicators, 
  toggleProperty, // action to select/deselect a property id for an event
  toggleAllProperties, // action to select/deselect all properties for an event
  hasPrivilegeOnProperty, // boolean to know if property is selected
  hasCurrentAllOfSpecificProperty, // boolean to know if all properties of event is selected
  hasGrantablePrivilegeForProperty, //boolean to know if the property is grantable,

) => {

  const toggleParentPrivilegeWithAllProperties = () => {
    if(isAllDossierEventSelected) {
      unselectAll(PrivilegeEnum.PRIVILEGE_EVENT_DOSSIER, 
        PrivilegeEnum.PRIVILEGE_EVENT_DOSSIER_ATTRIBUTE_UPDATED_ALL, 
        PrivilegeEnum.PRIVILEGE_EVENT_DOSSIER_CALCULATED_ATTRIBUTE_UPDATED_ALL,
        PrivilegeEnum.PRIVILEGE_EVENT_DOSSIER_INDICATOR_UPDATED_ALL,
      )
    } else {
      selectAllWithAllProperties();
    }
  }
  const hasPrivilegesWithPropertiesAllSelected = () => {
    const hasAllAttributes = hasCurrentAllOfSpecificProperty(PrivilegeEnum.PRIVILEGE_EVENT_DOSSIER_ATTRIBUTE_UPDATED);
    const hasAllCalculatedAttributes = hasCurrentAllOfSpecificProperty(PrivilegeEnum.PRIVILEGE_EVENT_DOSSIER_CALCULATED_ATTRIBUTE_UPDATED);
    const hasAllIndicators =  hasCurrentAllOfSpecificProperty(PrivilegeEnum.PRIVILEGE_EVENT_DOSSIER_INDICATOR_UPDATED);

    if(hasAllAttributes === true && hasAllCalculatedAttributes === true && hasAllIndicators === true) {
      return true;
    } else if(hasAllAttributes !== false || hasAllCalculatedAttributes !== false || hasAllIndicators !== false) {
      return EVENT_CHECK_INDETERMINED;
    }
    return false;
  }

  const isAllDossierEventSelected = hasPrivilegesWithPropertiesAllSelected() === true && hasCurrentAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_EVENT_DOSSIER) === true 
  ? true  : hasPrivilegesWithPropertiesAllSelected() === false && hasCurrentAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_EVENT_DOSSIER) === false 
  ? false : EVENT_CHECK_INDETERMINED;

  const selectAllWithAllProperties = () => {
    togglePrivileges(PrivilegeEnum.PRIVILEGE_EVENT_DOSSIER);
    selectAll(PrivilegeEnum.PRIVILEGE_EVENT_DOSSIER_ATTRIBUTE_UPDATED);
    selectAll(PrivilegeEnum.PRIVILEGE_EVENT_DOSSIER_CALCULATED_ATTRIBUTE_UPDATED);
    selectAll(PrivilegeEnum.PRIVILEGE_EVENT_DOSSIER_INDICATOR_UPDATED);
  }

  
  return {
  name: i18next.t(`privileges.PRIVILEGE_EVENT_DOSSIER`),
  action: toggleParentPrivilegeWithAllProperties,
  isActive:isAllDossierEventSelected,
  isGrantable: hasBaseAllOfPrivileges(PrivilegeEnum.PRIVILEGE_EVENT_DOSSIER),
  hasSelectActions: true,
  selectAllAction: selectAllWithAllProperties,
  unselectAllAction: () => { unselectAll(PrivilegeEnum.PRIVILEGE_EVENT_DOSSIER,
    PrivilegeEnum.PRIVILEGE_EVENT_DOSSIER_ATTRIBUTE_UPDATED_ALL, 
    PrivilegeEnum.PRIVILEGE_EVENT_DOSSIER_CALCULATED_ATTRIBUTE_UPDATED_ALL,
    PrivilegeEnum.PRIVILEGE_EVENT_DOSSIER_INDICATOR_UPDATED_ALL,
  ) },
  variant: "topDrop",
  items: [
    {
      name: i18next.t(`privileges.PRIVILEGE_EVENT_DOSSIER_ADDED_ARCHIVED`),
      action: () => { togglePrivileges(PrivilegeEnum.PRIVILEGE_EVENT_DOSSIER_ADDED, PrivilegeEnum.PRIVILEGE_EVENT_DOSSIER_ARCHIVED) },
      isActive: hasCurrentAllOfPrivileges(PrivilegeEnum.PRIVILEGE_EVENT_DOSSIER_ADDED, PrivilegeEnum.PRIVILEGE_EVENT_DOSSIER_ARCHIVED),
      isGrantable: hasBaseAllOfPrivileges(PrivilegeEnum.PRIVILEGE_EVENT_DOSSIER_ADDED, PrivilegeEnum.PRIVILEGE_EVENT_DOSSIER_ARCHIVED),
      variant: "topItem",
    },
    {
      name: i18next.t(`privileges.PRIVILEGE_EVENT_DOSSIER_UPDATED`),
      action: () => { togglePrivileges(PrivilegeEnum.PRIVILEGE_EVENT_DOSSIER_UPDATED) },
      isActive: hasCurrentAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_EVENT_DOSSIER_UPDATED),
      isGrantable: hasBaseAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_EVENT_DOSSIER_UPDATED),
      variant: "topItem",
    },
    {
      name: i18next.t(`privileges.PRIVILEGE_EVENT_DOSSIER_ATTRIBUTE_UPDATED`),
      action: () => { toggleAllProperties(PrivilegeEnum.PRIVILEGE_EVENT_DOSSIER_ATTRIBUTE_UPDATED) },
      isActive: hasCurrentAllOfSpecificProperty(PrivilegeEnum.PRIVILEGE_EVENT_DOSSIER_ATTRIBUTE_UPDATED),
      isGrantable:  hasBaseAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_EVENT_DOSSIER_ATTRIBUTE_UPDATED) && !_.isEmpty(dossierAttributes),
      variant: "subDrop",
      openDefault: true,
      toggleAllPropertiesAndFuture: () => togglePrivileges(PrivilegeEnum.PRIVILEGE_EVENT_DOSSIER_ATTRIBUTE_UPDATED_ALL),
      isAllPropertiesAndFuture: hasCurrentAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_EVENT_DOSSIER_ATTRIBUTE_UPDATED_ALL),
      hasPrivilegeAllPropertiesAndFuture: hasBaseAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_EVENT_DOSSIER_ATTRIBUTE_UPDATED_ALL),
      namePropertyPlurial: i18next.t(`events.popUp.dossier.attributes`)?.toLocaleLowerCase(),
      namePropertySingular: i18next.t(`events.popUp.dossier.attribute`),
      items: dossierAttributes?.map(attribute => {
          return {
            name: attribute?.label, 
            action: () => { toggleProperty(attribute?.id, null,
              PrivilegeEnum.PRIVILEGE_EVENT_DOSSIER_ATTRIBUTE_UPDATED_ALL, 
              PrivilegeEnum.PRIVILEGE_EVENT_DOSSIER_ATTRIBUTE_UPDATED) },
            isActive: hasPrivilegeOnProperty(attribute?.id, null, PrivilegeEnum.PRIVILEGE_EVENT_DOSSIER_ATTRIBUTE_UPDATED),
            isGrantable: hasGrantablePrivilegeForProperty(PrivilegeEnum.PRIVILEGE_EVENT_DOSSIER_ATTRIBUTE_UPDATED, attribute?.id, null), 
            variant: attribute.options ? "subDropProperties" : "topItem",
            items: !attribute.options ? null : attribute.options.map(optionAttribute => {
              const propertyCriterion = {operator: Operator.EQUAL, value: optionAttribute?.value};
              const valueOptionAttributeToShow = optionAttribute.translation ? i18next.t(optionAttribute?.translation)  : optionAttribute?.value;
              return {
                key: attribute.id + "_" + optionAttribute?.value, 
                name: <ColoredPrivilegeName name={"PRIVILEGE_EVENT_VALUE_CHANGE_TO"} values={{value : `${valueOptionAttributeToShow}`}} color={optionAttribute?.color} />,
                action: () => { toggleProperty(attribute?.id, propertyCriterion, 
                  PrivilegeEnum.PRIVILEGE_EVENT_DOSSIER_ATTRIBUTE_UPDATED_ALL,
                  PrivilegeEnum.PRIVILEGE_EVENT_DOSSIER_ATTRIBUTE_UPDATED) },
                isActive: hasPrivilegeOnProperty(attribute?.id, propertyCriterion, PrivilegeEnum.PRIVILEGE_EVENT_DOSSIER_ATTRIBUTE_UPDATED),
                isGrantable: hasGrantablePrivilegeForProperty(PrivilegeEnum.PRIVILEGE_EVENT_DOSSIER_ATTRIBUTE_UPDATED, attribute?.id, [propertyCriterion]),
                variant: "subItemProperties",
              }
            })
          }
    })
    },
    mapPrivilegeEventsForAttributesAndIndicators(
      PrivilegeEnum.PRIVILEGE_EVENT_DOSSIER_CALCULATED_ATTRIBUTE_UPDATED,
      PrivilegeEnum.PRIVILEGE_EVENT_DOSSIER_CALCULATED_ATTRIBUTE_UPDATED_ALL,
      dossierCalculatedAttributes,
      i18next.t(`calculatedAttributes`),
      i18next.t(`events.popUp.dossier.calculatedAttribute`),
      toggleAllProperties, 
      hasCurrentAllOfSpecificProperty, 
      hasBaseAnyOfPrivileges,
      togglePrivileges,
      hasCurrentAnyOfPrivileges,
      toggleProperty,
      hasPrivilegeOnProperty,
      hasGrantablePrivilegeForProperty
    ),
    mapPrivilegeEventsForAttributesAndIndicators(
      PrivilegeEnum.PRIVILEGE_EVENT_DOSSIER_INDICATOR_UPDATED,
      PrivilegeEnum.PRIVILEGE_EVENT_DOSSIER_INDICATOR_UPDATED_ALL,
      dossierIndicators,
      i18next.t(`indicators`),
      i18next.t(`events.popUp.dossier.indicator`),
      toggleAllProperties, 
      hasCurrentAllOfSpecificProperty, 
      hasBaseAnyOfPrivileges,
      togglePrivileges,
      hasCurrentAnyOfPrivileges,
      toggleProperty,
      hasPrivilegeOnProperty,
      hasGrantablePrivilegeForProperty
    ),
    // {
    //   name: "Changement de statut",
    //   action: () => { togglePrivileges(PrivilegeEnum.PRIVILEGE_EVENT_DOSSIER_STATUS_UPDATED) },
    //   isActive: hasCurrentAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_EVENT_DOSSIER_STATUS_UPDATED),
    //   isGrantable: hasBaseAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_EVENT_DOSSIER_STATUS_UPDATED),
    //   items: [{
    //     name: "Le statut devient absent",
    //     action: () => { togglePrivileges(PrivilegeEnum.PRIVILEGE_VIEW_DOSSIER_INFORMATION) },
    //     isActive: hasCurrentAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_VIEW_DOSSIER_INFORMATION),
    //     isGrantable: hasBaseAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_VIEW_DOSSIER_INFORMATION),
    //   },{
    //     name: "Le statut devient Réponse en cours",
    //     action: () => { togglePrivileges(PrivilegeEnum.PRIVILEGE_VIEW_DOSSIER_INFORMATION) },
    //     isActive: hasCurrentAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_VIEW_DOSSIER_INFORMATION),
    //     isGrantable: hasBaseAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_VIEW_DOSSIER_INFORMATION),
    //   },{
    //     name: "Le statut devient Réponse terminée",
    //     action: () => { togglePrivileges(PrivilegeEnum.PRIVILEGE_VIEW_DOSSIER_INFORMATION) },
    //     isActive: hasCurrentAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_VIEW_DOSSIER_INFORMATION),
    //     isGrantable: hasBaseAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_VIEW_DOSSIER_INFORMATION),
    //   }]
    // },
    {
      name: i18next.t(`privileges.PRIVILEGE_EVENT_DOSSIER_COMMENT`),
      action: () => { togglePrivileges(PrivilegeEnum.PRIVILEGE_EVENT_DOSSIER_COMMENT) },
      isActive: hasCurrentAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_EVENT_DOSSIER_COMMENT),
      isGrantable: hasBaseAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_EVENT_DOSSIER_COMMENT),
      variant: "subDrop",
      items: [{
        name: i18next.t(`privileges.PRIVILEGE_EVENT_DOSSIER_COMMENT_ALL`),
        action: () => {
          togglePrivileges(
            PrivilegeEnum.PRIVILEGE_EVENT_DOSSIER_COMMENT_ADDED,
            PrivilegeEnum.PRIVILEGE_EVENT_DOSSIER_COMMENT_UPDATED,
            PrivilegeEnum.PRIVILEGE_EVENT_DOSSIER_COMMENT_DELETED
          )
        },
        isActive: hasCurrentAllOfPrivileges(
          PrivilegeEnum.PRIVILEGE_EVENT_DOSSIER_COMMENT_ADDED,
          PrivilegeEnum.PRIVILEGE_EVENT_DOSSIER_COMMENT_UPDATED,
          PrivilegeEnum.PRIVILEGE_EVENT_DOSSIER_COMMENT_DELETED
        ),
        isGrantable: hasBaseAllOfPrivileges(
          PrivilegeEnum.PRIVILEGE_EVENT_DOSSIER_COMMENT_ADDED,
          PrivilegeEnum.PRIVILEGE_EVENT_DOSSIER_COMMENT_UPDATED,
          PrivilegeEnum.PRIVILEGE_EVENT_DOSSIER_COMMENT_DELETED
        ),
        variant: "subItem",
        },{
          name: i18next.t(`privileges.PRIVILEGE_EVENT_DOSSIER_COMMENT_MENTION_ALL`),
          action: () => {
            togglePrivileges(
              PrivilegeEnum.PRIVILEGE_EVENT_DOSSIER_COMMENT_MENTION_ADDED,
              PrivilegeEnum.PRIVILEGE_EVENT_DOSSIER_COMMENT_MENTION_UPDATED,
              PrivilegeEnum.PRIVILEGE_EVENT_DOSSIER_COMMENT_MENTION_DELETED
            )
          },
          isActive: hasCurrentAllOfPrivileges(
            PrivilegeEnum.PRIVILEGE_EVENT_DOSSIER_COMMENT_MENTION_ADDED,
            PrivilegeEnum.PRIVILEGE_EVENT_DOSSIER_COMMENT_MENTION_UPDATED,
            PrivilegeEnum.PRIVILEGE_EVENT_DOSSIER_COMMENT_MENTION_DELETED
          ),
          isGrantable: hasBaseAllOfPrivileges(
            PrivilegeEnum.PRIVILEGE_EVENT_DOSSIER_COMMENT_MENTION_ADDED,
            PrivilegeEnum.PRIVILEGE_EVENT_DOSSIER_COMMENT_MENTION_UPDATED,
            PrivilegeEnum.PRIVILEGE_EVENT_DOSSIER_COMMENT_MENTION_DELETED
          ),
          variant: "subItem",
      }]
    },
    {
      name: i18next.t(`privileges.PRIVILEGE_EVENT_DOSSIER_STATUS_UPDATED`),
      action: () => { togglePrivileges(PrivilegeEnum.PRIVILEGE_EVENT_DOSSIER_STATUS_UPDATED) },
      isActive: hasCurrentAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_EVENT_DOSSIER_STATUS_UPDATED),
      isGrantable: hasBaseAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_EVENT_DOSSIER_STATUS_UPDATED),
      variant: "subDrop",
      openDefault: true,
      items: [
        {
          name: <ColoredPrivilegeName name="PRIVILEGE_EVENT_DOSSIER_STATUS_UPDATED_ABSENT" color={darkGrey}/>,
          action: () => { togglePrivileges(PrivilegeEnum.PRIVILEGE_EVENT_DOSSIER_STATUS_UPDATED_ABSENT) },
          isActive: hasCurrentAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_EVENT_DOSSIER_STATUS_UPDATED_ABSENT),
          isGrantable: hasBaseAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_EVENT_DOSSIER_STATUS_UPDATED_ABSENT),
          variant: "subItem",
        },
        {
          name: <ColoredPrivilegeName name="PRIVILEGE_EVENT_DOSSIER_STATUS_UPDATED_ONGOING" color={infoColor}/>,
          action: () => { togglePrivileges(PrivilegeEnum.PRIVILEGE_EVENT_DOSSIER_STATUS_UPDATED_ONGOING) },
          isActive: hasCurrentAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_EVENT_DOSSIER_STATUS_UPDATED_ONGOING),
          isGrantable: hasBaseAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_EVENT_DOSSIER_STATUS_UPDATED_ONGOING),
          variant: "subItem",
        },
        {
          name: <ColoredPrivilegeName name="PRIVILEGE_EVENT_DOSSIER_STATUS_UPDATED_ACTION_REQUIRED" color={orangeColor}/>,
          action: () => { togglePrivileges(PrivilegeEnum.PRIVILEGE_EVENT_DOSSIER_STATUS_UPDATED_ACTION_REQUIRED) },
          isActive: hasCurrentAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_EVENT_DOSSIER_STATUS_UPDATED_ACTION_REQUIRED),
          isGrantable: hasBaseAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_EVENT_DOSSIER_STATUS_UPDATED_ACTION_REQUIRED),
          variant: "subItem",
        },
        {
          name: <ColoredPrivilegeName name="PRIVILEGE_EVENT_DOSSIER_STATUS_UPDATED_VALID" color={primary}/>,
          action: () => { togglePrivileges(PrivilegeEnum.PRIVILEGE_EVENT_DOSSIER_STATUS_UPDATED_VALID) },
          isActive: hasCurrentAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_EVENT_DOSSIER_STATUS_UPDATED_VALID),
          isGrantable: hasBaseAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_EVENT_DOSSIER_STATUS_UPDATED_VALID),
          variant: "subItem",
        },
        {
          name: <ColoredPrivilegeName name="PRIVILEGE_EVENT_DOSSIER_STATUS_UPDATED_BELOW_EXPECTATION" color={purpleColor}/>,
          action: () => { togglePrivileges(PrivilegeEnum.PRIVILEGE_EVENT_DOSSIER_STATUS_UPDATED_BELOW_EXPECTATION) },
          isActive: hasCurrentAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_EVENT_DOSSIER_STATUS_UPDATED_BELOW_EXPECTATION),
          isGrantable: hasBaseAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_EVENT_DOSSIER_STATUS_UPDATED_BELOW_EXPECTATION),
          variant: "subItem",
        },
        {
          name: <ColoredPrivilegeName name="PRIVILEGE_EVENT_DOSSIER_STATUS_UPDATED_INVALID" color={errorColor}/>,
          action: () => { togglePrivileges(PrivilegeEnum.PRIVILEGE_EVENT_DOSSIER_STATUS_UPDATED_INVALID) },
          isActive: hasCurrentAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_EVENT_DOSSIER_STATUS_UPDATED_INVALID),
          isGrantable: hasBaseAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_EVENT_DOSSIER_STATUS_UPDATED_INVALID),
          variant: "subItem",
        }]
    },
  ]
}
};


//Events Document
export const DOCUMENT_EVENT_MANAGEMENT = (togglePrivileges, hasCurrentAnyOfPrivileges, hasBaseAnyOfPrivileges, hasCurrentAllOfPrivileges, hasBaseAllOfPrivileges, selectAll, unselectAll) => ({
  name: i18next.t(`privileges.PRIVILEGE_EVENT_DOCUMENT`),
  action: () => { togglePrivileges(PrivilegeEnum.PRIVILEGE_EVENT_DOCUMENT) },
  isActive: hasCurrentAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_EVENT_DOCUMENT),
  isGrantable: hasBaseAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_EVENT_DOCUMENT),
  hasSelectActions: true,
  selectAllAction: () => { selectAll(PrivilegeEnum.PRIVILEGE_EVENT_DOCUMENT) },
  unselectAllAction: () => { unselectAll(PrivilegeEnum.PRIVILEGE_EVENT_DOCUMENT) },
  variant: "topDrop",
  items: [
    {
      name: i18next.t(`privileges.PRIVILEGE_EVENT_DOCUMENT_ADDED`),
      action: () => { togglePrivileges(PrivilegeEnum.PRIVILEGE_EVENT_DOCUMENT_ADDED) },
      isActive: hasCurrentAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_EVENT_DOCUMENT_ADDED),
      isGrantable: hasBaseAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_EVENT_DOCUMENT_ADDED),
      variant: "topItem",
    },
    {
      name: i18next.t(`privileges.PRIVILEGE_EVENT_DOCUMENT_STOPPED`),
      action: () => { togglePrivileges(PrivilegeEnum.PRIVILEGE_EVENT_DOCUMENT_STOPPED) },
      isActive: hasCurrentAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_EVENT_DOCUMENT_STOPPED),
      isGrantable: hasBaseAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_EVENT_DOCUMENT_STOPPED),
      variant: "topItem",
    },
    {
      name: i18next.t(`privileges.PRIVILEGE_EVENT_DOCUMENT_ANSWER_ADDED`),
      action: () => { togglePrivileges(PrivilegeEnum.PRIVILEGE_EVENT_DOCUMENT_ANSWER_ADDED) },
      isActive: hasCurrentAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_EVENT_DOCUMENT_ANSWER_ADDED),
      isGrantable: hasBaseAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_EVENT_DOCUMENT_ANSWER_ADDED),
      variant: "topItem",
    },
    {
      name: i18next.t(`privileges.PRIVILEGE_EVENT_DOCUMENT_ANSWER_UPDATED`),
      action: () => { togglePrivileges(PrivilegeEnum.PRIVILEGE_EVENT_DOCUMENT_ANSWER_UPDATED) },
      isActive: hasCurrentAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_EVENT_DOCUMENT_ANSWER_UPDATED),
      isGrantable: hasBaseAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_EVENT_DOCUMENT_ANSWER_UPDATED),
      variant: "topItem",
    },
    {
      name: i18next.t(`privileges.PRIVILEGE_EVENT_DOCUMENT_ANSWER_INTERRUPTED`),
      action: () => { togglePrivileges(PrivilegeEnum.PRIVILEGE_EVENT_DOCUMENT_ANSWER_INTERRUPTED) },
      isActive: hasCurrentAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_EVENT_DOCUMENT_ANSWER_INTERRUPTED),
      isGrantable: hasBaseAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_EVENT_DOCUMENT_ANSWER_INTERRUPTED),
      variant: "topItem",
    },
    {
      name: i18next.t(`privileges.PRIVILEGE_EVENT_DOCUMENT_ANSWER_SCOPE_UPDATED`),
      action: () => { togglePrivileges(PrivilegeEnum.PRIVILEGE_EVENT_DOCUMENT_ANSWER_SCOPE_UPDATED) },
      isActive: hasCurrentAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_EVENT_DOCUMENT_ANSWER_SCOPE_UPDATED),
      isGrantable: hasBaseAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_EVENT_DOCUMENT_ANSWER_SCOPE_UPDATED),
      variant: "topItem",
    },
    {
      name: i18next.t(`privileges.PRIVILEGE_EVENT_DOCUMENT_ANSWER_EXPIRED`),
      action: () => { togglePrivileges(PrivilegeEnum.PRIVILEGE_EVENT_DOCUMENT_ANSWER_EXPIRED) },
      isActive: hasCurrentAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_EVENT_DOCUMENT_ANSWER_EXPIRED),
      isGrantable: hasBaseAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_EVENT_DOCUMENT_ANSWER_EXPIRED),
      variant: "topItem",
    },
    {
      name: i18next.t(`privileges.PRIVILEGE_EVENT_DOCUMENT_ANSWER_ACCEPTED`),
      action: () => { togglePrivileges(PrivilegeEnum.PRIVILEGE_EVENT_DOCUMENT_ANSWER_ACCEPTED) },
      isActive: hasCurrentAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_EVENT_DOCUMENT_ANSWER_ACCEPTED),
      isGrantable: hasBaseAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_EVENT_DOCUMENT_ANSWER_ACCEPTED),
      variant: "topItem",
    },
    {
      name: i18next.t(`privileges.PRIVILEGE_EVENT_DOCUMENT_ANSWER_DENIED`),
      action: () => { togglePrivileges(PrivilegeEnum.PRIVILEGE_EVENT_DOCUMENT_ANSWER_DENIED) },
      isActive: hasCurrentAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_EVENT_DOCUMENT_ANSWER_DENIED),
      isGrantable: hasBaseAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_EVENT_DOCUMENT_ANSWER_DENIED),
      variant: "topItem",
    },
    {
      name: i18next.t(`privileges.PRIVILEGE_EVENT_DOCUMENT_COMMENT`),
      action: () => { togglePrivileges(PrivilegeEnum.PRIVILEGE_EVENT_DOCUMENT_COMMENT) },
      isActive: hasCurrentAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_EVENT_DOCUMENT_COMMENT),
      isGrantable: hasBaseAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_EVENT_DOCUMENT_COMMENT),
      variant: "subDrop",
      items: [{
        name: i18next.t(`privileges.PRIVILEGE_EVENT_DOCUMENT_COMMENT_ALL`),
        action: () => {
          togglePrivileges(
            PrivilegeEnum.PRIVILEGE_EVENT_DOCUMENT_COMMENT_ADDED,
            PrivilegeEnum.PRIVILEGE_EVENT_DOCUMENT_COMMENT_UPDATED,
            PrivilegeEnum.PRIVILEGE_EVENT_DOCUMENT_COMMENT_DELETED
          )
        },
        isActive: hasCurrentAllOfPrivileges(
          PrivilegeEnum.PRIVILEGE_EVENT_DOCUMENT_COMMENT_ADDED,
          PrivilegeEnum.PRIVILEGE_EVENT_DOCUMENT_COMMENT_UPDATED,
          PrivilegeEnum.PRIVILEGE_EVENT_DOCUMENT_COMMENT_DELETED
        ),
        isGrantable: hasBaseAllOfPrivileges(
          PrivilegeEnum.PRIVILEGE_EVENT_DOCUMENT_COMMENT_ADDED,
          PrivilegeEnum.PRIVILEGE_EVENT_DOCUMENT_COMMENT_UPDATED,
          PrivilegeEnum.PRIVILEGE_EVENT_DOCUMENT_COMMENT_DELETED
        ),
        variant: "subItem",
        },{
          name: i18next.t(`privileges.PRIVILEGE_EVENT_DOCUMENT_COMMENT_MENTION_ALL`),
          action: () => {
            togglePrivileges(
              PrivilegeEnum.PRIVILEGE_EVENT_DOCUMENT_COMMENT_MENTION_ADDED,
              PrivilegeEnum.PRIVILEGE_EVENT_DOCUMENT_COMMENT_MENTION_UPDATED,
              PrivilegeEnum.PRIVILEGE_EVENT_DOCUMENT_COMMENT_MENTION_DELETED
            )
          },
          isActive: hasCurrentAllOfPrivileges(
            PrivilegeEnum.PRIVILEGE_EVENT_DOCUMENT_COMMENT_MENTION_ADDED,
            PrivilegeEnum.PRIVILEGE_EVENT_DOCUMENT_COMMENT_MENTION_UPDATED,
            PrivilegeEnum.PRIVILEGE_EVENT_DOCUMENT_COMMENT_MENTION_DELETED
          ),
          isGrantable: hasBaseAllOfPrivileges(
            PrivilegeEnum.PRIVILEGE_EVENT_DOCUMENT_COMMENT_MENTION_ADDED,
            PrivilegeEnum.PRIVILEGE_EVENT_DOCUMENT_COMMENT_MENTION_UPDATED,
            PrivilegeEnum.PRIVILEGE_EVENT_DOCUMENT_COMMENT_MENTION_DELETED
          ),
          variant: "subItem",
      }]
    },
    {
      name: i18next.t(`privileges.PRIVILEGE_EVENT_DOCUMENT_ANSWER_STATUS_UPDATE`),
      action: () => { togglePrivileges(PrivilegeEnum.PRIVILEGE_EVENT_DOCUMENT_ANSWER_STATUS_UPDATE) },
      isActive: hasCurrentAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_EVENT_DOCUMENT_ANSWER_STATUS_UPDATE),
      isGrantable: hasBaseAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_EVENT_DOCUMENT_ANSWER_STATUS_UPDATE),
      variant: "subDrop",
      openDefault: true,
      items: [
        {
          name: <ColoredPrivilegeName name="PRIVILEGE_EVENT_DOCUMENT_ANSWER_STATUS_UPDATE_ABSENT" color={darkGrey} />,
          action: () => { togglePrivileges(PrivilegeEnum.PRIVILEGE_EVENT_DOCUMENT_ANSWER_STATUS_UPDATE_ABSENT) },
          isActive: hasCurrentAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_EVENT_DOCUMENT_ANSWER_STATUS_UPDATE_ABSENT),
          isGrantable: hasBaseAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_EVENT_DOCUMENT_ANSWER_STATUS_UPDATE_ABSENT),
          variant: "subItem",
        },
        {
          name: <ColoredPrivilegeName name="PRIVILEGE_EVENT_DOCUMENT_ANSWER_STATUS_UPDATE_ONGOING" color={infoColor} />,
          action: () => { togglePrivileges(PrivilegeEnum.PRIVILEGE_EVENT_DOCUMENT_ANSWER_STATUS_UPDATE_ONGOING) },
          isActive: hasCurrentAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_EVENT_DOCUMENT_ANSWER_STATUS_UPDATE_ONGOING),
          isGrantable: hasBaseAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_EVENT_DOCUMENT_ANSWER_STATUS_UPDATE_ONGOING),
          variant: "subItem",
        },
        {
          name: <ColoredPrivilegeName name="PRIVILEGE_EVENT_DOCUMENT_ANSWER_STATUS_UPDATE_ACTION_REQUIRED" color={orangeColor} />,
          action: () => { togglePrivileges(PrivilegeEnum.PRIVILEGE_EVENT_DOCUMENT_ANSWER_STATUS_UPDATE_ACTION_REQUIRED) },
          isActive: hasCurrentAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_EVENT_DOCUMENT_ANSWER_STATUS_UPDATE_ACTION_REQUIRED),
          isGrantable: hasBaseAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_EVENT_DOCUMENT_ANSWER_STATUS_UPDATE_ACTION_REQUIRED),
          variant: "subItem",
        },
        {
          name: <ColoredPrivilegeName name="PRIVILEGE_EVENT_DOCUMENT_ANSWER_STATUS_UPDATE_VALID" color={primary} />,
          action: () => { togglePrivileges(PrivilegeEnum.PRIVILEGE_EVENT_DOCUMENT_ANSWER_STATUS_UPDATE_VALID) },
          isActive: hasCurrentAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_EVENT_DOCUMENT_ANSWER_STATUS_UPDATE_VALID),
          isGrantable: hasBaseAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_EVENT_DOCUMENT_ANSWER_STATUS_UPDATE_VALID),
          variant: "subItem",
        },
        {
          name: <ColoredPrivilegeName name="PRIVILEGE_EVENT_DOCUMENT_ANSWER_STATUS_UPDATE_BELOW_EXPECTATION" color={purpleColor} />,
          action: () => { togglePrivileges(PrivilegeEnum.PRIVILEGE_EVENT_DOCUMENT_ANSWER_STATUS_UPDATE_BELOW_EXPECTATION) },
          isActive: hasCurrentAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_EVENT_DOCUMENT_ANSWER_STATUS_UPDATE_BELOW_EXPECTATION),
          isGrantable: hasBaseAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_EVENT_DOCUMENT_ANSWER_STATUS_UPDATE_BELOW_EXPECTATION),
          variant: "subItem",
        },
        {
          name: <ColoredPrivilegeName name="PRIVILEGE_EVENT_DOCUMENT_ANSWER_STATUS_UPDATE_INVALID" color={errorColor} />,
          action: () => { togglePrivileges(PrivilegeEnum.PRIVILEGE_EVENT_DOCUMENT_ANSWER_STATUS_UPDATE_INVALID) },
          isActive: hasCurrentAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_EVENT_DOCUMENT_ANSWER_STATUS_UPDATE_INVALID),
          isGrantable: hasBaseAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_EVENT_DOCUMENT_ANSWER_STATUS_UPDATE_INVALID),
          variant: "subItem",
        }]
    },
    {
      name: i18next.t(`privileges.PRIVILEGE_EVENT_DOCUMENT_DELETION_WARNING`),
      action: () => { togglePrivileges(PrivilegeEnum.PRIVILEGE_EVENT_DOCUMENT_DELETION_WARNING) },
      isActive: hasCurrentAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_EVENT_DOCUMENT_DELETION_WARNING),
      isGrantable: hasBaseAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_EVENT_DOCUMENT_DELETION_WARNING),
      variant: "topItem",
    },
    // {
    //   name: i18next.t(`privileges.PRIVILEGE_EVENT_DOCUMENT_INDUED`),
    //   action: () => { togglePrivileges(PrivilegeEnum.PRIVILEGE_EVENT_DOCUMENT_INDUED) },
    //   isActive: hasCurrentAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_EVENT_DOCUMENT_INDUED),
    //   isGrantable: hasBaseAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_EVENT_DOCUMENT_INDUED),
    //   variant: "subDrop",
    //   items: [{
    //     name: i18next.t(`privileges.PRIVILEGE_EVENT_DOCUMENT_INDUED_REPORT_GENERATED`),
    //     action: () => { togglePrivileges(PrivilegeEnum.PRIVILEGE_EVENT_DOCUMENT_INDUED_REPORT_GENERATED) },
    //     isActive: hasCurrentAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_EVENT_DOCUMENT_INDUED_REPORT_GENERATED),
    //     isGrantable: hasBaseAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_EVENT_DOCUMENT_INDUED_REPORT_GENERATED),
    //     variant: "subItem",
    //   }, {
    //     name: i18next.t(`privileges.PRIVILEGE_EVENT_DOCUMENT_INDUED_REPORT_RECEIVED`),
    //     action: () => { togglePrivileges(PrivilegeEnum.PRIVILEGE_EVENT_DOCUMENT_INDUED_REPORT_RECEIVED) },
    //     isActive: hasCurrentAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_EVENT_DOCUMENT_INDUED_REPORT_RECEIVED),
    //     isGrantable: hasBaseAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_EVENT_DOCUMENT_INDUED_REPORT_RECEIVED),
    //     variant: "subItem",
    //   }]
    // },
    {
      name: i18next.t(`privileges.PRIVILEGE_EVENT_DOCUMENT_TASKS`),
      action: () => { togglePrivileges(PrivilegeEnum.PRIVILEGE_EVENT_DOCUMENT_TASKS) },
      isActive: hasCurrentAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_EVENT_DOCUMENT_TASKS),
      isGrantable: hasBaseAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_EVENT_DOCUMENT_TASKS),
      variant: "subDrop",
      openDefault: true,
      backgroundColor: "#FFEBCC",
      items: [{
        name: i18next.t(`privileges.PRIVILEGE_EVENT_DOCUMENT_TASK_AWAITING_VALIDATION`),
        action: () => { togglePrivileges(PrivilegeEnum.PRIVILEGE_EVENT_DOCUMENT_TASK_AWAITING_VALIDATION) },
        isActive: hasCurrentAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_EVENT_DOCUMENT_TASK_AWAITING_VALIDATION),
        isGrantable: hasBaseAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_EVENT_DOCUMENT_TASK_AWAITING_VALIDATION),
        variant: "subItem",
      }, {
        name: i18next.t(`privileges.PRIVILEGE_EVENT_DOCUMENT_TASK_DUE_DILIGENCE`),
        action: () => { togglePrivileges(PrivilegeEnum.PRIVILEGE_EVENT_DOCUMENT_TASK_DUE_DILIGENCE) },
        isActive: hasCurrentAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_EVENT_DOCUMENT_TASK_DUE_DILIGENCE),
        isGrantable: hasBaseAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_EVENT_DOCUMENT_TASK_DUE_DILIGENCE),
        variant: "subItem",
      }, {
        name: i18next.t(`privileges.PRIVILEGE_EVENT_TASK_INDUED_FALSE_POSITIVE`),
        action: () => { togglePrivileges(PrivilegeEnum.PRIVILEGE_EVENT_TASK_INDUED_FALSE_POSITIVE) },
        isActive: hasCurrentAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_EVENT_TASK_INDUED_FALSE_POSITIVE),
        isGrantable: hasBaseAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_EVENT_TASK_INDUED_FALSE_POSITIVE),
        variant: "subItem",
      }]
    },
  ]
});

//Events Tiers
export const TIERS_EVENT_MANAGEMENT = (
  hasOptionSubscription, 
  togglePrivileges, // action to select/deselect a event (for classical event without properties)
  hasCurrentPrivilege, // boolean to know if the event is select/deselect (for classical event without properties)
  hasBaseAnyOfPrivileges, // boolean to know if there are privilege granted to select/deselect an event
  hasCurrentAllOfPrivileges, // boolean to know if all child events is select/deselect
  hasBaseAllOfPrivileges, // boolean to know if there are privilege granted to select/deselect all child events
  selectAll, // select all events.
  unselectAll, // unselect all events
  accountThirdpartyAttributes, //list of properties : thirdparty attributes
  accountCalculatedAttributes,  // list of properties : calculated attributes
  accountIndicators, // list of properties : indicators
  toggleProperty, // action to select/deselect a property id for an event
  toggleAllProperties, // action to select/deselect all properties for an event
  hasPrivilegeOnProperty, // boolean to know if property is selected
  hasCurrentAllOfSpecificProperty, // boolean to know if all properties of event is selected
  hasGrantablePrivilegeForProperty, //boolean to know if the property is grantable,
  dataHubSources
) => 
{
  const toggleParentPrivilegeWithAllProperties = () => {
    if(isAllTiersEventSelected) {
      unselectAll(PrivilegeEnum.PRIVILEGE_EVENT_THIRD_PARTY, 
        PrivilegeEnum.PRIVILEGE_EVENT_THIRD_PARTY_ATTRIBUTE_UPDATED_ALL, 
        PrivilegeEnum.PRIVILEGE_EVENT_THIRD_PARTY_CALCULATED_ATTRIBUTE_UPDATED_ALL,
        PrivilegeEnum.PRIVILEGE_EVENT_THIRD_PARTY_INDICATOR_UPDATED_ALL,
        PrivilegeEnum.PRIVILEGE_EVENT_THIRD_PARTY_DATA_HUB_UPDATED_ALL
      )
    } else {
      selectAllWithAllProperties();
    }
  }
  const hasPrivilegesWithPropertiesAllSelected = () => {
    const hasAllAttributes = hasCurrentAllOfSpecificProperty(PrivilegeEnum.PRIVILEGE_EVENT_THIRD_PARTY_ATTRIBUTE_UPDATED);
    const hasAllCalculatedAttributes = hasCurrentAllOfSpecificProperty(PrivilegeEnum.PRIVILEGE_EVENT_THIRD_PARTY_CALCULATED_ATTRIBUTE_UPDATED);
    const hasAllIndicators =  hasCurrentAllOfSpecificProperty(PrivilegeEnum.PRIVILEGE_EVENT_THIRD_PARTY_INDICATOR_UPDATED);
    const hasAllDataHubSources = hasCurrentAllOfSpecificProperty(PrivilegeEnum.PRIVILEGE_EVENT_THIRD_PARTY_DATA_HUB_UPDATED);

    if(hasAllAttributes === true && hasAllCalculatedAttributes === true && hasAllIndicators === true && hasAllDataHubSources === true) {
      return true;
    } else if(hasAllAttributes !== false || hasAllCalculatedAttributes !== false || hasAllIndicators !== false || hasAllDataHubSources !== false ) {
      return EVENT_CHECK_INDETERMINED;
    }
    return false;
  }
  const isAllTiersEventSelected = hasPrivilegesWithPropertiesAllSelected() === true && hasCurrentPrivilege(PrivilegeEnum.PRIVILEGE_EVENT_THIRD_PARTY) === true 
  ? true  : hasPrivilegesWithPropertiesAllSelected() === false && hasCurrentPrivilege(PrivilegeEnum.PRIVILEGE_EVENT_THIRD_PARTY) === false 
  ? false : EVENT_CHECK_INDETERMINED;

  const selectAllWithAllProperties = () => {
    togglePrivileges(PrivilegeEnum.PRIVILEGE_EVENT_THIRD_PARTY);
    selectAll(PrivilegeEnum.PRIVILEGE_EVENT_THIRD_PARTY_ATTRIBUTE_UPDATED);
    selectAll(PrivilegeEnum.PRIVILEGE_EVENT_THIRD_PARTY_CALCULATED_ATTRIBUTE_UPDATED);
    selectAll(PrivilegeEnum.PRIVILEGE_EVENT_THIRD_PARTY_INDICATOR_UPDATED);
    selectAll(PrivilegeEnum.PRIVILEGE_EVENT_THIRD_PARTY_DATA_HUB_UPDATED);
  }


  return {
  name: i18next.t(`privileges.PRIVILEGE_EVENT_THIRD_PARTY`),
  action: toggleParentPrivilegeWithAllProperties,
  isActive: isAllTiersEventSelected,
  isGrantable: hasBaseAllOfPrivileges(PrivilegeEnum.PRIVILEGE_EVENT_THIRD_PARTY),
  hasSelectActions: true,
  selectAllAction: selectAllWithAllProperties,
  unselectAllAction: () => unselectAll(PrivilegeEnum.PRIVILEGE_EVENT_THIRD_PARTY, 
    PrivilegeEnum.PRIVILEGE_EVENT_THIRD_PARTY_ATTRIBUTE_UPDATED_ALL, 
    PrivilegeEnum.PRIVILEGE_EVENT_THIRD_PARTY_CALCULATED_ATTRIBUTE_UPDATED_ALL,
    PrivilegeEnum.PRIVILEGE_EVENT_THIRD_PARTY_INDICATOR_UPDATED_ALL,
    PrivilegeEnum.PRIVILEGE_EVENT_THIRD_PARTY_DATA_HUB_UPDATED_ALL
  ),
  variant: "topDrop",
  items: [
    {
      name: i18next.t(`privileges.PRIVILEGE_EVENT_THIRD_PARTY_ADDED`),
      action: () => { togglePrivileges(PrivilegeEnum.PRIVILEGE_EVENT_THIRD_PARTY_ADDED) },
      isActive: hasCurrentPrivilege(PrivilegeEnum.PRIVILEGE_EVENT_THIRD_PARTY_ADDED),
      isGrantable: hasBaseAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_EVENT_THIRD_PARTY_ADDED),
      variant: "topItem",
    },{
      name: i18next.t(`privileges.PRIVILEGE_EVENT_THIRD_PARTY_ARCHIVED`),
      action: () => { togglePrivileges(PrivilegeEnum.PRIVILEGE_EVENT_THIRD_PARTY_ARCHIVED) },
      isActive: hasCurrentPrivilege(PrivilegeEnum.PRIVILEGE_EVENT_THIRD_PARTY_ARCHIVED),
      isGrantable: hasBaseAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_EVENT_THIRD_PARTY_ARCHIVED),
      variant: "topItem",
    },{
      name: i18next.t(`privileges.PRIVILEGE_EVENT_THIRD_PARTY_CODE_UPDATED`),
      action: () => { togglePrivileges(PrivilegeEnum.PRIVILEGE_EVENT_THIRD_PARTY_CODE_UPDATED) },
      isActive: hasCurrentPrivilege(PrivilegeEnum.PRIVILEGE_EVENT_THIRD_PARTY_CODE_UPDATED),
      isGrantable: hasBaseAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_EVENT_THIRD_PARTY_CODE_UPDATED),
      variant: "topItem",
    },
    {
      name: i18next.t(`privileges.PRIVILEGE_EVENT_THIRD_PARTY_SOURCE_CHANGED_TO_UNMANAGED`),
      action: () => { togglePrivileges(PrivilegeEnum.PRIVILEGE_EVENT_THIRD_PARTY_SOURCE_CHANGED_TO_UNMANAGED) },
      isActive: hasCurrentPrivilege(PrivilegeEnum.PRIVILEGE_EVENT_THIRD_PARTY_SOURCE_CHANGED_TO_UNMANAGED),
      isGrantable: hasBaseAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_EVENT_THIRD_PARTY_SOURCE_CHANGED_TO_UNMANAGED),
      variant: "topItem",
    },
     {
      name: i18next.t(`privileges.PRIVILEGE_EVENT_THIRD_PARTY_PROCEDURE_COLLECTIVE_UPDATED`),
      action: () => { togglePrivileges(PrivilegeEnum.PRIVILEGE_EVENT_THIRD_PARTY_PROCEDURE_COLLECTIVE_UPDATED) },
      isActive: hasCurrentPrivilege(PrivilegeEnum.PRIVILEGE_EVENT_THIRD_PARTY_PROCEDURE_COLLECTIVE_UPDATED),
      isGrantable: hasBaseAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_EVENT_THIRD_PARTY_PROCEDURE_COLLECTIVE_UPDATED),
      variant: "subDrop",
      items:
      [
        {privilege: PrivilegeEnum.PRIVILEGE_EVENT_THIRD_PARTY_PROCEDURE_COLLECTIVE_UPDATED_ETAPE_01, etape: "1"},
        {privilege: PrivilegeEnum.PRIVILEGE_EVENT_THIRD_PARTY_PROCEDURE_COLLECTIVE_UPDATED_ETAPE_02, etape: "2"},
        {privilege: PrivilegeEnum.PRIVILEGE_EVENT_THIRD_PARTY_PROCEDURE_COLLECTIVE_UPDATED_ETAPE_03, etape: "3"},
        {privilege: PrivilegeEnum.PRIVILEGE_EVENT_THIRD_PARTY_PROCEDURE_COLLECTIVE_UPDATED_ETAPE_04, etape: "4"},
        {privilege: PrivilegeEnum.PRIVILEGE_EVENT_THIRD_PARTY_PROCEDURE_COLLECTIVE_UPDATED_ETAPE_05, etape: "5"},
        {privilege: PrivilegeEnum.PRIVILEGE_EVENT_THIRD_PARTY_PROCEDURE_COLLECTIVE_UPDATED_ETAPE_06, etape: "6"},
        {privilege: PrivilegeEnum.PRIVILEGE_EVENT_THIRD_PARTY_PROCEDURE_COLLECTIVE_UPDATED_ETAPE_07, etape: "7"},
        {privilege: PrivilegeEnum.PRIVILEGE_EVENT_THIRD_PARTY_PROCEDURE_COLLECTIVE_UPDATED_ETAPE_08, etape: "8"},
        {privilege: PrivilegeEnum.PRIVILEGE_EVENT_THIRD_PARTY_PROCEDURE_COLLECTIVE_UPDATED_ETAPE_09, etape: "9"},
        {privilege: PrivilegeEnum.PRIVILEGE_EVENT_THIRD_PARTY_PROCEDURE_COLLECTIVE_UPDATED_ETAPE_10, etape: "10"},
        {privilege: PrivilegeEnum.PRIVILEGE_EVENT_THIRD_PARTY_PROCEDURE_COLLECTIVE_UPDATED_ETAPE_11, etape: "11"},
        {privilege: PrivilegeEnum.PRIVILEGE_EVENT_THIRD_PARTY_PROCEDURE_COLLECTIVE_UPDATED_ETAPE_12, etape: "12"},
        {privilege: PrivilegeEnum.PRIVILEGE_EVENT_THIRD_PARTY_PROCEDURE_COLLECTIVE_UPDATED_ETAPE_13, etape: "13"},
        {privilege: PrivilegeEnum.PRIVILEGE_EVENT_THIRD_PARTY_PROCEDURE_COLLECTIVE_UPDATED_ETAPE_14, etape: "14"},
        {privilege: PrivilegeEnum.PRIVILEGE_EVENT_THIRD_PARTY_PROCEDURE_COLLECTIVE_UPDATED_ETAPE_15, etape: "15"},
        {privilege: PrivilegeEnum.PRIVILEGE_EVENT_THIRD_PARTY_PROCEDURE_COLLECTIVE_UPDATED_ETAPE_16, etape: "16"},
        {privilege: PrivilegeEnum.PRIVILEGE_EVENT_THIRD_PARTY_PROCEDURE_COLLECTIVE_UPDATED_ETAPE_17, etape: "17"},
        {privilege: PrivilegeEnum.PRIVILEGE_EVENT_THIRD_PARTY_PROCEDURE_COLLECTIVE_UPDATED_ETAPE_18, etape: "18"},
        {privilege: PrivilegeEnum.PRIVILEGE_EVENT_THIRD_PARTY_PROCEDURE_COLLECTIVE_UPDATED_ETAPE_19, etape: "19"},
        {privilege: PrivilegeEnum.PRIVILEGE_EVENT_THIRD_PARTY_PROCEDURE_COLLECTIVE_UPDATED_ETAPE_20, etape: "20"},
      ].map(pclPrivilege => {
        return {
          key: pclPrivilege.privilege,
          name: i18next.t(`collectiveProceedingStage.${pclPrivilege.etape}`),
          action: () => { togglePrivileges(pclPrivilege.privilege) },
          isActive: hasCurrentPrivilege(pclPrivilege.privilege),
          isGrantable: hasBaseAnyOfPrivileges(pclPrivilege.privilege),
          variant: "subItem",
          }
      })
    },
    {
      name: i18next.t(`privileges.PRIVILEGE_EVENT_THIRD_PARTY_LEGAL_ANNOUNCEMENT_CREATION`),
      action: () => { togglePrivileges(PrivilegeEnum.PRIVILEGE_EVENT_THIRD_PARTY_LEGAL_ANNOUNCEMENT_CREATION) },
      isActive: hasCurrentPrivilege(PrivilegeEnum.PRIVILEGE_EVENT_THIRD_PARTY_LEGAL_ANNOUNCEMENT_CREATION),
      isGrantable: hasBaseAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_EVENT_THIRD_PARTY_LEGAL_ANNOUNCEMENT_CREATION),
      variant: "topItem",
    },
    {
      name: i18next.t(`privileges.PRIVILEGE_EVENT_THIRD_PARTY_LEGAL_ANNOUNCEMENT_CHANGES`),
      action: () => { togglePrivileges(PrivilegeEnum.PRIVILEGE_EVENT_THIRD_PARTY_LEGAL_ANNOUNCEMENT_CHANGES) },
      isActive: hasCurrentPrivilege(PrivilegeEnum.PRIVILEGE_EVENT_THIRD_PARTY_LEGAL_ANNOUNCEMENT_CHANGES),
      isGrantable: hasBaseAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_EVENT_THIRD_PARTY_LEGAL_ANNOUNCEMENT_CHANGES),
      variant: "topItem",
    },
    {
      name: i18next.t(`privileges.PRIVILEGE_EVENT_THIRD_PARTY_LEGAL_ANNOUNCEMENT_CESSATION`),
      action: () => { togglePrivileges(PrivilegeEnum.PRIVILEGE_EVENT_THIRD_PARTY_LEGAL_ANNOUNCEMENT_CESSATION) },
      isActive: hasCurrentPrivilege(PrivilegeEnum.PRIVILEGE_EVENT_THIRD_PARTY_LEGAL_ANNOUNCEMENT_CESSATION),
      isGrantable: hasBaseAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_EVENT_THIRD_PARTY_LEGAL_ANNOUNCEMENT_CESSATION),
      variant: "topItem",
    },
    {
      name: i18next.t(`privileges.PRIVILEGE_EVENT_THIRD_PARTY_LEGAL_ANNOUNCEMENT_TRANSFERT`),
      action: () => { togglePrivileges(PrivilegeEnum.PRIVILEGE_EVENT_THIRD_PARTY_LEGAL_ANNOUNCEMENT_TRANSFERT) },
      isActive: hasCurrentPrivilege(PrivilegeEnum.PRIVILEGE_EVENT_THIRD_PARTY_LEGAL_ANNOUNCEMENT_TRANSFERT),
      isGrantable: hasBaseAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_EVENT_THIRD_PARTY_LEGAL_ANNOUNCEMENT_TRANSFERT),
      variant: "topItem",
    },
    {
      name: i18next.t(`privileges.PRIVILEGE_EVENT_THIRD_PARTY_ATTRIBUTE_UPDATED`),
      action: () => { toggleAllProperties(PrivilegeEnum.PRIVILEGE_EVENT_THIRD_PARTY_ATTRIBUTE_UPDATED) },
      isActive: hasCurrentAllOfSpecificProperty(PrivilegeEnum.PRIVILEGE_EVENT_THIRD_PARTY_ATTRIBUTE_UPDATED),
      isGrantable:  hasBaseAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_EVENT_THIRD_PARTY_ATTRIBUTE_UPDATED) && !_.isEmpty(accountThirdpartyAttributes),
      variant: "subDrop",
      openDefault: true,
      toggleAllPropertiesAndFuture: () => togglePrivileges(PrivilegeEnum.PRIVILEGE_EVENT_THIRD_PARTY_ATTRIBUTE_UPDATED_ALL),
      isAllPropertiesAndFuture: hasCurrentPrivilege(PrivilegeEnum.PRIVILEGE_EVENT_THIRD_PARTY_ATTRIBUTE_UPDATED_ALL),
      hasPrivilegeAllPropertiesAndFuture: hasBaseAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_EVENT_THIRD_PARTY_ATTRIBUTE_UPDATED_ALL),
      namePropertyPlurial: i18next.t(`events.popUp.thirdParty.attributes`)?.toLocaleLowerCase(),
      namePropertySingular: i18next.t(`events.popUp.thirdParty.attribute`),
      items: accountThirdpartyAttributes?.map(attribute => {
          return {
            name: attribute?.label, 
            action: () => { toggleProperty(attribute?.id, null,
              PrivilegeEnum.PRIVILEGE_EVENT_THIRD_PARTY_ATTRIBUTE_UPDATED_ALL, 
              PrivilegeEnum.PRIVILEGE_EVENT_THIRD_PARTY_ATTRIBUTE_UPDATED) },
            isActive: hasPrivilegeOnProperty(attribute?.id, null, PrivilegeEnum.PRIVILEGE_EVENT_THIRD_PARTY_ATTRIBUTE_UPDATED),
            isGrantable: hasGrantablePrivilegeForProperty(PrivilegeEnum.PRIVILEGE_EVENT_THIRD_PARTY_ATTRIBUTE_UPDATED, attribute?.id, null),
            variant: attribute.options ? "subDropProperties" : "topItem",
            items: !attribute.options ? null : attribute.options.map(optionAttribute => {
              const propertyCriterion = {operator: Operator.EQUAL, value: optionAttribute?.value};
              const valueOptionAttributeToShow = optionAttribute.translation ? i18next.t(optionAttribute?.translation)  : optionAttribute?.value;
              return {
                key: attribute.id + "_" + optionAttribute?.value,
                name: <ColoredPrivilegeName name={"PRIVILEGE_EVENT_VALUE_CHANGE_TO"} values={{value : `${valueOptionAttributeToShow}`}} color={optionAttribute?.color} />,
                action: () => { toggleProperty(attribute?.id, propertyCriterion, 
                  PrivilegeEnum.PRIVILEGE_EVENT_THIRD_PARTY_ATTRIBUTE_UPDATED_ALL,
                  PrivilegeEnum.PRIVILEGE_EVENT_THIRD_PARTY_ATTRIBUTE_UPDATED) },
                isActive: hasPrivilegeOnProperty(attribute?.id, propertyCriterion, PrivilegeEnum.PRIVILEGE_EVENT_THIRD_PARTY_ATTRIBUTE_UPDATED),
                isGrantable: hasGrantablePrivilegeForProperty(PrivilegeEnum.PRIVILEGE_EVENT_THIRD_PARTY_ATTRIBUTE_UPDATED, attribute?.id, [propertyCriterion]),
                variant: "subItemProperties",
              }
            })
          }
    })
    },
    mapPrivilegeEventsForAttributesAndIndicators(
      PrivilegeEnum.PRIVILEGE_EVENT_THIRD_PARTY_CALCULATED_ATTRIBUTE_UPDATED,
      PrivilegeEnum.PRIVILEGE_EVENT_THIRD_PARTY_CALCULATED_ATTRIBUTE_UPDATED_ALL,
      accountCalculatedAttributes,
      i18next.t(`calculatedAttributes`),
      i18next.t(`events.popUp.thirdParty.calculatedAttribute`),
      toggleAllProperties, 
      hasCurrentAllOfSpecificProperty, 
      hasBaseAnyOfPrivileges,
      togglePrivileges,
      hasCurrentPrivilege,
      toggleProperty,
      hasPrivilegeOnProperty,
      hasGrantablePrivilegeForProperty
    ),
    mapPrivilegeEventsForAttributesAndIndicators(
      PrivilegeEnum.PRIVILEGE_EVENT_THIRD_PARTY_INDICATOR_UPDATED,
      PrivilegeEnum.PRIVILEGE_EVENT_THIRD_PARTY_INDICATOR_UPDATED_ALL,
      accountIndicators,
      i18next.t(`indicators`),
      i18next.t(`events.popUp.thirdParty.indicator`),
      toggleAllProperties, 
      hasCurrentAllOfSpecificProperty, 
      hasBaseAnyOfPrivileges,
      togglePrivileges,
      hasCurrentPrivilege,
      toggleProperty,
      hasPrivilegeOnProperty,
      hasGrantablePrivilegeForProperty
    ),
    {
      name: i18next.t(`privileges.PRIVILEGE_EVENT_THIRD_PARTY_COMMENT`),
      action: () => { togglePrivileges(PrivilegeEnum.PRIVILEGE_EVENT_THIRD_PARTY_COMMENT) },
      isActive: hasCurrentPrivilege(PrivilegeEnum.PRIVILEGE_EVENT_THIRD_PARTY_COMMENT),
      isGrantable: hasBaseAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_EVENT_THIRD_PARTY_COMMENT),
      variant: "subDrop",
      items: [{
        name: i18next.t(`privileges.PRIVILEGE_EVENT_THIRD_PARTY_COMMENT_ALL`),
        action: () => {
          togglePrivileges(
            PrivilegeEnum.PRIVILEGE_EVENT_THIRD_PARTY_COMMENT_ADDED,
            PrivilegeEnum.PRIVILEGE_EVENT_THIRD_PARTY_COMMENT_UPDATED,
            PrivilegeEnum.PRIVILEGE_EVENT_THIRD_PARTY_COMMENT_DELETED
          )
        },
        isActive: hasCurrentAllOfPrivileges(
          PrivilegeEnum.PRIVILEGE_EVENT_THIRD_PARTY_COMMENT_ADDED,
          PrivilegeEnum.PRIVILEGE_EVENT_THIRD_PARTY_COMMENT_UPDATED,
          PrivilegeEnum.PRIVILEGE_EVENT_THIRD_PARTY_COMMENT_DELETED
        ),
        isGrantable: hasBaseAllOfPrivileges(
          PrivilegeEnum.PRIVILEGE_EVENT_THIRD_PARTY_COMMENT_ADDED,
          PrivilegeEnum.PRIVILEGE_EVENT_THIRD_PARTY_COMMENT_UPDATED,
          PrivilegeEnum.PRIVILEGE_EVENT_THIRD_PARTY_COMMENT_DELETED
        ),
        variant: "subItem",
        },{
          name: i18next.t(`privileges.PRIVILEGE_EVENT_THIRD_PARTY_COMMENT_MENTION_ALL`),
          action: () => {
            togglePrivileges(
              PrivilegeEnum.PRIVILEGE_EVENT_THIRD_PARTY_COMMENT_MENTION_ADDED,
              PrivilegeEnum.PRIVILEGE_EVENT_THIRD_PARTY_COMMENT_MENTION_UPDATED,
              PrivilegeEnum.PRIVILEGE_EVENT_THIRD_PARTY_COMMENT_MENTION_DELETED
            )
          },
          isActive: hasCurrentAllOfPrivileges(
            PrivilegeEnum.PRIVILEGE_EVENT_THIRD_PARTY_COMMENT_MENTION_ADDED,
            PrivilegeEnum.PRIVILEGE_EVENT_THIRD_PARTY_COMMENT_MENTION_UPDATED,
            PrivilegeEnum.PRIVILEGE_EVENT_THIRD_PARTY_COMMENT_MENTION_DELETED
          ),
          isGrantable: hasBaseAllOfPrivileges(
            PrivilegeEnum.PRIVILEGE_EVENT_THIRD_PARTY_COMMENT_MENTION_ADDED,
            PrivilegeEnum.PRIVILEGE_EVENT_THIRD_PARTY_COMMENT_MENTION_UPDATED,
            PrivilegeEnum.PRIVILEGE_EVENT_THIRD_PARTY_COMMENT_MENTION_DELETED
          ),
          variant: "subItem",
      }]
    },
    {
      name: i18next.t(`privileges.PRIVILEGE_EVENT_THIRD_PARTY_CONTACT`),
      action: () => { togglePrivileges(PrivilegeEnum.PRIVILEGE_EVENT_THIRD_PARTY_CONTACT) },
      isActive: hasCurrentPrivilege(PrivilegeEnum.PRIVILEGE_EVENT_THIRD_PARTY_CONTACT),
      isGrantable: hasBaseAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_EVENT_THIRD_PARTY_CONTACT),
      variant: "subDrop",
      items: [{
        name: i18next.t(`privileges.PRIVILEGE_EVENT_THIRD_PARTY_CONTACT_INVITED`),
        action: () => togglePrivileges(PrivilegeEnum.PRIVILEGE_EVENT_THIRD_PARTY_CONTACT_INVITED),
        isActive: hasCurrentAllOfPrivileges(PrivilegeEnum.PRIVILEGE_EVENT_THIRD_PARTY_CONTACT_INVITED ),
        isGrantable: hasBaseAllOfPrivileges(PrivilegeEnum.PRIVILEGE_EVENT_THIRD_PARTY_CONTACT_INVITED),
        variant: "subItem",
      },{
        name: i18next.t(`privileges.PRIVILEGE_EVENT_THIRD_PARTY_CONTACT_RE_INVITED`),
        action: () => togglePrivileges(PrivilegeEnum.PRIVILEGE_EVENT_THIRD_PARTY_CONTACT_RE_INVITED),
        isActive: hasCurrentAllOfPrivileges(PrivilegeEnum.PRIVILEGE_EVENT_THIRD_PARTY_CONTACT_RE_INVITED ),
        isGrantable: hasBaseAllOfPrivileges(PrivilegeEnum.PRIVILEGE_EVENT_THIRD_PARTY_CONTACT_RE_INVITED),
        variant: "subItem",
      },{
        name: i18next.t(`privileges.PRIVILEGE_EVENT_THIRD_PARTY_CONTACT_RE_INVITED_AUTO`),
        action: () => togglePrivileges(PrivilegeEnum.PRIVILEGE_EVENT_THIRD_PARTY_CONTACT_RE_INVITED_AUTO),
        isActive: hasCurrentAllOfPrivileges(PrivilegeEnum.PRIVILEGE_EVENT_THIRD_PARTY_CONTACT_RE_INVITED_AUTO ),
        isGrantable: hasBaseAllOfPrivileges(PrivilegeEnum.PRIVILEGE_EVENT_THIRD_PARTY_CONTACT_RE_INVITED_AUTO),
        variant: "subItem",
      },{
        name: i18next.t(`privileges.PRIVILEGE_EVENT_THIRD_PARTY_CONTACT_DELETED`),
        action: () => togglePrivileges(PrivilegeEnum.PRIVILEGE_EVENT_THIRD_PARTY_CONTACT_DELETED),
        isActive: hasCurrentAllOfPrivileges(PrivilegeEnum.PRIVILEGE_EVENT_THIRD_PARTY_CONTACT_DELETED ),
        isGrantable: hasBaseAllOfPrivileges(PrivilegeEnum.PRIVILEGE_EVENT_THIRD_PARTY_CONTACT_DELETED),
        variant: "subItem",
      },{
        name: i18next.t(`privileges.PRIVILEGE_EVENT_THIRD_PARTY_CONTACT_DELETED_DUE_TO_NO_FAVORABLE_RESPONSE`),
        action: () => togglePrivileges(PrivilegeEnum.PRIVILEGE_EVENT_THIRD_PARTY_CONTACT_DELETED_DUE_TO_NO_FAVORABLE_RESPONSE),
        isActive: hasCurrentAllOfPrivileges(PrivilegeEnum.PRIVILEGE_EVENT_THIRD_PARTY_CONTACT_DELETED_DUE_TO_NO_FAVORABLE_RESPONSE ),
        isGrantable: hasBaseAllOfPrivileges(PrivilegeEnum.PRIVILEGE_EVENT_THIRD_PARTY_CONTACT_DELETED_DUE_TO_NO_FAVORABLE_RESPONSE),
        variant: "subItem",
        }]
    },
    {
      name: i18next.t(`privileges.PRIVILEGE_EVENT_THIRD_PARTY_CONTACT_WEEKLY_REMINDER`),
      action: () => { togglePrivileges(PrivilegeEnum.PRIVILEGE_EVENT_THIRD_PARTY_CONTACT_WEEKLY_REMINDER) },
      isActive: hasCurrentPrivilege(PrivilegeEnum.PRIVILEGE_EVENT_THIRD_PARTY_CONTACT_WEEKLY_REMINDER),
      isGrantable: hasBaseAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_EVENT_THIRD_PARTY_CONTACT_WEEKLY_REMINDER),
      variant: "topItem",
    },
    hasOptionSubscription(Subscription.DATABLOCKS_ESG) ? {
      name: i18next.t(`privileges.PRIVILEGE_EVENT_THIRD_PARTY_ESG_UPDATED`),
      action: () => { togglePrivileges(PrivilegeEnum.PRIVILEGE_EVENT_THIRD_PARTY_ESG_UPDATED) },
      isActive: hasCurrentPrivilege(PrivilegeEnum.PRIVILEGE_EVENT_THIRD_PARTY_ESG_UPDATED),
      isGrantable: hasBaseAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_EVENT_THIRD_PARTY_ESG_UPDATED),
      variant: "subDrop",
      items: 
      [
        {privilege: PrivilegeEnum.PRIVILEGE_EVENT_THIRD_PARTY_ESG_UPDATED_GLOBAL},
        {privilege: PrivilegeEnum.PRIVILEGE_EVENT_THIRD_PARTY_ESG_UPDATED_ENVIRONMENTAL},
        {privilege: PrivilegeEnum.PRIVILEGE_EVENT_THIRD_PARTY_ESG_UPDATED_SOCIAL},
        {privilege: PrivilegeEnum.PRIVILEGE_EVENT_THIRD_PARTY_ESG_UPDATED_GOVERNANCE}
      ].map(esgPrivilege => {
        return {
          key: esgPrivilege.privilege,
          name: i18next.t(`privileges.${esgPrivilege.privilege?.name}`),
          action: () => { togglePrivileges(esgPrivilege.privilege) },
          isActive: hasCurrentPrivilege(esgPrivilege.privilege),
          isGrantable: hasBaseAnyOfPrivileges(esgPrivilege.privilege),
          variant: "subDrop",
          items: [
            {privilegeParent: esgPrivilege.privilege?.name, scoreName: "_SCORE_1", color: getEsgScoresColor(1)}, 
            {privilegeParent: esgPrivilege.privilege?.name, scoreName: "_SCORE_2", color: getEsgScoresColor(2)},
            {privilegeParent: esgPrivilege.privilege?.name, scoreName: "_SCORE_3", color: getEsgScoresColor(3)},
            {privilegeParent: esgPrivilege.privilege?.name, scoreName: "_SCORE_4", color: getEsgScoresColor(4)},
            {privilegeParent: esgPrivilege.privilege?.name, scoreName: "_SCORE_5", color: getEsgScoresColor(5)}
          ].map(item => {
            return {
                key: item.scoreName + item.privilegeParent,
                name: <ColoredPrivilegeName name={"PRIVILEGE_EVENT_THIRD_PARTY_ESG_UPDATED_XX" + item.scoreName} color={item.color} />,
                action: () => { togglePrivileges(PrivilegeEnum[`${item.privilegeParent}${item.scoreName}`]) },
                isActive: hasCurrentPrivilege(PrivilegeEnum[`${item.privilegeParent}${item.scoreName}`]),
                isGrantable: hasBaseAnyOfPrivileges(PrivilegeEnum[`${item.privilegeParent}${item.scoreName}`]),
                variant: "subItem",
            }
          })
          }
      })
    } : null,
    hasOptionSubscription(Subscription.ECOVADIS) ? {
      name: i18next.t(`privileges.PRIVILEGE_EVENT_THIRD_PARTY_ECOVADIS_UPDATED`),
      action: () => { togglePrivileges(PrivilegeEnum.PRIVILEGE_EVENT_THIRD_PARTY_ECOVADIS_UPDATED) },
      isActive: hasCurrentPrivilege(PrivilegeEnum.PRIVILEGE_EVENT_THIRD_PARTY_ECOVADIS_UPDATED),
      isGrantable: hasBaseAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_EVENT_THIRD_PARTY_ECOVADIS_UPDATED),
      variant: "subDrop",
      // {privilege: PrivilegeEnum.PRIVILEGE_EVENT_THIRD_PARTY_ECOVADIS_UPDATED_CURRENT_STAGE_STATUS}
      items: [
        // Ecovadis Scores Privileges.
        ...Object.keys(EcovadisScoresEnum).map(scoreName => {
          const parentPrivilege = PrivilegeEnum[`PRIVILEGE_EVENT_THIRD_PARTY_ECOVADIS_UPDATED_${scoreName}_SCORE`]
          return {
            key: parentPrivilege,
            name: i18next.t(`privileges.${parentPrivilege?.name}`),
            action: () => { togglePrivileges(parentPrivilege) },
            isActive: hasCurrentPrivilege(parentPrivilege),
            isGrantable: hasBaseAnyOfPrivileges(parentPrivilege),
            variant: "subDrop",
            items: Object.keys(EVScorePerfomance)
              .map(evPerformanceKey => {
                const color = EcovadisPerformanceColor[EVScorePerfomance[evPerformanceKey]];
                const childPrivilege = PrivilegeEnum[`${parentPrivilege?.name}_${evPerformanceKey}`];
                return {
                    key: childPrivilege?.name,
                    name: <ColoredPrivilegeName name={`PRIVILEGE_EVENT_THIRD_PARTY_ECOVADIS_UPDATED_SCORE_${evPerformanceKey}`} color={color} />,
                    action: () => { togglePrivileges(childPrivilege) },
                    isActive: hasCurrentPrivilege(childPrivilege),
                    isGrantable: hasBaseAnyOfPrivileges(childPrivilege),
                    variant: "subItem",
                }
              })
          }
        }),
        // Ecovadis Status Privileges.
        {
          key: PrivilegeEnum.PRIVILEGE_EVENT_THIRD_PARTY_ECOVADIS_UPDATED_CURRENT_STAGE_STATUS,
          name: i18next.t(`privileges.${PrivilegeEnum.PRIVILEGE_EVENT_THIRD_PARTY_ECOVADIS_UPDATED_CURRENT_STAGE_STATUS.name}`),
          action: () => { togglePrivileges(PrivilegeEnum.PRIVILEGE_EVENT_THIRD_PARTY_ECOVADIS_UPDATED_CURRENT_STAGE_STATUS) },
          isActive: hasCurrentPrivilege(PrivilegeEnum.PRIVILEGE_EVENT_THIRD_PARTY_ECOVADIS_UPDATED_CURRENT_STAGE_STATUS),
          isGrantable: hasBaseAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_EVENT_THIRD_PARTY_ECOVADIS_UPDATED_CURRENT_STAGE_STATUS),
          variant: "subDrop",
          items: Object.keys(EcovadisCurrentStageProgressSteps)
            .map(evProgressStep => {
              const childPrivilege = PrivilegeEnum[`${PrivilegeEnum.PRIVILEGE_EVENT_THIRD_PARTY_ECOVADIS_UPDATED_CURRENT_STAGE_STATUS.name}_${evProgressStep}`];
              return {
                  key: childPrivilege?.name,
                  name: i18next.t(`privileges.${childPrivilege?.name}`),
                  action: () => { togglePrivileges(childPrivilege) },
                  isActive: hasCurrentPrivilege(childPrivilege),
                  isGrantable: hasBaseAnyOfPrivileges(childPrivilege),
                  variant: "subItem",
              }
            })
        }
      ]
    } : null,
    hasOptionSubscription(Subscription.DATABLOCKS_FINANCIAL_STRENGTH) ? {
      name: i18next.t(`privileges.PRIVILEGE_EVENT_THIRD_PARTY_FINANCIAL_STRENGTH_UPDATED`),
      action: () => { togglePrivileges(PrivilegeEnum.PRIVILEGE_EVENT_THIRD_PARTY_FINANCIAL_STRENGTH_UPDATED) },
      isActive: hasCurrentPrivilege(PrivilegeEnum.PRIVILEGE_EVENT_THIRD_PARTY_FINANCIAL_STRENGTH_UPDATED),
      isGrantable: hasBaseAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_EVENT_THIRD_PARTY_FINANCIAL_STRENGTH_UPDATED),
      variant: "subDrop",
      items: [
        {privilegeParent: PrivilegeEnum.PRIVILEGE_EVENT_THIRD_PARTY_FINANCIAL_STRENGTH_UPDATED.name, scoreName: "_SCORE_UNKNOWN", 
          color: getFinancialStrengthScoreColorAndLabel("-").color}, 
        {privilegeParent: PrivilegeEnum.PRIVILEGE_EVENT_THIRD_PARTY_FINANCIAL_STRENGTH_UPDATED.name, scoreName: "_SCORE_HIGH", 
          color: getFinancialStrengthScoreColorAndLabel("4").color},
        {privilegeParent: PrivilegeEnum.PRIVILEGE_EVENT_THIRD_PARTY_FINANCIAL_STRENGTH_UPDATED.name, scoreName: "_SCORE_MEDIUM", 
          color: getFinancialStrengthScoreColorAndLabel("3").color},
        {privilegeParent: PrivilegeEnum.PRIVILEGE_EVENT_THIRD_PARTY_FINANCIAL_STRENGTH_UPDATED.name, scoreName: "_SCORE_LOW", 
          color: getFinancialStrengthScoreColorAndLabel("2").color},
        {privilegeParent: PrivilegeEnum.PRIVILEGE_EVENT_THIRD_PARTY_FINANCIAL_STRENGTH_UPDATED.name, scoreName: "_SCORE_VERY_LOW", 
          color: getFinancialStrengthScoreColorAndLabel("1").color}
      ].map(item => {
          const privilegeChild = item.privilegeParent +  item.scoreName;
        return {
            key:item.privilegeChild,
            name: <ColoredPrivilegeName name={privilegeChild} color={item.color} />,
            action: () => { togglePrivileges(PrivilegeEnum[`${privilegeChild}`]) },
            isActive: hasCurrentPrivilege(PrivilegeEnum[`${privilegeChild}`]),
            isGrantable: hasBaseAnyOfPrivileges(PrivilegeEnum[`${privilegeChild}`]),
            variant: "subItem",
        }
      })
    } : null,
    hasOptionSubscription(Subscription.INDUED) ? {
      name: i18next.t(`privileges.PRIVILEGE_EVENT_THIRD_PARTY_INDUED_UPDATED`),
      action: () => { togglePrivileges(PrivilegeEnum.PRIVILEGE_EVENT_THIRD_PARTY_INDUED_UPDATED) },
      isActive: hasCurrentPrivilege(PrivilegeEnum.PRIVILEGE_EVENT_THIRD_PARTY_INDUED_UPDATED),
      isGrantable: hasBaseAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_EVENT_THIRD_PARTY_INDUED_UPDATED),
      variant: "subDrop",
      items: 
      [
        {privilege: PrivilegeEnum.PRIVILEGE_EVENT_THIRD_PARTY_INDUED_UPDATED_STATUS},
        {privilege: PrivilegeEnum.PRIVILEGE_EVENT_THIRD_PARTY_INDUED_UPDATED_RISK},
        {privilege: PrivilegeEnum.PRIVILEGE_EVENT_THIRD_PARTY_INDUED_UPDATED_COUNTRY},
        {privilege: PrivilegeEnum.PRIVILEGE_EVENT_THIRD_PARTY_INDUED_UPDATED_ACTIVITY},
        {privilege: PrivilegeEnum.PRIVILEGE_EVENT_THIRD_PARTY_INDUED_UPDATED_SANCTIONS},
        {privilege: PrivilegeEnum.PRIVILEGE_EVENT_THIRD_PARTY_INDUED_UPDATED_PEP},
        {privilege: PrivilegeEnum.PRIVILEGE_EVENT_THIRD_PARTY_INDUED_UPDATED_PN},
      ].map(induedPrivilege => {
        return {
          key: induedPrivilege.privilege,
          name: i18next.t(`privileges.${induedPrivilege.privilege?.name}`),
          action: () => { togglePrivileges(induedPrivilege.privilege) },
          isActive: hasCurrentPrivilege(induedPrivilege.privilege),
          isGrantable: hasBaseAnyOfPrivileges(induedPrivilege.privilege),
          variant: "subDrop",
          items: induedPrivilege.privilege === PrivilegeEnum.PRIVILEGE_EVENT_THIRD_PARTY_INDUED_UPDATED_STATUS ? undefined : [
            {privilegeParent: induedPrivilege.privilege?.name, scoreName: "_SCORE_0", color: getInduedColor(0)},
            {privilegeParent: induedPrivilege.privilege?.name, scoreName: "_SCORE_1", color: getInduedColor(1)}, 
            {privilegeParent: induedPrivilege.privilege?.name, scoreName: "_SCORE_2", color: getInduedColor(2)},
            {privilegeParent: induedPrivilege.privilege?.name, scoreName: "_SCORE_3", color: getInduedColor(3)},
          ].map(item => {
            return {
                key: item.scoreName + item.privilegeParent,
                name: <ColoredPrivilegeName name={"PRIVILEGE_EVENT_THIRD_PARTY_INDUED_UPDATED_XX" + item.scoreName} color={item.color} />,
                action: () => { togglePrivileges(PrivilegeEnum[`${item.privilegeParent}${item.scoreName}`]) },
                isActive: hasCurrentPrivilege(PrivilegeEnum[`${item.privilegeParent}${item.scoreName}`]),
                isGrantable: hasBaseAnyOfPrivileges(PrivilegeEnum[`${item.privilegeParent}${item.scoreName}`]),
                variant: "subItem",
            }
          })
          }
      })
    } : null,
    hasOptionSubscription(Subscription.SECURITY_RATING) ? {
      name: i18next.t(`privileges.PRIVILEGE_EVENT_THIRD_PARTY_SECURITY_RATING_UPDATED`),
      action: () => { togglePrivileges(PrivilegeEnum.PRIVILEGE_EVENT_THIRD_PARTY_SECURITY_RATING_UPDATED) },
      isActive: hasCurrentPrivilege(PrivilegeEnum.PRIVILEGE_EVENT_THIRD_PARTY_SECURITY_RATING_UPDATED),
      isGrantable: hasBaseAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_EVENT_THIRD_PARTY_SECURITY_RATING_UPDATED),
      variant: "subDrop",
      items: [
        // Global Score Performance
        ...Object.keys(SecurityRatingGlobalScoreMaturityEnum).map(globalPerformance => {
          const globalPerformancePrivilege = PrivilegeEnum[`PRIVILEGE_EVENT_THIRD_PARTY_SECURITY_RATING_UPDATED_GLOBAL_SCORE_${globalPerformance}`]
          const color = SecurityRatingGlobalScorePerformanceColor[globalPerformance];
          return {
            key: globalPerformancePrivilege,
            name: <ColoredPrivilegeName name={`PRIVILEGE_EVENT_THIRD_PARTY_SECURITY_RATING_UPDATED_GLOBAL_SCORE_${globalPerformance}`} color={color} />,
            action: () => { togglePrivileges(globalPerformancePrivilege) },
            isActive: hasCurrentPrivilege(globalPerformancePrivilege),
            isGrantable: hasBaseAnyOfPrivileges(globalPerformancePrivilege),
            variant: "subDrop",
            items:[]
          }
        }),
        // Security Rating Scores Privileges.
        ...Object.keys(SecurityRatingScoresEnum).map(scoreName => {
          const parentPrivilege = PrivilegeEnum[`PRIVILEGE_EVENT_THIRD_PARTY_SECURITY_RATING_UPDATED_${scoreName}_SCORE`]
          return {
            key: parentPrivilege,
            name: i18next.t(`privileges.${parentPrivilege?.name}`),
            action: () => { togglePrivileges(parentPrivilege) },
            isActive: hasCurrentPrivilege(parentPrivilege),
            isGrantable: hasBaseAnyOfPrivileges(parentPrivilege),
            variant: "subDrop",
            items: Object.keys(SecurityRatingPerformanceEnum)
              .map(securityRatingPerformanceKey => {
                const color = SecurityRatingPerformanceColor[SecurityRatingPerformanceEnum[securityRatingPerformanceKey]];
                const childPrivilege = PrivilegeEnum[`${parentPrivilege?.name}_${securityRatingPerformanceKey}`];
                return {
                    key: childPrivilege?.name,
                    name: <ColoredPrivilegeName name={`PRIVILEGE_EVENT_THIRD_PARTY_SECURITY_RATING_UPDATED_SCORE_${securityRatingPerformanceKey}`} color={color} />,
                    action: () => { togglePrivileges(childPrivilege) },
                    isActive: hasCurrentPrivilege(childPrivilege),
                    isGrantable: hasBaseAnyOfPrivileges(childPrivilege),
                    variant: "subItem",
                }
              })
          }
        }),
      ]
    } : null,
    hasOptionSubscription(Subscription.CREDITSAFE) ? {
      name: i18next.t(`privileges.PRIVILEGE_EVENT_THIRD_PARTY_CREDITSAFE_UPDATED`),
      action: () => { togglePrivileges(PrivilegeEnum.PRIVILEGE_EVENT_THIRD_PARTY_CREDITSAFE_UPDATED) },
      isActive: hasCurrentPrivilege(PrivilegeEnum.PRIVILEGE_EVENT_THIRD_PARTY_CREDITSAFE_UPDATED),
      isGrantable: hasBaseAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_EVENT_THIRD_PARTY_CREDITSAFE_UPDATED),
      variant: "subDrop",
      items: [
        // Global Score Performance
        ...Object.keys(CreditsafeForEventsBasic).map(score => {
          const basicPrivilege = PrivilegeEnum[`PRIVILEGE_EVENT_THIRD_PARTY_CREDITSAFE_UPDATED_${score}`]
          return {
            key: basicPrivilege,
            name: i18next.t(`privileges.${basicPrivilege.name}`),
            action: () => { togglePrivileges(basicPrivilege) },
            isActive: hasCurrentPrivilege(basicPrivilege),
            isGrantable: hasBaseAnyOfPrivileges(basicPrivilege),
            variant: "subDrop",
            items:[]
          }
        }),
        // Creditsafe Scores Privileges.
        ...Object.keys(CreditsafeForEventsWithDetail).map(scoreName => {
          const parentPrivilege = PrivilegeEnum[`PRIVILEGE_EVENT_THIRD_PARTY_CREDITSAFE_UPDATED_${scoreName}`]
          return {
            key: parentPrivilege,
            name: i18next.t(`privileges.${parentPrivilege?.name}`),
            action: () => { togglePrivileges(parentPrivilege) },
            isActive: hasCurrentPrivilege(parentPrivilege),
            isGrantable: hasBaseAnyOfPrivileges(parentPrivilege),
            variant: "subDrop",
            items: Object.keys(CreditsafeForEventsWithDetail[scoreName].scores)
              .map(scoreValues => {
                const color = CreditsafeForEventsWithDetail[scoreName].colors[CreditsafeForEventsWithDetail[scoreName].scores[scoreValues]];
                const childPrivilege = PrivilegeEnum[`${parentPrivilege?.name}_${scoreValues}`];
                return {
                    key: childPrivilege?.name,
                    name: <ColoredPrivilegeName name={childPrivilege?.name} color={color} />,
                    action: () => { togglePrivileges(childPrivilege) },
                    isActive: hasCurrentPrivilege(childPrivilege),
                    isGrantable: hasBaseAnyOfPrivileges(childPrivilege),
                    variant: "subItem",
                }
              })
          }
        }),
      ]
    } : null,
    hasOptionSubscription(Subscription.DATA_HUB) ? {
      name: i18next.t(`privileges.PRIVILEGE_EVENT_THIRD_PARTY_DATA_HUB_UPDATED`),
      action: () => { toggleAllProperties(PrivilegeEnum.PRIVILEGE_EVENT_THIRD_PARTY_DATA_HUB_UPDATED) },
      isActive: hasCurrentAllOfSpecificProperty(PrivilegeEnum.PRIVILEGE_EVENT_THIRD_PARTY_DATA_HUB_UPDATED),
      isGrantable: hasBaseAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_EVENT_THIRD_PARTY_DATA_HUB_UPDATED) && !_.isEmpty(dataHubSources),
      variant: "subDropProperties",
      openDefault: true,
      toggleAllPropertiesAndFuture: () => togglePrivileges(PrivilegeEnum.PRIVILEGE_EVENT_THIRD_PARTY_DATA_HUB_UPDATED_ALL),
      isAllPropertiesAndFuture: hasCurrentPrivilege(PrivilegeEnum.PRIVILEGE_EVENT_THIRD_PARTY_DATA_HUB_UPDATED_ALL),
      hasPrivilegeAllPropertiesAndFuture: hasBaseAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_EVENT_THIRD_PARTY_DATA_HUB_UPDATED_ALL),
      namePropertyPlurial: i18next.t(`thirdpartySheet.dataHub.availableSources`)?.toLocaleLowerCase(),
      namePropertySingular: i18next.t(`thirdpartySheet.dataHub.source`),
      items: dataHubSources?.map(source => {
          return {
            name: source?.label,
            action: () => { toggleProperty(source?.sourceId, null,
              PrivilegeEnum.PRIVILEGE_EVENT_THIRD_PARTY_DATA_HUB_UPDATED_ALL,
              PrivilegeEnum.PRIVILEGE_EVENT_THIRD_PARTY_DATA_HUB_UPDATED) },
            isActive: hasPrivilegeOnProperty(source?.sourceId, null, PrivilegeEnum.PRIVILEGE_EVENT_THIRD_PARTY_DATA_HUB_UPDATED),
            isGrantable: hasGrantablePrivilegeForProperty(PrivilegeEnum.PRIVILEGE_EVENT_THIRD_PARTY_DATA_HUB_UPDATED, source?.sourceId, null),
            variant: "subItem",
          }
    })
    } : null,
  ].filter(item => item !== null)}};


const PrivilegeNameWithRequirementMention = ({name, isRequired}) => {
            if(isRequired) {
              return <>{name}<span style={{marginLeft: "5px", fontSize:"11px", fontStyle: "italic", color: theme.palette.grey.grey400}}>{i18next.t("required")}</span> </>
            } else {
              return name;
            }
          }


const mapPrivilegeEventsForAttributesAndIndicators = (privilegeWithProperty, privilegeWithPropertyAll, listItem, namePropertyPlurial, namePropertySingular,
  toggleAllProperties, hasCurrentAllOfSpecificProperty, hasBaseAnyOfPrivileges, togglePrivileges, hasCurrentAnyOfPrivileges, toggleProperty, hasPrivilegeOnProperty,
  hasGrantablePrivilegeForProperty
) => {
  return {
      name: i18next.t(`privileges.`.concat(privilegeWithProperty?.name)),
      action: () => { toggleAllProperties(privilegeWithProperty) },
      isActive: hasCurrentAllOfSpecificProperty(privilegeWithProperty),
      isGrantable: hasBaseAnyOfPrivileges(privilegeWithProperty) && !_.isEmpty(listItem),
      variant: "subDropProperties",
      openDefault: true,
      toggleAllPropertiesAndFuture: () => togglePrivileges(privilegeWithPropertyAll),
      isAllPropertiesAndFuture: hasCurrentAnyOfPrivileges(privilegeWithPropertyAll),
      hasPrivilegeAllPropertiesAndFuture: hasBaseAnyOfPrivileges(privilegeWithPropertyAll),
      namePropertyPlurial: namePropertyPlurial?.toLocaleLowerCase(),
      namePropertySingular: namePropertySingular,
      items: listItem?.map(item => {
          return {
            name: item?.label, 
            action: () => { toggleProperty(item?.value, null,privilegeWithPropertyAll, privilegeWithProperty) },
            isActive: hasPrivilegeOnProperty(item?.value, null, privilegeWithProperty),
            isGrantable: hasGrantablePrivilegeForProperty(privilegeWithProperty, item?.value, null),
            variant: item.options ? "subDropProperties" : "subItem",
            items: !item.options ? null : item.options.map(optionsBranche => {
              const propertyCriterion = {operator: Operator.EQUAL, value: optionsBranche?.value};
              return {
                key: item.value + "_" + optionsBranche?.value,
                name: <ColoredPrivilegeName name={"PRIVILEGE_EVENT_VALUE_CHANGE_TO"} values={{value : `${optionsBranche?.value}`}} color={optionsBranche?.color} />,
                action: () => { toggleProperty(item?.value, propertyCriterion, privilegeWithPropertyAll, privilegeWithProperty) },
                isActive: hasPrivilegeOnProperty(item?.value, propertyCriterion, privilegeWithProperty),
                isGrantable: hasGrantablePrivilegeForProperty(privilegeWithProperty, item?.value, [propertyCriterion]),
                variant: "subItemProperties",
              }
            })
          }
    })
    };
};

//Features
export const THIRD_PARTY_MANAGEMENT_ACTIONS = (
  togglePrivileges, 
  grantPrivileges, 
  revokePrivileges, 
  hasCurrentAnyOfPrivileges, 
  hasBaseAnyOfPrivileges, 
  accountThirdpartyAttributes, //list of properties : thirdparty attributes
  accountCalculatedAttributes,  // list of properties : calculated attributes
  accountIndicators, // list of properties : indicators
  toggleProperty, // action to select/deselect a property id for an event
  toggleAllProperties, // action to select/deselect all properties for an event
  hasCurrentAnyOfSpecificProperty, // boolean to know if property is selected
  hasCurrentAllOfSpecificProperty, // boolean to know if all properties of event is selected
  hasGrantablePrivilegeForProperty, //boolean to know if the property is grantable
  ) => {

    const selectAllAttributesAndIndicatorsProperties = () => {
      if(hasCurrentAllOfSpecificProperty(PrivilegeEnum.PRIVILEGE_VIEW_THIRD_PARTY_ATTRIBUTE_VALUE) !== true) {
            toggleAllProperties(PrivilegeEnum.PRIVILEGE_VIEW_THIRD_PARTY_ATTRIBUTE_VALUE);
        } 
      if(hasCurrentAllOfSpecificProperty(PrivilegeEnum.PRIVILEGE_VIEW_THIRD_PARTY_CALCULATED_ATTRIBUTE) !== true) {
            toggleAllProperties(PrivilegeEnum.PRIVILEGE_VIEW_THIRD_PARTY_CALCULATED_ATTRIBUTE);
        }
      if(hasCurrentAllOfSpecificProperty(PrivilegeEnum.PRIVILEGE_VIEW_THIRD_PARTY_INDICATOR) !== true) {
            toggleAllProperties(PrivilegeEnum.PRIVILEGE_VIEW_THIRD_PARTY_INDICATOR);
        } 
          
    }
    
  return ({
  rootActions: [{
    name: i18next.t("roles.switchButton.read"),
    action: (isActive) => { 
      togglePrivileges(PrivilegeEnum.PRIVILEGE_VIEW_THIRD_PARTY);     
    },
    isActive: hasCurrentAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_VIEW_THIRD_PARTY),
    isGrantable: hasBaseAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_VIEW_THIRD_PARTY),
  }],
  items: [
    {
      name: i18next.t("roles.switchButton.identity"),
      actions: [{
        name: i18next.t("roles.switchButton.read"),
        action: (isActive) => { togglePrivileges(PrivilegeEnum.PRIVILEGE_VIEW_THIRD_PARTY_ID_CARD) },
        isActive: hasCurrentAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_VIEW_THIRD_PARTY_ID_CARD),
        isGrantable: hasBaseAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_VIEW_THIRD_PARTY_ID_CARD),
      },
        {
      name: i18next.t("roles.switchButton.modify"),
      action: (isActive) => { togglePrivileges(PrivilegeEnum.PRIVILEGE_EDIT_THIRD_PARTY_CODE) },
      isActive: hasCurrentAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_EDIT_THIRD_PARTY_CODE),
      isGrantable: hasBaseAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_EDIT_THIRD_PARTY_CODE),
    },]
    },
    {
      name: i18next.t("attributesLabel"),
      actions: [{
        name: i18next.t("roles.switchButton.read"),
        action: (isActive) => { togglePrivileges(PrivilegeEnum.PRIVILEGE_VIEW_THIRD_PARTY_ATTRIBUTES_VALUES_AND_INDICATORS);
          if(!isActive) {
            //si on active le privilege parent "PRIVILEGE_VIEW_THIRD_PARTY_ATTRIBUTES_AND_INDICATORS", on active toutes les propriétés des privileges enfants
            selectAllAttributesAndIndicatorsProperties(); 
          }
        },
        isActive: hasCurrentAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_VIEW_THIRD_PARTY_ATTRIBUTES_VALUES_AND_INDICATORS),
        isGrantable: hasBaseAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_VIEW_THIRD_PARTY_ATTRIBUTES_VALUES_AND_INDICATORS),
        
      }], 
      items : [
          {
            name: i18next.t("attributesEntered"),
            actions: [{
              name: i18next.t("roles.switchButton.read"),
              action: (isActive) => {
                if(! isActive && hasCurrentAllOfSpecificProperty(PrivilegeEnum.PRIVILEGE_VIEW_THIRD_PARTY_ATTRIBUTE_VALUE) !== true) {
                  //On active le privilege "read" pour tous les attributs qui ne le sont pas encore
                  toggleAllProperties(PrivilegeEnum.PRIVILEGE_VIEW_THIRD_PARTY_ATTRIBUTE_VALUE);
                }
                togglePrivileges(PrivilegeEnum.PRIVILEGE_VIEW_THIRD_PARTY_ATTRIBUTE_VALUE_ALL);
              },
              isActive: hasCurrentAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_VIEW_THIRD_PARTY_ATTRIBUTE_VALUE_ALL),
              isGrantable: hasBaseAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_VIEW_THIRD_PARTY_ATTRIBUTE_VALUE_ALL),
            }, {
              name: i18next.t("roles.switchButton.modify"),
              action: (isActive) => {
                if (!isActive && hasCurrentAllOfSpecificProperty(PrivilegeEnum.PRIVILEGE_EDIT_THIRD_PARTY_ATTRIBUTE_VALUE) !== true) {
                  //On active le privilege "modify" pour tous les attributs qui ne le sont pas encore, ainsi que le privilege "read"
                  toggleAllProperties(PrivilegeEnum.PRIVILEGE_EDIT_THIRD_PARTY_ATTRIBUTE_VALUE);
                  if(hasCurrentAllOfSpecificProperty(PrivilegeEnum.PRIVILEGE_VIEW_THIRD_PARTY_ATTRIBUTE_VALUE) !== true) {
                    toggleAllProperties(PrivilegeEnum.PRIVILEGE_VIEW_THIRD_PARTY_ATTRIBUTE_VALUE);
                  }
                }
                togglePrivileges(PrivilegeEnum.PRIVILEGE_EDIT_THIRD_PARTY_ATTRIBUTE_VALUE_ALL);
              },
              isActive: hasCurrentAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_EDIT_THIRD_PARTY_ATTRIBUTE_VALUE_ALL),
              isGrantable: hasBaseAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_EDIT_THIRD_PARTY_ATTRIBUTE_VALUE_ALL),
            }],
            items: accountThirdpartyAttributes?.map((attribute, index) => { 
                return {
                  key: "attribute"+index,
                  name: <PrivilegeNameWithRequirementMention name={attribute.label} isRequired={attribute.isRequired} />, 
                  actions: [{
                    name: i18next.t("roles.switchButton.read"),
                    action: (isActive) => { 
                      toggleProperty(attribute?.id, null, PrivilegeEnum.PRIVILEGE_VIEW_THIRD_PARTY_ATTRIBUTE_VALUE_ALL, PrivilegeEnum.PRIVILEGE_VIEW_THIRD_PARTY_ATTRIBUTE_VALUE);
                      //si on désactive la lecture, on désactive aussi la modification
                      if(isActive && hasCurrentAnyOfSpecificProperty(attribute?.id, null, PrivilegeEnum.PRIVILEGE_EDIT_THIRD_PARTY_ATTRIBUTE_VALUE) !== false) {
                        toggleProperty(attribute?.id, null, PrivilegeEnum.PRIVILEGE_EDIT_THIRD_PARTY_ATTRIBUTE_VALUE_ALL, PrivilegeEnum.PRIVILEGE_EDIT_THIRD_PARTY_ATTRIBUTE_VALUE);
                      }
                    },
                    isActive: hasCurrentAnyOfSpecificProperty(attribute?.id, null, PrivilegeEnum.PRIVILEGE_VIEW_THIRD_PARTY_ATTRIBUTE_VALUE),
                    isGrantable: hasGrantablePrivilegeForProperty(PrivilegeEnum.PRIVILEGE_VIEW_THIRD_PARTY_ATTRIBUTE_VALUE, attribute?.id, null),
                  },{
                    name: i18next.t("roles.switchButton.modify"),
                    action: (isActive) => { 
                      toggleProperty(attribute?.id, null, PrivilegeEnum.PRIVILEGE_EDIT_THIRD_PARTY_ATTRIBUTE_VALUE_ALL, PrivilegeEnum.PRIVILEGE_EDIT_THIRD_PARTY_ATTRIBUTE_VALUE);
                      //if on active la modification, on active aussi la lecture
                      if(!isActive && hasCurrentAnyOfSpecificProperty(attribute?.id, null, PrivilegeEnum.PRIVILEGE_VIEW_THIRD_PARTY_ATTRIBUTE_VALUE) !== true ) {
                          toggleProperty(attribute?.id, null, PrivilegeEnum.PRIVILEGE_VIEW_THIRD_PARTY_ATTRIBUTE_VALUE_ALL, PrivilegeEnum.PRIVILEGE_VIEW_THIRD_PARTY_ATTRIBUTE_VALUE) 
                      }
                    },
                    isActive: hasCurrentAnyOfSpecificProperty(attribute?.id, null, PrivilegeEnum.PRIVILEGE_EDIT_THIRD_PARTY_ATTRIBUTE_VALUE),
                    isGrantable: hasGrantablePrivilegeForProperty(PrivilegeEnum.PRIVILEGE_EDIT_THIRD_PARTY_ATTRIBUTE_VALUE, attribute?.id, null),
                  }]
                }
            })
          },{
            name: i18next.t("calculatedAttributes"),
            actions : [{
              name: i18next.t("roles.switchButton.read"),
              action: (isActive) => { 
                if( !isActive && hasCurrentAllOfSpecificProperty(PrivilegeEnum.PRIVILEGE_VIEW_THIRD_PARTY_CALCULATED_ATTRIBUTE) !== true) {
                  toggleAllProperties(PrivilegeEnum.PRIVILEGE_VIEW_THIRD_PARTY_CALCULATED_ATTRIBUTE);
                }
                togglePrivileges(PrivilegeEnum.PRIVILEGE_VIEW_THIRD_PARTY_CALCULATED_ATTRIBUTE_ALL);
              },
              isActive: hasCurrentAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_VIEW_THIRD_PARTY_CALCULATED_ATTRIBUTE_ALL),
              isGrantable: hasBaseAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_VIEW_THIRD_PARTY_CALCULATED_ATTRIBUTE_ALL),
            }],
            items: accountCalculatedAttributes?.map((calculatedAttribute, index) => {
                return {
                  key: "calculatedAttribute"+index,
                  name: calculatedAttribute?.label, 
                  actions: [{
                    name: i18next.t("roles.switchButton.read"),
                    action: (isActive) => { toggleProperty(calculatedAttribute?.value, null, PrivilegeEnum.PRIVILEGE_VIEW_THIRD_PARTY_CALCULATED_ATTRIBUTE_ALL, PrivilegeEnum.PRIVILEGE_VIEW_THIRD_PARTY_CALCULATED_ATTRIBUTE) },
                    isActive: hasCurrentAnyOfSpecificProperty(calculatedAttribute?.value, null, PrivilegeEnum.PRIVILEGE_VIEW_THIRD_PARTY_CALCULATED_ATTRIBUTE),
                    isGrantable: hasGrantablePrivilegeForProperty(PrivilegeEnum.PRIVILEGE_VIEW_THIRD_PARTY_CALCULATED_ATTRIBUTE, calculatedAttribute?.value, null),
                  }]
                }
            })
          },{
            name: i18next.t("indicators"),
            actions: [{
              name: i18next.t("roles.switchButton.read"),
              action: (isActive) => { 
                if(!isActive && hasCurrentAllOfSpecificProperty(PrivilegeEnum.PRIVILEGE_VIEW_THIRD_PARTY_INDICATOR) !== true) {
                  toggleAllProperties(PrivilegeEnum.PRIVILEGE_VIEW_THIRD_PARTY_INDICATOR);
                }
                togglePrivileges(PrivilegeEnum.PRIVILEGE_VIEW_THIRD_PARTY_INDICATOR_ALL);                
              },
              isActive: hasCurrentAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_VIEW_THIRD_PARTY_INDICATOR_ALL),
              isGrantable: hasBaseAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_VIEW_THIRD_PARTY_INDICATOR_ALL),
            }],
            items: accountIndicators?.map((indicator, index) => {
                return {
                  key: "indicator"+index,
                  name: indicator?.label, 
                  actions: [{
                    name: i18next.t("roles.switchButton.read"),
                    action: (isActive) => { toggleProperty(indicator?.value, null, PrivilegeEnum.PRIVILEGE_VIEW_THIRD_PARTY_INDICATOR_ALL, PrivilegeEnum.PRIVILEGE_VIEW_THIRD_PARTY_INDICATOR) },
                    isActive: hasCurrentAnyOfSpecificProperty(indicator?.value, null, PrivilegeEnum.PRIVILEGE_VIEW_THIRD_PARTY_INDICATOR),
                    isGrantable: hasGrantablePrivilegeForProperty(PrivilegeEnum.PRIVILEGE_VIEW_THIRD_PARTY_INDICATOR, indicator?.value, null),
                  }]
                }
            })
          },
        ]
    },
    {
      name: i18next.t("roles.switchButton.principalsAndRepresentatives"),
      actions: [{
        name: i18next.t("roles.switchButton.read"),
        action: (isActive) => { togglePrivileges(PrivilegeEnum.PRIVILEGE_VIEW_THIRD_PARTY_OFFICERS_AGENTS) },
        isActive: hasCurrentAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_VIEW_THIRD_PARTY_OFFICERS_AGENTS),
        isGrantable: hasBaseAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_VIEW_THIRD_PARTY_OFFICERS_AGENTS),
      }]
    },
    {
      name: i18next.t("roles.switchButton.balanceSheetData"),
      actions: [{
        name: i18next.t("roles.switchButton.read"),
        action: (isActive) => { togglePrivileges(PrivilegeEnum.PRIVILEGE_VIEW_THIRD_PARTY_BALANCE_SHEET) },
        isActive: hasCurrentAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_VIEW_THIRD_PARTY_BALANCE_SHEET),
        isGrantable: hasBaseAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_VIEW_THIRD_PARTY_BALANCE_SHEET),
      }]
    },
    {
      name: i18next.t("roles.switchButton.risks"),
      actions: [{
        name: i18next.t("roles.switchButton.read"),
        action: (isActive) => { togglePrivileges(PrivilegeEnum.PRIVILEGE_VIEW_THIRD_PARTY_RISKS) },
        isActive: hasCurrentAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_VIEW_THIRD_PARTY_RISKS),
        isGrantable: hasBaseAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_VIEW_THIRD_PARTY_RISKS),
      }]
    },
    {
      name: i18next.t("roles.switchButton.auditTrail"),
      actions: [{
        name: i18next.t("roles.switchButton.read"),
        action: (isActive) => { togglePrivileges(PrivilegeEnum.PRIVILEGE_VIEW_THIRD_PARTY_AUDIT_TRAIL) },
        isActive: hasCurrentAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_VIEW_THIRD_PARTY_AUDIT_TRAIL),
        isGrantable: hasBaseAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_VIEW_THIRD_PARTY_AUDIT_TRAIL),
      }]
    },
    {
      name: i18next.t("roles.switchButton.notes"),
      actions: [{
        name: i18next.t("roles.switchButton.read"),
        action: (isActive) => { togglePrivileges(PrivilegeEnum.PRIVILEGE_VIEW_THIRD_PARTY_NOTES) },
        isActive: hasCurrentAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_VIEW_THIRD_PARTY_NOTES),
        isGrantable: hasBaseAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_VIEW_THIRD_PARTY_NOTES),
      }, {
        name: i18next.t("roles.switchButton.add"),
        action: (isActive) => { togglePrivileges(PrivilegeEnum.PRIVILEGE_CREATE_THIRD_PARTY_NOTES) },
        isActive: hasCurrentAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_CREATE_THIRD_PARTY_NOTES),
        isGrantable: hasBaseAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_CREATE_THIRD_PARTY_NOTES),
      }, {
        name: i18next.t("roles.switchButton.modify"),
        action: (isActive) => { togglePrivileges(PrivilegeEnum.PRIVILEGE_EDIT_THIRD_PARTY_NOTES) },
        isActive: hasCurrentAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_EDIT_THIRD_PARTY_NOTES),
        isGrantable: hasBaseAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_EDIT_THIRD_PARTY_NOTES),
      }]
    },
    {
      name: i18next.t("roles.switchButton.unmanaged"),
      actions: [
        {
        name: i18next.t("roles.switchButton.add"),
        action: (isActive) => {
          togglePrivileges(PrivilegeEnum.PRIVILEGE_CREATE_THIRD_PARTY_UNMANAGED)
        },
        isActive: hasCurrentAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_CREATE_THIRD_PARTY_UNMANAGED),
        isGrantable: hasBaseAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_CREATE_THIRD_PARTY_UNMANAGED),
      }, {
        name: i18next.t("roles.switchButton.modify"),
        action: (isActive) => {
          togglePrivileges(PrivilegeEnum.PRIVILEGE_EDIT_THIRD_PARTY_UNMANAGED)
        },
        isActive: hasCurrentAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_EDIT_THIRD_PARTY_UNMANAGED),
        isGrantable: hasBaseAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_EDIT_THIRD_PARTY_UNMANAGED),
      }]
    },
    // {
    //   name: i18next.t("roles.switchButton.status"),
    //   actions: [{
    //     name: i18next.t("roles.switchButton.read"),
    //     action: (isActive) => { togglePrivileges(PrivilegeEnum.PRIVILEGE_VIEW_THIRD_PARTY_STATUS) },
    //     isActive: hasCurrentAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_VIEW_THIRD_PARTY_STATUS),
    //     isGrantable : hasBaseAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_VIEW_THIRD_PARTY_STATUS),
    //   }]
    // },
  ],
  actions: [
    {
      name: i18next.t("roles.switchButton.add"),
      action: (isActive) => { togglePrivileges(PrivilegeEnum.PRIVILEGE_CREATE_THIRD_PARTY) },
      isActive: hasCurrentAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_CREATE_THIRD_PARTY),
      isGrantable: hasBaseAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_CREATE_THIRD_PARTY),
    },
    {
      name: i18next.t("roles.switchButton.archive"),
      action: (isActive) => { togglePrivileges(PrivilegeEnum.PRIVILEGE_ARCHIVE_THIRD_PARTY) },
      isActive: hasCurrentAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_ARCHIVE_THIRD_PARTY),
      isGrantable: hasBaseAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_ARCHIVE_THIRD_PARTY),
    },
  ],
})
};

export const EXPORTS_MANAGEMENT_ACTIONS = (
  togglePrivileges,
  hasCurrentAnyOfPrivileges,
  hasBaseAnyOfPrivileges,
  accountReports, // list of reports
  toggleProperty,
  toggleAllProperties,
  hasCurrentAnyOfSpecificProperty,
  hasCurrentAllOfSpecificProperty,
  hasGrantablePrivilegeForProperty,
  rolePrivileges, // current role privileges (may include inherited ones)
  baseRolePrivileges // parent/base role privileges
) => {
  // Check if PRIVILEGE_VIEW_REPORTS_ALL is in the CURRENT role ONLY (not inherited)
  // Strategy: Check if it exists in rolePrivileges but is NOT ONLY in baseRolePrivileges
  // If it's in baseRolePrivileges but we toggled it off locally, it shouldn't count as "enabled"
  const inRolePrivileges = rolePrivileges?.some(p => p.name === 'PRIVILEGE_VIEW_REPORTS_ALL') || false;
  const inBaseRolePrivileges = baseRolePrivileges?.some(p => p.name === 'PRIVILEGE_VIEW_REPORTS_ALL') || false;

  // If it's in rolePrivileges, we need to determine if it's explicitly set on THIS role
  // The logic: if it's in rolePrivileges AND (no base role OR not only from base)
  // For a child role without propagation, rolePrivileges would only have locally set privileges
  const allReportsEnabled = inRolePrivileges;

  const reportsItems = (accountReports || [])
    .filter(report => report && (report.assignmentId != null || report.reportId != null))
    .map((report, index) => {
      const reportId = report.assignmentId || report.reportId;

      // A report is grantable if EITHER:
      // 1. PRIVILEGE_VIEW_REPORTS (individual reports) is grantable for this report, OR
      // 2. PRIVILEGE_VIEW_REPORTS_ALL is grantable (parent has "all reports")
      const isReportGrantable = hasGrantablePrivilegeForProperty(PrivilegeEnum.PRIVILEGE_VIEW_REPORTS, reportId, null) ||
        inBaseRolePrivileges; // If parent has PRIVILEGE_VIEW_REPORTS_ALL, child can select individual reports

      return {
        key: "report" + index,
        name: report.name || `Rapport ${index + 1}`,
        actions: [{
          name: i18next.t("roles.switchButton.read"),
          action: () => {
            // When clicking on an individual report, remove PRIVILEGE_VIEW_REPORTS_ALL if present
            if (allReportsEnabled) {
              togglePrivileges(PrivilegeEnum.PRIVILEGE_VIEW_REPORTS_ALL);
            }
            toggleProperty(reportId, null, PrivilegeEnum.PRIVILEGE_VIEW_REPORTS_ALL, PrivilegeEnum.PRIVILEGE_VIEW_REPORTS);
          },
          isActive: hasCurrentAnyOfSpecificProperty(reportId, null, PrivilegeEnum.PRIVILEGE_VIEW_REPORTS),
          isGrantable: isReportGrantable,
        }]
      };
    });

  // Check if we should show individual reports:
  // - Always show if "All reports" is disabled
  // - Show even if "All reports" is enabled IF individual reports are grantable (child role case)
  const shouldShowIndividualReports = !allReportsEnabled ||
    (allReportsEnabled && reportsItems.some(item => item.actions[0].isGrantable));

  return {
    rootActions: [],
    items: [
      {
        name: i18next.t("roles.features.allReports"),
        actions: [{
          name: i18next.t("roles.switchButton.read"),
          action: (isActive) => {
            if (!isActive) {
              // Activating "All reports": remove all individual reports first
              // First, remove all individual report selections by toggling PRIVILEGE_VIEW_REPORTS off if it exists
              toggleAllProperties(PrivilegeEnum.PRIVILEGE_VIEW_REPORTS); // This will clear all individual reports
            }
            // Then toggle PRIVILEGE_VIEW_REPORTS_ALL
            togglePrivileges(PrivilegeEnum.PRIVILEGE_VIEW_REPORTS_ALL);
          },
          isActive: allReportsEnabled,
          isGrantable: hasBaseAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_VIEW_REPORTS_ALL),
        }]
      },
      // Show individual reports if "All reports" is disabled OR if individual reports are grantable
      ...(shouldShowIndividualReports ? reportsItems : [])
    ]
  };
};

export const DOSSIER_MANAGEMENT_ACTIONS = (
  togglePrivileges, 
  grantPrivileges, 
  revokePrivileges, 
  hasCurrentAnyOfPrivileges, 
  hasBaseAnyOfPrivileges,
  dossierAttributes, 
  dossierCalculatedAttributes,
  dossierIndicators,
  toggleProperty, // action to select/deselect a property id for an event
  toggleAllProperties, // action to select/deselect all properties for an event
  hasCurrentAnyOfSpecificProperty, // boolean to know if property is selected
  hasCurrentAllOfSpecificProperty, // boolean to know if all properties of event is selected
  hasGrantablePrivilegeForProperty, //boolean to know if the property is grantable
  ) => {
    const selectAllAttributesAndIndicatorsProperties = () => {
      if(hasCurrentAllOfSpecificProperty(PrivilegeEnum.PRIVILEGE_VIEW_DOSSIER_ATTRIBUTE_VALUE) !== true) {
            toggleAllProperties(PrivilegeEnum.PRIVILEGE_VIEW_DOSSIER_ATTRIBUTE_VALUE);
        } 
      if(hasCurrentAllOfSpecificProperty(PrivilegeEnum.PRIVILEGE_VIEW_DOSSIER_CALCULATED_ATTRIBUTE) !== true) {
            toggleAllProperties(PrivilegeEnum.PRIVILEGE_VIEW_DOSSIER_CALCULATED_ATTRIBUTE);
        }
      if(hasCurrentAllOfSpecificProperty(PrivilegeEnum.PRIVILEGE_VIEW_DOSSIER_INDICATOR) !== true) {
            toggleAllProperties(PrivilegeEnum.PRIVILEGE_VIEW_DOSSIER_INDICATOR);
        } 
          
    }
    
  return {
  rootActions: [{
    name: i18next.t("roles.switchButton.read"),
    action: (isActive) => { togglePrivileges(PrivilegeEnum.PRIVILEGE_VIEW_DOSSIER) },
    isActive: hasCurrentAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_VIEW_DOSSIER),
    isGrantable: hasBaseAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_VIEW_DOSSIER),
  }],
  items: [
    {
      name: i18next.t("roles.switchButton.dossierInformation"),
      actions: [{
        name: i18next.t("roles.switchButton.read"),
        action: (isActive) => { togglePrivileges(PrivilegeEnum.PRIVILEGE_VIEW_DOSSIER_INFORMATION) },
        isActive: hasCurrentAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_VIEW_DOSSIER_INFORMATION),
        isGrantable: hasBaseAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_VIEW_DOSSIER_INFORMATION),
      }]
    },
   {
      name: i18next.t("attributesLabel"),
      actions: [{
        name: i18next.t("roles.switchButton.read"),
        action: (isActive) => { togglePrivileges(PrivilegeEnum.PRIVILEGE_VIEW_DOSSIER_ATTRIBUTES_VALUES_AND_INDICATORS);
          if(!isActive) {
            //si on active le privilege parent "PRIVILEGE_VIEW_DOSSIER_ATTRIBUTES_AND_INDICATORS", on active toutes les propriétés des privileges enfants
            selectAllAttributesAndIndicatorsProperties(); 
          }
        },
        isActive: hasCurrentAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_VIEW_DOSSIER_ATTRIBUTES_VALUES_AND_INDICATORS),
        isGrantable: hasBaseAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_VIEW_DOSSIER_ATTRIBUTES_VALUES_AND_INDICATORS),
        
      }], 
      items : [
          {
            name: i18next.t("attributesEntered"),
            actions: [{
              name: i18next.t("roles.switchButton.read"),
              action: (isActive) => {
                if(! isActive && hasCurrentAllOfSpecificProperty(PrivilegeEnum.PRIVILEGE_VIEW_DOSSIER_ATTRIBUTE_VALUE) !== true) {
                  //On active le privilege "read" pour tous les attributs qui ne le sont pas encore
                  toggleAllProperties(PrivilegeEnum.PRIVILEGE_VIEW_DOSSIER_ATTRIBUTE_VALUE);
                }
                togglePrivileges(PrivilegeEnum.PRIVILEGE_VIEW_DOSSIER_ATTRIBUTE_VALUE_ALL);
              },
              isActive: hasCurrentAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_VIEW_DOSSIER_ATTRIBUTE_VALUE_ALL),
              isGrantable: hasBaseAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_VIEW_DOSSIER_ATTRIBUTE_VALUE_ALL),
            }, {
              name: i18next.t("roles.switchButton.modify"),
              action: (isActive) => {
                if (!isActive && hasCurrentAllOfSpecificProperty(PrivilegeEnum.PRIVILEGE_EDIT_DOSSIER_ATTRIBUTE_VALUE) !== true) {
                  //On active le privilege "modify" pour tous les attributs qui ne le sont pas encore, ainsi que le privilege "read"
                  toggleAllProperties(PrivilegeEnum.PRIVILEGE_EDIT_DOSSIER_ATTRIBUTE_VALUE);
                  if(hasCurrentAllOfSpecificProperty(PrivilegeEnum.PRIVILEGE_VIEW_DOSSIER_ATTRIBUTE_VALUE) !== true) {
                    toggleAllProperties(PrivilegeEnum.PRIVILEGE_VIEW_DOSSIER_ATTRIBUTE_VALUE);
                  }
                }
                togglePrivileges(PrivilegeEnum.PRIVILEGE_EDIT_DOSSIER_ATTRIBUTE_VALUE_ALL);
              },
              isActive: hasCurrentAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_EDIT_DOSSIER_ATTRIBUTE_VALUE_ALL),
              isGrantable: hasBaseAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_EDIT_DOSSIER_ATTRIBUTE_VALUE_ALL),
            }],
            items: dossierAttributes?.map((attribute, index) => { 
                return {
                  key: "attribute"+index,
                  name: <PrivilegeNameWithRequirementMention name={attribute.label} isRequired={attribute.isRequired} />, 
                  actions: [{
                    name: i18next.t("roles.switchButton.read"),
                    action: (isActive) => { 
                      toggleProperty(attribute?.id, null, PrivilegeEnum.PRIVILEGE_VIEW_DOSSIER_ATTRIBUTE_VALUE_ALL, PrivilegeEnum.PRIVILEGE_VIEW_DOSSIER_ATTRIBUTE_VALUE);
                      //si on désactive la lecture, on désactive aussi la modification
                      if(isActive && hasCurrentAnyOfSpecificProperty(attribute?.id, null, PrivilegeEnum.PRIVILEGE_EDIT_DOSSIER_ATTRIBUTE_VALUE) !== false) {
                        toggleProperty(attribute?.id, null, PrivilegeEnum.PRIVILEGE_EDIT_DOSSIER_ATTRIBUTE_VALUE_ALL, PrivilegeEnum.PRIVILEGE_EDIT_DOSSIER_ATTRIBUTE_VALUE);
                      }
                    },
                    isActive: hasCurrentAnyOfSpecificProperty(attribute?.id, null, PrivilegeEnum.PRIVILEGE_VIEW_DOSSIER_ATTRIBUTE_VALUE),
                    isGrantable: hasGrantablePrivilegeForProperty(PrivilegeEnum.PRIVILEGE_VIEW_DOSSIER_ATTRIBUTE_VALUE, attribute?.id, null),
                  },{
                    name: i18next.t("roles.switchButton.modify"),
                    action: (isActive) => { 
                      toggleProperty(attribute?.id, null, PrivilegeEnum.PRIVILEGE_EDIT_DOSSIER_ATTRIBUTE_VALUE_ALL, PrivilegeEnum.PRIVILEGE_EDIT_DOSSIER_ATTRIBUTE_VALUE);
                      //if on active la modification, on active aussi la lecture
                      if(!isActive && hasCurrentAnyOfSpecificProperty(attribute?.id, null, PrivilegeEnum.PRIVILEGE_VIEW_DOSSIER_ATTRIBUTE_VALUE) !== true ) {
                          toggleProperty(attribute?.id, null, PrivilegeEnum.PRIVILEGE_VIEW_DOSSIER_ATTRIBUTE_VALUE_ALL, PrivilegeEnum.PRIVILEGE_VIEW_DOSSIER_ATTRIBUTE_VALUE) 
                      }
                    },
                    isActive: hasCurrentAnyOfSpecificProperty(attribute?.id, null, PrivilegeEnum.PRIVILEGE_EDIT_DOSSIER_ATTRIBUTE_VALUE),
                    isGrantable: hasGrantablePrivilegeForProperty(PrivilegeEnum.PRIVILEGE_EDIT_DOSSIER_ATTRIBUTE_VALUE, attribute?.id, null),
                  }]
                }
            })
          },{
            name: i18next.t("calculatedAttributes"),
            actions : [{
              name: i18next.t("roles.switchButton.read"),
              action: (isActive) => { 
                if( !isActive && hasCurrentAllOfSpecificProperty(PrivilegeEnum.PRIVILEGE_VIEW_DOSSIER_CALCULATED_ATTRIBUTE) !== true) {
                  toggleAllProperties(PrivilegeEnum.PRIVILEGE_VIEW_DOSSIER_CALCULATED_ATTRIBUTE);
                }
                togglePrivileges(PrivilegeEnum.PRIVILEGE_VIEW_DOSSIER_CALCULATED_ATTRIBUTE_ALL);
              },
              isActive: hasCurrentAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_VIEW_DOSSIER_CALCULATED_ATTRIBUTE_ALL),
              isGrantable: hasBaseAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_VIEW_DOSSIER_CALCULATED_ATTRIBUTE_ALL),
            }],
            items: dossierCalculatedAttributes?.map((calculatedAttribute, index) => {
                return {
                  key: "calculatedAttribute"+index,
                  name: calculatedAttribute?.label, 
                  actions: [{
                    name: i18next.t("roles.switchButton.read"),
                    action: (isActive) => { toggleProperty(calculatedAttribute?.value, null, PrivilegeEnum.PRIVILEGE_VIEW_DOSSIER_CALCULATED_ATTRIBUTE_ALL, PrivilegeEnum.PRIVILEGE_VIEW_DOSSIER_CALCULATED_ATTRIBUTE) },
                    isActive: hasCurrentAnyOfSpecificProperty(calculatedAttribute?.value, null, PrivilegeEnum.PRIVILEGE_VIEW_DOSSIER_CALCULATED_ATTRIBUTE),
                    isGrantable: hasGrantablePrivilegeForProperty(PrivilegeEnum.PRIVILEGE_VIEW_DOSSIER_CALCULATED_ATTRIBUTE, calculatedAttribute?.value, null),
                  }]
                }
            })
          },{
            name: i18next.t("indicators"),
            actions: [{
              name: i18next.t("roles.switchButton.read"),
              action: (isActive) => { 
                if(!isActive && hasCurrentAllOfSpecificProperty(PrivilegeEnum.PRIVILEGE_VIEW_DOSSIER_INDICATOR) !== true) {
                  toggleAllProperties(PrivilegeEnum.PRIVILEGE_VIEW_DOSSIER_INDICATOR);
                }
                togglePrivileges(PrivilegeEnum.PRIVILEGE_VIEW_DOSSIER_INDICATOR_ALL);                
              },
              isActive: hasCurrentAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_VIEW_DOSSIER_INDICATOR_ALL),
              isGrantable: hasBaseAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_VIEW_DOSSIER_INDICATOR_ALL),
            }],
            items: dossierIndicators?.map((indicator, index) => {
                return {
                  key: "indicator"+index,
                  name: indicator?.label, 
                  actions: [{
                    name: i18next.t("roles.switchButton.read"),
                    action: (isActive) => { toggleProperty(indicator?.value, null, PrivilegeEnum.PRIVILEGE_VIEW_DOSSIER_INDICATOR_ALL, PrivilegeEnum.PRIVILEGE_VIEW_DOSSIER_INDICATOR) },
                    isActive: hasCurrentAnyOfSpecificProperty(indicator?.value, null, PrivilegeEnum.PRIVILEGE_VIEW_DOSSIER_INDICATOR),
                    isGrantable: hasGrantablePrivilegeForProperty(PrivilegeEnum.PRIVILEGE_VIEW_DOSSIER_INDICATOR, indicator?.value, null),
                  }]
                }
            })
          },
        ]
      },
    {
      name: i18next.t("roles.switchButton.auditTrail"),
      actions: [{
        name: i18next.t("roles.switchButton.read"),
        action: (isActive) => { togglePrivileges(PrivilegeEnum.PRIVILEGE_VIEW_DOSSIER_AUDIT_TRAIL) },
        isActive: hasCurrentAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_VIEW_DOSSIER_AUDIT_TRAIL),
        isGrantable: hasBaseAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_VIEW_DOSSIER_AUDIT_TRAIL),
      }]
    },
    {
      name: i18next.t("roles.switchButton.notes"),
      actions: [{
        name: i18next.t("roles.switchButton.read"),
        action: (isActive) => { togglePrivileges(PrivilegeEnum.PRIVILEGE_VIEW_DOSSIER_NOTES) },
        isActive: hasCurrentAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_VIEW_DOSSIER_NOTES),
        isGrantable: hasBaseAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_VIEW_DOSSIER_NOTES),
      }, {
        name: i18next.t("roles.switchButton.add"),
        action: (isActive) => { togglePrivileges(PrivilegeEnum.PRIVILEGE_CREATE_DOSSIER_NOTES) },
        isActive: hasCurrentAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_CREATE_DOSSIER_NOTES),
        isGrantable: hasBaseAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_CREATE_DOSSIER_NOTES),
      }, {
        name: i18next.t("roles.switchButton.modify"),
        action: (isActive) => { togglePrivileges(PrivilegeEnum.PRIVILEGE_EDIT_DOSSIER_NOTES) },
        isActive: hasCurrentAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_EDIT_DOSSIER_NOTES),
        isGrantable: hasBaseAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_EDIT_DOSSIER_NOTES),
      }]
    },
  ],
  actions: [
    {
      name: i18next.t("roles.switchButton.add"),
      action: (isActive) => { togglePrivileges(PrivilegeEnum.PRIVILEGE_CREATE_DOSSIER) },
      isActive: hasCurrentAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_CREATE_DOSSIER),
      isGrantable: hasBaseAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_CREATE_DOSSIER),
    },
    {
      name: i18next.t("roles.switchButton.archive"),
      action: (isActive) => { togglePrivileges(PrivilegeEnum.PRIVILEGE_ARCHIVE_DOSSIER) },
      isActive: hasCurrentAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_ARCHIVE_DOSSIER),
      isGrantable: hasBaseAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_ARCHIVE_DOSSIER),
    },
    {
      name: i18next.t("roles.switchButton.modify"),
      action: (isActive) => { togglePrivileges(PrivilegeEnum.PRIVILEGE_EDIT_DOSSIER) },
      isActive: hasCurrentAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_EDIT_DOSSIER),
      isGrantable: hasBaseAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_EDIT_DOSSIER),
    },
  ],
}};

export const CONTEXT_MANAGEMENT_ACTIONS = (togglePrivileges, grantPrivileges, revokePrivileges, hasCurrentAnyOfPrivileges, hasBaseAnyOfPrivileges) => ({
  rootActions: [{
    name: i18next.t("roles.switchButton.read"),
    action: (isActive) => { togglePrivileges(PrivilegeEnum.PRIVILEGE_VIEW_CONTEXT) },
    isActive: hasCurrentAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_VIEW_CONTEXT),
    isGrantable: hasBaseAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_VIEW_CONTEXT),
  }],
  items: [
    {
      name: i18next.t("roles.switchButton.contextInformation"),
      actions: [{
        name: i18next.t("roles.switchButton.read"),
        action: (isActive) => { togglePrivileges(PrivilegeEnum.PRIVILEGE_VIEW_CONTEXT_INFORMATION) },
        isActive: hasCurrentAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_VIEW_CONTEXT_INFORMATION),
        isGrantable: hasBaseAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_VIEW_CONTEXT_INFORMATION),
      }]
    },
    {
      name: i18next.t("roles.switchButton.contextAttributes"),
      actions: [{
        name: i18next.t("roles.switchButton.read"),
        action: (isActive) => { togglePrivileges(PrivilegeEnum.PRIVILEGE_VIEW_CONTEXT_ATTRIBUTES) },
        isActive: hasCurrentAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_VIEW_CONTEXT_ATTRIBUTES),
        isGrantable: hasBaseAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_VIEW_CONTEXT_ATTRIBUTES),
      },{
        name: i18next.t("roles.switchButton.modify"),
        action: (isActive) => { togglePrivileges(PrivilegeEnum.PRIVILEGE_EDIT_CONTEXT_ATTRIBUTES) },
        isActive: hasCurrentAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_EDIT_CONTEXT_ATTRIBUTES),
        isGrantable: hasBaseAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_EDIT_CONTEXT_ATTRIBUTES),
      }]
    },
    {
      name: i18next.t("roles.switchButton.contextThirdpartyAttributes"),
      actions: [{
        name: i18next.t("roles.switchButton.read"),
        action: (isActive) => { togglePrivileges(PrivilegeEnum.PRIVILEGE_VIEW_CONTEXT_THIRD_PARTY_ATTRIBUTES) },
        isActive: hasCurrentAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_VIEW_CONTEXT_THIRD_PARTY_ATTRIBUTES),
        isGrantable: hasBaseAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_VIEW_CONTEXT_THIRD_PARTY_ATTRIBUTES),
      },{
        name: i18next.t("roles.switchButton.modify"),
        action: (isActive) => { togglePrivileges(PrivilegeEnum.PRIVILEGE_EDIT_CONTEXT_THIRD_PARTY_ATTRIBUTES) },
        isActive: hasCurrentAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_EDIT_CONTEXT_THIRD_PARTY_ATTRIBUTES),
        isGrantable: hasBaseAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_EDIT_CONTEXT_THIRD_PARTY_ATTRIBUTES),
      }]
    },
    {
      name: i18next.t("roles.switchButton.contextThirdpartyDemandAttributes"),
      actions: [{
        name: i18next.t("roles.switchButton.read"),
        action: (isActive) => { togglePrivileges(PrivilegeEnum.PRIVILEGE_VIEW_CONTEXT_THIRD_PARTY_DEMAND_ATTRIBUTES) },
        isActive: hasCurrentAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_VIEW_CONTEXT_THIRD_PARTY_DEMAND_ATTRIBUTES),
        isGrantable: hasBaseAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_VIEW_CONTEXT_THIRD_PARTY_DEMAND_ATTRIBUTES),
      }]
    },
    {
      name: i18next.t("roles.switchButton.notes"),
      actions: [{
        name: i18next.t("roles.switchButton.read"),
        action: (isActive) => { togglePrivileges(PrivilegeEnum.PRIVILEGE_VIEW_CONTEXT_NOTES) },
        isActive: hasCurrentAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_VIEW_CONTEXT_NOTES),
        isGrantable: hasBaseAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_VIEW_CONTEXT_NOTES),
      }, {
        name: i18next.t("roles.switchButton.add"),
        action: (isActive) => { togglePrivileges(PrivilegeEnum.PRIVILEGE_CREATE_CONTEXT_NOTES) },
        isActive: hasCurrentAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_CREATE_CONTEXT_NOTES),
        isGrantable: hasBaseAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_CREATE_CONTEXT_NOTES),
      }, {
        name: i18next.t("roles.switchButton.modify"),
        action: (isActive) => { togglePrivileges(PrivilegeEnum.PRIVILEGE_EDIT_CONTEXT_NOTES) },
        isActive: hasCurrentAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_EDIT_CONTEXT_NOTES),
        isGrantable: hasBaseAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_EDIT_CONTEXT_NOTES),
      }]
    },
  ],
  actions: [
    {
      name: i18next.t("roles.switchButton.add"),
      action: (isActive) => { togglePrivileges(PrivilegeEnum.PRIVILEGE_CREATE_CONTEXT) },
      isActive: hasCurrentAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_CREATE_CONTEXT),
      isGrantable: hasBaseAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_CREATE_CONTEXT),
    },
    {
      name: i18next.t("roles.switchButton.archive"),
      action: (isActive) => { togglePrivileges(PrivilegeEnum.PRIVILEGE_ARCHIVE_CONTEXT) },
      isActive: hasCurrentAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_ARCHIVE_CONTEXT),
      isGrantable: hasBaseAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_ARCHIVE_CONTEXT),
    },
    {
      name: i18next.t("roles.switchButton.modify"),
      action: (isActive) => { togglePrivileges(PrivilegeEnum.PRIVILEGE_EDIT_CONTEXT) },
      isActive: hasCurrentAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_EDIT_CONTEXT),
      isGrantable: hasBaseAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_EDIT_CONTEXT),
    },
/*    {
      name: i18next.t("roles.switchButton.viewSupplyChain"),
      action: (isActive) => { togglePrivileges(PrivilegeEnum.PRIVILEGE_VIEW_SUPPLYCHAIN_CONTEXT) },
      isActive: hasCurrentAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_VIEW_SUPPLYCHAIN_CONTEXT),
      isGrantable: hasBaseAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_VIEW_SUPPLYCHAIN_CONTEXT),
    },
    {
      name: i18next.t("roles.switchButton.activateSupplyChain"),
      action: (isActive) => { togglePrivileges(PrivilegeEnum.PRIVILEGE_ACTIVATE_SUPPLYCHAIN_CONTEXT) },
      isActive: hasCurrentAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_ACTIVATE_SUPPLYCHAIN_CONTEXT),
      isGrantable: hasBaseAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_ACTIVATE_SUPPLYCHAIN_CONTEXT),
    },*/
    {
      name: i18next.t("roles.switchButton.addThirdPartyContext"),
      action: (isActive) => { togglePrivileges(PrivilegeEnum.PRIVILEGE_ADD_THIRDPARTY_CONTEXT) },
      isActive: hasCurrentAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_ADD_THIRDPARTY_CONTEXT),
      isGrantable: hasBaseAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_ADD_THIRDPARTY_CONTEXT),
    },
  ],
});

export const DOCUMENT_MANAGEMENT_ACTIONS = (togglePrivileges, toggleSubPrivilege, togglePrivilegesForAllResources, grantPrivileges, revokePrivileges, hasCurrentAnyOfPrivileges, hasCurrentAnyOfPrivilegesOnResource, hasCurrentAllOfPrivilegesOnResource, hasBaseAnyOfPrivileges) => ({
  rootActions: [
    {
      name: i18next.t("roles.switchButton.read"),
      action: (isActive) => { togglePrivileges(PrivilegeEnum.PRIVILEGE_VIEW_DOCUMENT) },
      isActive: hasCurrentAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_VIEW_DOCUMENT),
      isGrantable: hasBaseAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_VIEW_DOCUMENT),
    },
    // {
    //   name: i18next.t("roles.switchButton.answer"),
    //   action: (isActive) => { togglePrivileges(PrivilegeEnum.PRIVILEGE_EDIT_DOCUMENT) },
    //   isActive: hasCurrentAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_EDIT_DOCUMENT),
    //   isGrantable: hasBaseAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_EDIT_DOCUMENT),
    // },

    //TODO: add other actions
  ],
  items: [
    {
      name: i18next.t("roles.switchButton.auditTrail"),
      actions: [{
        name: i18next.t("roles.switchButton.read"),
        action: (isActive) => { togglePrivileges(PrivilegeEnum.PRIVILEGE_VIEW_DOCUMENT_AUDIT_TRAIL) },
        isActive: hasCurrentAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_VIEW_DOCUMENT_AUDIT_TRAIL),
        isGrantable: hasBaseAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_VIEW_DOCUMENT_AUDIT_TRAIL),
      }]
    },
    {
      name: i18next.t("roles.switchButton.notes"),
      actions: [{
        name: i18next.t("roles.switchButton.read"),
        action: (isActive) => { togglePrivileges(PrivilegeEnum.PRIVILEGE_VIEW_DOCUMENT_NOTES) },
        isActive: hasCurrentAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_VIEW_DOCUMENT_NOTES),
        isGrantable: hasBaseAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_VIEW_DOCUMENT_NOTES),
      }, {
        name: i18next.t("roles.switchButton.add"),
        action: (isActive) => { togglePrivileges(PrivilegeEnum.PRIVILEGE_CREATE_DOCUMENT_NOTES) },
        isActive: hasCurrentAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_CREATE_DOCUMENT_NOTES),
        isGrantable: hasBaseAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_CREATE_DOCUMENT_NOTES),
      }, {
        name: i18next.t("roles.switchButton.modify"),
        action: (isActive) => { togglePrivileges(PrivilegeEnum.PRIVILEGE_EDIT_DOCUMENT_NOTES) },
        isActive: hasCurrentAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_EDIT_DOCUMENT_NOTES),
        isGrantable: hasBaseAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_EDIT_DOCUMENT_NOTES),
      }]
    }
  ],
  actions: [
    {
      name: i18next.t("roles.switchButton.answer"),
      action: (isActive, resourceId, resources) => { togglePrivilegesForAllResources(resources, PrivilegeEnum.PRIVILEGE_EDIT_DOCUMENT_ANSWER) },
      isActive: (resources) => { return hasCurrentAllOfPrivilegesOnResource(resources, PrivilegeEnum.PRIVILEGE_EDIT_DOCUMENT_ANSWER) },
      isGrantable: hasBaseAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_EDIT_DOCUMENT_ANSWER),
    },
    {
      name: i18next.t("roles.switchButton.validate"),
      action: (isActive, resourceId, resources) => { togglePrivilegesForAllResources(resources, PrivilegeEnum.PRIVILEGE_EDIT_DOCUMENT_APPROVE) },
      isActive: (resources) => { return hasCurrentAllOfPrivilegesOnResource(resources, PrivilegeEnum.PRIVILEGE_EDIT_DOCUMENT_APPROVE) },
      isGrantable: hasBaseAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_EDIT_DOCUMENT_APPROVE),
    },
    {
      name: i18next.t("roles.switchButton.reject"),
      action: (isActive, resourceId, resources) => { togglePrivilegesForAllResources(resources, PrivilegeEnum.PRIVILEGE_EDIT_DOCUMENT_REJECT) },
      isActive: (resources) => { return hasCurrentAllOfPrivilegesOnResource(resources, PrivilegeEnum.PRIVILEGE_EDIT_DOCUMENT_REJECT) },
      isGrantable: hasBaseAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_EDIT_DOCUMENT_REJECT),
    }
  ],
  subActions: [
    {
      name: i18next.t("roles.switchButton.answer"),
      action: (isActive, resourceId) => { toggleSubPrivilege(resourceId, PrivilegeEnum.PRIVILEGE_EDIT_DOCUMENT_ANSWER) },
      isActive: (resourceId) => { return hasCurrentAnyOfPrivilegesOnResource(resourceId, PrivilegeEnum.PRIVILEGE_EDIT_DOCUMENT_ANSWER) },
      isGrantable: hasBaseAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_EDIT_DOCUMENT_ANSWER),
    },
    {
      name: i18next.t("roles.switchButton.validate"),
      action: (isActive, resourceId) => { toggleSubPrivilege(resourceId, PrivilegeEnum.PRIVILEGE_EDIT_DOCUMENT_APPROVE) },
      isActive: (resourceId) => { return hasCurrentAnyOfPrivilegesOnResource(resourceId, PrivilegeEnum.PRIVILEGE_EDIT_DOCUMENT_APPROVE) },
      isGrantable: hasBaseAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_EDIT_DOCUMENT_APPROVE),
    },
    {
      name: i18next.t("roles.switchButton.reject"),
      action: (isActive, resourceId) => { toggleSubPrivilege(resourceId, PrivilegeEnum.PRIVILEGE_EDIT_DOCUMENT_REJECT) },
      isActive: (resourceId) => { return hasCurrentAnyOfPrivilegesOnResource(resourceId, PrivilegeEnum.PRIVILEGE_EDIT_DOCUMENT_REJECT) },
      isGrantable: hasBaseAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_EDIT_DOCUMENT_REJECT),
    }
  ]

});

export const CONTACT_MANAGEMENT_ACTIONS = (togglePrivileges, grantPrivileges, revokePrivileges, hasCurrentAnyOfPrivileges, hasBaseAnyOfPrivileges) => ({
  rootActions: [
    {
      name: i18next.t("roles.switchButton.read"),
      action: (isActive) => { togglePrivileges(PrivilegeEnum.PRIVILEGE_VIEW_CONTACT) },
      isActive: hasCurrentAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_VIEW_CONTACT),
      isGrantable: hasBaseAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_VIEW_CONTACT),
    },
    {
      name: i18next.t("roles.switchButton.invite"),
      action: (isActive) => {
        !isActive ?
          grantPrivileges(PrivilegeEnum.PRIVILEGE_ALL_CONTACT)
          :
          revokePrivileges(
            PrivilegeEnum.PRIVILEGE_EDIT_CONTACT,
            PrivilegeEnum.PRIVILEGE_CREATE_CONTACT,
            PrivilegeEnum.PRIVILEGE_DELETE_CONTACT
          )
      },
      isActive: hasCurrentAnyOfPrivileges(
        PrivilegeEnum.PRIVILEGE_EDIT_CONTACT,
        PrivilegeEnum.PRIVILEGE_CREATE_CONTACT,
        PrivilegeEnum.PRIVILEGE_DELETE_CONTACT
      ),
      isGrantable: hasBaseAnyOfPrivileges(
        PrivilegeEnum.PRIVILEGE_EDIT_CONTACT,
        PrivilegeEnum.PRIVILEGE_CREATE_CONTACT,
        PrivilegeEnum.PRIVILEGE_DELETE_CONTACT
      ),
    },
  ],
  items: [],
  actions: []
});

//Sources
export const INDUED_MANAGEMENT_ACTIONS = (togglePrivileges, grantPrivileges, revokePrivileges, hasCurrentAnyOfPrivileges, hasBaseAnyOfPrivileges) => ({
  rootActions: [
    {
      name: i18next.t("roles.switchButton.read"),
      action: (isActive) => { togglePrivileges(PrivilegeEnum.PRIVILEGE_VIEW_INDUED_SOURCE) },
      isActive: hasCurrentAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_VIEW_INDUED_SOURCE),
      isGrantable: hasBaseAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_VIEW_INDUED_SOURCE),
    },
    {
      name: i18next.t("roles.switchButton.modify"),
      action: (isActive) => { togglePrivileges(PrivilegeEnum.PRIVILEGE_REFRESH_INDUED_SOURCE) },
      isActive: hasCurrentAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_REFRESH_INDUED_SOURCE),
      isGrantable: hasBaseAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_REFRESH_INDUED_SOURCE),
    },
  ],
  items: [],
  actions: []
});
export const TRANSPARENCY_MANAGEMENT_ACTIONS = (togglePrivileges, grantPrivileges, revokePrivileges, hasCurrentAnyOfPrivileges, hasBaseAnyOfPrivileges) => ({
  rootActions: [
    {
      name: i18next.t("roles.switchButton.read"),
      action: (isActive) => { togglePrivileges(PrivilegeEnum.PRIVILEGE_VIEW_TRANSPARENCY_SOURCE) },
      isActive: hasCurrentAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_VIEW_TRANSPARENCY_SOURCE),
      isGrantable: hasBaseAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_VIEW_TRANSPARENCY_SOURCE),
    }
  ],
  items: [],
  actions: []
});
export const DATA_HUB_MANAGEMENT_ACTIONS = (togglePrivileges, hasCurrentAnyOfPrivileges, hasBaseAnyOfPrivileges) => ({
  rootActions: [
    {
      name: i18next.t("roles.switchButton.read"),
      action: (isActive) => { togglePrivileges(PrivilegeEnum.PRIVILEGE_VIEW_DATA_HUB_SOURCE) },
      isActive: hasCurrentAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_VIEW_DATA_HUB_SOURCE),
      isGrantable: hasBaseAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_VIEW_DATA_HUB_SOURCE),
    }
  ],
  items: [],
  actions: []
});
export const FINANCIAL_STRENGTH_MANAGEMENT_ACTIONS = (togglePrivileges, grantPrivileges, revokePrivileges, hasCurrentAnyOfPrivileges, hasBaseAnyOfPrivileges) => ({
  rootActions: [
    {
      name: i18next.t("roles.switchButton.read"),
      action: (isActive) => { togglePrivileges(PrivilegeEnum.PRIVILEGE_VIEW_FINANCIAL_STRENGTH_SOURCE) },
      isActive: hasCurrentAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_VIEW_FINANCIAL_STRENGTH_SOURCE),
      isGrantable: hasBaseAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_VIEW_FINANCIAL_STRENGTH_SOURCE),
    },
    {
      name: i18next.t("roles.switchButton.modify"),
      action: (isActive) => { togglePrivileges(PrivilegeEnum.PRIVILEGE_EDIT_FINANCIAL_STRENGTH_SOURCE) },
      isActive: hasCurrentAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_EDIT_FINANCIAL_STRENGTH_SOURCE),
      isGrantable: hasBaseAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_EDIT_FINANCIAL_STRENGTH_SOURCE),
    },
  ],
  items: [],
  actions: []
});

export const OWLIN_MANAGEMENT_ACTIONS = (togglePrivileges, grantPrivileges, revokePrivileges, hasCurrentAnyOfPrivileges, hasBaseAnyOfPrivileges) => ({
  rootActions: [
    {
      name: i18next.t("roles.switchButton.read"),
      action: (isActive) => { togglePrivileges(PrivilegeEnum.PRIVILEGE_VIEW_OWLIN_SOURCE) },
      isActive: hasCurrentAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_VIEW_OWLIN_SOURCE),
      isGrantable: hasBaseAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_VIEW_OWLIN_SOURCE),
    },
    {
      name: i18next.t("roles.switchButton.modify"),
      action: (isActive) => { togglePrivileges(PrivilegeEnum.PRIVILEGE_EDIT_OWLIN_SOURCE) },
      isActive: hasCurrentAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_EDIT_OWLIN_SOURCE),
      isGrantable: hasBaseAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_EDIT_OWLIN_SOURCE),
    },
  ],
  items: [],
  actions: []
});
export const DNB_ESG_MANAGEMENT_ACTIONS = (togglePrivileges, grantPrivileges, revokePrivileges, hasCurrentAnyOfPrivileges, hasBaseAnyOfPrivileges) => ({
  rootActions: [
    {
      name: i18next.t("roles.switchButton.read"),
      action: (isActive) => { togglePrivileges(PrivilegeEnum.PRIVILEGE_VIEW_DNB_ESG_SOURCE) },
      isActive: hasCurrentAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_VIEW_DNB_ESG_SOURCE),
      isGrantable: hasBaseAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_VIEW_DNB_ESG_SOURCE),
    },
    {
      name: i18next.t("roles.switchButton.modify"),
      action: (isActive) => { togglePrivileges(PrivilegeEnum.PRIVILEGE_EDIT_DNB_ESG_SOURCE) },
      isActive: hasCurrentAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_EDIT_DNB_ESG_SOURCE),
      isGrantable: hasBaseAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_EDIT_DNB_ESG_SOURCE),
    },
  ],
  items: [],
  actions: []
});

export const ECOVADIS_MANAGEMENT_ACTIONS = (togglePrivileges, grantPrivileges, revokePrivileges, hasCurrentAnyOfPrivileges, hasBaseAnyOfPrivileges) => ({
  rootActions: [
    {
      name: i18next.t("roles.switchButton.read"),
      action: (isActive) => { togglePrivileges(PrivilegeEnum.PRIVILEGE_VIEW_ECOVADIS_SOURCE) },
      isActive: hasCurrentAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_VIEW_ECOVADIS_SOURCE),
      isGrantable: hasBaseAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_VIEW_ECOVADIS_SOURCE),
    },
    {
      name: i18next.t("roles.switchButton.modify"),
      action: (isActive) => { togglePrivileges(PrivilegeEnum.PRIVILEGE_EDIT_ECOVADIS_SOURCE) },
      isActive: hasCurrentAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_EDIT_ECOVADIS_SOURCE),
      isGrantable: hasBaseAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_EDIT_ECOVADIS_SOURCE),
    },
  ],
  items: [],
  actions: []
});

export const SECURITY_RATING_MANAGEMENT_ACTIONS = (togglePrivileges, grantPrivileges, revokePrivileges, hasCurrentAnyOfPrivileges, hasBaseAnyOfPrivileges) => ({
  rootActions: [
    {
      name: i18next.t("roles.switchButton.read"),
      action: (isActive) => { togglePrivileges(PrivilegeEnum.PRIVILEGE_VIEW_SECURITY_RATING_SOURCE) },
      isActive: hasCurrentAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_VIEW_SECURITY_RATING_SOURCE),
      isGrantable: hasBaseAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_VIEW_SECURITY_RATING_SOURCE),
    },
    {
      name: i18next.t("roles.switchButton.modify"),
      action: (isActive) => { togglePrivileges(PrivilegeEnum.PRIVILEGE_EDIT_SECURITY_RATING_SOURCE) },
      isActive: hasCurrentAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_EDIT_SECURITY_RATING_SOURCE),
      isGrantable: hasBaseAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_EDIT_SECURITY_RATING_SOURCE),
    },
  ],
  items: [],
  actions: []
});

export const CREDITSAFE_MANAGEMENT_ACTIONS = (togglePrivileges, grantPrivileges, revokePrivileges, hasCurrentAnyOfPrivileges, hasBaseAnyOfPrivileges) => ({
  rootActions: [
    {
      name: i18next.t("roles.switchButton.read"),
      action: (isActive) => { togglePrivileges(PrivilegeEnum.PRIVILEGE_VIEW_CREDITSAFE_SOURCE) },
      isActive: hasCurrentAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_VIEW_CREDITSAFE_SOURCE),
      isGrantable: hasBaseAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_VIEW_CREDITSAFE_SOURCE),
    },
    {
      name: i18next.t("roles.switchButton.modify"),
      action: (isActive) => { togglePrivileges(PrivilegeEnum.PRIVILEGE_EDIT_CREDITSAFE_SOURCE) },
      isActive: hasCurrentAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_EDIT_CREDITSAFE_SOURCE),
      isGrantable: hasBaseAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_EDIT_CREDITSAFE_SOURCE),
    },
  ],
  items: [],
  actions: []
});

// Required privileges to navigate to the resource.
export const NAVIGATION_REQUIRED_PRIVILEGES = {
  thirdparty: [
    PrivilegeEnum.PRIVILEGE_VIEW_THIRD_PARTY,
    PrivilegeEnum.PRIVILEGE_VIEW_THIRD_PARTY_ID_CARD,
    PrivilegeEnum.PRIVILEGE_VIEW_THIRD_PARTY_ATTRIBUTE_VALUE_ALL,
    PrivilegeEnum.PRIVILEGE_VIEW_THIRD_PARTY_OFFICERS_AGENTS,
    PrivilegeEnum.PRIVILEGE_VIEW_THIRD_PARTY_BALANCE_SHEET,
    PrivilegeEnum.PRIVILEGE_VIEW_THIRD_PARTY_RISKS,
    PrivilegeEnum.PRIVILEGE_VIEW_THIRD_PARTY_NOTES,
    PrivilegeEnum.PRIVILEGE_VIEW_THIRD_PARTY_STATUS,
    // PrivilegeEnum.PRIVILEGE_VIEW_DOSSIER, // A voir
  ],
  dossier: [
    PrivilegeEnum.PRIVILEGE_VIEW_DOSSIER,
    PrivilegeEnum.PRIVILEGE_VIEW_DOSSIER_INFORMATION,
    PrivilegeEnum.PRIVILEGE_VIEW_DOSSIER_ATTRIBUTE_VALUE_ALL,
    PrivilegeEnum.PRIVILEGE_VIEW_DOSSIER_NOTES,
  ],
  document: [
    PrivilegeEnum.PRIVILEGE_VIEW_DOCUMENT,
    PrivilegeEnum.PRIVILEGE_VIEW_DOCUMENT_NOTES,
  ],
  user: [
    PrivilegeEnum.PRIVILEGE_VIEW_USER,
  ],
  role: [
    PrivilegeEnum.PRIVILEGE_VIEW_ROLE,
  ],
  admin: [
    PrivilegeEnum.PRIVILEGE_VIEW_THIRD_PARTY_ATTRIBUTE,
    PrivilegeEnum.PRIVILEGE_VIEW_DOSSIER_ATTRIBUTE,
    PrivilegeEnum.PRIVILEGE_VIEW_RULE,
    PrivilegeEnum.PRIVILEGE_VIEW_CALCULATION,
    PrivilegeEnum.PRIVILEGE_VIEW_CONTEXT_ATTRIBUTE,
    PrivilegeEnum.PRIVILEGE_VIEW_THEME,
    PrivilegeEnum.PRIVILEGE_VIEW_AUTHORIZED_DOCUMENT_TYPE,
    PrivilegeEnum.PRIVILEGE_VIEW_DOSSIER_TYPE,
  ],
  context: [PrivilegeEnum.PRIVILEGE_VIEW_CONTEXT],
  reports: [
    PrivilegeEnum.PRIVILEGE_VIEW_REPORTS_ALL,
    PrivilegeEnum.PRIVILEGE_VIEW_REPORTS
  ]
};

// --------- //
//   Scope   //
// --------- //

export const getThirdPartyCriterionOptions = (accountCountries = [], accountActivities = [], attributes = [], calculatedAttributes = [], indicators = [], state) => {
  return [
    {
      value: 'name',
      label: i18next.t("roles.targetOption.thirdParties"),
      type: FieldType.TEXT,
      valueOptions: [],
    },
    {
      value: 'thirdpartyCode',
      label: i18next.t("roles.targetOption.thirdPartyCode"),
      type: FieldType.TEXT,
      valueOptions: [],
    },
    {
      value: 'etab_activity_sic_major',
      label: i18next.t("roles.targetOption.activitySicMajor"),
      type: FieldType.ACTIVITY,
      valueOptions: accountActivities.filter(activity => activity.level === ActivityLevel.SIC_MAJOR),
      optionsDisplayVariant: FieldDisplayVariant.AUTOCOMPLETE
    },
    {
      value: 'etab_activity_sic_2',
      label: i18next.t("roles.targetOption.activitySic", {number: 2}),
      type: FieldType.ACTIVITY,
      valueOptions: accountActivities.filter(activity => activity.level === ActivityLevel.SIC_2_DIGITS),
      optionsDisplayVariant: FieldDisplayVariant.AUTOCOMPLETE
    },
    {
      value: 'etab_activity_sic_3',
      label: i18next.t("roles.targetOption.activitySic", {number: 3}),
      type: FieldType.ACTIVITY,
      valueOptions: accountActivities.filter(activity => activity.level === ActivityLevel.SIC_3_DIGITS),
      optionsDisplayVariant: FieldDisplayVariant.AUTOCOMPLETE
    },
    {
      value: 'etab_activity_sic_4',
      label: i18next.t("roles.targetOption.activitySic", {number: 4}),
      type: FieldType.ACTIVITY,
      valueOptions: accountActivities.filter(activity => activity.level === ActivityLevel.SIC_4_DIGITS),
      optionsDisplayVariant: FieldDisplayVariant.AUTOCOMPLETE
    },
    {
      value: 'etab_activity_sic_6',
      label: i18next.t("roles.targetOption.activitySic", {number: 6}),
      type: FieldType.ACTIVITY,
      valueOptions: accountActivities.filter(activity => activity.level === ActivityLevel.SIC_6_DIGITS),
      optionsDisplayVariant: FieldDisplayVariant.AUTOCOMPLETE
    },
    {
      value: 'etab_activity_sic_8',
      label: i18next.t("roles.targetOption.activitySic", {number: 8}),
      type: FieldType.ACTIVITY,
      valueOptions: accountActivities.filter(activity => activity.level === ActivityLevel.SIC_8_DIGITS),
      optionsDisplayVariant: FieldDisplayVariant.AUTOCOMPLETE
    },
    {
      value: 'headquarter',
      label: i18next.t("roles.targetOption.headquarters"),
      type: FieldType.BOOLEAN,
      valueOptions: [
        {
          value: 'true',
          label: i18next.t("headquarters")
        },
        {
          value: 'false',
          label: i18next.t("secondaryBranch")
        },
      ],
    },
    {
      value: 'addressPostCode',
      label: i18next.t("roles.targetOption.postalCode"),
      type: FieldType.TEXT,
      valueOptions: [],
    },
    {
      value: 'brandName',
      label: i18next.t("roles.targetOption.brandName"),
      type: FieldType.TEXT,
      valueOptions: [],
    },
    {
      value: 'addressCity',
      label: i18next.t("roles.targetOption.city"),
      type: FieldType.TEXT,
      valueOptions: [],
    },
    {
      value: 'addressCountryCode',
      label: i18next.t("roles.targetOption.country"),
      type: FieldType.CUSTOM,
      valueOptions: accountCountries,
    },
    ...attributes?.map((attr) => {
      return {
        value: `attribute-${attr.id}`,
        label: attr.label,
        type: FieldType.ATTRIBUTE,
        subType: attr.type,
        attributeId: attr.id,
        valueOptions: attr.values?.map((valueEntry) => {
          return {
            value: valueEntry.value,
            label: valueEntry.value
          }
        }) || [],
      };
    }),
    ...calculatedAttributes?.map((calculatedAttr) => {
      return {
        value: `${calculatedAttr.value}`,
        label: calculatedAttr.label,
        type: FieldType.CALCULATED_ATTRIBUTE,
        subType: calculatedAttr.type,
        valueOptions: [],
      };
    }),
    ...indicators?.map((ind) => {
      return {
        value: `${ind.value}`,
        label: ind.label,
        type: FieldType.INDICATOR,
        subType: ind.type,
        valueOptions: [],
      };
    }),
    // D&B Score ESG 
    ...(hasOption(state?.accounts, state?.account?.id, "ESG") ? getEsgScopesFields() : [])
]}

export const getDossierCriterionOptions = (dossierTypes = [], attributes = []) => {
  return [
    {
      value: 'dossierTypeCode',
      label: i18next.t("roles.targetOption.typeDossier"),
      type: FieldType.CUSTOM,
      valueOptions: dossierTypes,
    },
    ...attributes?.map((attr) => {
      return {
        value: `attribute-${attr.id}`,
        label: attr.label,
        type: FieldType.ATTRIBUTE,
        subType: attr.type,
        attributeId: attr.id,
        valueOptions: attr.values?.map((valueEntry) => {
          return {
            value: valueEntry.value,
            label: valueEntry.value
          }
        }) || [],
      };
    }),
  ];
}

export const getContextCriterionOptions = (contextModels = [], contextInstances= [], attributes = []) => {
  return [
    {
      value: 'contextModel',
      label: i18next.t("roles.targetOption.context"),
      type: FieldType.CUSTOM,
      valueOptions: contextModels,
    },
    {
      value: 'reference',
      label: i18next.t("roles.targetOption.reference"),
      type: FieldType.CUSTOM,
      valueOptions: contextInstances,
    },
    ...attributes?.map((attr) => {
      return {
        value: `attribute-${attr.id}`,
        label: attr.label,
        type: FieldType.ATTRIBUTE,
        subType: attr.type,
        attributeId: attr.id,
        valueOptions: attr.values?.map((valueEntry) => {
          return {
            value: valueEntry.value,
            label: valueEntry.value
          }
        }) || [],
      };
    }),
  ];
}

export const RESOURCE_PRIVILEGES = [
  {
    privilege: PrivilegeEnum.PRIVILEGE_EDIT_DOCUMENT_ANSWER.name,
    type: ScopedResourceType.DOCUMENT,
  },
  {
    privilege: PrivilegeEnum.PRIVILEGE_EDIT_DOCUMENT_APPROVE.name,
    type: ScopedResourceType.DOCUMENT,
  },
  {
    privilege: PrivilegeEnum.PRIVILEGE_EDIT_DOCUMENT_REJECT.name,
    type: ScopedResourceType.DOCUMENT,
  },
  {
    privilege: PrivilegeEnum.PRIVILEGE_VIEW_REPORTS.name,
    type: "REPORT"
  },
];

export const PROPERTY_PRIVILEGES = [
  {
    privilege: PrivilegeEnum.PRIVILEGE_EVENT_THIRD_PARTY_ATTRIBUTE_UPDATED.name,
    type: "THIRD_PARTY_ATTRIBUTE",
  },
  {
    privilege: PrivilegeEnum.PRIVILEGE_EVENT_THIRD_PARTY_CALCULATED_ATTRIBUTE_UPDATED.name,
    type: "THIRD_PARTY_CALCULATED_ATTRIBUTE",
  },
  {
    privilege: PrivilegeEnum.PRIVILEGE_EVENT_THIRD_PARTY_INDICATOR_UPDATED.name,
    type: "THIRD_PARTY_INDICATOR"
  },
  {
    privilege: PrivilegeEnum.PRIVILEGE_VIEW_THIRD_PARTY_ATTRIBUTE_VALUE.name,
    type: "THIRD_PARTY_ATTRIBUTE"
  },
  {
    privilege: PrivilegeEnum.PRIVILEGE_EDIT_THIRD_PARTY_ATTRIBUTE_VALUE.name,
    type: "THIRD_PARTY_ATTRIBUTE"
  },
  {
    privilege: PrivilegeEnum.PRIVILEGE_VIEW_THIRD_PARTY_CALCULATED_ATTRIBUTE.name,
    type: "THIRD_PARTY_CALCULATED_ATTRIBUTE"
  },
  {
    privilege: PrivilegeEnum.PRIVILEGE_VIEW_THIRD_PARTY_INDICATOR.name,
    type: "THIRD_PARTY_INDICATOR"
  },
    {
    privilege: PrivilegeEnum.PRIVILEGE_VIEW_DOSSIER_ATTRIBUTE_VALUE.name,
    type: "DOSSIER_ATTRIBUTE"
  },
  {
    privilege: PrivilegeEnum.PRIVILEGE_EDIT_DOSSIER_ATTRIBUTE_VALUE.name,
    type: "DOSSIER_ATTRIBUTE"
  },
  {
    privilege: PrivilegeEnum.PRIVILEGE_VIEW_DOSSIER_CALCULATED_ATTRIBUTE.name,
    type: "DOSSIER_CALCULATED_ATTRIBUTE"
  },
  {
    privilege: PrivilegeEnum.PRIVILEGE_VIEW_DOSSIER_INDICATOR.name,
    type: "DOSSIER_INDICATOR"
  },
  {
    privilege: PrivilegeEnum.PRIVILEGE_EVENT_DOSSIER_ATTRIBUTE_UPDATED.name,
    type: "DOSSIER_ATTRIBUTE",
  },
  {
    privilege: PrivilegeEnum.PRIVILEGE_EVENT_DOSSIER_CALCULATED_ATTRIBUTE_UPDATED.name,
    type: "DOSSIER_CALCULATED_ATTRIBUTE",
  },
  {
    privilege: PrivilegeEnum.PRIVILEGE_EVENT_DOSSIER_INDICATOR_UPDATED.name,
    type: "DOSSIER_INDICATOR"
  },
  {
    privilege: PrivilegeEnum.PRIVILEGE_EVENT_THIRD_PARTY_DATA_HUB_UPDATED.name,
    type: "DATA_HUB_SOURCE"
  },
];


export const findRoleByIdInHierarchy = (roleId, role) => {
  if (role.id === roleId) {
    return role;
  } else {

    for (const derivedRole of role.derivedRoles) {
      const foundRole = findRoleByIdInHierarchy(roleId, derivedRole);

      if (foundRole) {
        return foundRole;
      }
    }

  }

  return null;
}

export const findRoleByIdInHierarchyFromList = (roleId, roles) => {
  if (!_.isEmpty(roles)) {
    for (const role of roles) {
      const foundRole = findRoleByIdInHierarchy(roleId, role);

      if (foundRole) {
        return foundRole;
      }
    }
  }

  return null;
}

export const cleanRoleHierarchy = (roles) => {
  const idsToRemove = new Set();

  roles.forEach((role) => {
    for (const potentialDescendant of roles) {
      let foundRole = findRoleByIdInHierarchy(potentialDescendant.id, role);

      if (!foundRole || (foundRole.id === role.id)) {
        continue;
      }

      idsToRemove.add(potentialDescendant.id);
    }
  })

  return roles.filter((role) => !idsToRemove.has(role.id));
}

export const getAllChildRoles = (role) => {
  let childRoles = new Set();

  role.derivedRoles.forEach(dr => {
    childRoles.add({ id: dr.id, name: dr.name, baseRoleId: dr.baseRoleId });

    let ccr = getAllChildRoles(dr);

    ccr.forEach(ddr => {
      childRoles.add(ddr);
    })
  })

  return Array.from(childRoles);
}

export const getFlatRoles = (roles) => {
  let flatRoles = new Set();

  roles.forEach(role => {
    flatRoles.add({ id: role.id, name: role.name, baseRoleId: role.baseRoleId })

    getAllChildRoles(role).forEach(childRole => {
      flatRoles.add({ id: childRole.id, name: childRole.name, baseRoleId: childRole.baseRoleId })
    })
  });

  return Array.from(flatRoles);
}

export const getSortedFlatRoles = (roles) => {
  return getFlatRoles(roles).sort(function (a, b) {
    let labelA = a.name.toUpperCase();
    let labelB = b.name.toUpperCase();

    if (labelA < labelB) { return -1; }
    if (labelA > labelB) { return 1; }
    return 0;
  });
}

export const getAvailableValueOptions = (field, criterionFieldOptions) => {
  let criterionFieldEntry = _.find(criterionFieldOptions, { value: field });

  return criterionFieldEntry?.valueOptions || [];
};

export const getCriterionOperatorOptions = () => {
  return {
    TEXT: [
      {
        value: Operator.EQUAL,
        label: i18next.t("operator.equalTo"),
      },
      // {
      //     value: Operator.NOT_EQUAL,
      //     label: "N'est pas égal à",
      // },
      {
        value: Operator.CONTAINS,
        label: i18next.t("operator.contains"),
      },
      // {
      //     value: Operator.NOT_CONTAINS,
      //     label: "Ne contient pas",
      // },
      {
        value: Operator.STARTS_WITH,
        label: i18next.t("operator.startsWith"),
      },
      // {
      //     value: Operator.NOT_STARTS_WITH,
      //     label: "Ne commence pas par",
      // },
      {
        value: Operator.ENDS_WITH,
        label: i18next.t("operator.endsWith"),
      },
      // {
      //     value: Operator.NOT_ENDS_WITH,
      //     label: "Ne se termine pas par",
      // },
      {
        value: Operator.IS_NULL, //IS_EMPTY
        label: i18next.t("operator.isNull"),
      },
      {
        value: Operator.NOT_NULL, //IS_NOT_EMPTY
        label: i18next.t("operator.notNull"),
      },
      {
        value: Operator.IN_SET, //IS_ANY_OF
        label: i18next.t("operator.inSet"),
      },
      // {
      //     value: Operator.NOT_IN_SET, //IS_NOT_ANY_OF
      //     label: "Ne fait pas partie de",
      // },
    ],

    NUMERIC: [
      {
        value: Operator.EQUAL, // "="
        label: "="
      },
      {
        value: Operator.NOT_EQUAL, // "!="
        label: "!="
      },
      {
        value: Operator.GREATER_THAN, // ">"
        label: ">"
      },
      {
        value: Operator.GREATER_OR_EQUAL, // ">="
        label: ">="
      },
      {
        value: Operator.LESS_THAN, // "<"
        label: "<"
      },
      {
        value: Operator.LESS_OR_EQUAL, // "<="
        label: "<="
      },
      // {
      //     value: Operator.BETWEEN,
      //     label: "Est entre",
      // },
      {
        value: Operator.IS_NULL, //"IS_EMPTY"
        label: i18next.t("operator.isNull"),
      },
      {
        value: Operator.NOT_NULL, //"IS_NOT_EMPTY"
        label: i18next.t("operator.notNull"),
      },
      {
        value: Operator.IN_SET, //"IS_ANY_OF"
        label: i18next.t("operator.inSet"),
      },
      // {
      //     value: Operator.NOT_IN_SET, //"IS_NOT_ANY_OF",
      //     label: "Ne fait pas partie de"
      // }
    ],

    DATE: [
      {
        value: Operator.EQUAL, // ""IS
        label: i18next.t("operator.equal"),
      },
      {
        value: Operator.NOT_EQUAL, // "NOT"
        label: i18next.t("operator.notEqual"),
      },
      {
        value: Operator.GREATER_THAN, // "AFTER"
        label: i18next.t("operator.date.greaterThan"),
      },
      {
        value: Operator.GREATER_OR_EQUAL, // "ON_OR_AFTER"
        label: i18next.t("operator.date.greaterOrEqual"),
      },
      {
        value: Operator.LESS_THAN, // "BEFORE",
        label: i18next.t("operator.date.lessThan"),
      },
      {
        value: Operator.LESS_OR_EQUAL, // "ON_OR_BEFORE"
        label: i18next.t("operator.date.lessOrEqual"),
      },
      // {
      //     value: Operator.BETWEEN,
      //     label: "Est entre",
      // },
      {
        value: Operator.IS_NULL, // "IS_EMPTY"
        label: i18next.t("operator.isNull"),
      },
      {
        value: Operator.NOT_NULL, //IS_NOT_EMPTY"
        label: i18next.t("operator.notNull"),
      },
      // {
      //   value: Operator.IN_SET, //"IS_ANY_OF",
      //   label: "Fait partie de",
      // },
      // {
      //     value: Operator.NOT_IN_SET, //"IS_NOT_ANY_OF",
      //     label: "Ne fait pas partie de",
      // }
    ],

    BOOLEAN: [
      {
        value: Operator.EQUAL, // "IS"
        label: i18next.t("operator.equal"),
      },
      {
        value: Operator.NOT_EQUAL, //"NOT"
        label: i18next.t("operator.notEqual"),
      },
      {
        value: Operator.IN_SET, //"IS_ANY_OF"
        label: i18next.t("operator.inSet"),
      },
      // {
      //     value: Operator.NOT_IN_SET, //"IS_NOT_ANY_OF"
      //     label: "Ne fait pas partie de"
      // }
    ],

    CUSTOM: [
      {
        value: Operator.EQUAL, // "IS"
        label: i18next.t("operator.equal"),
      },
      {
        value: Operator.NOT_EQUAL, //"NOT"
        label: i18next.t("operator.notEqual"),
      },
      {
        value: Operator.IN_SET, //"IS_ANY_OF"
        label: i18next.t("operator.inSet"),
      },
      // {
      //     value: Operator.NOT_IN_SET, //"IS_NOT_ANY_OF"
      //     label: "Ne fait pas partie de"
      // }
    ],

    ATTRIBUTE: [
      /*For all attribute types: LIST_MULTIPLE, LIST_UNIQUE, DECIMAL, INTEGER, PERCENTAGE, BOOLEAN*/
      {
        value: Operator.EQUAL, //EQUALS
        label: i18next.t("operator.equalTo"),
      },
      // {
      //     value: Operator.NOT_EQUAL,
      //     label: "N'est pas égal à",
      // },

      /*For LIST_MULTIPLE, LIST_UNIQUE, */
      {
        value: Operator.CONTAINS,
        label: i18next.t("operator.contains"),
      },
      // {
      //     value: Operator.NOT_CONTAINS,
      //     label: "Ne contient pas",
      // },
      {
        value: Operator.STARTS_WITH,
        label: i18next.t("operator.startsWith"),
      },
      // {
      //     value: Operator.NOT_STARTS_WITH,
      //     label: "Ne commence pas par",
      // },
      {
        value: Operator.ENDS_WITH,
        label: i18next.t("operator.endsWith"),
      },
      // {
      //     value: Operator.NOT_ENDS_WITH,
      //     label: "Ne se termine pas par",
      // },

      /*For: DECIMAL, INTEGER, PERCENTAGE*/
      {
        value: Operator.GREATER_THAN, // ">"
        label: i18next.t("operator.greaterThan"),
      },
      {
        value: Operator.GREATER_OR_EQUAL, // ">="
        label: i18next.t("operator.greaterOrEqual"),
      },
      {
        value: Operator.LESS_THAN, // "<"
        label: i18next.t("operator.lessThan"),
      },
      {
        value: Operator.LESS_OR_EQUAL, // "<="
        label: i18next.t("operator.lessOrEqual"),
      },
      // {
      //     value: Operator.BETWEEN,
      //     label: "Est entre",
      // },

      /*For all attribute types: LIST_MULTIPLE, LIST_UNIQUE, DECIMAL, INTEGER, PERCENTAGE, BOOLEAN*/
      {
        value: Operator.IS_NULL, //IS_EMPTY
        label: i18next.t("operator.isNull"),
      },

      /*For all attribute types: LIST_MULTIPLE, LIST_UNIQUE, DECIMAL, INTEGER, PERCENTAGE, BOOLEAN*/
      {
        value: Operator.NOT_NULL, //IS_NOT_EMPTY
        label: i18next.t("operator.notNull"),
      },
      /*For all attribute types: LIST_MULTIPLE, LIST_UNIQUE*/
      {
        value: Operator.IN_SET, //IS_ANY_OF
        label: i18next.t("operator.inSet"),
      },
      // {
      //     value: Operator.NOT_IN_SET, //IS_NOT_ANY_OF
      //     label: "Ne fait pas partie de",
      // },
    ],

    RISK: [
      {
        value: Operator.EQUAL, // "IS"
        label: i18next.t("operator.equal"),
      },
      {
        value: Operator.NOT_EQUAL, //"NOT"
        label: i18next.t("operator.notEqual"),
      },
      {
        value: Operator.IN_SET, //"IS_ANY_OF"
        label: i18next.t("operator.inSet"),
      },
      // {
      //     value: Operator.NOT_IN_SET, //"IS_NOT_ANY_OF"
      //     label: "Ne fait pas partie de"
      // }
      {
        value: Operator.IS_NULL, //"IS_EMPTY"
        label: i18next.t("operator.isNull"),
      },
      // {
      //   value: Operator.NOT_NULL, //"IS_NOT_EMPTY"
      //   label: "N'est pas vide",
      // },
    ],

    CALCULATED_ATTRIBUTE: [
      {
        value: Operator.EQUAL,
        label: i18next.t("operator.equalTo"),
      },
      {
        value: Operator.CONTAINS,
        label: i18next.t("operator.contains"),
      },
      {
        value: Operator.STARTS_WITH,
        label: i18next.t("operator.startsWith"),
      },
      {
        value: Operator.ENDS_WITH,
        label: i18next.t("operator.endsWith"),
      },
      {
        value: Operator.GREATER_THAN,
        label: i18next.t("operator.greaterThan"),
      },
      {
        value: Operator.GREATER_OR_EQUAL,
        label: i18next.t("operator.greaterOrEqual"),
      },
      {
        value: Operator.LESS_THAN,
        label: i18next.t("operator.lessThan"),
      },
      {
        value: Operator.LESS_OR_EQUAL,
        label: i18next.t("operator.lessOrEqual"),
      },
      {
        value: Operator.IS_NULL,
        label: i18next.t("operator.isNull"),
      },
      {
        value: Operator.NOT_NULL,
        label: i18next.t("operator.notNull"),
      },
      {
        value: Operator.IN_SET,
        label: i18next.t("operator.inSet"),
      },
    ],

    INDICATOR: [
      {
        value: Operator.EQUAL,
        label: i18next.t("operator.equalTo"),
      },
      {
        value: Operator.CONTAINS,
        label: i18next.t("operator.contains"),
      },
      {
        value: Operator.STARTS_WITH,
        label: i18next.t("operator.startsWith"),
      },
      {
        value: Operator.ENDS_WITH,
        label: i18next.t("operator.endsWith"),
      },
      {
        value: Operator.GREATER_THAN,
        label: i18next.t("operator.greaterThan"),
      },
      {
        value: Operator.GREATER_OR_EQUAL,
        label: i18next.t("operator.greaterOrEqual"),
      },
      {
        value: Operator.LESS_THAN,
        label: i18next.t("operator.lessThan"),
      },
      {
        value: Operator.LESS_OR_EQUAL,
        label: i18next.t("operator.lessOrEqual"),
      },
      {
        value: Operator.IS_NULL,
        label: i18next.t("operator.isNull"),
      },
      {
        value: Operator.NOT_NULL,
        label: i18next.t("operator.notNull"),
      },
      {
        value: Operator.IN_SET,
        label: i18next.t("operator.inSet"),
      },
    ],

    ACTIVITY: [
      {
        value: Operator.EQUAL, // "IS"
        label: i18next.t("operator.equal"),
      },
      {
        value: Operator.NOT_EQUAL, //"NOT"
        label: i18next.t("operator.notEqual"),
      },
      {
        value: Operator.IN_SET, //"IS_ANY_OF"
        label: i18next.t("operator.inSet"),
      }
    ]

  };
}

export const getAvailableOperatorOptions = (field, criterionFieldOptions) => {
  let operatorOptions = [];
  let criterionFieldEntry = _.find(criterionFieldOptions, { value: field });
  if (criterionFieldEntry !== undefined) {
    operatorOptions = getCriterionOperatorOptions()[criterionFieldEntry.type];

    switch (criterionFieldEntry.type) {
      case FieldType.ATTRIBUTE:
        switch (criterionFieldEntry.subType) {
          case AttributeType.LIST_UNIQUE:
            operatorOptions = operatorOptions.filter((entry) => ![
              Operator.GREATER_THAN,
              Operator.GREATER_OR_EQUAL,
              Operator.GREATER_OR_EQUAL,
              Operator.LESS_THAN,
              Operator.LESS_OR_EQUAL,
              Operator.BETWEEN,
            ].includes(entry.value));

            break;

          case AttributeType.LIST_MULTIPLE:
            operatorOptions = operatorOptions.filter((entry) => ![
              Operator.STARTS_WITH,
              Operator.NOT_STARTS_WITH,
              Operator.ENDS_WITH,
              Operator.NOT_ENDS_WITH,
              Operator.GREATER_THAN,
              Operator.GREATER_OR_EQUAL,
              Operator.GREATER_OR_EQUAL,
              Operator.LESS_THAN,
              Operator.LESS_OR_EQUAL,
              Operator.BETWEEN,
              Operator.IN_SET,
              Operator.NOT_IN_SET,
            ].includes(entry.value)).map((entry) => {
              switch (entry.value) {
                case Operator.CONTAINS:
                  return {
                    value: entry.value,
                    label: i18n.t("operator.containsAtLeast"),
                  }

                default:
                  return entry;
              }
            });

            break;

          case AttributeType.DECIMAL:
          case AttributeType.INTEGER:
          case AttributeType.PERCENTAGE:
            operatorOptions = operatorOptions.filter((entry) => ![
              Operator.CONTAINS,
              Operator.NOT_CONTAINS,
              Operator.STARTS_WITH,
              Operator.NOT_STARTS_WITH,
              Operator.ENDS_WITH,
              Operator.NOT_ENDS_WITH,
            ].includes(entry.value));

            break;

          case AttributeType.BOOLEAN:
            operatorOptions = operatorOptions.filter((entry) => ![
              Operator.CONTAINS,
              Operator.NOT_CONTAINS,
              Operator.STARTS_WITH,
              Operator.NOT_STARTS_WITH,
              Operator.ENDS_WITH,
              Operator.NOT_ENDS_WITH,
              Operator.GREATER_THAN,
              Operator.GREATER_OR_EQUAL,
              Operator.GREATER_OR_EQUAL,
              Operator.LESS_THAN,
              Operator.LESS_OR_EQUAL,
              Operator.BETWEEN,
              Operator.IN_SET,
              Operator.NOT_IN_SET,
            ].includes(entry.value));
            break;

          case AttributeType.DATE:
            operatorOptions = operatorOptions.filter((entry) => ![
              Operator.CONTAINS,
              Operator.NOT_CONTAINS,
              Operator.STARTS_WITH,
              Operator.NOT_STARTS_WITH,
              Operator.ENDS_WITH,
              Operator.NOT_ENDS_WITH,
              Operator.BETWEEN, //TODO: Add these 3 later if asked for
              Operator.IN_SET,
              Operator.NOT_IN_SET,
            ].includes(entry.value)).map((entry) => formatDateOperatorLabel(entry));
            break;

          default:
            break;
        }
        break;

      case FieldType.CALCULATED_ATTRIBUTE:
      case FieldType.INDICATOR:
        switch (criterionFieldEntry.subType) {
          case CalculationResultType.STRING:
            operatorOptions = operatorOptions.filter((entry) => ![
              Operator.GREATER_THAN,
              Operator.GREATER_OR_EQUAL,
              Operator.GREATER_OR_EQUAL,
              Operator.LESS_THAN,
              Operator.LESS_OR_EQUAL,
              Operator.BETWEEN,
            ].includes(entry.value));

            break;

          case CalculationResultType.ARITHMETIC_FORMULA_OUTPUT:
            operatorOptions = operatorOptions.filter((entry) => ![
              Operator.CONTAINS,
              Operator.NOT_CONTAINS,
              Operator.STARTS_WITH,
              Operator.NOT_STARTS_WITH,
              Operator.ENDS_WITH,
              Operator.NOT_ENDS_WITH,
            ].includes(entry.value));
            break;

          case CalculationResultType.BOOLEAN:
            operatorOptions = operatorOptions.filter((entry) => ![
              Operator.CONTAINS,
              Operator.NOT_CONTAINS,
              Operator.STARTS_WITH,
              Operator.NOT_STARTS_WITH,
              Operator.ENDS_WITH,
              Operator.NOT_ENDS_WITH,
              Operator.GREATER_THAN,
              Operator.GREATER_OR_EQUAL,
              Operator.GREATER_OR_EQUAL,
              Operator.LESS_THAN,
              Operator.LESS_OR_EQUAL,
              Operator.BETWEEN,
              Operator.IN_SET,
              Operator.NOT_IN_SET,
            ].includes(entry.value));

            break;

          default:
            break;
        }
        break;

      default:
        break;
    }
  }

  return operatorOptions;
};

// All event tasks, add new ones when ready.
export const EVENT_TASKS = [
  PrivilegeEnum.PRIVILEGE_EVENT_DOCUMENT_TASK_AWAITING_VALIDATION,
  PrivilegeEnum.PRIVILEGE_EVENT_DOCUMENT_TASK_DUE_DILIGENCE,
  PrivilegeEnum.PRIVILEGE_EVENT_TASK_INDUED_FALSE_POSITIVE,
]
